import { Component, type ErrorInfo, Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { BrowserRouter, Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  Banknote,
  BarChart3,
  BellRing,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  Clock3,
  CreditCard,
  Download,
  FileDown,
  Filter,
  Megaphone,
  Receipt,
  Search,
  TrendingUp,
  Upload,
  UserPlus,
  Users,
  WalletCards
} from "lucide-react";
import {
  bookingFeedbackUpdateInputSchema,
  bookingCreateInputSchema,
  bookingStatusValues,
  customerInputSchema,
  customerMagicLinkRequestSchema,
  customerPortalProfileUpdateSchema,
  giftCardPurchaseSessionInputSchema,
  loginInputSchema,
  normalizeAdminDetailsRecord,
  publicBookingCreateInputSchema,
  publicWaitlistCreateInputSchema,
  type BookingFeedback,
  type BookingFeedbackPhoto,
  type CustomerPortalSession,
  type EmailTemplateInput,
  type GiftCardStorefrontSettings,
  type GiftCardTheme,
  type GiftCardThemeSnapshot,
  type InvoiceDraftSnapshot,
  type LoginMfaChallengeResponse,
  type LoginResponse,
  type SessionUser,
  type StaffFeedbackFeedItem
} from "@booking-seconds/types";
import { AppShell, Badge, Button, Card, DataTable, Field, SectionHeader } from "@booking-seconds/ui";
import {
  Temporal,
  buildSmsReminderOptInDisclosure,
  configureMoneyFormatting,
  formatCurrency,
  resolveCheckoutDepositTerms,
  type QuoteComputationResult
} from "@booking-seconds/utils";
import { relationalSettingsSections, singletonSettingsSections } from "@booking-seconds/db";
import { apiFetch, getCookie } from "./lib/api";
import { CommunicationsPage } from "./communications";
import { DateInput } from "./date-input";
import { DashboardPage } from "./pages/admin/dashboard";
import { OpsHeader, OpsMetricCard, OpsMetricGrid, OpsPageShell } from "./pages/admin/ops-ui";
import { GiftCardThemePreview } from "./gift-card-theme-preview";
import { GiftCardsPage } from "./gift-cards";
import { LeadsPage } from "./leads";
import { DispatcherPage, SchedulerPage } from "./scheduler";
import { SettingsSectionPage } from "./settings";
import { getArrivalFlexibilityDiscountSummary } from "../shared/arrival-flexibility";
import {
  getPublicBookingSubmitLabel,
  PUBLIC_BOOKING_LEGAL_CONSENT_ERROR,
  resolvePublicBookingLegalPagesConfig
} from "../shared/legal-consent";
import { customerSmsTemplates } from "../shared/customer-message-templates";
import { StripePaymentCard, type StripePaymentCardHandle } from "./stripe-payment-card";

const queryClient = new QueryClient();

type CustomerTabKey =
  | "overview"
  | "history"
  | "properties"
  | "payments"
  | "files"
  | "issues"
  | "internal-notes";

type SectionJumpItem<TTab extends string = string> = {
  id: string;
  label: string;
  tab?: TTab;
};

type PublicClientConfig = {
  publicBaseUrl: string;
  turnstileSiteKey: string | null;
  currency: string;
  locale: string;
  defaultTimezone?: string;
  frequencyOptions?: Array<{
    id: string;
    bookingSecondsFrequencyRuleId?: string;
    code: string;
    label: string;
    discountType: string;
    discountValue: number;
    displayOrder: number;
    active: boolean;
    isDefault?: boolean;
  }>;
};

type GiftCardStorefrontConfigResponse = {
  publicBaseUrl: string;
  currency: string;
  locale: string;
  defaultTimezone: string;
  storefront: GiftCardStorefrontSettings;
  themes: GiftCardTheme[];
};

declare global {
  interface Window {
    Stripe?: (publishableKey: string) => {
      elements: (options: { clientSecret: string; appearance?: Record<string, unknown> }) => {
        create: (type: string) => {
          mount: (element: HTMLElement) => void;
          unmount?: () => void;
          destroy?: () => void;
        };
      };
      confirmPayment: (options: {
        elements: {
          create: (type: string) => {
            mount: (element: HTMLElement) => void;
            unmount?: () => void;
            destroy?: () => void;
          };
        };
        redirect?: "if_required" | "always";
        confirmParams?: Record<string, unknown>;
      }) => Promise<{ error?: { message?: string }; paymentIntent?: { status?: string } }>;
      confirmSetup: (options: {
        elements: {
          create: (type: string) => {
            mount: (element: HTMLElement) => void;
            unmount?: () => void;
            destroy?: () => void;
          };
        };
        redirect?: "if_required" | "always";
        confirmParams?: Record<string, unknown>;
      }) => Promise<{ error?: { message?: string }; setupIntent?: { status?: string } }>;
    };
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          appearance?: "always" | "execute" | "interaction-only";
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        }
      ) => string;
      remove?: (widgetId: string) => void;
    };
    __bookingSecondsTurnstileLoader?: Promise<void>;
    __bookingSecondsStripeLoader?: Promise<void>;
    __bookingSecondsGoogleMapsLoader?: Promise<void>;
    __bookingSecondsGoogleMapsAuthFailed?: boolean;
    gm_authFailure?: () => void;
    google?: {
      maps?: {
        places?: {
          Autocomplete: new (
            element: HTMLInputElement,
            options?: { types?: string[]; componentRestrictions?: { country?: string | string[] }; fields?: string[] }
          ) => {
            addListener: (eventName: string, handler: () => void) => { remove?: () => void };
            getPlace: () => any;
          };
        };
      };
    };
  }
}

type QuotePreview = QuoteComputationResult & {
  serviceName: string;
  basePriceAmount?: number;
  baseDurationMinutes?: number;
  hourlyRateCentsPerMaidHour?: number;
  optionNames: string[];
  optionDetails?: Array<{
    id: string;
    name: string;
    priceDeltaAmount: number;
    durationDeltaMinutes: number;
    quantity?: number;
    quantityLabel?: string;
  }>;
  promotionCode: string | null;
  promoStatus?: string | null;
  promoMessage?: string | null;
  frequencyRuleName?: string | null;
  frequencyDiscount?: number;
  arrivalFlexibilityEligible?: boolean;
  arrivalFlexibilitySelected?: boolean;
  arrivalFlexibilityLabel?: string;
  arrivalFlexibilityDiscount?: number;
  customerSuppliesEquipmentSelected?: boolean;
  customerSuppliesEquipmentDiscount?: number;
  customerSuppliesEquipmentRequiredItems?: string[];
  promoDiscount?: number;
  scenarioCode?: string;
  scenarioName?: string;
  scenarioPricingMode?: string;
  scenarioCustomerNote?: string;
  pricingDisplayMode?: "exact" | "manual_review" | "remodeling_hourly" | "remodeling_review" | string;
  remodelingOptionCode?: string | null;
  remodelingOptionLabel?: string | null;
  remodelingHourlyRatePerMaidHour?: number;
  remodelingMinimumMaidCount?: number;
  remodelingMinimumHours?: number;
  remodelingMinimumLaborHours?: number;
  remodelingStartingAtAmount?: number;
  skipAreaDeductionAmount?: number;
  skipAreaDurationDeductionMinutes?: number;
  skipAreaDeductionRows?: Array<{
    code: string;
    label: string;
    quantityLabel: string;
    oneMaidMinutes: number;
    twoMaidMinutes: number;
    amountCents: number;
  }>;
  giftCardCode?: string | null;
  giftCardMaskedCode?: string | null;
  giftCardStatus?: string | null;
  giftCardMessage?: string | null;
  giftCardAppliedAmount?: number;
  giftCardRemainingBalance?: number;
  remainingBookingBalance?: number;
};

const ADDRESS_AUTOCOMPLETE_FALLBACK_MESSAGE =
  "Address autocomplete is unavailable right now. Continue entering the address manually.";
type BookingFormSettings = {
  stepOrder: string[];
  enableExtrasStep: boolean;
  enableFrequencyStep: boolean;
  enableCalendarStep: boolean;
  enableLocationSelection: boolean;
  enablePromoCodes: boolean;
  enablePaymentPreference: boolean;
  depositRate: number;
  arrivalFlexibility?: {
    enabled?: boolean;
    startTime?: string;
    endTime?: string;
    discountType?: "fixed" | "percentage" | string;
    discountValue?: number;
    excludedWindowStartTimes?: string[];
  };
  customerSuppliesEquipment?: {
    enabled?: boolean;
    discountAmount?: number;
    requiredItems?: string[];
  };
};

const customerSuppliesEquipmentFallbackItems = [
  "Vacuum cleaner",
  "Mop and bucket or reusable floor mop system",
  "All-purpose cleaner",
  "Bathroom cleaner",
  "Glass cleaner",
  "Floor cleaner suitable for your floors",
  "Sponges, scrub pads, and microfiber towels",
  "Trash bags and paper towels",
  "Toilet brush",
  "Step stool, if high dusting is requested"
];

type BookingFormService = {
  id: string;
  name: string;
  icon?: string;
  displayOrder?: number;
  durationMinutes: number;
  basePriceAmount: number;
  assignedExtraIds?: string[];
  availableLocationIds?: string[];
  allowAllActiveFrequencyRules?: boolean;
  active?: number | boolean;
  staffOnly?: number | boolean;
  allowedFrequencyRuleIds?: string[];
  defaultCalendarProfileId?: string;
  extraVisibilityRules?: Record<string, unknown>;
};

type BookingFormServiceOption = {
  id: string;
  serviceId: string;
  name: string;
  description?: string;
  icon?: string;
  displayOrder?: number;
  quantityBased?: boolean;
  quantityLabel?: string;
  quantityUnitLabel?: string;
  quantityAdminDetailKey?: string;
  minQuantity?: number;
  maxQuantity?: number;
  priceDeltaAmount: number;
  durationDeltaMinutes: number;
  assignedServiceIds?: string[];
  excludeFromFrequencyDiscount?: boolean;
  excludeFromPromoDiscount?: boolean;
  recurringPolicy?: string;
  carpetShampooingConfig?: {
    enabled?: boolean;
    minimumOrderAmount?: number;
    units?: Array<{
      code: "rooms" | "hallways" | "steps" | "area_rugs";
      label: string;
      priceDeltaAmount?: number;
      durationDeltaMinutes?: number;
      maxQuantity?: number;
      displayOrder?: number;
      active?: number | boolean;
    }>;
  };
  active?: number | boolean;
};

type BookingFormServiceScenario = {
  id: string;
  baseServiceId: string;
  name: string;
  code: string;
  displayOrder?: number;
  pricingMode?: "exact" | "approximate" | "manual_review" | string;
  questionProfile?: "residential" | "office_commercial" | "building_common_areas" | "carpet_standalone" | string;
  remodelProfile?: "residential" | "office_commercial" | "building_common_areas" | "none" | string;
  extraFilterProfile?: "residential" | "office_commercial" | "building_common_areas" | "carpet_standalone" | string;
  customerNote?: string;
  internalNote?: string;
  squareFootageMin?: number | null;
  squareFootageMax?: number | null;
  priority?: number;
  showFrequency?: boolean;
  showHomeDetails?: boolean;
  showConditionSection?: boolean;
  showRemodelingSection?: boolean;
  showExtras?: boolean;
  showTip?: boolean;
  allowedSkipAreas?: string[];
  visibleExtraIds?: string[];
  remodelingPricingRules?: Array<{
    optionCode: string;
    optionLabel: string;
    hourlyRatePerMaidHour: number;
    minimumMaidCount: number;
    minimumHours: number;
    active?: boolean;
  }>;
  defaultFrequencyRuleId?: string;
  active?: number | boolean;
};

type ScenarioQuestionProfile = "residential" | "office_commercial" | "building_common_areas" | "carpet_standalone";
type ScenarioRemodelProfile = "residential" | "office_commercial" | "building_common_areas" | "none";
type ScenarioExtraFilterProfile = "residential" | "office_commercial" | "building_common_areas" | "carpet_standalone";

type BookingFormFrequencyRule = {
  id: string;
  name: string;
  code: string;
  cadenceLabel?: string;
  displayOrder?: number;
  isDefault?: boolean;
  applicableServiceIds?: string[];
  discountType: string;
  discountValue: number;
  active?: number | boolean;
};

type BookingFormCalendarProfile = {
  id: string;
  name: string;
  linkedLocationIds?: string[];
  linkedBookingSpotIds?: string[];
  allowedWeekdays?: string[];
  blackoutDates?: string[];
  advanceNoticeMinutes?: number;
  bookingWindowDays?: number;
  active?: number | boolean;
};

type BookingFormRuntimeConfig = {
  general: {
    businessName?: string;
    taxRate?: number;
    showTaxOnBookingForm?: boolean;
  };
  bookingForm: BookingFormSettings;
  googleMapsApi?: {
    apiKey?: string;
    enableAddressAutocomplete?: boolean;
    hasAddressAutocomplete?: boolean;
  };
  textMessages?: {
    offerReminderOptInOnBookingForm?: boolean;
    preselectReminderOptInOnBookingForm?: boolean;
  };
  waitlist?: {
    enabled?: boolean;
    sendEmailNotifications?: boolean;
    sendSmsNotifications?: boolean;
    emailSubjectTemplate?: string;
    emailBodyTemplate?: string;
    smsBodyTemplate?: string;
  };
  other?: {
    legalPages?: {
      termsUrl?: string;
      privacyUrl?: string;
      termsVersion?: string;
      privacyVersion?: string;
    };
  };
  services: BookingFormService[];
  serviceScenarios: BookingFormServiceScenario[];
  serviceOptions: BookingFormServiceOption[];
  frequencyRules: BookingFormFrequencyRule[];
  calendarProfiles: BookingFormCalendarProfile[];
  locations: LocationSettingsItem[];
  bookingSpots: BookingSpotSettingsItem[];
};

type BookingListItem = {
  id: string;
  publicReference: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  serviceAddress: string;
  status: string;
  startsAt: string;
  endsAt: string;
  amountDue: number;
  locationId: string;
  locationName: string;
  timeZone: string;
  serviceDateLocal: string;
  serviceTimeLabel: string;
  frequencyLabel: string;
  assignmentSummary: string;
};

type BookingsIndexResponse = {
  items: BookingListItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  selectedDate: string;
  timeZone: string;
};

type BookingsCalendarSummaryResponse = {
  month: string;
  timeZone: string;
  items: Array<{
    date: string;
    count: number;
  }>;
};

type ZillowPropertySummary = {
  address?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  bedrooms?: number | null;
  bathrooms?: number | null;
  livingArea?: number | null;
  yearBuilt?: number | null;
  homeType?: string;
  price?: number | null;
  zestimate?: number | null;
  rentZestimate?: number | null;
  lastSoldPrice?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  zpid?: string;
  zillowUrl?: string;
  photosCount?: number;
  priceHistoryCount?: number;
  schoolsCount?: number;
  hasAgent?: boolean;
  agentName?: string;
  agentEmail?: string;
};

type ZillowEnrichmentRecord = {
  id: string;
  accountId: string;
  customerId: string;
  customerAddressId: string;
  bookingId: string | null;
  provider: string;
  zpid: string;
  zillowUrl: string;
  lookupSource: string;
  status: string;
  fetchedAt: string | null;
  summary: ZillowPropertySummary;
  rawResponse: unknown;
  error: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

type ZillowSearchCandidate = {
  id: string;
  zpid: string;
  zillowUrl: string;
  address: string;
  summary: ZillowPropertySummary;
};

type BookingPropertyLookupSummary = {
  provider: "zillow" | "redfin" | "rentcast" | "estated";
  confidence: "high" | "medium" | "low" | "none";
  squareFeet: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  propertyType: string;
  yearBuilt: number | null;
  sourceId: string;
  sourceUrl: string;
};

type BookingPropertyLookupResponse = {
  addressQuery: string;
  summary: BookingPropertyLookupSummary | null;
  providerStatuses: Array<{
    provider: "zillow" | "redfin" | "rentcast" | "estated";
    status: "success" | "not_configured" | "no_match" | "failed" | "timeout";
    message?: string;
    durationMs?: number;
    summary?: BookingPropertyLookupSummary | null;
  }>;
};

type BookingDetail = {
  id: string;
  customerId: string;
  customerAddressId?: string;
  publicReference: string;
  customerName: string;
  customerCompanyName: string;
  customerEmail: string;
  customerPhone: string;
  customerSecondaryPhone: string;
  customerType: string;
  pets: string;
  sensitivities: string;
  locationName: string;
  serviceAddress: string;
  status: string;
  startsAt: string;
  endsAt: string;
  totalAmount: number;
  amountDue: number;
  notes?: string;
  internalNotes?: string;
  adminDetails: Record<string, string>;
  zillowEnrichment?: ZillowEnrichmentRecord | null;
  feedback?: BookingFeedback | null;
  items: Array<{
    id: string;
    name: string;
    durationMinutes: number;
    totalPriceAmount: number;
  }>;
  payments: Array<{
    id: string;
    provider: string;
    providerPaymentId?: string | null;
    amount: number;
    paymentType: string;
    status: string;
    receiptUrl?: string | null;
    createdAt: string;
  }>;
  refunds: Array<{
    id: string;
    amount: number;
    reason: string;
    status: string;
    createdAt: string;
  }>;
  assignments: Array<{
    id: string;
    staffId: string;
    staffName: string;
    roleLabel: string;
  }>;
};

type InvoiceRevisionSummary = {
  id: string;
  version: number;
  status: string;
  pdfKey?: string | null;
  sentAt?: string | null;
  sentTo?: string | null;
  emailSubject?: string | null;
  createdAt: string;
  pdfDownloadUrl?: string | null;
};

type InvoiceEventSummary = {
  id: string;
  eventType: string;
  metadata: Record<string, unknown>;
  createdAt: string;
};

type InvoiceDetail = {
  id: string;
  bookingId: string;
  customerId: string;
  invoiceNumber: string;
  status: string;
  issueDate: string;
  dueDate: string;
  currentVersion: number;
  subtotalAmount: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  draft: InvoiceDraftSnapshot;
  latestRevisionId?: string | null;
  lastSentAt?: string | null;
  revisions: InvoiceRevisionSummary[];
  events: InvoiceEventSummary[];
  defaultRecipientEmail: string;
  defaultEmailSubject: string;
  defaultEmailBody: string;
  pdfDownloadUrl?: string | null;
};

type BookingChangeFieldChange = {
  path: string;
  fieldLabel: string;
  changeType: "added" | "removed" | "updated";
  before: unknown;
  after: unknown;
};

type BookingChangeLogEntry = {
  id: string;
  action: string;
  source: string;
  actorType: string;
  actorUserId?: string | null;
  actorDisplay: string;
  relatedEntityType?: string | null;
  relatedEntityId?: string | null;
  summary: string;
  metadata: Record<string, unknown>;
  changes: BookingChangeFieldChange[];
  createdAt: string;
};

type PaymentListItem = {
  id: string;
  status: string;
  payment_type: string;
  amount: number;
  provider: string;
  created_at: string;
  public_reference: string;
  customer_name: string;
};

type PaymentChargeQueueItem = {
  id: string;
  publicReference: string;
  status: string;
  paymentStatus: "unpaid" | "partially_paid" | "paid" | "on_hold" | "refunded";
  startsAt: string;
  endsAt: string;
  serviceDateLocal: string;
  timeZone: string;
  locationId: string;
  locationName: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  serviceAddress: string;
  serviceSummary: string;
  subtotalAmount: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  refundedAmount: number;
  succeededPaymentAmount: number;
  paymentHoldAt: string;
  paymentHoldReason: string;
  paymentMethodBucket: string;
  legacyPaymentMethod: string;
  savedCardBrand: string;
  savedCardLast4: string;
  latestPaymentId: string;
  latestPaymentProvider: string;
  latestProviderIntentId: string;
  latestProviderPaymentId: string;
  latestReceiptUrl: string;
  refundablePaymentId: string;
  refundableAmount: number;
  invoiceId: string;
  invoiceNumber: string;
  invoiceStatus: string;
  assignedMaids: Array<{ staffId: string; name: string }>;
  laborHours: number;
  assignedMaidCount: number;
  perMaidHours: number;
  estimatedMaidPayAmount: number | null;
  estimatedMaidPaySource: string;
  canChargeCard: boolean;
  canMarkPaid: boolean;
  canRefund: boolean;
  canSendInvoice: boolean;
};

type PaymentChargeQueueResponse = {
  items: PaymentChargeQueueItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  startDate: string;
  endDate: string;
  timeZone: string;
  summary: {
    totalAmountDue: number;
    totalAmountPaid: number;
    totalLaborHours: number;
    totalEstimatedMaidPay: number;
    chargeableCount: number;
  };
};

type PaymentChargeBatch = {
  id: string;
  actionMode: string;
  status: string;
  totalItems: number;
  succeededItems: number;
  failedItems: number;
  skippedItems: number;
  createdAt: string;
  completedAt?: string | null;
  items: Array<{
    id: string;
    bookingId: string;
    publicReference: string;
    customerName: string;
    amount: number;
    status: string;
    message: string;
  }>;
};

type BookingPaymentSession = {
  publishableKey: string;
  clientSecret: string;
  paymentIntentId: string;
  setupIntentId?: string;
  status: string;
  amount: number;
  mode?: "payment" | "authorization" | "setup" | "not_required";
};

type GiftCardPurchaseSession = BookingPaymentSession & {
  orderId: string;
  confirmationToken: string;
  giftCardThemeId: string;
  giftCardTheme: GiftCardThemeSnapshot;
  scheduledFor: string;
  schedulingPolicy: string;
  buyerRevealPolicy: string;
  deliverySkipped: boolean;
};

type GiftCardBackgroundUpload = {
  key: string;
  name: string;
  contentType: string;
  size: number;
  backgroundImageUrl: string;
};

type GiftCardOrderConfirmation = {
  id: string;
  status: string;
  publicStatus: string;
  amount: number;
  purchaserName: string;
  recipientName: string;
  recipientEmail: string;
  message: string;
  deliveredAt: string;
  fulfilledAt: string;
  scheduledFor: string;
  scheduledForLabel?: string;
  schedulingPolicy: string;
  buyerRevealPolicy: string;
  deliverySkipped: boolean;
  revealAvailable: boolean;
  giftCardCode: string;
  giftCardMaskedCode: string;
  giftCardBalance: number;
  giftCardStatus: string;
  giftCardThemeId: string;
  giftCardTheme: GiftCardThemeSnapshot;
  allowPrintPage: boolean;
  allowPdfDownload: boolean;
  printPageUrl?: string | null;
  pdfDownloadUrl?: string | null;
  storefrontLegalDisclaimer?: string;
  storefrontPrintFooterText?: string;
};

type ManageBookingData = {
  id: string;
  publicReference: string;
  customerName: string;
  status: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  depositAmount?: number;
  locationId: string;
  bookingSpotId: string;
  calendarProfileId?: string;
  recurringSeriesId?: string;
  serviceAddress?: string;
  adminDetails: Record<string, string>;
  items: Array<{
    id: string;
    serviceId: string;
    serviceOptionIdsJson?: string | null;
    name: string;
    totalPriceAmount: number;
    durationMinutes: number;
  }>;
  payments?: Array<{
    provider: string;
    status: string;
    paymentType: string;
    amount: number;
  }>;
};

type ExtrasSaveOutcome = {
  kind: "payment" | "saved" | "review";
  title: string;
  body: string;
  detail: string;
  updatedAmountDue: number;
};

function buildExtrasSaveOutcome(
  previous: { totalAmount?: number; amountDue?: number } | null | undefined,
  next: { totalAmount?: number; amountDue?: number } | null | undefined
): ExtrasSaveOutcome {
  const previousTotal = Math.max(0, Number(previous?.totalAmount ?? 0));
  const previousAmountDue = Math.max(0, Number(previous?.amountDue ?? 0));
  const updatedTotal = Math.max(0, Number(next?.totalAmount ?? previousTotal));
  const updatedAmountDue = Math.max(0, Number(next?.amountDue ?? previousAmountDue));
  const totalDelta = updatedTotal - previousTotal;
  const amountDueDelta = updatedAmountDue - previousAmountDue;
  const detail =
    totalDelta > 0
      ? `Previous total ${formatCurrency(previousTotal)}. Updated total ${formatCurrency(updatedTotal)}. ${formatCurrency(totalDelta)} was added.`
      : totalDelta < 0
        ? `Previous total ${formatCurrency(previousTotal)}. Updated total ${formatCurrency(updatedTotal)}. ${formatCurrency(Math.abs(totalDelta))} was removed.`
        : `Previous total ${formatCurrency(previousTotal)}. Updated total ${formatCurrency(updatedTotal)}. The total stayed the same.`;

  if (totalDelta < 0) {
    return {
      kind: "review",
      title: "Changes saved",
      body:
        updatedAmountDue > 0
          ? `Your updated total is lower. Our team will review any credit or refund, and ${formatCurrency(updatedAmountDue)} is currently due for this appointment.`
          : "Your updated total is lower. Our team will review any credit or refund based on the payment already on file.",
      detail,
      updatedAmountDue
    };
  }

  if (updatedAmountDue > 0 && amountDueDelta > 0) {
    return {
      kind: "payment",
      title: "Additional payment required",
      body: `Your extras changes are saved. ${formatCurrency(updatedAmountDue)} is now due for this updated appointment.`,
      detail,
      updatedAmountDue
    };
  }

  if (updatedAmountDue > 0) {
    return {
      kind: "saved",
      title: "Changes saved",
      body: `Your extras changes are saved. ${formatCurrency(updatedAmountDue)} is still due for this appointment.`,
      detail,
      updatedAmountDue
    };
  }

  if (totalDelta > 0) {
    return {
      kind: "saved",
      title: "Changes saved",
      body: "Your extras changes are saved. The total is higher, but no extra payment is needed right now.",
      detail,
      updatedAmountDue
    };
  }

  return {
    kind: "saved",
    title: "Changes saved",
    body: "Your extras changes are saved. No extra payment is needed for this update.",
    detail,
    updatedAmountDue
  };
}

type AddressSuggestion = {
  place: string;
  text: string;
  mainText: string;
  secondaryText: string;
};

type CustomerListItem = {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
  default_service_address: string;
  profile_photo_url: string;
  customer_type: string;
  account_status: string;
  email: string | null;
  phone: string;
  secondary_phone: string;
  notes: string;
  blocked_until: string | null;
  blocked_reason: string;
  lifetime_value_amount: number;
  outstanding_balance_amount: number;
  created_at: string;
  booking_count: number;
  active_booking_count: number;
  cancelled_booking_count: number;
  first_booking_at: string | null;
  last_booking_at: string | null;
  next_booking_at: string | null;
  lead_source_name: string | null;
};

type CustomerTag = {
  id: string;
  name: string;
  color: string;
};

type LeadSourceOption = {
  id: string;
  name: string;
};

type CustomerIssueEntryDraft = {
  bookingId: string;
  description: string;
};

type CustomerAddress = {
  id: string;
  label: string;
  line1: string;
  line2: string;
  unitNumber: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isPrimary: boolean | number;
  instructions: string;
  zillowEnrichment?: ZillowEnrichmentRecord | null;
};

type CustomerPortalBooking = {
  id: string;
  status: string;
  startsAt: string;
  timeZone: string;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  publicReference: string;
  locationName: string;
  serviceNames: string;
  serviceAddress: string;
};

type CustomerPortalSupport = {
  email: string;
  phone: string;
};

type CustomerPortalOverview = {
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    name: string;
    email: string;
    phone: string;
    secondaryPhone: string;
    emailPreferences: {
      smsOptInStatus?: boolean;
      smsReminderOptInStatus?: boolean;
      emailOptInStatus?: boolean;
      bookingConfirmation?: boolean;
      reminders?: boolean;
      receipts?: boolean;
      marketing?: boolean;
    };
      address: Omit<CustomerAddress, "id">;
  };
  support: CustomerPortalSupport;
  upcomingBookings: CustomerPortalBooking[];
  pastBookings: CustomerPortalBooking[];
};

type CustomerPortalBookingDetail = {
  id: string;
  status: string;
  startsAt: string;
  endsAt: string;
  timeZone: string;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  publicReference: string;
  locationName: string;
  serviceNames: string;
  serviceAddress: string;
  notes: string;
  frequencyLabel: string;
  extrasSummary: string;
  recurringExtrasSummary: string;
  addressInstructions: string;
  entryInformation: string;
  paymentStatus: string;
  items: Array<{
    id: string;
    name: string;
    totalPriceAmount: number;
    durationMinutes: number;
  }>;
  policy: {
    cancellationPolicy: string;
    changeCutoffAt: string;
    changesLocked: boolean;
    support: CustomerPortalSupport;
  };
  actions: {
    canManageOnline: boolean;
    canModifyOnline: boolean;
    canCompletePayment: boolean;
  };
};

type CustomerPortalRebookPrefill = {
  sourceBookingId: string;
  sourcePublicReference: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    secondaryPhone: string;
    emailPreferences: {
      smsReminderOptInStatus: boolean;
    };
    address: Omit<CustomerAddress, "id">;
  };
  locationId: string;
  serviceId: string;
  bookingSpotId: string;
  calendarProfileId: string;
  frequencyRuleId: string;
  optionIds: string[];
  adminDetails: Record<string, string>;
};

type CustomerHistoryBooking = {
  id: string;
  status: string;
  startsAt: string;
  totalAmount: number;
  amountDue: number;
  publicReference: string;
  locationName: string;
  serviceNames: string;
  teamNames: string;
  durationMinutes: number;
  notes?: string;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  invoiceStatus?: string | null;
  invoiceAmountDue?: number | null;
  invoiceLastSentAt?: string | null;
};

type CustomerPaymentRecord = {
  id: string;
  invoiceNumber: string;
  invoiceId?: string | null;
  createdAt: string;
  amount: number;
  provider: string;
  paymentType: string;
  status: string;
};

type CustomerDetail = {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string;
  profilePhotoUrl: string;
  customerType: string;
  accountStatus: string;
  email: string | null;
  phone: string;
  secondaryPhone: string;
  portalInvitedAt: string | null;
  portalActivatedAt: string | null;
  portalLastLoginAt: string | null;
  pets: string;
  sensitivities: string;
  documents: string[];
  notes: string;
  serviceFrequency: string;
  blockedUntil: string | null;
  blockedReason: string;
  preferredStaffIds: string[];
  blockedStaffIds: string[];
  preferredContactMethod: string;
  englishSpeakingStaff: boolean | number;
  createdAt: string;
  bookingCount: number;
  activeBookingCount: number;
  cancelledBookingCount: number;
  firstBookingAt: string | null;
  lastBookingAt: string | null;
  nextBookingAt: string | null;
  lifetimeValueAmount: number;
  outstandingBalanceAmount: number;
  leadSourceId: string | null;
  leadSourceName: string | null;
  leadSourceChannel: string | null;
  stripeCustomerId: string;
  paymentMethods: string[];
  propertyPhotos: string[];
  refundsDisputes: string[];
  problemAreas: string[];
  damageReports: string[];
  specialIncidents: string[];
  clientRating: number | null;
  customerSatisfactionScore: number | null;
  rewards: {
    points?: number;
    tier?: string;
    notes?: string;
  };
  emailPreferences: {
    smsOptInStatus?: boolean;
    smsReminderOptInStatus?: boolean;
    emailOptInStatus?: boolean;
    bookingConfirmation?: boolean;
    reminders?: boolean;
    receipts?: boolean;
    marketing?: boolean;
  };
  taxExempt: boolean | number;
  specialTaxRate: number | null;
  addresses: CustomerAddress[];
  zillowEnrichments?: ZillowEnrichmentRecord[];
  bookings: CustomerHistoryBooking[];
  feedbackHistory: Array<{
    bookingId: string;
    bookingPublicReference: string;
    startsAt: string;
    locationName: string;
    serviceName: string;
    effectiveRating: number | null;
    feedback: BookingFeedback;
  }>;
  payments: CustomerPaymentRecord[];
  tags: CustomerTag[];
};

type CustomerMarketingSummary = {
  customerId: string;
  marketingEnabled: boolean;
  lastCompletedBookingAt: string;
  lastMarketingEmailAt: string;
  nextAutomationName: string;
  nextAutomationDueAt: string;
  activeAutomationCount: number;
};

type EmailTemplateRecord = EmailTemplateInput & {
  id: string;
  accountId?: string;
  layoutName?: string;
  createdAt?: string;
  updatedAt?: string;
};

type CustomerTransferRecord = {
  id: string;
  createdAt: string;
  updatedAt: string;
  firstBookingAt: string | null;
  lastBookingAt: string | null;
  nextBookingAt: string | null;
  bookingCount: number;
  activeBookingCount: number;
  cancelledBookingCount: number;
  lifetimeValueAmount: number;
  outstandingBalanceAmount: number;
  leadSourceName: string | null;
  leadSourceChannel: string | null;
  tags: CustomerTag[];
  customer: any;
};

type CustomerTransferPayload = {
  version: number;
  exportedAt: string;
  items: CustomerTransferRecord[];
};

type CustomerTransferImportSource = CustomerTransferPayload | { items?: unknown[]; customers?: unknown[] } | unknown[];

type CustomerImportResult = {
  imported: number;
  ids?: string[];
  unsubscribers?: number;
  suppressed?: number;
  alreadySuppressed?: number;
  marketingEligible?: number;
  marketingBlocked?: number;
  duplicateEmails?: number;
};

type BookingTransferRow = Record<string, string>;

type BookingTransferPayload = {
  version: number;
  exportedAt: string;
  headers: string[];
  items: BookingTransferRow[];
};

type BookingTransferImportSource = BookingTransferPayload | { items?: unknown[]; bookings?: unknown[] } | unknown[];

type BookingAdminFieldConfig = {
  key: string;
  label: string;
  multiline?: boolean;
  hint?: string;
  readOnly?: boolean;
};

type StaffSettingsItem = {
  id: string;
  code?: string;
  fullName?: string;
  firstName: string;
  lastName: string;
  active?: number | boolean;
  averageRating?: number | null;
  ratingCount?: number;
  lowRatingCount?: number;
};

type PublicBookingFeedbackContext = {
  token: string;
  booking: {
    id: string;
    publicReference: string;
    customerId: string;
    customerName: string;
    serviceName: string;
    locationName: string;
    status: string;
    startsAt: string;
    endsAt: string;
    timeZone: string;
  };
  feedback: BookingFeedback | null;
  questionProfile: string;
  showAreaRatings: boolean;
};

type LocationServiceAreaRule = {
  zipCode: string;
  earliestStartTime?: string;
  latestStartTime?: string;
  allowedWeekdays?: string[];
  active?: number | boolean;
  note?: string;
};

type LocationSettingsItem = {
  id: string;
  name: string;
  timezone: string;
  serviceArea?: string[];
  serviceAreaRules?: LocationServiceAreaRule[];
  active?: number | boolean;
};

type ServiceSettingsItem = {
  id: string;
  name: string;
  active?: number | boolean;
};

type BookingSpotSettingsItem = {
  id: string;
  name: string;
  locationId: string;
  linkedServiceIds?: string[];
  teamSize?: number;
  active?: number | boolean;
};

type AvailabilitySlotSummary = {
  startsAt: string;
  startsAtLocal: string;
  remainingCapacity: number;
};

type AvailabilityDateSummary = {
  date: string;
  hasAvailability: boolean;
  firstStartsAt: string | null;
};

type WaitlistPreferredWindow = "any" | "morning" | "midday" | "afternoon";

type PublicWaitlistCreateResponse =
  | {
      status: "available_now";
      message: string;
      liveSlots: AvailabilitySlotSummary[];
    }
  | {
      status: "waitlist_joined";
      requestId: string;
      message: string;
    };

type PublicWaitlistPrefill = {
  requestId: string;
  status: "active" | "fulfilled" | "expired" | "closed";
  requestedDate: string;
  preferredWindow: WaitlistPreferredWindow;
  customer: Record<string, any>;
  locationId: string;
  serviceId: string;
  bookingSpotId?: string;
  calendarProfileId?: string;
  frequencyRuleId?: string;
  optionIds: string[];
  adminDetails: Record<string, string>;
  liveSlots: AvailabilitySlotSummary[];
  lastNotifiedAt: string;
  message: string;
};

class RenderErrorBoundary extends Component<
  {
    children: ReactNode;
    resetKey?: string;
    fallbackTitle?: string;
    showDebugDetails?: boolean;
  },
  { error: Error | null; errorInfo: ErrorInfo | null }
> {
  state = { error: null as Error | null, errorInfo: null as ErrorInfo | null };

  static getDerivedStateFromError(error: Error) {
    return { error, errorInfo: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("RenderErrorBoundary caught an error", error, info);
    this.setState({ error, errorInfo: info });
  }

  componentDidUpdate(prevProps: Readonly<{ resetKey?: string }>) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, errorInfo: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <Card title={this.props.fallbackTitle ?? "Something went wrong"}>
          <p className="error-text">
            We hit a temporary form error while refreshing this section. Please try the service selection again.
          </p>
          {this.props.showDebugDetails ? (
            <details style={{ marginTop: "1rem" }}>
              <summary>Staging error details</summary>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  overflowX: "auto",
                  fontSize: "0.84rem",
                  lineHeight: 1.4,
                  background: "rgba(24, 38, 31, 0.04)",
                  borderRadius: "16px",
                  padding: "0.9rem"
                }}
              >
                {[
                  `${this.state.error.name}: ${this.state.error.message}`,
                  this.state.error.stack ?? "",
                  this.state.errorInfo?.componentStack ?? ""
                ]
                  .filter(Boolean)
                  .join("\n\n")}
              </pre>
            </details>
          ) : null}
        </Card>
      );
    }

    return this.props.children;
  }
}

function loadTurnstileScript() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.turnstile) {
    return Promise.resolve();
  }

  if (!window.__bookingSecondsTurnstileLoader) {
    window.__bookingSecondsTurnstileLoader = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]');
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Unable to load Turnstile.")), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.turnstileScript = "true";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Unable to load Turnstile."));
      document.head.appendChild(script);
    });
  }

  return window.__bookingSecondsTurnstileLoader;
}

function loadGoogleMapsScript(apiKey: string) {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.__bookingSecondsGoogleMapsAuthFailed) {
    return Promise.reject(new Error("Google Maps autocomplete is unavailable. Continue entering the address manually."));
  }

  if (window.google?.maps?.places?.Autocomplete) {
    return Promise.resolve();
  }

  if (!apiKey) {
    return Promise.reject(new Error("Google Maps API key is not configured."));
  }

  if (!window.__bookingSecondsGoogleMapsLoader) {
    window.__bookingSecondsGoogleMapsLoader = new Promise<void>((resolve, reject) => {
      window.gm_authFailure = () => {
        window.__bookingSecondsGoogleMapsAuthFailed = true;
        window.dispatchEvent(new CustomEvent("booking-seconds-google-maps-auth-failed"));
        reject(new Error("Google Maps autocomplete is unavailable. Continue entering the address manually."));
      };

      const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-maps-script="true"]');
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Unable to load Google Maps. Continue entering the address manually.")),
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.dataset.googleMapsScript = "true";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Unable to load Google Maps. Continue entering the address manually."));
      document.head.appendChild(script);
    });
  }

  return window.__bookingSecondsGoogleMapsLoader;
}

function TurnstileField({
  siteKey,
  token,
  onTokenChange,
  appearance = "always",
  compact = false
}: {
  siteKey: string | null | undefined;
  token: string;
  onTokenChange: (token: string) => void;
  appearance?: "always" | "execute" | "interaction-only";
  compact?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const onTokenChangeRef = useRef(onTokenChange);

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    if (!siteKey || !container) {
      return;
    }

    let isActive = true;
    let widgetId: string | undefined;

    loadTurnstileScript()
      .then(() => {
        if (!isActive || !window.turnstile) {
          return;
        }

        container.innerHTML = "";
        widgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          theme: "light",
          appearance,
          callback: (nextToken) => {
            setError(null);
            onTokenChangeRef.current(nextToken);
          },
          "expired-callback": () => {
            onTokenChangeRef.current("");
            setError("Verification expired. Please complete the challenge again.");
          },
          "error-callback": () => {
            onTokenChangeRef.current("");
            setError("Turnstile could not verify this request. Please try again.");
          }
        });
      })
      .catch((scriptError) => {
        if (!isActive) {
          return;
        }
        onTokenChangeRef.current("");
        setError(scriptError instanceof Error ? scriptError.message : "Unable to load Turnstile.");
      });

    return () => {
      isActive = false;
      if (widgetId && window.turnstile?.remove) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [appearance, container, siteKey]);

  if (!siteKey) {
    return (
      <div className={`turnstile-shell ${compact ? "turnstile-shell--compact" : ""}`}>
        <p className="card-copy">Turnstile is not configured for this environment yet.</p>
      </div>
    );
  }

  return (
    <div className={`turnstile-shell ${compact ? "turnstile-shell--compact" : ""}`}>
      <div className="turnstile-widget" ref={setContainer} />
      {!compact && !token ? <p className="card-copy">Complete the verification to continue.</p> : null}
      {error ? <p className="error-text">{error}</p> : null}
    </div>
  );
}

function AddressAutocompleteField({
  value,
  enabled,
  onChange,
  onResolved
}: {
  value: string;
  enabled?: boolean;
  onChange: (value: string) => void;
  onResolved: (address: {
    line1: string;
    unitNumber?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    formattedAddress?: string;
    placeId?: string;
  }) => void;
}) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddressValue, setSelectedAddressValue] = useState<string | null>(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const autocompleteEnabled = Boolean(enabled);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value.trim()), 200);
    return () => window.clearTimeout(timeout);
  }, [value]);

  useEffect(() => {
    if (!autocompleteEnabled) {
      setShowSuggestions(false);
      setSelectedAddressValue(null);
      setDetailsError(null);
    }
  }, [autocompleteEnabled]);

  useEffect(() => {
    if (!showSuggestions) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!shellRef.current || shellRef.current.contains(event.target as Node)) {
        return;
      }
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [showSuggestions]);

  const suggestionsQuery = useQuery({
    queryKey: ["places-autocomplete", debouncedValue],
    queryFn: () =>
      apiFetch<{ items: AddressSuggestion[] }>(`/api/places/autocomplete?input=${encodeURIComponent(debouncedValue)}`),
    enabled: Boolean(autocompleteEnabled && debouncedValue.length >= 3 && value.trim() !== selectedAddressValue)
  });

  const placeDetailsMutation = useMutation({
    mutationFn: async ({ place }: { place: string; displayText: string }) =>
      apiFetch<{
        line1: string;
        unitNumber?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        formattedAddress?: string;
        placeId?: string;
      }>(
        `/api/places/details?place=${encodeURIComponent(place)}`
      ),
    onSuccess: (address, variables) => {
      const preferredLine1 = choosePreferredAddressLine(address.line1, variables.displayText);
      const preferredUnitNumber = String(address.unitNumber ?? "").trim() || extractUnitNumber(variables.displayText);
      setDetailsError(null);
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      setDebouncedValue("");
      onChange(variables.displayText);
      onResolved({
        ...address,
        line1: preferredLine1,
        unitNumber: preferredUnitNumber
      });
    },
    onError: (error) => {
      setDetailsError(error instanceof Error ? error.message : "Address details are unavailable right now.");
    }
  });

  const suggestions = suggestionsQuery.data?.items ?? [];
  const activeSuggestion =
    activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length ? suggestions[activeSuggestionIndex] : null;
  const isSearching = autocompleteEnabled && showSuggestions && suggestionsQuery.isFetching;
  const showNoSuggestions =
    autocompleteEnabled &&
    showSuggestions &&
    !isSearching &&
    debouncedValue.length >= 3 &&
    suggestionsQuery.isSuccess &&
    !suggestions.length;
  const showAutocompleteFallback =
    autocompleteEnabled && (Boolean(suggestionsQuery.error) || Boolean(detailsError));
  const statusMessage = placeDetailsMutation.isPending
    ? "Checking selected address..."
    : isSearching
      ? "Searching addresses..."
      : showNoSuggestions
        ? "No suggestions found. You can keep typing manually."
        : "";

  useEffect(() => {
    if (!showSuggestions || !suggestions.length) {
      setActiveSuggestionIndex(-1);
      return;
    }

    setActiveSuggestionIndex((current) =>
      current >= 0 && current < suggestions.length ? current : 0
    );
  }, [showSuggestions, suggestions.length]);

  const selectSuggestion = (suggestion: AddressSuggestion) => {
    const selectedValue = suggestion.mainText || suggestion.text;
    setSelectedAddressValue(selectedValue);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    setDebouncedValue("");
    setDetailsError(null);
    onChange(selectedValue);
    placeDetailsMutation.mutate({ place: suggestion.place, displayText: selectedValue });
  };

  return (
    <div className="stack stack--tight" ref={shellRef}>
      <input
        value={value}
        aria-label="Street address"
        placeholder="Start typing your address"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        data-1p-ignore
        data-lpignore
        enterKeyHint="next"
        aria-autocomplete={autocompleteEnabled ? "list" : undefined}
        aria-expanded={autocompleteEnabled ? showSuggestions : undefined}
        aria-controls={autocompleteEnabled ? "booking-address-autocomplete-list" : undefined}
        onFocus={() => {
          setShowSuggestions(autocompleteEnabled && value.trim().length >= 3);
        }}
        onKeyDown={(event) => {
          if (!autocompleteEnabled || !showSuggestions) {
            return;
          }

          if (event.key === "ArrowDown" && suggestions.length) {
            event.preventDefault();
            setActiveSuggestionIndex((current) => (current + 1) % suggestions.length);
            return;
          }

          if (event.key === "ArrowUp" && suggestions.length) {
            event.preventDefault();
            setActiveSuggestionIndex((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
            return;
          }

          if (event.key === "Enter" && activeSuggestion) {
            event.preventDefault();
            selectSuggestion(activeSuggestion);
            return;
          }

          if (event.key === "Escape") {
            setShowSuggestions(false);
            setActiveSuggestionIndex(-1);
          }
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          setSelectedAddressValue(null);
          setShowSuggestions(autocompleteEnabled && nextValue.trim().length >= 3);
          setActiveSuggestionIndex(-1);
          setDetailsError(null);
          onChange(nextValue);
          onResolved({
            line1: stripUnitNumber(nextValue) || nextValue,
            unitNumber: extractUnitNumber(nextValue),
            postalCode: extractPostalCode(nextValue)
          });
        }}
      />
      {statusMessage ? <p className="autocomplete-status">{statusMessage}</p> : null}
      {autocompleteEnabled && showSuggestions && suggestions.length ? (
        <div className="autocomplete-list" id="booking-address-autocomplete-list" role="listbox">
          {suggestions.map((suggestion, index) => (
            <button
              className={`autocomplete-item ${index === activeSuggestionIndex ? "is-active" : ""}`}
              key={suggestion.place}
              role="option"
              aria-selected={index === activeSuggestionIndex}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectSuggestion(suggestion)}
            >
              <strong>{suggestion.mainText || suggestion.text}</strong>
              {suggestion.secondaryText ? <span>{suggestion.secondaryText}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
      {!autocompleteEnabled && !detailsError ? (
        <p className="card-copy">Type the full address manually. We'll still check the ZIP code against your service area.</p>
      ) : null}
      {showAutocompleteFallback ? <p className="error-text">{ADDRESS_AUTOCOMPLETE_FALLBACK_MESSAGE}</p> : null}
    </div>
  );
}

function getBookingFormDefaults() {
  return {
    customer: {
      firstName: "",
      lastName: "",
      companyName: "",
      email: "",
      phone: "",
      secondaryPhone: "",
      notes: "",
      address: {
        label: "Service address",
        line1: "",
        line2: "",
        unitNumber: "",
        city: "Chicago",
        state: "IL",
        postalCode: "",
        country: "US",
        instructions: "",
        isPrimary: true
      },
      emailPreferences: {
        smsReminderOptInStatus: false,
        marketing: true
      },
      tagIds: []
    },
    locationId: "loc_downtown_demo",
    serviceId: "",
    bookingSpotId: "spot_downtown_team_a",
    optionIds: [] as string[],
    frequencyRuleId: "",
    calendarProfileId: "",
    startsAt: new Date().toISOString(),
    notes: "",
    internalNotes: "",
    promoCode: "",
    giftCardCode: "",
    source: "website",
    depositChoice: "deposit" as const,
    turnstileToken: "",
    legalConsentAccepted: false,
    adminDetails: {}
  };
}

function padDateValue(value: number) {
  return String(value).padStart(2, "0");
}

function toDateTimeInputValue(iso: string | undefined, timeZone?: string | null) {
  if (!iso) {
    return "";
  }

  const normalizedTimeZone = String(timeZone ?? "").trim();
  if (normalizedTimeZone) {
    try {
      const zoned = Temporal.Instant.from(iso).toZonedDateTimeISO(normalizedTimeZone);
      return `${zoned.year}-${padDateValue(zoned.month)}-${padDateValue(zoned.day)}T${padDateValue(zoned.hour)}:${padDateValue(zoned.minute)}`;
    } catch {
      // Fall back to browser-local formatting.
    }
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${padDateValue(date.getMonth() + 1)}-${padDateValue(date.getDate())}T${padDateValue(date.getHours())}:${padDateValue(date.getMinutes())}`;
}

function fromDateTimeInputValue(value: string, timeZone?: string | null) {
  if (!value) {
    return "";
  }

  const normalizedTimeZone = String(timeZone ?? "").trim();
  if (normalizedTimeZone) {
    try {
      const [datePart = "", timePart = ""] = value.split("T");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hour, minute] = timePart.split(":").map(Number);
      if ([year, month, day, hour, minute].some((part) => !Number.isFinite(part))) {
        return "";
      }

      return Temporal.ZonedDateTime.from({
        timeZone: normalizedTimeZone,
        year,
        month,
        day,
        hour,
        minute
      }).toInstant().toString();
    } catch {
      return "";
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function normalizeCustomerValues(values: any) {
  const addressSource = values.addresses?.length ? values.addresses : values.address ? [values.address] : [];
  const addresses = addressSource
    .map((address: any, index: number) => ({
      label: address?.label || `Service address ${index + 1}`,
      line1: address?.line1 || "",
      line2: address?.line2 || "",
      unitNumber: address?.unitNumber || "",
      city: address?.city || "",
      state: address?.state || "",
      postalCode: address?.postalCode || "",
      country: address?.country || "US",
      instructions: address?.instructions || "",
      isPrimary: index === 0 ? true : Boolean(address?.isPrimary)
    }))
    .filter((address: any) =>
      [address.line1, address.unitNumber, address.city, address.state, address.postalCode].some((value: string) => String(value ?? "").trim() !== "")
    )
    .map((address: any, index: number) => ({
      ...address,
      isPrimary: index === 0 ? true : address.isPrimary
    }));
  const primaryAddress = addresses[0];

  return {
    ...values,
    companyName: values.companyName ?? "",
    profilePhotoUrl: values.profilePhotoUrl ?? "",
    customerType: values.customerType ?? "Residential",
    accountStatus: values.accountStatus ?? "Active",
    email: values.email ?? "",
    secondaryPhone: values.secondaryPhone ?? "",
    pets: values.pets ?? "",
    sensitivities: values.sensitivities ?? "",
    documents: values.documents ?? [],
    serviceFrequency: values.serviceFrequency ?? "",
    blockedUntil: values.blockedUntil || "",
    blockedReason: values.blockedReason ?? "",
    preferredStaffIds: values.preferredStaffIds ?? [],
    blockedStaffIds: values.blockedStaffIds ?? [],
    preferredContactMethod: values.preferredContactMethod ?? "Phone",
    englishSpeakingStaff: Boolean(values.englishSpeakingStaff),
    stripeCustomerId: values.stripeCustomerId ?? "",
    paymentMethods: values.paymentMethods ?? [],
    propertyPhotos: values.propertyPhotos ?? [],
    refundsDisputes: values.refundsDisputes ?? [],
    problemAreas: values.problemAreas ?? [],
    damageReports: values.damageReports ?? [],
    specialIncidents: values.specialIncidents ?? [],
    clientRating:
      values.clientRating === "" || values.clientRating === null || values.clientRating === undefined
        ? undefined
        : Number(values.clientRating),
    customerSatisfactionScore:
      values.customerSatisfactionScore === "" || values.customerSatisfactionScore === null || values.customerSatisfactionScore === undefined
        ? undefined
        : Number(values.customerSatisfactionScore),
    rewards: {
      points: Number(values.rewards?.points ?? 0),
      tier: values.rewards?.tier ?? "",
      notes: values.rewards?.notes ?? ""
    },
    emailPreferences: {
      smsOptInStatus: values.emailPreferences?.smsOptInStatus ?? false,
      smsReminderOptInStatus: values.emailPreferences?.smsReminderOptInStatus ?? false,
      emailOptInStatus: values.emailPreferences?.emailOptInStatus ?? true,
      bookingConfirmation: values.emailPreferences?.bookingConfirmation ?? true,
      reminders: values.emailPreferences?.reminders ?? true,
      receipts: values.emailPreferences?.receipts ?? true,
      marketing: values.emailPreferences?.marketing ?? true
    },
    taxExempt: Boolean(values.taxExempt),
    specialTaxRate:
      values.specialTaxRate === "" || values.specialTaxRate === null || values.specialTaxRate === undefined
        ? undefined
        : Number(values.specialTaxRate),
    tagIds: values.tagIds ?? [],
    addresses,
    address: primaryAddress
      ? {
          label: primaryAddress.label,
          line1: primaryAddress.line1,
          line2: primaryAddress.line2,
          unitNumber: primaryAddress.unitNumber,
          city: primaryAddress.city,
          state: primaryAddress.state,
          postalCode: primaryAddress.postalCode,
          country: primaryAddress.country,
          instructions: primaryAddress.instructions,
          isPrimary: true
        }
      : undefined
  };
}

function createEmptyCustomerAddress(index = 0) {
  return {
    label: index === 0 ? "Primary service address" : `Additional address ${index}`,
    line1: "",
    line2: "",
    unitNumber: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
    instructions: "",
    isPrimary: index === 0
  };
}

function getCustomerFormDefaults() {
  return {
    firstName: "",
    lastName: "",
    companyName: "",
    profilePhotoUrl: "",
    customerType: "Residential",
    accountStatus: "Active",
    email: "",
    phone: "",
    secondaryPhone: "",
    pets: "",
    sensitivities: "",
    documents: [] as string[],
    notes: "",
    serviceFrequency: "",
    blockedUntil: "",
    blockedReason: "",
    preferredStaffIds: [] as string[],
    blockedStaffIds: [] as string[],
    preferredContactMethod: "Phone",
    englishSpeakingStaff: false,
    leadSourceId: "",
    stripeCustomerId: "",
    paymentMethods: [] as string[],
    propertyPhotos: [] as string[],
    refundsDisputes: [] as string[],
    problemAreas: [] as string[],
    damageReports: [] as string[],
    specialIncidents: [] as string[],
    clientRating: undefined as number | undefined,
    customerSatisfactionScore: undefined as number | undefined,
    rewards: {
      points: 0,
      tier: "",
      notes: ""
    },
    emailPreferences: {
      smsOptInStatus: false,
      smsReminderOptInStatus: false,
      emailOptInStatus: true,
      bookingConfirmation: true,
      reminders: true,
      receipts: true,
      marketing: true
    },
    taxExempt: false,
    specialTaxRate: undefined as number | undefined,
    address: createEmptyCustomerAddress(0),
    addresses: [createEmptyCustomerAddress(0)],
    tagIds: [] as string[]
  };
}

type CustomerAddressDraft = ReturnType<typeof createEmptyCustomerAddress>;

function todayDateInputValue(timeZone = "America/Chicago") {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(new Date());
}

function formatDateLabel(value: string | null | undefined) {
  if (!value) {
    return "Not yet";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function formatDateLabelInTimeZone(value: string | null | undefined, timeZone?: string | null) {
  if (!value) {
    return "Not yet";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-US", {
    timeZone: String(timeZone ?? "").trim() || undefined,
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function formatLongDateLabel(value: string | null | undefined) {
  if (!value) {
    return "Not yet";
  }

  const parsed =
    /^\d{4}-\d{2}-\d{2}$/.test(String(value))
      ? new Date(`${value}T12:00:00`)
      : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric"
  });
}

function formatDateTimeLabel(value: string | null | undefined) {
  if (!value) {
    return "Not scheduled";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function compactClockText(value: string) {
  return value.endsWith(":00") ? value.slice(0, -3) : value;
}

function bookingDetailValue(details: Record<string, string> | undefined, key: string, fallback = "") {
  const value = details?.[key];
  return value && value.trim() ? value : fallback;
}

function bookingFieldRows(items: Array<[string, string | null | undefined]>) {
  return items
    .filter(([, value]) => value != null && String(value).trim() !== "")
    .map(([label, value]) => [label, String(value)]);
}

type AddressLinkInput = {
  line1?: string | null;
  line2?: string | null;
  unitNumber?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

function addressLinkQuery(address: string | AddressLinkInput | null | undefined) {
  if (typeof address === "string") {
    return address.trim();
  }

  if (!address) {
    return "";
  }

  return [
    address.line1,
    address.unitNumber ? `Unit ${address.unitNumber}` : "",
    address.line2,
    address.city,
    address.state,
    address.postalCode,
    address.country && String(address.country).toUpperCase() !== "US" ? address.country : ""
  ]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

function addressExternalLinks(address: string | AddressLinkInput | null | undefined) {
  const query = addressLinkQuery(address);
  if (!query) {
    return null;
  }

  return {
    googleMapsUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
    zillowUrl: `https://www.zillow.com/homes/${encodeURIComponent(query)}_rb/`
  };
}

function AddressLinkButtons({
  address,
  compact = false
}: {
  address: string | AddressLinkInput | null | undefined;
  compact?: boolean;
}) {
  const links = addressExternalLinks(address);
  if (!links) {
    return null;
  }

  return (
    <div className={`address-link-actions ${compact ? "address-link-actions--compact" : ""}`} onClick={(event) => event.stopPropagation()}>
      <a className="button button--secondary" href={links.googleMapsUrl} target="_blank" rel="noreferrer">
        View on Google Maps
      </a>
      <a className="button button--secondary" href={links.zillowUrl} target="_blank" rel="noreferrer">
        View on Zillow
      </a>
    </div>
  );
}

function formatZillowAddressSummary(address: AddressLinkInput | null | undefined) {
  return addressLinkQuery(address);
}

function formatZillowNumber(value: number | null | undefined, suffix = "") {
  if (value == null || Number.isNaN(Number(value))) {
    return "";
  }
  return `${Number(value).toLocaleString("en-US")}${suffix}`;
}

function formatZillowCurrency(value: number | null | undefined) {
  if (value == null || Number.isNaN(Number(value))) {
    return "";
  }
  return `$${Number(value).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function zillowSummaryRows(summary: ZillowPropertySummary | null | undefined) {
  if (!summary) {
    return [];
  }
  return [
    ["Bedrooms", formatZillowNumber(summary.bedrooms)],
    ["Bathrooms", formatZillowNumber(summary.bathrooms)],
    ["Living area", formatZillowNumber(summary.livingArea, " sq ft")],
    ["Year built", formatZillowNumber(summary.yearBuilt)],
    ["Home type", summary.homeType ?? ""],
    ["Zestimate", formatZillowCurrency(summary.zestimate)],
    ["Price", formatZillowCurrency(summary.price)],
    ["Last sold price", formatZillowCurrency(summary.lastSoldPrice)],
    ["Rent Zestimate", formatZillowCurrency(summary.rentZestimate)],
    ["Photos", formatZillowNumber(summary.photosCount)],
    ["Price history", formatZillowNumber(summary.priceHistoryCount)],
    ["Schools", formatZillowNumber(summary.schoolsCount)],
    ["Agent", summary.agentName || (summary.hasAgent ? "Available" : "")]
  ].filter(([, value]) => String(value ?? "").trim() !== "");
}

function flattenZillowJson(value: unknown, prefix = "", output: Array<{ path: string; value: string }> = []) {
  if (output.length >= 350) {
    return output;
  }

  if (value == null || typeof value !== "object") {
    output.push({ path: prefix || "$", value: String(value ?? "") });
    return output;
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      output.push({ path: prefix || "$", value: "[]" });
      return output;
    }
    value.slice(0, 20).forEach((item, index) => {
      flattenZillowJson(item, `${prefix}[${index}]`, output);
    });
    if (value.length > 20) {
      output.push({ path: `${prefix}[...]`, value: `${value.length - 20} more item(s)` });
    }
    return output;
  }

  const entries = Object.entries(value as Record<string, unknown>);
  if (!entries.length) {
    output.push({ path: prefix || "$", value: "{}" });
    return output;
  }
  for (const [key, child] of entries) {
    flattenZillowJson(child, prefix ? `${prefix}.${key}` : key, output);
    if (output.length >= 350) {
      break;
    }
  }
  return output;
}

function formatZillowFieldValue(value: string) {
  return value.length > 220 ? `${value.slice(0, 217)}...` : value;
}

function ZillowSummaryGrid({ enrichment }: { enrichment: ZillowEnrichmentRecord | null | undefined }) {
  if (!enrichment || enrichment.status !== "fetched") {
    return null;
  }
  const summary = enrichment.summary ?? {};
  const address = summary.address || [summary.streetAddress, summary.city, summary.state, summary.postalCode].filter(Boolean).join(", ");
  return (
    <div className="zillow-summary">
      <div className="zillow-summary-grid">
        <div className="zillow-summary-item">
          <p className="eyebrow">Zillow property</p>
          <strong>{address || "Property details fetched"}</strong>
          <p>{summary.zpid ? `ZPID ${summary.zpid}` : "No zpid returned"}</p>
        </div>
        <div className="zillow-summary-item">
          <p className="eyebrow">Size</p>
          <strong>{formatZillowNumber(summary.livingArea, " sq ft") || "No square footage"}</strong>
          <p>{[formatZillowNumber(summary.bedrooms) && `${formatZillowNumber(summary.bedrooms)} bed`, formatZillowNumber(summary.bathrooms) && `${formatZillowNumber(summary.bathrooms)} bath`].filter(Boolean).join(" / ") || "No bedroom or bathroom count"}</p>
        </div>
        <div className="zillow-summary-item">
          <p className="eyebrow">Value</p>
          <strong>{formatZillowCurrency(summary.zestimate) || formatZillowCurrency(summary.price) || "No value returned"}</strong>
          <p>{summary.homeType || (summary.yearBuilt ? `Built ${summary.yearBuilt}` : "No home type returned")}</p>
        </div>
      </div>
    </div>
  );
}

function ZillowPropertyEnrichmentPanel({
  customerId,
  address,
  onFetched
}: {
  customerId: string;
  address: CustomerAddress;
  onFetched: () => void;
}) {
  const queryClient = useQueryClient();
  const [manualLookup, setManualLookup] = useState("");
  const [searchAddress, setSearchAddress] = useState(formatZillowAddressSummary(address));
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");
  const [searchResponse, setSearchResponse] = useState<{
    addressQuery: string;
    candidates: ZillowSearchCandidate[];
    rawResponse: unknown;
  } | null>(null);

  const enrichmentQuery = useQuery({
    queryKey: ["customer-zillow", customerId, address.id],
    queryFn: () =>
      apiFetch<{
        address: CustomerAddress;
        enrichment: ZillowEnrichmentRecord | null;
      }>(`/api/customers/${customerId}/addresses/${address.id}/zillow`),
    enabled: Boolean(customerId && address.id),
    initialData: address.zillowEnrichment
      ? {
          address,
          enrichment: address.zillowEnrichment
        }
      : undefined
  });

  const enrichment = enrichmentQuery.data?.enrichment ?? address.zillowEnrichment ?? null;
  const candidates = searchResponse?.candidates ?? [];
  const selectedCandidate =
    candidates.find((candidate) => candidate.id === selectedCandidateId) ?? candidates[0] ?? null;
  const rawFields = useMemo(() => {
    const source = enrichment?.rawResponse ?? {};
    const filter = fieldFilter.trim().toLowerCase();
    return flattenZillowJson(source).filter((entry) =>
      filter ? `${entry.path} ${entry.value}`.toLowerCase().includes(filter) : true
    );
  }, [enrichment?.rawResponse, fieldFilter]);

  const searchMutation = useMutation({
    mutationFn: () =>
      apiFetch<{
        addressQuery: string;
        candidates: ZillowSearchCandidate[];
        rawResponse: unknown;
      }>(`/api/customers/${customerId}/addresses/${address.id}/zillow/search`, {
        method: "POST",
        body: JSON.stringify({ address: searchAddress })
      }),
    onSuccess: (result) => {
      setSearchResponse(result);
      setSelectedCandidateId(result.candidates[0]?.id ?? "");
    }
  });

  const fetchMutation = useMutation({
    mutationFn: (input: { lookup?: string; zpid?: string; zillowUrl?: string; lookupSource: "manual" | "address_search" }) =>
      apiFetch<{ enrichment: ZillowEnrichmentRecord | null }>(`/api/customers/${customerId}/addresses/${address.id}/zillow/fetch`, {
        method: "POST",
        body: JSON.stringify(input)
      }),
    onSuccess: async (result) => {
      queryClient.setQueryData(["customer-zillow", customerId, address.id], { address, enrichment: result.enrichment });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customer", customerId] }),
        queryClient.invalidateQueries({ queryKey: ["customer-zillow", customerId, address.id] })
      ]);
      onFetched();
    }
  });

  const fetchManual = () => {
    fetchMutation.mutate({ lookup: manualLookup, lookupSource: "manual" });
  };

  const fetchSelectedCandidate = () => {
    if (!selectedCandidate) {
      return;
    }
    fetchMutation.mutate({
      zpid: selectedCandidate.zpid,
      zillowUrl: selectedCandidate.zillowUrl,
      lookupSource: "address_search"
    });
  };

  const refreshExisting = () => {
    if (!enrichment?.zpid && !enrichment?.zillowUrl) {
      return;
    }
    fetchMutation.mutate({
      zpid: enrichment.zpid,
      zillowUrl: enrichment.zillowUrl,
      lookupSource: enrichment.lookupSource === "address_search" ? "address_search" : "manual"
    });
  };

  return (
    <div className="zillow-enrichment-panel">
      <div className="zillow-enrichment-panel__header">
        <div>
          <p className="eyebrow">Zillow enrichment</p>
          <strong>{enrichment?.status === "fetched" ? "Details cached" : enrichment?.status === "failed" ? "Fetch failed" : "Not fetched yet"}</strong>
          <p>{enrichment?.fetchedAt ? `Fetched ${formatDateTimeLabel(enrichment.fetchedAt)}` : "Review-only snapshot storage."}</p>
        </div>
        <div className="button-row">
          <Button type="button" variant="secondary" onClick={refreshExisting} disabled={!enrichment || fetchMutation.isPending}>
            {fetchMutation.isPending ? "Fetching..." : "Refresh Zillow data"}
          </Button>
          {enrichment?.zillowUrl ? (
            <a className="button button--secondary" href={enrichment.zillowUrl} target="_blank" rel="noreferrer">
              Open Zillow
            </a>
          ) : null}
        </div>
      </div>

      {enrichment?.status === "failed" ? (
        <p className="error-text">{String(enrichment.error?.message ?? "Last Zillow fetch failed.")}</p>
      ) : null}

      <ZillowSummaryGrid enrichment={enrichment} />

      {enrichment?.status === "fetched" ? (
        <DataTable columns={["Field", "Value"]} rows={zillowSummaryRows(enrichment.summary)} />
      ) : null}

      <div className="zillow-lookup-grid">
        <div className="zillow-lookup-box">
          <Field label="Zillow URL or zpid">
            <input
              value={manualLookup}
              onChange={(event) => setManualLookup(event.target.value)}
              placeholder="https://www.zillow.com/homedetails/... or 12345678"
            />
          </Field>
          <Button type="button" onClick={fetchManual} disabled={fetchMutation.isPending || !manualLookup.trim()}>
            {fetchMutation.isPending ? "Fetching..." : "Fetch details"}
          </Button>
        </div>
        <div className="zillow-lookup-box">
          <Field label="Search by saved address">
            <input value={searchAddress} onChange={(event) => setSearchAddress(event.target.value)} />
          </Field>
          <Button type="button" variant="secondary" onClick={() => searchMutation.mutate()} disabled={searchMutation.isPending || !searchAddress.trim()}>
            {searchMutation.isPending ? "Searching..." : "Search Zillow"}
          </Button>
        </div>
      </div>

      {searchMutation.error ? <p className="error-text">{(searchMutation.error as Error).message}</p> : null}
      {fetchMutation.error ? <p className="error-text">{(fetchMutation.error as Error).message}</p> : null}

      {candidates.length ? (
        <div className="zillow-search-results">
          <Field label="Matched Zillow property">
            <select value={selectedCandidate?.id ?? ""} onChange={(event) => setSelectedCandidateId(event.target.value)}>
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.address || candidate.zpid || candidate.zillowUrl || candidate.id}
                </option>
              ))}
            </select>
          </Field>
          <Button type="button" onClick={fetchSelectedCandidate} disabled={!selectedCandidate || fetchMutation.isPending}>
            Fetch selected property
          </Button>
          {selectedCandidate ? <DataTable columns={["Field", "Value"]} rows={zillowSummaryRows(selectedCandidate.summary)} /> : null}
        </div>
      ) : searchResponse ? (
        <p className="card-copy">No Zillow matches were returned for that address.</p>
      ) : null}

      {enrichment?.rawResponse ? (
        <details className="zillow-field-explorer">
          <summary>Raw Zillow response explorer</summary>
          <div className="stack">
            <Field label="Filter fields">
              <input value={fieldFilter} onChange={(event) => setFieldFilter(event.target.value)} placeholder="Search field paths or values" />
            </Field>
            <DataTable
              columns={["Path", "Value"]}
              rows={rawFields.slice(0, 80).map((entry) => [entry.path, formatZillowFieldValue(entry.value)])}
            />
            {rawFields.length > 80 ? <p className="card-copy">Showing first 80 matching fields of {rawFields.length}.</p> : null}
            <textarea className="zillow-raw-json" readOnly rows={10} value={JSON.stringify(enrichment.rawResponse, null, 2)} />
          </div>
        </details>
      ) : null}
    </div>
  );
}

function toTitleCaseWords(value: string) {
  return value
    .split(/[_\s]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatBookingChangeSourceLabel(value: string) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) {
    return "System";
  }

  if (normalized === "public_manage") {
    return "Public manage";
  }

  return toTitleCaseWords(normalized);
}

function bookingChangeSourceTone(source: string) {
  const normalized = String(source ?? "").trim().toLowerCase();
  if (normalized === "payment" || normalized === "gift_card") {
    return "success" as const;
  }
  if (normalized === "public_booking" || normalized === "public_manage" || normalized === "feedback") {
    return "warning" as const;
  }
  return "info" as const;
}

function bookingChangeTypeTone(changeType: BookingChangeFieldChange["changeType"]) {
  if (changeType === "added") {
    return "success" as const;
  }
  if (changeType === "removed") {
    return "warning" as const;
  }
  return "info" as const;
}

function formatBookingChangeValue(value: unknown): string {
  if (value == null) {
    return "Empty";
  }

  if (typeof value === "string") {
    return value.trim() ? value : "Empty";
  }

  if (typeof value === "number" || typeof value === "bigint") {
    return String(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return "Empty";
    }
    return value.map((entry) => formatBookingChangeValue(entry)).join(" | ");
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) {
      return "Empty";
    }

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

const SHORTER_DURATION_BOOKING_NOTE_SENTENCE =
  "If needed, you can request a shorter cleaning duration in the booking notes.";

function cleanEstimateSummaryNote(value: string | null | undefined) {
  return String(value ?? "")
    .replace(SHORTER_DURATION_BOOKING_NOTE_SENTENCE, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function isCheckedConsentValue(value: unknown) {
  if (value === true) {
    return true;
  }
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "on" || normalized === "1" || normalized === "yes";
}

function parsePortalSummaryLines(value: string | null | undefined) {
  return String(value ?? "")
    .split(/\r?\n|\|/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function summarizePortalPayment(booking: {
  totalAmount: number;
  amountPaid?: number;
  amountDue: number;
}) {
  const totalAmount = Number(booking.totalAmount ?? 0);
  const amountDue = Number(booking.amountDue ?? 0);
  const amountPaid = Number(booking.amountPaid ?? Math.max(totalAmount - amountDue, 0));

  if (amountDue <= 0) {
    return {
      label: "Paid in full",
      detail: `Total ${formatCurrency(totalAmount)}`
    };
  }

  if (amountPaid > 0) {
    return {
      label: "Deposit received",
      detail: `${formatCurrency(amountDue)} remaining`
    };
  }

  return {
    label: `${formatCurrency(amountDue)} due`,
    detail: `Total ${formatCurrency(totalAmount)}`
  };
}

function buildPortalPolicyMessage(detail: CustomerPortalBookingDetail) {
  const cutoffLabel = detail.policy.changeCutoffAt ? formatDateTimeLabel(detail.policy.changeCutoffAt) : "";

  if (!detail.actions.canManageOnline) {
    return "This appointment can no longer be changed online.";
  }

  if (detail.policy.changesLocked && cutoffLabel) {
    return `Online changes closed on ${cutoffLabel}. Contact support if you still need help with this appointment.`;
  }

  if (cutoffLabel) {
    return `Online changes are available until ${cutoffLabel}.`;
  }

  return detail.policy.cancellationPolicy || "Online changes are available while this appointment remains in a self-service status.";
}

function describeBookingFrequency(bookings: CustomerHistoryBooking[], fallback?: string) {
  if (fallback && fallback.trim()) {
    return fallback;
  }

  if (bookings.length < 2) {
    return "Not enough history";
  }

  const latest = new Date(bookings[0].startsAt).getTime();
  const previous = new Date(bookings[1].startsAt).getTime();
  const diffDays = Math.max(1, Math.round(Math.abs(latest - previous) / (1000 * 60 * 60 * 24)));
  return `About every ${diffDays} day${diffDays === 1 ? "" : "s"}`;
}

function parseLineSeparatedValues(value: string) {
  return value
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function formatCustomerIssueEntry(booking: CustomerHistoryBooking | undefined, description: string) {
  const details = description.trim();
  if (!details) {
    return "";
  }

  const reference = booking?.publicReference || "No appointment";
  const visitDate = booking?.startsAt ? formatDateTimeLabel(booking.startsAt) : "No appointment date";
  return `${reference} - ${visitDate} - ${details}`;
}

const CUSTOMER_FILE_PREFIX = "__FILE__";

function isMaidSafeUser(user: SessionUser | null | undefined) {
  if (!user) {
    return false;
  }

  return user.roles.includes("staff") && !user.roles.some((role) => ["owner", "admin", "manager", "accountant", "viewer"].includes(role));
}

function emailTemplateHtmlToText(value: string) {
  return String(value ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function renderSimpleTemplate(template: string, variables: Record<string, unknown>) {
  const resolve = (key: string) =>
    key.split(".").reduce<unknown>((current, part) => {
      if (current && typeof current === "object" && part in current) {
        return (current as Record<string, unknown>)[part];
      }
      return undefined;
    }, variables);

  return String(template ?? "").replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_match, key) => {
    const value = resolve(String(key));
    return value == null ? "" : String(value);
  });
}

function parseCustomerDocumentEntry(value: string): { key?: string; name: string; contentType?: string; isUploadedFile: boolean } {
  if (!String(value).startsWith(CUSTOMER_FILE_PREFIX)) {
    return {
      name: value,
      isUploadedFile: false
    };
  }

  try {
    const parsed = JSON.parse(String(value).slice(CUSTOMER_FILE_PREFIX.length)) as {
      key?: string;
      name?: string;
      contentType?: string;
    };
    return {
      key: parsed.key,
      name: parsed.name || "Document",
      contentType: parsed.contentType,
      isUploadedFile: true
    };
  } catch {
    return {
      name: value,
      isUploadedFile: false
    };
  }
}

const bookingSectionJumpItems: SectionJumpItem[] = [
  { id: "booking-section-details", label: "Booking details" },
  { id: "booking-section-basics", label: "Booking basics" },
  { id: "booking-section-customer-property", label: "Customer & property" },
  { id: "booking-section-pricing-payment", label: "Pricing & payment" },
  { id: "booking-section-service-notes", label: "Service notes" },
  { id: "booking-section-staff-preferences", label: "Staff preferences" },
  { id: "booking-section-home-profile", label: "Home / property profile" },
  { id: "booking-section-condition-quality", label: "Condition / quality" },
  { id: "booking-section-customer-feedback", label: "Customer feedback" },
  { id: "booking-section-admin-history", label: "Admin / history" },
  { id: "booking-section-change-log", label: "Change log" }
];

const customerSectionJumpItems: SectionJumpItem<CustomerTabKey>[] = [
  { id: "customer-overview-upcoming-bookings", label: "Overview: Upcoming bookings", tab: "overview" },
  { id: "customer-overview-service-profile", label: "Overview: Service profile", tab: "overview" },
  { id: "customer-overview-account-health", label: "Overview: Account health", tab: "overview" },
  { id: "customer-overview-rewards-follow-up", label: "Overview: Rewards & follow-up", tab: "overview" },
  { id: "customer-overview-marketing", label: "Overview: Marketing & re-engagement", tab: "overview" },
  { id: "customer-history-bookings", label: "Booking history", tab: "history" },
  { id: "customer-history-feedback", label: "Feedback history", tab: "history" },
  { id: "customer-properties-details", label: "Property details", tab: "properties" },
  { id: "customer-properties-manage", label: "Manage properties", tab: "properties" },
  { id: "customer-payments-card", label: "Payments", tab: "payments" },
  { id: "customer-files-profile-photo", label: "Profile photo", tab: "files" },
  { id: "customer-files-property-photos", label: "Property photos", tab: "files" },
  { id: "customer-files-documents", label: "Documents", tab: "files" },
  { id: "customer-issues-incidents", label: "Issues / incidents", tab: "issues" },
  { id: "customer-issues-damages", label: "Damages", tab: "issues" },
  { id: "customer-issues-staff-quality", label: "Staff blocks & quality", tab: "issues" },
  { id: "customer-profile-main", label: "Customer profile", tab: "internal-notes" },
  { id: "customer-profile-preferences", label: "Staff, tags, and preferences", tab: "internal-notes" },
  { id: "customer-profile-portal", label: "Customer portal" }
];

const paymentSectionJumpItems: SectionJumpItem[] = [
  { id: "payments-section-ledger", label: "Payment ledger" },
  { id: "payments-section-refund", label: "Refund payment" }
];

function scrollToSectionTarget(targetId: string) {
  if (typeof document === "undefined") {
    return false;
  }

  const target = document.getElementById(targetId);
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  target.scrollIntoView({
    behavior: "smooth",
    block: "start"
  });
  window.setTimeout(() => target.focus({ preventScroll: true }), 160);
  return true;
}

function SectionJumpTarget({
  id,
  children,
  className
}: {
  id: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div id={id} tabIndex={-1} className={["section-jump-target", className].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

function SectionJumpNav<TTab extends string = string>({
  items,
  onSelect,
  label = "Jump to section"
}: {
  items: SectionJumpItem<TTab>[];
  onSelect: (item: SectionJumpItem<TTab>) => void;
  label?: string;
}) {
  const [selectedId, setSelectedId] = useState("");

  return (
    <div className="section-jump-nav">
      <label className="section-jump-nav__label">
        <select
          className="section-jump-nav__select"
          aria-label={label}
          value={selectedId}
          onChange={(event) => {
            const nextItem = items.find((item) => item.id === event.target.value);
            if (nextItem) {
              onSelect(nextItem);
            }
            setSelectedId("");
          }}
        >
          <option value="">{label}</option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

const BOOKING_ADMIN_SELECT_OPTIONS: Record<string, string[]> = {
  customer_type: ["Residentail", "Commercial", "Industrial", "Vacation rental"],
  tip_type: ["$", "Percentage"],
  payment_method: ["Credit card", "Cash", "Check", "Zelle", "Venmo", "ACH", "Other"],
  frequency: ["One-time", "Every week", "Every 2 weeks", "Every 3 weeks", "Every 4 weeks", "Every 5 weeks", "Every 6 weeks", "Other"],
  frequency_discount_type: ["$", "Percentage"],
  services: [
    "Basic cleaning",
    "Deep cleaning",
    "Regular cleaning",
    "Move-in cleaning",
    "Move-out cleaning",
    "Office / commercial cleaning",
    "Building commion areas cleaning",
    "Carpet shampooing (standalone service)",
    "After remodeling cleaning",
    "Vacation rental cleaning"
  ],
  type: [
    "Apartment / condo",
    "Townhouse",
    "Single-family house",
    "Office / commercial",
    "Building commion areas",
    "Carpet shampooing (standalone service)"
  ],
  bedrooms: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
  bathrooms: ["0", "0.5", "1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5", "6", "7", "8", "9", "10"],
  levels: ["1", "1.5", "2 (duplex)", "3", "4", "5"],
  square_footage: [
    "400–600",
    "601–800",
    "801–1,000",
    "1,001–1,200",
    "1,201–1,400",
    "1,401–1,600",
    "1,601–1,800",
    "1,801–2,000",
    "2,001–2,200",
    "2,201–2,400",
    "2,401–2,600",
    "2,601–2,800",
    "2,801–3,000",
    "3,001–3,200",
    "3,201–3,400",
    "3,401–3,600",
    "3,601–3,800",
    "3,801–4,000",
    "4,000–5,000",
    "5,000–6,000"
  ],
  green_supplies_only: ["Yes", "No"],
  english_speaking_staff: ["Yes", "No"],
  overall_condition: ["Very dirty", "Needs work", "Balanced", "Fairly clean", "Very clean"],
  kitchen_condition: ["Very dirty", "Needs work", "Balanced", "Fairly clean", "Very clean"],
  bathrooms_condition: ["Very dirty", "Needs work", "Balanced", "Fairly clean", "Very clean"],
  bedrooms_condition: ["Very dirty", "Needs work", "Balanced", "Fairly clean", "Very clean"],
  living_areas_condition: ["Very dirty", "Needs work", "Balanced", "Fairly clean", "Very clean"],
  supplies_provided_by: ["Company", "Customer"],
  customer_provides_supplies_equipment: ["yes", "no"],
  rating_value: ["1", "2", "3", "4", "5"],
  new_or_returning_customer: ["New", "Returning"],
  referral_source: ["Direct", "Email", "Google organic", "Google paid", "Bing organic", "Bing paid", "Facebook organic", "Facebook paid", "Referral", "Postcard", "Doorhanger", "Other"],
  required_documents: ["No", "ID", "Driver's license", "Insurance"],
  consent_received: ["Yes", "No"],
  quote_received: ["Yes", "No"]
};

type EntryAccessOption = {
  value: string;
  label: string;
  detailKey?: string;
  detailLabel?: string;
  multiline?: boolean;
};

type AccessCodeField = {
  detailKey: string;
  detailLabel: string;
  multiline?: boolean;
};

type AccessCodeOption = {
  value: string;
  label: string;
  fields: AccessCodeField[];
};

const ENTRY_ACCESS_OPTIONS: EntryAccessOption[] = [
  { value: "text_before_arrival", label: "Text 20 mins before arrival" },
  { value: "front_desk_key", label: "Permission left / key with front desk" },
  { value: "call_upon_arrival", label: "Call upon arrival" },
  { value: "will_be_home", label: "I will be home" },
  { value: "intercom_doorbell", label: "Use intercom / doorbell" },
  { value: "let_you_in_and_leave", label: "I will let you in and leave" },
  { value: "remote_open", label: "Will open the door remotely" },
  { value: "key_under_mat", label: "Key is under the entrance mat" },
  { value: "door_unlocked", label: "Door is unlocked" },
  { value: "keys_on_file", label: "My keys are on file" },
  {
    value: "custom",
    label: "Custom",
    detailKey: "entry_information_custom",
    detailLabel: "Custom entry notes",
    multiline: true
  }
];

type EntryAccessOptionCode = (typeof ENTRY_ACCESS_OPTIONS)[number]["value"];
const ENTRY_ACCESS_OPTION_VALUE_SET = new Set<string>(ENTRY_ACCESS_OPTIONS.map((option) => option.value));

const ACCESS_CODE_OPTIONS: AccessCodeOption[] = [
  {
    value: "lockbox_code",
    label: "Lockbox code",
    fields: [
      { detailKey: "lockbox_code", detailLabel: "Lockbox combination" },
      { detailKey: "lockbox_location", detailLabel: "Lockbox location" }
    ]
  },
  {
    value: "gate_code",
    label: "Gate code",
    fields: [{ detailKey: "gate_code", detailLabel: "Gate code" }]
  },
  {
    value: "building_code",
    label: "Building code",
    fields: [{ detailKey: "building_code", detailLabel: "Building code" }]
  },
  {
    value: "unit_entry_code",
    label: "Unit door code",
    fields: [{ detailKey: "unit_code", detailLabel: "Unit door code" }]
  },
  {
    value: "alarm_system_code",
    label: "Alarm system code",
    fields: [{ detailKey: "alarm_code", detailLabel: "Alarm system code" }]
  }
];

type AccessCodeOptionCode = (typeof ACCESS_CODE_OPTIONS)[number]["value"];
const ACCESS_CODE_OPTION_VALUE_SET = new Set<string>(ACCESS_CODE_OPTIONS.map((option) => option.value));

const BOOKING_ADMIN_CHECKBOX_OPTIONS: Record<string, string[]> = {
  extras: [
    "Refrigerator interior",
    "Oven interior",
    "Windows interior",
    "Blinds dusting",
    "Baseboards",
    "Ceiling fans",
    "Cobwebs",
    "Doors & light switches",
    "Cabinets interior (if empty)",
    "Laundry service",
    "Dishes hand-washing",
    "Washer and dryer cleaning",
    "Balcony or patio",
    "Carpet deodorizing",
    "Vapor (steam) cleaning of kitchen and bathrooms",
    "Building common areas (front)",
    "Bed linens change",
    "Antimicrobial treatment",
    "Pet hair clean-up",
    "Rooms freshener",
    "Water the plants",
    "Green supplies"
  ],
  entry_information: ENTRY_ACCESS_OPTIONS.map((option) => option.label),
  priority_areas: ["Kitchen", "Bathroom(s)", "Bedroom(s)", "Living area(s)"],
  skip_areas: [
    "Skip guest bedroom",
    "Skip primary bedroom",
    "Skip guest bathroom",
    "Skip all bedrooms",
    "Skip all bathrooms",
    "Skip living/dining room",
    "Skip hallways",
    "Skip the kitchen"
  ],
  appointment_notifications: ["Email", "SMS", "Phone"]
};

const EXTRA_TOOLTIP_COPY: Record<string, string> = {
  "Refrigerator interior":
    "We clean the interior of your refrigerator, removing spills and buildup to leave it fresh and food-safe. Exterior surfaces are already included in basic service. For best results, please empty the refrigerator and defrost the freezer before we arrive.",
  "Oven interior":
    "We remove grease, debris, and burnt-on food from all interior surfaces to leave your oven clean and fresh. Stovetop cleaning is already included in standard service. Please ensure the oven is cool and empty before cleaning.",
  "Windows interior":
    "We clean interior window glass to remove dust, smudges, and streaks, leaving it clear and bright. Window sills are already included in standard service.",
  "Blinds dusting":
    "We remove dust and allergens from blinds.",
  Baseboards:
    "We dust and wipe all baseboards to remove buildup and refresh edges and corners.",
  "Cabinets interior (if empty)":
    "We clean inside kitchen and bathroom cabinets and drawers, removing dust, crumbs, and buildup. Please empty all cabinets and drawers before we arrive.",
  "Dishes hand-washing":
    "Select this add-on for a full sink of dishes. We will wash, rinse, and neatly place them to dry. Light dishwashing or loading a few items into the dishwasher is included with standard service.",
  "Balcony or patio":
    "We wipe down furniture, sweep and wash floors, and clean accessible windows to remove dust, dirt, and outdoor buildup.",
  "Bed linens change":
    "We remove used linens and replace them with fresh sheets and pillowcases. Please leave clean linens on the bed for our team to use.",
  "Pet hair clean-up":
    "We remove pet hair from floors, carpets, rugs, and furniture using tools designed to lift stubborn fur and dander. This service helps reduce allergens and leaves your home cleaner, fresher, and more guest-ready.",
  "Laundry service":
    "We wash and dry your laundry. Washer and dryer must be available in-unit or in the building. Please separate whites and colors in advance and provide detergent and coins if needed. Off-site laundry is not offered.",
  "Washer and dryer cleaning":
    "We clean the inside and outside of your washer and dryer, removing detergent residue, lint, and buildup.",
  "Laundry room":
    "We clean shared laundry-room surfaces and floors, including accessible counters, machine exteriors, and touch points.",
  "Carpet deodorizing":
    "We apply and gently brush in a deodorizing treatment to neutralize odors and leave carpets fresh and clean.",
  "Rooms freshener":
    "We lightly apply a long-lasting fragrance to leave your home smelling fresh, clean, and inviting."
};

const INCLUDED_BASIC_CLEANING_GROUPS = [
  {
    key: "kitchen",
    title: "Kitchen",
    meta: "12 core tasks",
    body: "Food-prep surfaces, appliances, cabinet fronts, and floors are handled on each visit.",
    points: [
      "Scrub and sanitize sinks",
      "Clean countertops and stovetops",
      "Wipe the exterior of the oven, refrigerator, and dishwasher",
      "Clean the microwave inside and out",
      "Wipe cabinet doors, tables, and chairs",
      "Vacuum and wash floors"
    ]
  },
  {
    key: "bathrooms",
    title: "Bathrooms",
    meta: "13 core tasks",
    body: "Bathrooms are cleaned, disinfected, and detailed from sinks to mirrors to tiled areas.",
    points: [
      "Sanitize sinks, faucets, and counters",
      "Disinfect toilets inside and out",
      "Scrub showers, tubs, and tiles",
      "Polish mirrors and glass surfaces",
      "Wipe bathroom fixtures and cabinet fronts",
      "Sweep, vacuum, and mop floors"
    ]
  },
  {
    key: "bedrooms",
    title: "Bedrooms",
    meta: "10 core tasks",
    body: "Bedrooms get a calm reset with dusting, tidying, mirrors, and floor care handled in one visit.",
    points: [
      "Make beds if linens are provided",
      "Dust furniture, shelves, and room surfaces",
      "Clean mirrors and selected glass surfaces",
      "Straighten pillows, blankets, and loose items",
      "Vacuum under beds and reachable furniture",
      "Vacuum and mop floors with edge attention"
    ]
  },
  {
    key: "common_areas",
    title: "Living areas, hallways, and stairs",
    meta: "9 core tasks",
    body: "Shared spaces are cleaned for a polished, pulled-together feel throughout the home.",
    points: [
      "Dust furniture, shelves, and room surfaces",
      "Clean and polish glass tables and glass accents",
      "Vacuum sofas and chairs, including under cushions",
      "Straighten and organize items for a tidy appearance",
      "Dust railings, banisters, and handrails",
      "Vacuum stairs, hallways, and mop hard floors"
    ]
  },
  {
    key: "finishing",
    title: "Windows and finishing touches",
    meta: "4 finishing tasks",
    body: "Small finishing details help the clean feel complete.",
    points: [
      "Wash the window above the kitchen sink",
      "Clean patio door glass",
      "Clean interior windows when requested",
      "Vacuum and wipe window tracks"
    ]
  }
] as const;

const RESIDENTIAL_REMODELING_OPTIONS = [
  { value: "new_construction_heavy", label: "New construction (heavy debris present)" },
  { value: "new_construction_precleaned", label: "New construction (pre-cleaned by construction company)" },
  { value: "full_remodeling", label: "Full renovation" },
  { value: "partial_remodeling", label: "Partial renovation" }
] as const;

const RESIDENTIAL_PARTIAL_REMODELING_OPTIONS = [
  { value: "kitchen", label: "Kitchen renovation" },
  { value: "bathrooms", label: "Bathrooms renovation" },
  { value: "bedrooms", label: "Bedrooms renovation" },
  { value: "living_areas", label: "Living areas" },
  { value: "floors_replacement", label: "Floors replacement" },
  { value: "floors_refinishing", label: "Floors refinishing" },
  { value: "walls_painting", label: "Walls painting" }
] as const;

const BUILDING_COMMON_AREAS_REMODELING_OPTIONS = [
  { value: "new_construction_heavy", label: "New construction (heavy debris present)" },
  { value: "new_construction_precleaned", label: "New construction (pre-cleaned by construction company)" },
  { value: "full_remodeling", label: "Full renovation" },
  { value: "partial_remodeling", label: "Partial renovation" },
  { value: "floors_replacement", label: "Floors replacement" },
  { value: "carpets_replacement", label: "Carpets replacement" },
  { value: "walls_painting", label: "Walls painting" }
] as const;

const OFFICE_COMMERCIAL_REMODELING_OPTIONS = [
  { value: "new_construction_heavy", label: "New construction (heavy debris present)" },
  { value: "new_construction_precleaned", label: "New construction (pre-cleaned by construction company)" },
  { value: "full_remodeling", label: "Full renovation" },
  { value: "partial_remodeling", label: "Partial renovation" },
  { value: "kitchen", label: "Kitchen renovation" },
  { value: "bathrooms", label: "Restrooms renovation" },
  { value: "workstations", label: "Workstations renovation" },
  { value: "floors_replacement", label: "Floors replacement" },
  { value: "carpets_replacement", label: "Carpets replacement" },
  { value: "walls_painting", label: "Walls painting" }
] as const;

const HOME_CONDITION_FIELDS = [
  { key: "kitchen_condition", label: "Kitchen" },
  { key: "bathrooms_condition", label: "Bathrooms" },
  { key: "bedrooms_condition", label: "Bedrooms" },
  { key: "living_areas_condition", label: "Other common areas" }
] as const;

const SCENARIO_SKIP_AREA_OPTIONS = [
  { value: "kitchen", label: "Kitchen" },
  { value: "bathrooms", label: "Bathrooms" },
  { value: "bedrooms", label: "Bedrooms / offices / dens" },
  { value: "living_areas", label: "Living areas" },
  { value: "living_room", label: "Living room" },
  { value: "dining_room", label: "Dining room" },
  { value: "hallways", label: "Hallways" }
] as const;

const OFFICE_WORKSTATION_OPTIONS = [
  "1-3",
  "4-6",
  "7-9",
  "10-12",
  "13-15",
  "16-18",
  "19-30",
  "Over 30"
] as const;

const OFFICE_ROOM_COUNT_OPTIONS = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "Over 10"
] as const;

const BUILDING_APARTMENT_OPTIONS = [
  "1-3",
  "4-6",
  "7-9",
  "10-12",
  "13-15",
  "Over 15"
] as const;

const OFFICE_COMMERCIAL_HIDDEN_EXTRA_NAMES = new Set(
  [
    "Dishes hand-washing",
    "Laundry service",
    "Washer and dryer cleaning",
    "Building common areas (front)",
    "Bed linens change",
    "Pet hair clean-up",
    "Move In / Move Out",
    "Move in / Move out"
  ].map((value) => value.trim().toLowerCase())
);

const HOME_CONDITION_DEFAULT_LABEL = "Balanced";

function getConditionIndex(options: string[], value: string) {
  const index = options.indexOf(value);
  if (index >= 0) {
    return index;
  }

  const defaultIndex = options.indexOf(HOME_CONDITION_DEFAULT_LABEL);
  return defaultIndex >= 0 ? defaultIndex : Math.floor(options.length / 2);
}

function InfoTooltip({ text, children }: { text?: string; children?: ReactNode }) {
  return (
    <span className="booking-tooltip" tabIndex={0}>
      <span className="booking-info-dot" aria-hidden="true">
        i
      </span>
      <span className="booking-tooltip__bubble" role="tooltip">
        {children ?? text}
      </span>
    </span>
  );
}

function BookingFlowSection({
  eyebrow,
  title,
  children,
  className = ""
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`booking-flow-section ${className}`.trim()}>
      {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
      {title ? <h2 className="booking-flow-section__title">{title}</h2> : null}
      <div className="stack">{children}</div>
    </section>
  );
}

function formatPropertyLookupProviderName(provider: string | undefined) {
  switch (String(provider ?? "").toLowerCase()) {
    case "zillow":
      return "Zillow";
    case "redfin":
      return "Redfin";
    case "rentcast":
      return "RentCast";
    case "estated":
      return "Estated";
    default:
      return "";
  }
}

function PropertyLookupFieldNote({
  value,
  provider
}: {
  value: ReactNode;
  provider?: string;
}) {
  if (!value) {
    return null;
  }

  const providerName = formatPropertyLookupProviderName(provider);
  return (
    <p className="property-lookup-field-note">
      Public data: <strong>{value}</strong>
      {providerName ? ` from ${providerName}` : ""}
    </p>
  );
}

function getExtraTooltipText(name: string, description: unknown) {
  const explicit = String(description ?? "").trim();
  if (explicit) {
    return explicit;
  }

  return EXTRA_TOOLTIP_COPY[name] ?? "";
}

function normalizeOptionToken(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function isCarpetShampooingServiceOption(option: BookingFormServiceOption) {
  return Boolean(option.carpetShampooingConfig?.enabled) || normalizeOptionToken(option.name).includes("carpet_shampooing");
}

function isBuildingCommonAreasFrontServiceOption(option: BookingFormServiceOption) {
  const optionMetadata = option as BookingFormServiceOption & Record<string, unknown>;
  const token = normalizeOptionToken(
    [option.name, optionMetadata.estimatorExtraCode, optionMetadata.estimator_extra_code, optionMetadata.code]
      .filter(Boolean)
      .join(" ")
  );
  return token.includes("building_common_areas_front") || token.includes("building_common_front");
}

function isDishesHandWashingServiceOption(option: BookingFormServiceOption) {
  const optionMetadata = option as BookingFormServiceOption & Record<string, unknown>;
  const token = normalizeOptionToken(
    [option.name, optionMetadata.estimatorExtraCode, optionMetadata.estimator_extra_code, optionMetadata.code]
      .filter(Boolean)
      .join(" ")
  );
  return token.includes("dishes_hand_washing") || (token.includes("dishes") && token.includes("hand"));
}

function isLaundryServiceOption(option: BookingFormServiceOption) {
  const optionMetadata = option as BookingFormServiceOption & Record<string, unknown>;
  const token = normalizeOptionToken(
    [option.name, optionMetadata.estimatorExtraCode, optionMetadata.estimator_extra_code, optionMetadata.code]
      .filter(Boolean)
      .join(" ")
  );
  return token.includes("laundry_service") || token.includes("laundry");
}

function isSimpleQuantityServiceOption(option: BookingFormServiceOption) {
  return !isCarpetShampooingServiceOption(option) &&
    (Boolean(option.quantityBased) || isDishesHandWashingServiceOption(option) || isLaundryServiceOption(option));
}

function getSimpleQuantityAdminDetailKey(option: BookingFormServiceOption) {
  const configuredKey = String(
    option.quantityAdminDetailKey ??
      (option as BookingFormServiceOption & Record<string, unknown>).quantity_admin_detail_key ??
      ""
  ).trim();
  if (configuredKey) {
    return configuredKey;
  }
  if (isDishesHandWashingServiceOption(option)) {
    return "dishes_hand_washing_sinkfuls";
  }
  if (isLaundryServiceOption(option)) {
    return "laundry_service_loads";
  }
  return `extra_quantity_${normalizeOptionToken(option.id)}`;
}

function getSimpleQuantityLabel(option: BookingFormServiceOption) {
  const configuredLabel = String(option.quantityLabel ?? "").trim();
  if (configuredLabel) {
    return configuredLabel;
  }
  if (isDishesHandWashingServiceOption(option)) {
    return "How many sinkfuls?";
  }
  return isLaundryServiceOption(option) ? "How many loads?" : "Quantity";
}

function getSimpleQuantityRange(option: BookingFormServiceOption) {
  const minQuantity = Math.max(1, Math.round(Number(option.minQuantity ?? 1) || 1));
  const configuredMax = Math.round(Number(option.maxQuantity ?? 0) || 0);
  const maxQuantity = Math.max(minQuantity, configuredMax > 0 ? configuredMax : 10);
  return { minQuantity, maxQuantity };
}

function normalizeSimpleQuantityValue(value: unknown, option: BookingFormServiceOption) {
  const { minQuantity, maxQuantity } = getSimpleQuantityRange(option);
  const parsed = Math.round(Number(value ?? minQuantity));
  if (!Number.isFinite(parsed)) {
    return minQuantity;
  }
  return Math.min(maxQuantity, Math.max(minQuantity, parsed));
}

function formatSimpleQuantitySummary(quantity: unknown, quantityLabel: unknown) {
  const normalizedQuantity = Math.max(1, Math.round(Number(quantity ?? 1) || 1));
  const normalizedLabel = String(quantityLabel ?? "").trim();
  if (!normalizedLabel) {
    return normalizedQuantity > 1 ? `Quantity: ${normalizedQuantity}` : "";
  }
  const displayLabel = normalizedQuantity === 1 ? normalizedLabel : `${normalizedLabel}s`;
  return `${normalizedQuantity} ${displayLabel}`;
}

const EXTRA_TASK_AREA_GROUPS = [
  { key: "kitchen", title: "Kitchen" },
  { key: "rooms_surfaces", title: "Rooms and surfaces" },
  { key: "floors_carpets", title: "Floors and carpets" },
  { key: "laundry_linens", title: "Laundry and linens" },
  { key: "supplies_treatments", title: "Supplies and treatments" },
  { key: "other", title: "Other tasks" }
] as const;

type ExtraTaskAreaGroupKey = (typeof EXTRA_TASK_AREA_GROUPS)[number]["key"];

function getExtraTaskAreaGroupKey(option: BookingFormServiceOption): ExtraTaskAreaGroupKey {
  const optionMetadata = option as BookingFormServiceOption & Record<string, unknown>;
  const token = normalizeOptionToken(
    [
      option.name,
      option.description,
      optionMetadata.estimatorExtraCode,
      optionMetadata.estimator_extra_code,
      optionMetadata.code
    ]
      .filter(Boolean)
      .join(" ")
  );

  if (/(refrigerator|fridge|oven|cabinet|dish|kitchen)/.test(token)) {
    return "kitchen";
  }
  if (/(carpet|rug|floor|baseboard)/.test(token)) {
    return "floors_carpets";
  }
  if (/(laundry|linen|bed)/.test(token)) {
    return "laundry_linens";
  }
  if (/(supply|supplies|antimicrobial|freshener|deodor)/.test(token)) {
    return "supplies_treatments";
  }
  if (/(window|blind|fan|cobweb|door|switch|patio|balcony|pet|plant|room)/.test(token)) {
    return "rooms_surfaces";
  }

  return "other";
}

function groupVisibleExtraOptions(options: BookingFormServiceOption[]) {
  const byGroup = new Map<ExtraTaskAreaGroupKey, BookingFormServiceOption[]>();
  for (const group of EXTRA_TASK_AREA_GROUPS) {
    byGroup.set(group.key, []);
  }

  for (const option of options) {
    byGroup.get(getExtraTaskAreaGroupKey(option))?.push(option);
  }

  return EXTRA_TASK_AREA_GROUPS.map((group) => ({
    ...group,
    options: byGroup.get(group.key) ?? []
  })).filter((group) => group.options.length > 0);
}

function getBookingAdminSelectOptions(key: string, currentValue = "", extraOptions: string[] = []) {
  const options = [...(BOOKING_ADMIN_SELECT_OPTIONS[key] ?? []), ...extraOptions];
  const unique = Array.from(new Set(options.filter((option) => option.trim() !== "")));
  if (currentValue && !unique.includes(currentValue)) {
    unique.unshift(currentValue);
  }
  return unique;
}

function findNumericAdminSelectOption(key: "bedrooms" | "bathrooms", value: number | null | undefined) {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return "";
  }

  const normalizedValue = key === "bathrooms" ? Math.round(numericValue * 2) / 2 : Math.round(numericValue);
  const label = Number.isInteger(normalizedValue) ? String(normalizedValue) : normalizedValue.toFixed(1);
  return getBookingAdminSelectOptions(key).includes(label) ? label : "";
}

type BookingSquareFootageEstimatePropertyType = "apartment" | "townhouse" | "single_family";

type BookingSquareFootageFallback = {
  squareFeet: number;
  notice: string;
};

const BOOKING_SQUARE_FOOTAGE_ESTIMATE_BEDROOM_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const BOOKING_SQUARE_FOOTAGE_ESTIMATE_BATHROOM_VALUES = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 8];
const BOOKING_SQUARE_FOOTAGE_ESTIMATE_LEVEL_VALUES = [1, 1.5, 2, 3, 4, 5];

const BOOKING_SQUARE_FOOTAGE_BEDROOM_BASE: Record<BookingSquareFootageEstimatePropertyType, number[]> = {
  apartment: [500, 700, 950, 1200, 1450, 1700, 1950, 2200, 2450],
  townhouse: [650, 850, 1050, 1300, 1650, 2000, 2350, 2700, 3050],
  single_family: [750, 900, 1200, 1450, 1800, 2200, 2600, 3000, 3400]
};

const BOOKING_SQUARE_FOOTAGE_BATHROOM_ADJUSTMENTS: Record<
  BookingSquareFootageEstimatePropertyType,
  Record<number, number>
> = {
  apartment: {
    0.5: -50,
    1: 0,
    1.5: 75,
    2: 150,
    2.5: 225,
    3: 300,
    3.5: 375,
    4: 450,
    5: 600,
    6: 750,
    7: 900,
    8: 1050
  },
  townhouse: {
    0.5: -50,
    1: 0,
    1.5: 65,
    2: 125,
    2.5: 190,
    3: 250,
    3.5: 315,
    4: 375,
    5: 500,
    6: 625,
    7: 750,
    8: 875
  },
  single_family: {
    0.5: -50,
    1: 0,
    1.5: 75,
    2: 150,
    2.5: 225,
    3: 300,
    3.5: 375,
    4: 450,
    5: 600,
    6: 750,
    7: 900,
    8: 1050
  }
};

const BOOKING_SQUARE_FOOTAGE_LEVEL_ADJUSTMENTS: Record<
  BookingSquareFootageEstimatePropertyType,
  Record<number, number>
> = {
  apartment: {
    1: 0,
    1.5: 150,
    2: 250,
    3: 350,
    4: 450,
    5: 550
  },
  townhouse: {
    1: 0,
    1.5: 150,
    2: 250,
    3: 450,
    4: 650,
    5: 850
  },
  single_family: {
    1: 0,
    1.5: 150,
    2: 250,
    3: 500,
    4: 800,
    5: 1000
  }
};

function parseBookingNumericDetail(value: unknown) {
  const match = String(value ?? "")
    .replace(/,/g, "")
    .match(/\d+(?:\.\d+)?/);
  if (!match) {
    return null;
  }

  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function clampBookingSquareFootageEstimateValue(value: number, availableValues: number[]) {
  if (availableValues.includes(value)) {
    return value;
  }

  const sortedValues = [...availableValues].sort((a, b) => a - b);
  const minimum = sortedValues[0];
  const maximum = sortedValues[sortedValues.length - 1];

  if (value <= minimum) {
    return minimum;
  }

  if (value >= maximum) {
    return maximum;
  }

  return sortedValues.reduce((closest, candidate) =>
    Math.abs(candidate - value) < Math.abs(closest - value) ? candidate : closest
  );
}

function toBookingSquareFootageEstimatePropertyType(
  service: BookingFormService | undefined
): BookingSquareFootageEstimatePropertyType | null {
  const normalized = normalizeServiceName(service?.name);
  if (normalized === "townhouse") {
    return "townhouse";
  }
  if (normalized === "single family house") {
    return "single_family";
  }
  return normalized === "apartment condo" ? "apartment" : null;
}

function parseSquareFeetTextValue(value: unknown) {
  const normalized = String(value ?? "").replace(/[,\s]/g, "");
  const parsed = Number(normalized);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return Math.round(parsed);
}

function formatSquareFeetInputValue(value: number | null | undefined) {
  return Number.isFinite(value) ? String(Math.round(value!)) : "";
}

function isSquareFootageUnknownSelected(adminDetails: Record<string, string>) {
  return String(adminDetails.square_footage_unknown ?? "").trim().toLowerCase() === "yes";
}

function getBookingSquareFootageExactInputValue(adminDetails: Record<string, string>) {
  if (isSquareFootageUnknownSelected(adminDetails)) {
    return "";
  }

  const exactValue = String(adminDetails.square_footage_exact ?? "").trim();
  if (exactValue) {
    return exactValue;
  }

  const squareFeet = parseSquareFeetTextValue(adminDetails.square_footage);
  return squareFeet ? formatSquareFeetInputValue(squareFeet) : "";
}

function getBookingSquareFootageFallback(
  service: BookingFormService | undefined,
  adminDetails: Record<string, string>
): BookingSquareFootageFallback | null {
  const propertyType = toBookingSquareFootageEstimatePropertyType(service);
  if (!propertyType) {
    return null;
  }

  const bedrooms = parseBookingNumericDetail(adminDetails.bedrooms);
  const bathrooms = parseBookingNumericDetail(adminDetails.bathrooms);
  const levels = parseBookingNumericDetail(adminDetails.levels);

  if (
    !Number.isFinite(bedrooms) ||
    !Number.isFinite(bathrooms) ||
    !Number.isFinite(levels) ||
    levels == null ||
    levels < 1
  ) {
    return null;
  }

  const bedroomValue = clampBookingSquareFootageEstimateValue(
    bedrooms!,
    BOOKING_SQUARE_FOOTAGE_ESTIMATE_BEDROOM_VALUES
  );
  const bathroomValue = clampBookingSquareFootageEstimateValue(
    bathrooms!,
    BOOKING_SQUARE_FOOTAGE_ESTIMATE_BATHROOM_VALUES
  );
  const levelValue = clampBookingSquareFootageEstimateValue(levels, BOOKING_SQUARE_FOOTAGE_ESTIMATE_LEVEL_VALUES);
  const baseValues = BOOKING_SQUARE_FOOTAGE_BEDROOM_BASE[propertyType];
  const baseSquareFeet = baseValues[Math.max(0, Math.min(baseValues.length - 1, bedroomValue))];
  const bathroomAdjustment = BOOKING_SQUARE_FOOTAGE_BATHROOM_ADJUSTMENTS[propertyType][bathroomValue] ?? 0;
  const levelAdjustment = BOOKING_SQUARE_FOOTAGE_LEVEL_ADJUSTMENTS[propertyType][levelValue] ?? 0;
  const squareFeet = Math.max(
    400,
    Math.round((baseSquareFeet + bathroomAdjustment + levelAdjustment) / 50) * 50
  );

  return {
    squareFeet,
    notice: "Estimated using average square footage for this home size. Please update if different."
  };
}

function parseBookingMultiSelectValue(value: string | null | undefined) {
  return Array.from(
    new Set(
      String(value ?? "")
        .split(/\r?\n|,|\|/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
}

function getScenarioSkipAreaOptions(
  scenario: BookingFormServiceScenario | null | undefined,
  useResidentialDefaults = false
) {
  const allowed = new Set(normalizeStringArray(scenario?.allowedSkipAreas));
  if (!allowed.size) {
    return useResidentialDefaults
      ? [...SCENARIO_SKIP_AREA_OPTIONS]
      : ([] as Array<(typeof SCENARIO_SKIP_AREA_OPTIONS)[number]>);
  }
  return SCENARIO_SKIP_AREA_OPTIONS.filter((option) => allowed.has(option.value));
}

function getSelectedSkipAreaCodes(adminDetails: Record<string, string>) {
  return parseBookingMultiSelectValue(adminDetails.skip_areas)
    .map((entry) => String(entry).trim().toLowerCase())
    .filter(Boolean);
}

type IncludedChecklistStatus = "included" | "partial" | "excluded";

function getIncludedChecklistStatus(
  areaKey: string,
  selectedSkipAreaCodes: string[],
  adminDetails: Record<string, string>,
  skipAreasEnabled: boolean
): IncludedChecklistStatus {
  if (!skipAreasEnabled || !selectedSkipAreaCodes.length) {
    return "included";
  }

  const isSelected = (code: string) => selectedSkipAreaCodes.includes(code);
  const selectedBedrooms = Number(adminDetails.bedrooms || 0);
  const selectedBathrooms = Number(adminDetails.bathrooms || 0);
  const skippedBedrooms = Number(adminDetails.skip_bedrooms_count || 0);
  const skippedBathrooms = Number(adminDetails.skip_bathrooms_count || 0);
  const commonAreasSkipped = ["living_areas", "living_room", "dining_room", "hallways"].some(isSelected);
  const allCommonAreasSkipped =
    isSelected("living_areas") || (isSelected("living_room") && isSelected("dining_room") && isSelected("hallways"));

  switch (areaKey) {
    case "kitchen":
      return isSelected("kitchen") ? "excluded" : "included";
    case "bathrooms":
      if (!isSelected("bathrooms")) {
        return "included";
      }
      return selectedBathrooms > 0 && skippedBathrooms >= selectedBathrooms ? "excluded" : "partial";
    case "bedrooms":
      if (!isSelected("bedrooms")) {
        return "included";
      }
      return selectedBedrooms > 0 && skippedBedrooms >= selectedBedrooms ? "excluded" : "partial";
    case "common_areas":
      if (!commonAreasSkipped) {
        return "included";
      }
      return allCommonAreasSkipped ? "excluded" : "partial";
    default:
      return "included";
  }
}

function labelForIncludedChecklistStatus(status: IncludedChecklistStatus) {
  switch (status) {
    case "excluded":
      return "Excluded";
    case "partial":
      return "Partially included";
    default:
      return "Included";
  }
}

function getLimitedSkipCountOptions(
  key: "bedrooms" | "bathrooms",
  selectedTotalValue: string
) {
  const options = getBookingAdminSelectOptions(key).filter((option) => {
    const numeric = Number(option);
    return Number.isFinite(numeric) ? numeric > 0 : true;
  });
  const selectedTotal = Number(selectedTotalValue);
  if (!Number.isFinite(selectedTotal) || selectedTotal <= 0) {
    return options;
  }

  return options.filter((option) => {
    const numeric = Number(option);
    return Number.isFinite(numeric) ? numeric <= selectedTotal : true;
  });
}

function serializeBookingMultiSelectValue(values: string[]) {
  return values.join(" | ");
}

function toggleStringSelection(values: string[], nextValue: string, checked: boolean) {
  if (checked) {
    return values.includes(nextValue) ? values : [...values, nextValue];
  }

  return values.filter((value) => value !== nextValue);
}

function isActiveRecord(value: { active?: number | boolean } | null | undefined) {
  return value?.active !== 0 && value?.active !== false;
}

function normalizeStringArray(value: unknown) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean);
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(/[|,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [] as string[];
}

function getCustomerSuppliesChoice(adminDetails: Record<string, string>) {
  const providedBy = String(adminDetails.supplies_provided_by ?? "").trim().toLowerCase();
  const explicit = String(adminDetails.customer_provides_supplies_equipment ?? "").trim().toLowerCase();

  if (providedBy === "customer" || ["yes", "true", "1"].includes(explicit)) {
    return "Customer";
  }
  if (providedBy === "company" || ["no", "false", "0"].includes(explicit)) {
    return "Company";
  }
  return "";
}

function getCustomerSuppliesRequiredItems(settings: BookingFormSettings["customerSuppliesEquipment"] | undefined) {
  const items = normalizeStringArray(settings?.requiredItems);
  return items.length ? items : customerSuppliesEquipmentFallbackItems;
}

function normalizeScenarioProfileValue(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getScenarioQuestionProfile(
  scenario: BookingFormServiceScenario | null | undefined,
  service: BookingFormService | undefined
): ScenarioQuestionProfile {
  const explicit = normalizeScenarioProfileValue(scenario?.questionProfile);
  if (
    explicit === "residential" ||
    explicit === "office_commercial" ||
    explicit === "building_common_areas" ||
    explicit === "carpet_standalone"
  ) {
    return explicit;
  }

  const serviceFallback = normalizeScenarioProfileValue(service?.name);
  if (serviceFallback.includes("office_commercial") || serviceFallback.includes("office_commercial_cleaning")) {
    return "office_commercial";
  }
  if (serviceFallback.includes("building_common_areas")) {
    return "building_common_areas";
  }
  if (serviceFallback.includes("carpet_standalone") || serviceFallback.includes("carpet_shampooing_standalone")) {
    return "carpet_standalone";
  }

  const scenarioFallback = normalizeScenarioProfileValue(scenario?.code);
  if (scenarioFallback.includes("office_commercial") || scenarioFallback.includes("office_commercial_cleaning")) {
    return "office_commercial";
  }
  if (scenarioFallback.includes("building_common_areas")) {
    return "building_common_areas";
  }
  if (scenarioFallback.includes("carpet_standalone") || scenarioFallback.includes("carpet_shampooing_standalone")) {
    return "carpet_standalone";
  }
  return "residential";
}

function getScenarioRemodelProfile(
  scenario: BookingFormServiceScenario | null | undefined,
  service: BookingFormService | undefined
): ScenarioRemodelProfile {
  const explicit = normalizeScenarioProfileValue(scenario?.remodelProfile);
  if (
    explicit === "residential" ||
    explicit === "office_commercial" ||
    explicit === "building_common_areas" ||
    explicit === "none"
  ) {
    return explicit;
  }

  const questionProfile = getScenarioQuestionProfile(scenario, service);
  return questionProfile === "carpet_standalone" ? "none" : questionProfile;
}

function getScenarioExtraFilterProfile(
  scenario: BookingFormServiceScenario | null | undefined,
  service: BookingFormService | undefined
): ScenarioExtraFilterProfile {
  const explicit = normalizeScenarioProfileValue(scenario?.extraFilterProfile);
  if (
    explicit === "residential" ||
    explicit === "office_commercial" ||
    explicit === "building_common_areas" ||
    explicit === "carpet_standalone"
  ) {
    return explicit;
  }

  return getScenarioQuestionProfile(scenario, service);
}

function getScenarioRemodelOptions(profile: ScenarioRemodelProfile) {
  switch (profile) {
    case "office_commercial":
      return OFFICE_COMMERCIAL_REMODELING_OPTIONS;
    case "building_common_areas":
      return BUILDING_COMMON_AREAS_REMODELING_OPTIONS;
    case "none":
      return [] as const;
    default:
      return RESIDENTIAL_REMODELING_OPTIONS;
  }
}

function getScenarioPartialRemodelOptions(profile: ScenarioRemodelProfile) {
  switch (profile) {
    case "residential":
      return RESIDENTIAL_PARTIAL_REMODELING_OPTIONS;
    default:
      return [] as const;
  }
}

function normalizeRemodelingOptionCode(value: unknown) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (normalized === "bathroom_remodeling") {
    return "bathrooms";
  }
  if (normalized === "living_areas_remodeling") {
    return "living_areas";
  }
  if (normalized === "floor_refinishing") {
    return "floors_refinishing";
  }
  if (normalized === "wall_painting") {
    return "walls_painting";
  }
  if (normalized === "kitchen_remodeling") {
    return "kitchen";
  }

  return normalized;
}

function parseMultiSelectCodes(value: unknown) {
  return parseBookingMultiSelectValue(String(value ?? ""))
    .map((entry) => normalizeRemodelingOptionCode(entry))
    .filter(Boolean);
}

function normalizeEntryAccessOptionCode(value: unknown): EntryAccessOptionCode | "" {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  switch (normalized) {
    case "text_before_arrival":
    case "text_20_mins_before_arrival":
      return "text_before_arrival";
    case "front_desk_key":
    case "permission_left_key_with_front_desk":
    case "permission_key_from_front_desk":
      return "front_desk_key";
    case "call_upon_arrival":
      return "call_upon_arrival";
    case "will_be_home":
    case "i_will_be_home":
      return "will_be_home";
    case "intercom_doorbell":
    case "use_intercom_doorbell":
    case "use_intercom_or_doorbell":
      return "intercom_doorbell";
    case "let_you_in_and_leave":
    case "i_will_let_you_in_and_leave":
    case "will_let_you_in_and_leave_lock_the_door_when_done":
      return "let_you_in_and_leave";
    case "remote_open":
    case "will_open_the_door_remotely":
      return "remote_open";
    case "key_under_mat":
    case "key_is_under_the_entrance_mat":
    case "key_under_entrance_mat":
      return "key_under_mat";
    case "door_unlocked":
    case "door_is_unlocked":
      return "door_unlocked";
    case "lockbox_code":
    case "lockbox_code_provided_in_notes":
      return "lockbox_code";
    case "building_code":
      return "building_code";
    case "gate_code":
      return "gate_code";
    case "unit_entry_code":
    case "unit_code":
      return "unit_entry_code";
    case "alarm_system_code":
    case "alarm_code":
      return "alarm_system_code";
    case "key_hiding_spot_is":
    case "key_hiding_spot_provided_in_notes":
      return "key_hiding_spot";
    case "keys_on_file":
    case "my_keys_are_on_file_with_accessmaids":
    case "company_got_my_keys":
    case "accessmaids_has_my_keys":
      return "keys_on_file";
    case "parking_instructions":
      return "parking_instructions";
    case "custom":
      return "custom";
    default:
      return "";
  }
}

function parseEntryAccessCodes(value: unknown) {
  return parseBookingMultiSelectValue(String(value ?? ""))
    .map((entry) => normalizeEntryAccessOptionCode(entry))
    .filter((entry): entry is EntryAccessOptionCode => ENTRY_ACCESS_OPTION_VALUE_SET.has(entry));
}

function parseAccessCodeOptionCodes(value: unknown) {
  return parseBookingMultiSelectValue(String(value ?? ""))
    .map((entry) => normalizeEntryAccessOptionCode(entry))
    .filter((entry): entry is AccessCodeOptionCode => ACCESS_CODE_OPTION_VALUE_SET.has(entry));
}

function buildEntryAccessSummary(adminDetails: Record<string, string>) {
  const selectedEntryCodes = new Set(parseEntryAccessCodes(adminDetails.entry_information_selected));
  const accessCodesEnabled = String(adminDetails.access_codes_enabled ?? "").trim().toLowerCase() === "yes";
  const selectedAccessCodes = new Set(parseAccessCodeOptionCodes(adminDetails.access_code_selected));
  if (!selectedEntryCodes.size && !(accessCodesEnabled && selectedAccessCodes.size)) {
    return "";
  }

  const parts = ENTRY_ACCESS_OPTIONS.flatMap((option) => {
    if (!selectedEntryCodes.has(option.value)) {
      return [];
    }

    const detailKey = option.detailKey;
    if (detailKey) {
      const detailValue = String(adminDetails[detailKey] ?? "").trim();
      if (detailValue) {
        const summaryLabel = option.value === "custom" ? "Custom" : option.label;
        return [`${summaryLabel}: ${detailValue}`];
      }
    }

    return [option.label];
  });

  if (accessCodesEnabled && selectedAccessCodes.size) {
    for (const option of ACCESS_CODE_OPTIONS) {
      if (!selectedAccessCodes.has(option.value)) {
        continue;
      }

      for (const field of option.fields) {
        const detailValue = String(adminDetails[field.detailKey] ?? "").trim();
        if (detailValue) {
          parts.push(`${field.detailLabel}: ${detailValue}`);
        }
      }
    }
  }

  return parts.join(" | ");
}

function getRemodelingOptionLabel(
  profile: ScenarioRemodelProfile,
  optionCode: string,
  partialAreaCodes: string[] = []
) {
  const combinedOptions = [...getScenarioRemodelOptions(profile), ...getScenarioPartialRemodelOptions(profile)];
  const match = combinedOptions.find((option) => option.value === optionCode);
  if (optionCode === "partial_remodeling" && partialAreaCodes.length) {
    const partialLabels = getScenarioPartialRemodelOptions(profile)
      .filter((option) => partialAreaCodes.includes(option.value))
      .map((option) => option.label);
    return partialLabels.length ? `Partial renovation (${partialLabels.join(", ")})` : "Partial renovation";
  }
  return match?.label ?? String(optionCode ?? "");
}

function formatPartialRemodelAreaLabel(
  optionCode: string,
  baseLabel: string,
  adminDetails: Record<string, string>
) {
  if (optionCode === "bedrooms") {
    const count = String(adminDetails.partial_remodeling_bedrooms_count ?? "").trim();
    return count ? `${baseLabel} (${count})` : baseLabel;
  }

  if (optionCode === "bathrooms") {
    const count = String(adminDetails.partial_remodeling_bathrooms_count ?? "").trim();
    return count ? `${baseLabel} (${count})` : baseLabel;
  }

  return baseLabel;
}

function buildRemodelingSummary(
  profile: ScenarioRemodelProfile,
  adminDetails: Record<string, string>
) {
  if (String(adminDetails.recent_remodeling ?? "no").trim().toLowerCase() !== "yes") {
    return "";
  }

  const remodelingTypeCode = normalizeRemodelingOptionCode(adminDetails.remodeling_type);
  if (!remodelingTypeCode) {
    return "";
  }

  if (remodelingTypeCode === "partial_remodeling") {
    const selectedCodes = parseMultiSelectCodes(adminDetails.partial_remodeling_areas);
    if (!selectedCodes.length) {
      return "Partial renovation";
    }

    const labels = getScenarioPartialRemodelOptions(profile)
      .filter((option) => selectedCodes.includes(option.value))
      .map((option) => formatPartialRemodelAreaLabel(option.value, option.label, adminDetails));

    return labels.length ? `Partial renovation (${labels.join(", ")})` : "Partial renovation";
  }

  return getRemodelingOptionLabel(profile, remodelingTypeCode);
}

function getScenarioRemodelPrompt(profile: ScenarioRemodelProfile) {
  switch (profile) {
    case "office_commercial":
      return "Any recent renovation?";
    case "building_common_areas":
      return "Any recent building common areas renovation?";
    default:
      return "Any recent renovation?";
  }
}

function normalizePostalCode(value: string) {
  return value.replace(/\D/g, "").slice(0, 5);
}

function isProblemAccountRecord(input: {
  accountStatus?: string | null | undefined;
  blockedUntil?: string | null | undefined;
}) {
  return (
    String(input.accountStatus ?? "").trim().toLowerCase() === "problem account" ||
    Boolean(String(input.blockedUntil ?? "").trim())
  );
}

function normalizeWeekdayKeys(value: unknown) {
  return normalizeStringArray(value).map((entry) => String(entry).trim().toLowerCase());
}

function formatCrewHours(hours: number) {
  if (!Number.isFinite(hours) || hours <= 0) {
    return "0 hours";
  }

  const rounded = Math.round(hours * 100) / 100;
  const wholeNumber = Number.isInteger(rounded);
  const display = wholeNumber ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, "");
  return `${display} ${rounded === 1 ? "hour" : "hours"}`;
}

function formatLaborHours(hours: number) {
  if (!Number.isFinite(hours) || hours <= 0) {
    return "0 labor hours";
  }

  const rounded = Math.round(hours * 100) / 100;
  const wholeNumber = Number.isInteger(rounded);
  const display = wholeNumber ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, "");
  return `${display} ${rounded === 1 ? "labor hour" : "labor hours"}`;
}

function formatPublicCurrency(amountInCents: number) {
  const safeAmount = Number.isFinite(Number(amountInCents)) ? Math.round(Number(amountInCents)) : 0;
  const formatted = formatCurrency(safeAmount);
  return Math.abs(safeAmount) % 100 === 0 ? formatted.replace(/\.00\b/, "") : formatted;
}

function formatMinuteQuantity(totalMinutes: number) {
  const safeMinutes = Math.max(0, Number(totalMinutes) || 0);
  const rounded = Math.round(safeMinutes * 10) / 10;
  const display = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace(/\.0$/, "");
  return `${display} ${rounded === 1 ? "minute" : "minutes"}`;
}

function formatDurationHoursMinutes(totalMinutes: number) {
  const roundedMinutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;

  if (hours <= 0) {
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }

  if (minutes <= 0) {
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }

  return `${hours} ${hours === 1 ? "hour" : "hours"} ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}

function formatEstimatedDurationSummary(durationMinutes: number, teamSize: number) {
  const safeVisitMinutes = Math.max(0, durationMinutes);
  const safeTeamSize = Math.max(1, Math.round(teamSize || 1));
  const crewLabel = `${safeTeamSize} ${safeTeamSize === 1 ? "maid" : "maids"}`;
  const safeLaborHours = (safeVisitMinutes * safeTeamSize) / 60;
  const laborLabel = formatLaborHours(safeLaborHours);
  const visitLabel = formatDurationHoursMinutes(safeVisitMinutes);
  return {
    primary: `${crewLabel} x ${visitLabel}`,
    secondary: `(${laborLabel})`
  };
}

function buildLaborHoursExplanationRows(laborHours: number, amountCents: number) {
  const safeLaborHours = Math.max(0, Number(laborHours) || 0);
  return [1, 2, 3].map((maidCount) => {
    const visitMinutes = safeLaborHours > 0 ? (safeLaborHours * 60) / maidCount : 0;
    const durationLabel = maidCount === 1
      ? formatCrewHours(safeLaborHours)
      : formatDurationHoursMinutes(visitMinutes);
    return `${maidCount} ${maidCount === 1 ? "maid" : "maids"} x ${durationLabel} = ${formatPublicCurrency(amountCents)}`;
  });
}

function formatRemodelingMinimumSummary(maidCount: number, minimumHours: number) {
  const safeMaidCount = Math.max(1, Math.round(Number(maidCount) || 1));
  const safeMinimumHours = Math.max(0, Number(minimumHours) || 0);
  const hourLabel = formatCrewHours(safeMinimumHours);
  return `${safeMaidCount} ${safeMaidCount === 1 ? "maid" : "maids"} x ${hourLabel} minimum`;
}

function equalStringSets(left: string[], right: string[]) {
  if (left.length !== right.length) {
    return false;
  }

  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
}

function getWeekdayKey(date: string) {
  if (!date) {
    return "";
  }

  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][parsed.getDay()] ?? "";
}

function normalizeLocationServiceAreaRules(value: unknown) {
  if (!Array.isArray(value)) {
    return [] as LocationServiceAreaRule[];
  }

  return value
    .filter((entry): entry is LocationServiceAreaRule => Boolean(entry) && typeof entry === "object" && !Array.isArray(entry))
    .map((entry) => ({
      ...entry,
      zipCode: normalizePostalCode(String(entry.zipCode ?? "")),
      allowedWeekdays: normalizeWeekdayKeys(entry.allowedWeekdays),
      active: entry.active !== 0 && entry.active !== false
    }))
    .filter((entry) => entry.zipCode.length === 5);
}

function getLocationServiceAreaRule(location: LocationSettingsItem | undefined, zipCode: string, date: string) {
  if (!location || !zipCode) {
    return null;
  }

  const weekday = getWeekdayKey(date);
  const matchingRules = normalizeLocationServiceAreaRules(location.serviceAreaRules).filter(
    (rule) => rule.active !== false && normalizePostalCode(rule.zipCode) === zipCode
  );
  if (!matchingRules.length) {
    return null;
  }

  const weekdaySpecificRule = matchingRules.find((rule) => {
    const allowedWeekdays = normalizeWeekdayKeys(rule.allowedWeekdays);
    return allowedWeekdays.length > 0 && allowedWeekdays.includes(weekday);
  });
  if (weekdaySpecificRule) {
    return weekdaySpecificRule;
  }

  const unrestrictedRule = matchingRules.find((rule) => normalizeWeekdayKeys(rule.allowedWeekdays).length === 0);
  return unrestrictedRule ?? { ...matchingRules[0], allowedWeekdays: normalizeWeekdayKeys(matchingRules[0].allowedWeekdays) };
}

function getSlotTimeForLocation(startsAt: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(new Date(startsAt));
}

const ARRIVAL_WINDOW_END_BY_START: Record<string, string> = {
  "07:30": "08:00",
  "09:30": "11:00",
  "11:30": "13:00",
  "13:30": "15:30"
};

const CALENDAR_WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function formatLocalClockLabel(value: string) {
  const [rawHours, rawMinutes] = value.split(":");
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return { timeText: value, meridiem: "" };
  }

  const meridiem = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return {
    timeText: `${displayHours}:${String(minutes).padStart(2, "0")}`,
    meridiem
  };
}

const SMS_REMINDER_APPOINTMENT_NOTIFICATION_LABEL = "SMS reminders";

function parseAppointmentNotificationLabels(value: unknown) {
  return Array.from(
    new Set(
      String(value ?? "")
        .split(/[,\n|]/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
}

function formatArrivalWindowLabel(startTime: string, endTime: string) {
  const start = formatLocalClockLabel(startTime);
  const end = formatLocalClockLabel(endTime);
  const startText = compactClockText(start.timeText);
  const endText = compactClockText(end.timeText);
  if (start.meridiem && start.meridiem === end.meridiem) {
    return `${startText}-${endText} ${end.meridiem}`;
  }
  return `${startText}${start.meridiem ? ` ${start.meridiem}` : ""}-${endText}${end.meridiem ? ` ${end.meridiem}` : ""}`;
}

function getAvailabilityOptionLabel(
  slot: Pick<AvailabilitySlotSummary, "startsAt" | "startsAtLocal">,
  timeZone?: string
) {
  const localStart = timeZone
    ? getSlotTimeForLocation(slot.startsAt, timeZone)
    : String(slot.startsAtLocal).split("T")[1]?.slice(0, 5) ?? "";
  const normalizedLocalStart = localStart.trim();
  const arrivalWindowEnd = ARRIVAL_WINDOW_END_BY_START[normalizedLocalStart];
  if (arrivalWindowEnd) {
    return formatArrivalWindowLabel(normalizedLocalStart, arrivalWindowEnd);
  }

  const fallback = formatLocalClockLabel(normalizedLocalStart);
  return `${compactClockText(fallback.timeText)}${fallback.meridiem ? ` ${fallback.meridiem}` : ""}`.trim();
}

function formatPortalArrivalWindow(startsAt: string, timeZone?: string | null) {
  if (!startsAt) {
    return "Arrival window unavailable";
  }

  const localStart = String(timeZone ?? "").trim()
    ? getSlotTimeForLocation(startsAt, String(timeZone).trim())
    : new Intl.DateTimeFormat("en-CA", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).format(new Date(startsAt));
  const normalizedLocalStart = localStart.trim();
  const arrivalWindowEnd = ARRIVAL_WINDOW_END_BY_START[normalizedLocalStart];

  if (arrivalWindowEnd) {
    return formatArrivalWindowLabel(normalizedLocalStart, arrivalWindowEnd);
  }

  const fallback = formatLocalClockLabel(normalizedLocalStart);
  return `${compactClockText(fallback.timeText)}${fallback.meridiem ? ` ${fallback.meridiem}` : ""}`.trim();
}

function parseManageBookingOptionIds(items: Array<Record<string, unknown>> | undefined) {
  return Array.from(
    new Set(
      (items ?? []).flatMap((item) => {
        try {
          const parsed = JSON.parse(String(item?.serviceOptionIdsJson ?? "[]"));
          return Array.isArray(parsed) ? parsed.map((value) => String(value ?? "").trim()).filter(Boolean) : [];
        } catch {
          return [];
        }
      })
    )
  );
}

function formatManageBookingOptionMeta(option: {
  priceDeltaAmount?: number;
  durationDeltaMinutes?: number;
}) {
  const parts: string[] = [];
  const priceDeltaAmount = Number(option.priceDeltaAmount ?? 0);
  const durationDeltaMinutes = Number(option.durationDeltaMinutes ?? 0);

  if (priceDeltaAmount > 0) {
    parts.push(`+${formatCurrency(priceDeltaAmount)}`);
  } else if (priceDeltaAmount < 0) {
    parts.push(`-${formatCurrency(Math.abs(priceDeltaAmount))}`);
  }

  if (durationDeltaMinutes > 0) {
    parts.push(`+${durationDeltaMinutes} min`);
  } else if (durationDeltaMinutes < 0) {
    parts.push(`-${Math.abs(durationDeltaMinutes)} min`);
  }

  return parts.join(" • ");
}

function isTimeWithinRange(value: string, earliest?: string, latest?: string) {
  if (earliest && value < earliest) {
    return false;
  }
  if (latest && value > latest) {
    return false;
  }
  return true;
}

function shiftIsoDate(value: string, days: number) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value ?? ""))) {
    return String(value ?? "");
  }
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return String(value ?? "");
  }
  parsed.setDate(parsed.getDate() + days);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getTodayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getIsoMonth(value: string | null | undefined) {
  const normalized = String(value ?? "").trim();
  if (/^\d{4}-\d{2}$/.test(normalized)) {
    return normalized;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return normalized.slice(0, 7);
  }
  return getTodayIsoDate().slice(0, 7);
}

function shiftIsoMonth(value: string, months: number) {
  const normalized = getIsoMonth(value);
  const [rawYear, rawMonth] = normalized.split("-").map(Number);
  const parsed = new Date(rawYear, (rawMonth || 1) - 1, 1, 12, 0, 0);
  parsed.setMonth(parsed.getMonth() + months);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function formatMonthLabel(value: string) {
  const normalized = getIsoMonth(value);
  const [rawYear, rawMonth] = normalized.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(
    new Date(rawYear, (rawMonth || 1) - 1, 1, 12, 0, 0)
  );
}

function getIsoDatesForMonth(value: string) {
  const normalized = getIsoMonth(value);
  const [rawYear, rawMonth] = normalized.split("-").map(Number);
  const year = rawYear || new Date().getFullYear();
  const month = rawMonth || 1;
  const daysInMonth = new Date(year, month, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, index) => {
    const day = String(index + 1).padStart(2, "0");
    return `${year}-${String(month).padStart(2, "0")}-${day}`;
  });
}

function buildCalendarMonthCells(
  value: string,
  availabilityByDate: Record<string, AvailabilityDateSummary>
): Array<{
  key: string;
  date: string | null;
  dayNumber: number | null;
  hasAvailability: boolean;
  firstStartsAt: string | null;
  isToday: boolean;
}> {
  const dates = getIsoDatesForMonth(value);
  if (!dates.length) {
    return [];
  }

  const firstDate = dates[0];
  const firstWeekday = new Date(`${firstDate}T12:00:00`).getDay();
  const leadingCells = Array.from({ length: firstWeekday }, (_, index) => ({
    key: `blank-${value}-${index}`,
    date: null,
    dayNumber: null,
    hasAvailability: false as const,
    firstStartsAt: null,
    isToday: false
  }));
  const today = getTodayIsoDate();
  const dateCells = dates.map((date) => ({
    key: date,
    date,
    dayNumber: Number(date.slice(-2)),
    hasAvailability: Boolean(availabilityByDate[date]?.hasAvailability),
    firstStartsAt: availabilityByDate[date]?.firstStartsAt ?? null,
    isToday: date === today
  }));

  return [...leadingCells, ...dateCells];
}

function parseClockLabelToMinutes(value: string) {
  const [rawHours, rawMinutes] = String(value ?? "")
    .split(":")
    .map((entry) => Number(entry));
  const hours = Number.isFinite(rawHours) ? rawHours : 0;
  const minutes = Number.isFinite(rawMinutes) ? rawMinutes : 0;
  return hours * 60 + minutes;
}

function getWaitlistSlotClockLabel(startsAt: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(new Date(startsAt));
}

function slotMatchesWaitlistPreferredWindow(
  slot: AvailabilitySlotSummary,
  timeZone: string,
  preferredWindow: WaitlistPreferredWindow
) {
  if (preferredWindow === "any") {
    return true;
  }

  const minutes = parseClockLabelToMinutes(getWaitlistSlotClockLabel(slot.startsAt, timeZone));
  if (preferredWindow === "morning") {
    return minutes < 12 * 60;
  }
  if (preferredWindow === "midday") {
    return minutes >= 12 * 60 && minutes < 15 * 60;
  }
  return minutes >= 15 * 60;
}

function filterWaitlistSlotsByPreferredWindow(
  slots: AvailabilitySlotSummary[],
  timeZone: string,
  preferredWindow: WaitlistPreferredWindow
) {
  return slots.filter((slot) => slotMatchesWaitlistPreferredWindow(slot, timeZone, preferredWindow));
}

function formatWaitlistPreferredWindowLabel(value: WaitlistPreferredWindow) {
  switch (value) {
    case "morning":
      return "Morning";
    case "midday":
      return "Midday";
    case "afternoon":
      return "Afternoon";
    default:
      return "Any time";
  }
}

function filterAvailabilitySlotsForServiceArea(
  slots: AvailabilitySlotSummary[],
  location: LocationSettingsItem | undefined,
  matchedRule: LocationServiceAreaRule | null,
  date: string
) {
  if (!slots.length || !location || !matchedRule) {
    return slots;
  }

  const allowedWeekdays = normalizeWeekdayKeys(matchedRule.allowedWeekdays);
  const weekday = getWeekdayKey(date);
  if (allowedWeekdays.length > 0 && !allowedWeekdays.includes(weekday)) {
    return [] as AvailabilitySlotSummary[];
  }

  return slots.filter((slot) =>
    isTimeWithinRange(
      getSlotTimeForLocation(slot.startsAt, location.timezone),
      matchedRule.earliestStartTime,
      matchedRule.latestStartTime
    )
  );
}

function extractPostalCode(value: string) {
  const match = String(value ?? "").match(/\b(\d{5})(?:-\d{4})?\b/);
  return match ? normalizePostalCode(match[1]) : "";
}

function extractUnitNumber(value: string) {
  const match = String(value ?? "").match(/(?:^|[\s,])(?:apt|apartment|unit|suite|ste|#)\s*#?\s*([a-z0-9-]+)\s*$/i);
  return match ? match[1] : "";
}

function stripUnitNumber(value: string) {
  return String(value ?? "")
    .replace(/\s*(?:,|-)?\s*(?:apt|apartment|unit|suite|ste|#)\s*#?\s*[a-z0-9-]+\s*$/i, "")
    .trim();
}

function hasStreetNumber(value: string) {
  return /\d/.test(String(value ?? ""));
}

function normalizeAddressComparison(value: string) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function choosePreferredAddressLine(resolvedLine1: string, typedAddress: string) {
  const cleanResolved = String(resolvedLine1 ?? "").trim();
  const candidate = stripUnitNumber(typedAddress);

  if (!cleanResolved) {
    return candidate;
  }

  if (!candidate) {
    return cleanResolved;
  }

  if (hasStreetNumber(candidate) && !hasStreetNumber(cleanResolved)) {
    return candidate;
  }

  const normalizedCandidate = normalizeAddressComparison(candidate);
  const normalizedResolved = normalizeAddressComparison(cleanResolved);
  if (normalizedCandidate && normalizedResolved && normalizedCandidate.includes(normalizedResolved) && candidate.length > cleanResolved.length) {
    return candidate;
  }

  return cleanResolved;
}

function parseGooglePlaceAddress(place: any) {
  const components = Array.isArray(place?.address_components) ? place.address_components : [];
  const findComponent = (type: string) =>
    components.find((component: any) => Array.isArray(component.types) && component.types.includes(type));

  const streetNumber = findComponent("street_number")?.long_name ?? "";
  const route = findComponent("route")?.long_name ?? "";
  const city =
    findComponent("locality")?.long_name ??
    findComponent("postal_town")?.long_name ??
    findComponent("sublocality")?.long_name ??
    "";
  const state = findComponent("administrative_area_level_1")?.short_name ?? "";
  const postalCode = findComponent("postal_code")?.long_name ?? "";

  return {
    line1: [streetNumber, route].filter(Boolean).join(" ").trim() || String(place?.formatted_address ?? place?.name ?? ""),
    city,
    state,
    postalCode: normalizePostalCode(postalCode)
  };
}

function resolveCalendarProfileId(service: BookingFormService | undefined) {
  return service?.defaultCalendarProfileId ?? "";
}

function getFrequencyRulesForSelection(
  rules: BookingFormFrequencyRule[],
  service: BookingFormService | undefined
) {
  const sortedRules = rules
    .filter((rule) => {
      if (!isActiveRecord(rule)) {
        return false;
      }

      const serviceAllowed = normalizeStringArray(service?.allowedFrequencyRuleIds);
      const allowAllActiveFrequencyRules = service?.allowAllActiveFrequencyRules !== false;
      const applicableServiceIds = normalizeStringArray(rule.applicableServiceIds);

      if (!allowAllActiveFrequencyRules && serviceAllowed.length && !serviceAllowed.includes(rule.id)) {
        return false;
      }

      if (service && applicableServiceIds.length && !applicableServiceIds.includes(service.id)) {
        return false;
      }

      return true;
    })
    .sort((left, right) => {
      const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
      if (orderDelta !== 0) {
        return orderDelta;
      }

      if (Boolean(left.isDefault) !== Boolean(right.isDefault)) {
        return left.isDefault ? -1 : 1;
      }

      return String(left.name ?? "").localeCompare(String(right.name ?? ""));
    });

  const seenLabels = new Set<string>();
  return sortedRules.filter((rule) => {
    const labelKey = normalizeFrequencyDisplayText(formatFrequencyRuleLabel(rule)).toLowerCase();
    if (seenLabels.has(labelKey)) {
      return false;
    }
    seenLabels.add(labelKey);
    return true;
  });
}

function formatFrequencyRuleLabel(rule: BookingFormFrequencyRule) {
  const cadence = normalizeFrequencyDisplayText(rule.cadenceLabel ?? rule.name ?? "");
  const discountValue = Number(rule.discountValue ?? 0);
  if (rule.discountType === "percentage" && Number.isFinite(discountValue) && discountValue > 0) {
    return `${cadence} (${discountValue}% off)`;
  }
  if (rule.discountType === "fixed" && Number.isFinite(discountValue) && discountValue > 0) {
    return `${cadence} (${formatCurrency(discountValue)} off)`;
  }
  return cadence;
}

const RECURRING_EXTRA_SCOPE_OPTIONS = [
  { value: "initial_only", label: "Initial cleaning only" },
  { value: "every_cleaning", label: "Every cleaning" },
  { value: "every_2_cleanings", label: "Every 2 cleanings" },
  { value: "every_3_cleanings", label: "Every 3 cleanings" },
  { value: "every_4_cleanings", label: "Every 4 cleanings" }
] as const;

const TIP_QUICK_PICK_OPTIONS = [
  { value: "none", label: "No tip" },
  { value: "5", label: "5%" },
  { value: "10", label: "10%" },
  { value: "15", label: "15%" },
  { value: "custom", label: "Custom" }
] as const;

type RecurringExtraScopeValue = (typeof RECURRING_EXTRA_SCOPE_OPTIONS)[number]["value"];

const CARPET_UNIT_ADMIN_DETAIL_KEYS = {
  rooms: "carpetRooms",
  hallways: "carpetHallways",
  steps: "carpetSteps",
  area_rugs: "carpetAreaRugs"
} as const;

type CarpetUnitCode = keyof typeof CARPET_UNIT_ADMIN_DETAIL_KEYS;

function isOneTimeFrequencyRule(rule: BookingFormFrequencyRule | undefined) {
  if (!rule) {
    return true;
  }

  const normalizedCode = String(rule.code ?? "").trim().toLowerCase();
  const normalizedLabel = normalizeFrequencyDisplayText(rule.cadenceLabel ?? rule.name ?? "").trim().toLowerCase();
  return normalizedCode === "one_time" || normalizedCode === "onetime" || normalizedLabel === "one-time";
}

function getPreferredFrequencyRuleId(rules: BookingFormFrequencyRule[]) {
  return (
    rules.find((rule) => isOneTimeFrequencyRule(rule))?.id ??
    rules.find((rule) => Boolean(rule.isDefault))?.id ??
    rules[0]?.id ??
    ""
  );
}

function getDefaultRecurringExtraScope(option: BookingFormServiceOption | undefined): RecurringExtraScopeValue {
  return option?.recurringPolicy === "first_booking_only" ? "initial_only" : "every_cleaning";
}

function parseRecurringExtraScopes(value: string | null | undefined): Record<string, RecurringExtraScopeValue> {
  if (!value) {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed)
        .filter((entry): entry is [string, RecurringExtraScopeValue] =>
          typeof entry[0] === "string" &&
          RECURRING_EXTRA_SCOPE_OPTIONS.some((option) => option.value === entry[1])
        )
    );
  } catch {
    return {};
  }
}

function serializeRecurringExtraScopes(value: Record<string, RecurringExtraScopeValue>) {
  const filtered = Object.fromEntries(
    Object.entries(value).filter((entry) =>
      Boolean(entry[0]) && RECURRING_EXTRA_SCOPE_OPTIONS.some((option) => option.value === entry[1])
    )
  );

  return Object.keys(filtered).length ? JSON.stringify(filtered) : "";
}

function getRecurringExtraScopeLabel(value: string | null | undefined) {
  return RECURRING_EXTRA_SCOPE_OPTIONS.find((option) => option.value === value)?.label ?? "Every cleaning";
}

function getCarpetAdminDetailKey(code: CarpetUnitCode) {
  return CARPET_UNIT_ADMIN_DETAIL_KEYS[code];
}

function normalizeCarpetUnitQuantity(value: unknown) {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.max(0, Math.round(parsed));
}

function getActiveCarpetUnits(option: BookingFormServiceOption | undefined) {
  return (option?.carpetShampooingConfig?.units ?? [])
    .filter((unit) => Boolean(unit?.active ?? true))
    .sort((left, right) => Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0));
}

function getCarpetUnitQuestionLabel(unit: { code: CarpetUnitCode; label?: string }) {
  if (unit.code === "rooms") {
    return "How many rooms (up to 250 square feet)?";
  }

  return `How many ${String(unit.label ?? "").toLowerCase()}?`;
}

function formatBookingExtraDurationLabel(totalMinutes: number, unitSuffix = "") {
  const durationLabel = Number(totalMinutes) > 0
    ? `${formatDurationHoursMinutes(Number(totalMinutes))}${unitSuffix} for 1 maid`
    : "no extra time";
  return durationLabel;
}

function getBookingSimpleQuantityUnitLabel(option: BookingFormServiceOption) {
  if (isDishesHandWashingServiceOption(option)) {
    return "sinkful";
  }
  if (isLaundryServiceOption(option)) {
    return "load";
  }

  const configuredLabel = String(option.quantityUnitLabel ?? option.quantityLabel ?? "").trim();
  return configuredLabel.replace(/^per\s+/i, "").replace(/s$/i, "") || "item";
}

function formatBookingExtraCardMeta(option: BookingFormServiceOption) {
  if (option.carpetShampooingConfig?.enabled) {
    const unitLabels: Record<CarpetUnitCode, string> = {
      rooms: "room",
      hallways: "hallway",
      steps: "step",
      area_rugs: "area rug"
    };
    const unitPrices = getActiveCarpetUnits(option)
      .filter((unit) => Number(unit.priceDeltaAmount ?? 0) > 0)
      .map((unit) => `${formatPublicCurrency(Number(unit.priceDeltaAmount ?? 0))} per ${unitLabels[unit.code]}`);

    if (unitPrices.length) {
      const [firstPrice, ...otherPrices] = unitPrices;
      return [firstPrice, ...otherPrices.map((price) => `(${price})`)].join(" ");
    }
  }

  const priceDeltaAmount = Number(option.priceDeltaAmount ?? 0);
  const durationDeltaMinutes = Number(option.durationDeltaMinutes ?? 0);
  const normalizedName = normalizeOptionToken(option.name);

  if (isSimpleQuantityServiceOption(option)) {
    const unitLabel = getBookingSimpleQuantityUnitLabel(option);
    const unitSuffix = ` per ${unitLabel}`;
    const priceLabel = priceDeltaAmount > 0 ? `+${formatPublicCurrency(priceDeltaAmount)}${unitSuffix}` : "Free";
    return `${priceLabel} (${formatBookingExtraDurationLabel(durationDeltaMinutes, unitSuffix)})`;
  }

  if (priceDeltaAmount === 0 && durationDeltaMinutes === 0) {
    return normalizedName.includes("water_the_plants") || normalizedName.includes("move_in_move_out")
      ? "Price confirmed after review"
      : "";
  }

  const priceLabel =
    priceDeltaAmount > 0
      ? `+${formatPublicCurrency(priceDeltaAmount)}`
      : priceDeltaAmount < 0
        ? `-${formatPublicCurrency(Math.abs(priceDeltaAmount))}`
        : "Free";

  return `${priceLabel} (${formatBookingExtraDurationLabel(durationDeltaMinutes)})`;
}

function getCarpetOptionTotals(
  option: BookingFormServiceOption | undefined,
  adminDetails: Record<string, string> | null | undefined
) {
  const units = getActiveCarpetUnits(option);
  if (!option?.carpetShampooingConfig?.enabled || !units.length) {
    return {
      totalQuantity: 0,
      priceDeltaAmount: Number(option?.priceDeltaAmount ?? 0),
      durationDeltaMinutes: Number(option?.durationDeltaMinutes ?? 0)
    };
  }

  let totalQuantity = 0;
  let priceDeltaAmount = 0;
  let durationDeltaMinutes = 0;

  for (const unit of units) {
    const quantity = normalizeCarpetUnitQuantity(adminDetails?.[getCarpetAdminDetailKey(unit.code)]);
    totalQuantity += quantity;
    priceDeltaAmount += quantity * Number(unit.priceDeltaAmount ?? 0);
    durationDeltaMinutes += quantity * Number(unit.durationDeltaMinutes ?? 0);
  }

  if (totalQuantity > 0) {
    priceDeltaAmount = Math.max(
      priceDeltaAmount,
      Number(option.carpetShampooingConfig.minimumOrderAmount ?? 0)
    );
  }

  return {
    totalQuantity,
    priceDeltaAmount,
    durationDeltaMinutes
  };
}

function getCarpetQuoteBreakdownRows(
  option: BookingFormServiceOption | undefined,
  adminDetails: Record<string, string> | null | undefined
) {
  const units = getActiveCarpetUnits(option);
  if (!option?.carpetShampooingConfig?.enabled || !units.length) {
    return [];
  }

  return units
    .map((unit) => {
      const quantity = normalizeCarpetUnitQuantity(adminDetails?.[getCarpetAdminDetailKey(unit.code)]);
      if (quantity < 1) {
        return null;
      }

      return {
        code: unit.code,
        label: unit.label,
        quantity,
        amount: quantity * Number(unit.priceDeltaAmount ?? 0),
        durationMinutes: quantity * Number(unit.durationDeltaMinutes ?? 0)
      };
    })
    .filter(
      (
        row
      ): row is {
        code: CarpetUnitCode;
        label: string;
        quantity: number;
        amount: number;
        durationMinutes: number;
      } => row !== null
    );
}

function buildRecurringExtraSummary(
  options: BookingFormServiceOption[],
  scopes: Record<string, RecurringExtraScopeValue>
) {
  const parts = options.map((option) => {
    const scope = scopes[option.id] ?? getDefaultRecurringExtraScope(option);
    return `${String(option.name ?? "")} — ${getRecurringExtraScopeLabel(scope)}`;
  });
  return parts.join(" | ");
}

function applyRecurringExtraScopeUpdate(
  form: any,
  selectedOptions: BookingFormServiceOption[],
  currentScopes: Record<string, RecurringExtraScopeValue>,
  optionId: string,
  value: RecurringExtraScopeValue
) {
  const nextScopes = {
    ...currentScopes,
    [optionId]: value
  };
  form.setValue("adminDetails.extra_recurring_scopes", serializeRecurringExtraScopes(nextScopes), {
    shouldDirty: true
  });
  form.setValue("adminDetails.extra_recurring_summary", buildRecurringExtraSummary(selectedOptions, nextScopes), {
    shouldDirty: true
  });
}

function parseSquareFootageRange(value: string) {
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  const ascii = normalized.replace(/[–—â€“â€”]/g, "-").replace(/,/g, "");
  const match = ascii.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!match) {
    return null;
  }

  const min = Number(match[1]);
  const max = Number(match[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  return { min, max };
}

function resolveServiceScenario(
  scenarios: BookingFormServiceScenario[],
  service: BookingFormService | undefined,
  squareFootageValue: string
) {
  if (!service) {
    return null;
  }

  const selectedRange = parseSquareFootageRange(squareFootageValue);
  const candidates = scenarios.filter((scenario) => {
    if (!isActiveRecord(scenario) || String(scenario.baseServiceId ?? "") !== String(service.id ?? "")) {
      return false;
    }

    const min = scenario.squareFootageMin == null ? null : Number(scenario.squareFootageMin);
    const max = scenario.squareFootageMax == null ? null : Number(scenario.squareFootageMax);

    if (min == null && max == null) {
      return true;
    }

    if (!selectedRange) {
      return false;
    }

    if (min != null && selectedRange.max < min) {
      return false;
    }

    if (max != null && selectedRange.min > max) {
      return false;
    }

    return true;
  });

  if (!candidates.length) {
    return null;
  }

  return candidates.sort((left, right) => {
    const priorityDelta = Number(right.priority ?? 0) - Number(left.priority ?? 0);
    if (priorityDelta !== 0) {
      return priorityDelta;
    }

    const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
    if (orderDelta !== 0) {
      return orderDelta;
    }

    return String(left.name ?? "").localeCompare(String(right.name ?? ""));
  })[0];
}

function getVisibleOptionsForSelection(
  options: BookingFormServiceOption[],
  service: BookingFormService | undefined,
  scenario: BookingFormServiceScenario | null | undefined
) {
  if (!service) {
    return [];
  }

  const activeOptions = options.filter((option) => isActiveRecord(option));
  const assignedExtraIds = normalizeStringArray(service.assignedExtraIds);
  const serviceOptions = assignedExtraIds.length
    ? activeOptions.filter((option) => assignedExtraIds.includes(option.id))
    : activeOptions.filter((option) => {
        const assignedServiceIds = normalizeStringArray(option.assignedServiceIds);
        if (assignedServiceIds.length) {
          return assignedServiceIds.includes(service.id);
        }
        return option.serviceId === service.id;
      });
  const visibilityRules = (service.extraVisibilityRules ?? {}) as Record<string, unknown>;
  const scenarioVisibleExtraIds = normalizeStringArray(scenario?.visibleExtraIds);
  const extraFilterProfile = getScenarioExtraFilterProfile(scenario, service);
  const carpetAddOnOptions =
    extraFilterProfile === "carpet_standalone" || extraFilterProfile === "building_common_areas"
      ? []
      : activeOptions.filter((option) => isCarpetShampooingServiceOption(option));
  const serviceOptionsWithCarpet = [...serviceOptions, ...carpetAddOnOptions];

  const visibleIds = (() => {
    if (scenarioVisibleExtraIds.length) {
      return [...scenarioVisibleExtraIds, ...carpetAddOnOptions.map((option) => option.id)];
    }

    const direct = visibilityRules.visibleOptionIds ?? visibilityRules.allowedOptionIds;
    if (Array.isArray(direct)) {
      return [...normalizeStringArray(direct), ...carpetAddOnOptions.map((option) => option.id)];
    }

    return [] as string[];
  })();

  const dedupedOptions = !visibleIds.length
    ? dedupeServiceOptions(serviceOptionsWithCarpet)
    : dedupeServiceOptions(serviceOptionsWithCarpet.filter((option) => visibleIds.includes(option.id)));

  if (extraFilterProfile === "office_commercial") {
    return dedupedOptions.filter(
      (option) => !OFFICE_COMMERCIAL_HIDDEN_EXTRA_NAMES.has(String(option.name ?? "").trim().toLowerCase())
    );
  }

  if (extraFilterProfile === "building_common_areas") {
    return dedupedOptions.filter((option) => !isCarpetShampooingServiceOption(option));
  }

  const scenarioCode = normalizeOptionToken(scenario?.code ?? scenario?.name ?? service.name);
  if (scenarioCode.includes("townhouse") || scenarioCode.includes("single_family")) {
    return dedupedOptions.filter((option) => !isBuildingCommonAreasFrontServiceOption(option));
  }

  return dedupedOptions;
}

function dedupeServiceOptions(options: BookingFormServiceOption[]) {
  const bestByName = new Map<string, BookingFormServiceOption>();

  for (const option of options) {
    const key = String(option.name ?? "")
      .trim()
      .toLowerCase();
    if (!key) {
      continue;
    }

    const current = bestByName.get(key);
    if (!current) {
      bestByName.set(key, option);
      continue;
    }

    const currentOrder = Number(current.displayOrder ?? 0);
    const nextOrder = Number(option.displayOrder ?? 0);
    if (nextOrder < currentOrder) {
      bestByName.set(key, option);
      continue;
    }

    if (nextOrder === currentOrder && String(option.id ?? "").localeCompare(String(current.id ?? "")) < 0) {
      bestByName.set(key, option);
    }
  }

  return [...bestByName.values()].sort((left, right) => {
    const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
    if (orderDelta !== 0) {
      return orderDelta;
    }
    return String(left.name ?? "").localeCompare(String(right.name ?? ""));
  });
}

function getEligibleLocations(
  locations: LocationSettingsItem[],
  calendarProfile: BookingFormCalendarProfile | undefined,
  service?: BookingFormService
) {
  const activeLocations = locations.filter((location) => isActiveRecord(location));
  const linkedLocationIds = normalizeStringArray(calendarProfile?.linkedLocationIds);
  const serviceLocationIds = normalizeStringArray(service?.availableLocationIds);
  const scopedLocations = linkedLocationIds.length
    ? activeLocations.filter((location) => linkedLocationIds.includes(location.id))
    : activeLocations;

  if (!serviceLocationIds.length) {
    return scopedLocations;
  }

  return scopedLocations.filter((location) => serviceLocationIds.includes(location.id));
}

function getEligibleBookingSpots(
  bookingSpots: BookingSpotSettingsItem[],
  locationId: string,
  serviceId: string,
  calendarProfile: BookingFormCalendarProfile | undefined
) {
  const linkedSpotIds = normalizeStringArray(calendarProfile?.linkedBookingSpotIds);

  return bookingSpots.filter((item) => {
    if (!isActiveRecord(item) || item.locationId !== locationId) {
      return false;
    }

    if (linkedSpotIds.length && !linkedSpotIds.includes(item.id)) {
      return false;
    }

    const linkedServiceIds = normalizeStringArray(item.linkedServiceIds);
    return !linkedServiceIds.length || linkedServiceIds.includes(serviceId);
    });
}

function normalizeServiceName(value: string | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ");
}

function normalizeFrequencyDisplayText(value: string | null | undefined) {
  const text = String(value ?? "").trim();
  return /^one time$/i.test(text) ? "One-time" : text;
}

function isResidentialHomeService(service: BookingFormService | undefined) {
  const normalized = normalizeServiceName(service?.name);
  return [
    "apartment condo",
    "townhouse",
    "single family house"
  ].includes(normalized);
}

function formatUsPhoneInput(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 10);

  if (!digits) {
    return "";
  }

  if (digits.length < 4) {
    return `(${digits}`;
  }

  if (digits.length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function renderBookingAdminFieldControl({
  field,
  value,
  onChange,
  disabled = false
}: {
  field: BookingAdminFieldConfig;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const isReadOnly = disabled || field.readOnly;
  const checkboxOptions = BOOKING_ADMIN_CHECKBOX_OPTIONS[field.key];
  const selectOptions = getBookingAdminSelectOptions(field.key, value);

  if (checkboxOptions) {
    const selected = parseBookingMultiSelectValue(value);
    return (
      <Field key={field.key} label={field.label} hint={field.hint}>
        <div className="booking-checkbox-grid">
          {checkboxOptions.map((option) => {
            const checked = selected.includes(option);
            return (
              <label key={option} className="booking-checkbox-option">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={isReadOnly}
                  onChange={(event) => {
                    const next = event.target.checked ? [...selected, option] : selected.filter((item) => item !== option);
                    onChange(serializeBookingMultiSelectValue(next));
                  }}
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
      </Field>
    );
  }

  if (selectOptions.length) {
    return (
      <Field key={field.key} label={field.label} hint={field.hint}>
        <select value={value} disabled={isReadOnly} onChange={(event) => onChange(event.target.value)}>
          <option value="">Select...</option>
          {selectOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  if (field.key === "primary_phone" || field.key === "secondary_phone") {
    return (
      <Field key={field.key} label={field.label} hint={field.hint}>
        <input value={value} readOnly={isReadOnly} onChange={(event) => onChange(formatUsPhoneInput(event.target.value))} />
      </Field>
    );
  }

  if (field.multiline) {
    return (
      <Field key={field.key} label={field.label} hint={field.hint}>
        <textarea value={value} rows={3} readOnly={isReadOnly} onChange={(event) => onChange(event.target.value)} />
      </Field>
    );
  }

  return (
    <Field key={field.key} label={field.label} hint={field.hint}>
      <input value={value} readOnly={isReadOnly} onChange={(event) => onChange(event.target.value)} />
    </Field>
  );
}

const customerTransferHeaders = [
  "id",
  "created_at",
  "updated_at",
  "first_booking_at",
  "last_booking_at",
  "next_booking_at",
  "booking_count",
  "active_booking_count",
  "cancelled_booking_count",
  "lifetime_value_amount",
  "outstanding_balance_amount",
  "lead_source_name",
  "lead_source_channel",
  "first_name",
  "last_name",
  "company_name",
  "profile_photo_url",
  "customer_type",
  "account_status",
  "email",
  "phone",
  "secondary_phone",
  "pets",
  "sensitivities",
  "documents",
  "notes",
  "service_frequency",
  "blocked_until",
  "blocked_reason",
  "preferred_staff_ids",
  "blocked_staff_ids",
  "preferred_contact_method",
  "english_speaking_staff",
  "lead_source_id",
  "stripe_customer_id",
  "payment_methods",
  "property_photos",
  "refunds_disputes",
  "problem_areas",
  "damage_reports",
  "special_incidents",
  "client_rating",
  "customer_satisfaction_score",
  "rewards_points",
  "rewards_tier",
  "rewards_notes",
  "sms_opt_in_status",
  "sms_reminder_opt_in_status",
  "email_booking_confirmation",
  "email_reminders",
  "email_receipts",
  "email_marketing",
  "tax_exempt",
  "special_tax_rate",
  "default_address_label",
  "default_address_line1",
  "default_address_line2",
  "default_address_unit_number",
  "default_address_city",
  "default_address_state",
  "default_address_postal_code",
  "default_address_country",
  "default_address_instructions",
  "addresses_json",
  "tag_ids",
  "tag_names"
] as const;

function parseBooleanCsvValue(value: string | null | undefined) {
  return ["1", "true", "yes", "y"].includes(String(value ?? "").trim().toLowerCase());
}

function parseMarketingCsvValue(value: string | null | undefined) {
  return String(value ?? "").trim() === "" ? true : parseBooleanCsvValue(value);
}

function parseDelimitedCsvValues(value: string | null | undefined) {
  return Array.from(
    new Set(
      String(value ?? "")
        .split(/\r?\n|,|\|/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
}

function flattenCustomerTransferRecord(record: CustomerTransferRecord): Record<string, string> {
  const customer = record.customer ?? {};
  const defaultAddress = customer.addresses?.[0] ?? customer.address ?? {};

  return {
    id: String(record.id ?? ""),
    created_at: String(record.createdAt ?? ""),
    updated_at: String(record.updatedAt ?? ""),
    first_booking_at: String(record.firstBookingAt ?? ""),
    last_booking_at: String(record.lastBookingAt ?? ""),
    next_booking_at: String(record.nextBookingAt ?? ""),
    booking_count: String(record.bookingCount ?? 0),
    active_booking_count: String(record.activeBookingCount ?? 0),
    cancelled_booking_count: String(record.cancelledBookingCount ?? 0),
    lifetime_value_amount: String(record.lifetimeValueAmount ?? 0),
    outstanding_balance_amount: String(record.outstandingBalanceAmount ?? 0),
    lead_source_name: String(record.leadSourceName ?? ""),
    lead_source_channel: String(record.leadSourceChannel ?? ""),
    first_name: String(customer.firstName ?? ""),
    last_name: String(customer.lastName ?? ""),
    company_name: String(customer.companyName ?? ""),
    profile_photo_url: String(customer.profilePhotoUrl ?? ""),
    customer_type: String(customer.customerType ?? ""),
    account_status: String(customer.accountStatus ?? ""),
    email: String(customer.email ?? ""),
    phone: String(customer.phone ?? ""),
    secondary_phone: String(customer.secondaryPhone ?? ""),
    pets: String(customer.pets ?? ""),
    sensitivities: String(customer.sensitivities ?? ""),
    documents: (customer.documents ?? []).join(" | "),
    notes: String(customer.notes ?? ""),
    service_frequency: String(customer.serviceFrequency ?? ""),
    blocked_until: String(customer.blockedUntil ?? ""),
    blocked_reason: String(customer.blockedReason ?? ""),
    preferred_staff_ids: (customer.preferredStaffIds ?? []).join(" | "),
    blocked_staff_ids: (customer.blockedStaffIds ?? []).join(" | "),
    preferred_contact_method: String(customer.preferredContactMethod ?? ""),
    english_speaking_staff: customer.englishSpeakingStaff ? "true" : "false",
    lead_source_id: String(customer.leadSourceId ?? ""),
    stripe_customer_id: String(customer.stripeCustomerId ?? ""),
    payment_methods: (customer.paymentMethods ?? []).join(" | "),
    property_photos: (customer.propertyPhotos ?? []).join(" | "),
    refunds_disputes: (customer.refundsDisputes ?? []).join(" | "),
    problem_areas: (customer.problemAreas ?? []).join(" | "),
    damage_reports: (customer.damageReports ?? []).join(" | "),
    special_incidents: (customer.specialIncidents ?? []).join(" | "),
    client_rating: customer.clientRating == null ? "" : String(customer.clientRating),
    customer_satisfaction_score: customer.customerSatisfactionScore == null ? "" : String(customer.customerSatisfactionScore),
    rewards_points: customer.rewards?.points == null ? "" : String(customer.rewards.points),
    rewards_tier: String(customer.rewards?.tier ?? ""),
    rewards_notes: String(customer.rewards?.notes ?? ""),
    sms_opt_in_status: customer.emailPreferences?.smsOptInStatus ? "true" : "false",
    sms_reminder_opt_in_status: customer.emailPreferences?.smsReminderOptInStatus ? "true" : "false",
    email_opt_in_status:
      customer.emailPreferences?.emailOptInStatus == null || customer.emailPreferences?.emailOptInStatus ? "true" : "false",
    email_booking_confirmation: customer.emailPreferences?.bookingConfirmation ? "true" : "false",
    email_reminders: customer.emailPreferences?.reminders ? "true" : "false",
    email_receipts: customer.emailPreferences?.receipts ? "true" : "false",
    email_marketing: customer.emailPreferences?.marketing ? "true" : "false",
    tax_exempt: customer.taxExempt ? "true" : "false",
    special_tax_rate: customer.specialTaxRate == null ? "" : String(customer.specialTaxRate),
    default_address_label: String(defaultAddress.label ?? ""),
    default_address_line1: String(defaultAddress.line1 ?? ""),
    default_address_line2: String(defaultAddress.line2 ?? ""),
    default_address_unit_number: String(defaultAddress.unitNumber ?? ""),
    default_address_city: String(defaultAddress.city ?? ""),
    default_address_state: String(defaultAddress.state ?? ""),
    default_address_postal_code: String(defaultAddress.postalCode ?? ""),
    default_address_country: String(defaultAddress.country ?? ""),
    default_address_instructions: String(defaultAddress.instructions ?? ""),
    addresses_json: JSON.stringify(customer.addresses ?? []),
    tag_ids: (customer.tagIds ?? []).join(" | "),
    tag_names: (record.tags ?? []).map((tag) => tag.name).join(" | ")
  };
}

function parseCustomerTransferCsv(contents: string) {
  const rows = contents
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");

  if (!rows.length) {
    return [];
  }

  const headers = parseCsvLine(rows[0]);

  return rows.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const row = Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? ""])) as Record<string, string>;
    const parsedAddresses =
      (() => {
        try {
          const addresses = JSON.parse(row.addresses_json ?? "[]");
          return Array.isArray(addresses) ? addresses : [];
        } catch {
          return [];
        }
      })();

    const defaultAddress = {
      label: row.default_address_label || "Primary service address",
      line1: row.default_address_line1 || "",
      line2: row.default_address_line2 || "",
      unitNumber: row.default_address_unit_number || "",
      city: row.default_address_city || "",
      state: row.default_address_state || "",
      postalCode: row.default_address_postal_code || "",
      country: row.default_address_country || "US",
      instructions: row.default_address_instructions || "",
      isPrimary: true
    };

    const addresses = parsedAddresses.length
      ? parsedAddresses
      : [defaultAddress].filter((address) => [address.line1, address.city, address.state, address.postalCode].some(Boolean));

    return {
      firstName: row.first_name || "",
      lastName: row.last_name || "",
      companyName: row.company_name || "",
      profilePhotoUrl: row.profile_photo_url || "",
      customerType: row.customer_type || "Residential",
      accountStatus: row.account_status || "Active",
      email: row.email || "",
      phone: row.phone || "",
      secondaryPhone: row.secondary_phone || "",
      pets: row.pets || "",
      sensitivities: row.sensitivities || "",
      documents: parseDelimitedCsvValues(row.documents),
      notes: row.notes || "",
      serviceFrequency: row.service_frequency || "",
      blockedUntil: row.blocked_until || "",
      blockedReason: row.blocked_reason || "",
      preferredStaffIds: parseDelimitedCsvValues(row.preferred_staff_ids),
      blockedStaffIds: parseDelimitedCsvValues(row.blocked_staff_ids),
      preferredContactMethod: row.preferred_contact_method || "Phone",
      englishSpeakingStaff: parseBooleanCsvValue(row.english_speaking_staff),
      leadSourceId: row.lead_source_id || "",
      stripeCustomerId: row.stripe_customer_id || "",
      paymentMethods: parseDelimitedCsvValues(row.payment_methods),
      propertyPhotos: parseDelimitedCsvValues(row.property_photos),
      refundsDisputes: parseDelimitedCsvValues(row.refunds_disputes),
      problemAreas: parseDelimitedCsvValues(row.problem_areas),
      damageReports: parseDelimitedCsvValues(row.damage_reports),
      specialIncidents: parseDelimitedCsvValues(row.special_incidents),
      clientRating: row.client_rating === "" ? undefined : Number(row.client_rating),
      customerSatisfactionScore: row.customer_satisfaction_score === "" ? undefined : Number(row.customer_satisfaction_score),
      rewards: {
        points: row.rewards_points === "" ? 0 : Number(row.rewards_points),
        tier: row.rewards_tier || "",
        notes: row.rewards_notes || ""
      },
      emailPreferences: {
        smsOptInStatus: parseBooleanCsvValue(row.sms_opt_in_status),
        smsReminderOptInStatus: parseBooleanCsvValue(row.sms_reminder_opt_in_status),
        emailOptInStatus: row.email_opt_in_status === "" ? true : parseBooleanCsvValue(row.email_opt_in_status),
        bookingConfirmation: parseBooleanCsvValue(row.email_booking_confirmation),
        reminders: parseBooleanCsvValue(row.email_reminders),
        receipts: parseBooleanCsvValue(row.email_receipts),
        marketing: parseMarketingCsvValue(row.email_marketing)
      },
      unsubscribed: row.unsubscribed || "",
      email_unsubscribed: row.email_unsubscribed || "",
      marketing_unsubscribed: row.marketing_unsubscribed || "",
      marketing_status: row.marketing_status || "",
      email_status: row.email_status || "",
      unsubscribed_at: row.unsubscribed_at || "",
      taxExempt: parseBooleanCsvValue(row.tax_exempt),
      specialTaxRate: row.special_tax_rate === "" ? undefined : Number(row.special_tax_rate),
      addresses,
      address: addresses[0] ?? defaultAddress,
      tagIds: parseDelimitedCsvValues(row.tag_ids)
    };
  });
}

function parseCustomerTransferFile(name: string, contents: string) {
  if (name.toLowerCase().endsWith(".csv")) {
    return parseCustomerTransferCsv(contents);
  }

  const parsed = JSON.parse(contents) as CustomerTransferImportSource;

  const rawItems = Array.isArray(parsed)
    ? parsed
    : Array.isArray((parsed as { items?: unknown[] }).items)
      ? (parsed as { items: unknown[] }).items
      : Array.isArray((parsed as { customers?: unknown[] }).customers)
        ? (parsed as { customers: unknown[] }).customers
        : null;

  if (!rawItems) {
    throw new Error("Import file must be a JSON array or an object with an items array.");
  }

  return rawItems.map((item: unknown) =>
    item && typeof item === "object" && "customer" in item ? (item as { customer: unknown }).customer : item
  );
}

function normalizeCustomerImportPreviewEmail(value: unknown) {
  return String(value ?? "").trim().toLowerCase();
}

function customerImportPreviewText(value: unknown) {
  return String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function isCustomerImportPreviewUnsubscribed(item: unknown) {
  if (!item || typeof item !== "object") {
    return false;
  }

  const record = item as Record<string, unknown>;
  const truthyUnsubscribeValues = new Set([
    "1",
    "true",
    "yes",
    "y",
    "unsubscribed",
    "unsubscribe",
    "opted_out",
    "opt_out",
    "suppressed",
    "do_not_email",
    "dne"
  ]);
  const blockedStatusValues = new Set(["unsubscribed", "unsubscribe", "opted_out", "opt_out", "suppressed", "do_not_email", "dne"]);
  const flagKeys = ["unsubscribed", "emailUnsubscribed", "email_unsubscribed", "marketingUnsubscribed", "marketing_unsubscribed"];
  const statusKeys = ["marketingStatus", "marketing_status", "emailStatus", "email_status"];

  return (
    flagKeys.some((key) => {
      const value = record[key];
      if (typeof value === "boolean") {
        return value;
      }
      if (typeof value === "number") {
        return value === 1;
      }
      return truthyUnsubscribeValues.has(customerImportPreviewText(value));
    }) || statusKeys.some((key) => blockedStatusValues.has(customerImportPreviewText(record[key])))
  );
}

function buildCustomerImportPreview(items: unknown[]) {
  const emails = new Set<string>();
  const duplicateEmails = new Set<string>();
  const unsubscribedEmails = new Set<string>();
  let invalidEmails = 0;

  for (const item of items) {
    const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    const email = normalizeCustomerImportPreviewEmail(record.email);
    if (!email) {
      continue;
    }
    if (!email.includes("@")) {
      invalidEmails += 1;
    }
    if (emails.has(email)) {
      duplicateEmails.add(email);
    }
    emails.add(email);
    if (isCustomerImportPreviewUnsubscribed(item)) {
      unsubscribedEmails.add(email);
    }
  }

  return {
    totalRows: items.length,
    uniqueEmails: emails.size,
    unsubscribers: unsubscribedEmails.size,
    duplicateEmails: duplicateEmails.size,
    invalidEmails
  };
}

function formatCustomerImportPreviewMessage(preview: ReturnType<typeof buildCustomerImportPreview>, label: string) {
  return [
    `Import ${preview.totalRows} ${label}?`,
    `Unique emails: ${preview.uniqueEmails}`,
    `Unsubscribers to suppress: ${preview.unsubscribers}`,
    `Duplicate emails: ${preview.duplicateEmails}`,
    `Invalid-looking emails: ${preview.invalidEmails}`,
    "",
    "Suppression wins: rows marked unsubscribed will be blocked from marketing, and existing suppressions will not be removed."
  ].join("\n");
}

function parseCsvLine(line: string) {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
}

function normalizeBookingTransferRows(rawItems: unknown[]) {
  return rawItems
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const source =
        "booking" in (item as Record<string, unknown>) && (item as { booking?: unknown }).booking && typeof (item as { booking?: unknown }).booking === "object"
          ? ((item as { booking: Record<string, unknown> }).booking ?? {})
          : (item as Record<string, unknown>);

      const normalized = Object.fromEntries(
        Object.entries(source).map(([key, value]) => [key, value == null ? "" : String(value)])
      );

      return normalized;
    })
    .filter((item): item is BookingTransferRow => Boolean(item));
}

function parseBookingTransferCsv(contents: string) {
  const rows = contents
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");

  if (!rows.length) {
    return [];
  }

  const headers = parseCsvLine(rows[0]);
  const seen = new Set<string>();
  const headerIndexes = headers.flatMap((header, index) => {
    if (!header || seen.has(header)) {
      return [];
    }
    seen.add(header);
    return [{ header, index }];
  });

  return rows.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    return Object.fromEntries(
      headerIndexes.map(({ header, index }) => [header, cells[index] ?? ""])
    );
  });
}

function parseBookingTransferSpreadsheet(contents: string) {
  if (typeof DOMParser === "undefined") {
    throw new Error("Spreadsheet import is not available in this browser.");
  }

  const document = new DOMParser().parseFromString(contents, "application/xml");
  if (document.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Excel file could not be read. Use the app's exported .xls format or CSV.");
  }

  const rows = Array.from(document.getElementsByTagName("Row")).map((row) => {
    const values: string[] = [];
    Array.from(row.getElementsByTagName("Cell")).forEach((cell) => {
      const indexAttribute =
        cell.getAttribute("ss:Index") ??
        cell.getAttributeNS("urn:schemas-microsoft-com:office:spreadsheet", "Index");
      const targetIndex = indexAttribute ? Number(indexAttribute) - 1 : values.length;

      while (values.length < targetIndex) {
        values.push("");
      }

      const data = cell.getElementsByTagName("Data")[0];
      values.push(data?.textContent ?? "");
    });
    return values;
  });

  if (!rows.length) {
    return [];
  }

  const seen = new Set<string>();
  const headerIndexes = rows[0].flatMap((header, index) => {
    if (!header || seen.has(header)) {
      return [];
    }
    seen.add(header);
    return [{ header, index }];
  });

  return rows.slice(1).map((cells) =>
    Object.fromEntries(headerIndexes.map(({ header, index }) => [header, cells[index] ?? ""]))
  );
}

function parseBookingTransferFile(name: string, contents: string) {
  const lowerName = name.toLowerCase();

  if (lowerName.endsWith(".csv")) {
    return parseBookingTransferCsv(contents);
  }

  if (lowerName.endsWith(".xls") || lowerName.endsWith(".xml")) {
    return parseBookingTransferSpreadsheet(contents);
  }

  const parsed = JSON.parse(contents) as BookingTransferImportSource;
  const rawItems = Array.isArray(parsed)
    ? parsed
    : Array.isArray((parsed as { items?: unknown[] }).items)
      ? (parsed as { items: unknown[] }).items
      : Array.isArray((parsed as { bookings?: unknown[] }).bookings)
        ? (parsed as { bookings: unknown[] }).bookings
        : null;

  if (!rawItems) {
    throw new Error("Import file must be CSV or the app's Excel export.");
  }

  return normalizeBookingTransferRows(rawItems);
}

function escapeCsvValue(value: string) {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function serializeBookingTransferCsv(headers: string[], items: BookingTransferRow[]) {
  const rows = [
    headers.map(escapeCsvValue).join(","),
    ...items.map((item) => headers.map((header) => escapeCsvValue(item[header] ?? "")).join(","))
  ];

  return rows.join("\r\n");
}

function escapeXmlValue(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function serializeBookingTransferSpreadsheet(headers: string[], items: BookingTransferRow[]) {
  const headerRow = headers
    .map((header) => `<Cell><Data ss:Type="String">${escapeXmlValue(header)}</Data></Cell>`)
    .join("");
  const itemRows = items
    .map(
      (item) =>
        `<Row>${headers
          .map((header) => `<Cell><Data ss:Type="String">${escapeXmlValue(item[header] ?? "")}</Data></Cell>`)
          .join("")}</Row>`
    )
    .join("");

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="Bookings">
    <Table>
      <Row>${headerRow}</Row>
      ${itemRows}
    </Table>
  </Worksheet>
</Workbook>`;
}

function downloadTransferFile(filename: string, type: string, contents: string) {
  if (typeof window === "undefined") {
    return;
  }

  const blob = new Blob([contents], { type });
  const url = window.URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

function formatDaysInactive(lastBookingAt: string | null | undefined, createdAt: string | null | undefined) {
  const anchor = lastBookingAt || createdAt;
  if (!anchor) {
    return "No activity yet";
  }

  const date = new Date(anchor);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  return `${diffDays} day${diffDays === 1 ? "" : "s"}`;
}

function centsToDollarInput(value: number | string | null | undefined) {
  const cents = Number(value ?? 0);
  if (!Number.isFinite(cents)) {
    return "0.00";
  }
  return (cents / 100).toFixed(2);
}

function dollarsToCents(value: string) {
  const normalized = Number(value || 0);
  if (!Number.isFinite(normalized)) {
    return 0;
  }
  return Math.round(normalized * 100);
}

function recomputeInvoiceDraft(snapshot: InvoiceDraftSnapshot): InvoiceDraftSnapshot {
  const lineItems = (snapshot.lineItems ?? []).map((item, index) => {
    const quantity = Math.max(0, Number(item.quantity ?? 1));
    const unitPriceAmount = Number(item.unitPriceAmount ?? 0);
    const totalAmount = Number.isFinite(Number(item.totalAmount)) ? Number(item.totalAmount) : Math.round(quantity * unitPriceAmount);
    return {
      ...item,
      id: item.id || `line_${index + 1}`,
      quantity,
      unitPriceAmount,
      totalAmount
    };
  });
  const subtotalAmount = lineItems.reduce((sum, item) => sum + Number(item.totalAmount ?? 0), 0);
  const discountAmount = Math.max(0, Number(snapshot.discountAmount ?? 0));
  const taxAmount = Math.max(0, Number(snapshot.taxAmount ?? 0));
  const totalAmount = Math.max(0, subtotalAmount - discountAmount + taxAmount);
  const amountPaid = Math.max(0, Number(snapshot.amountPaid ?? 0));
  return {
    ...snapshot,
    lineItems,
    subtotalAmount,
    discountAmount,
    taxAmount,
    totalAmount,
    amountPaid,
    amountDue: Math.max(0, totalAmount - amountPaid)
  };
}

function badgeToneForStatus(status: string) {
  const normalized = status.toLowerCase();
  if (normalized.includes("problem") || normalized.includes("blocked")) {
    return "danger" as const;
  }
  if (normalized.includes("cancel")) {
    return "warning" as const;
  }
  if (normalized.includes("pending") || normalized.includes("resched")) {
    return "info" as const;
  }
  if (normalized.includes("inactive")) {
    return "warning" as const;
  }
  return "success" as const;
}

const demoServices = [
  { id: "svc_standard_clean_demo", name: "Standard Cleaning" },
  { id: "svc_deep_clean_demo", name: "Deep Cleaning" },
  { id: "svc_move_out_demo", name: "Move In / Move Out" }
];

const demoOptions = [
  { id: "opt_fridge_demo", label: "Inside fridge" },
  { id: "opt_oven_demo", label: "Inside oven" },
  { id: "opt_windows_demo", label: "Interior windows" }
];

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <MoneyFormattingBootstrap />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/book" element={<BookingPage />} />
          <Route path="/book/confirmation/:bookingId" element={<ConfirmationPage />} />
          <Route path="/feedback/:token" element={<FeedbackPage />} />
          <Route path="/gift-cards/purchase" element={<GiftCardPurchasePage />} />
          <Route path="/gift-cards/confirmation/:token" element={<GiftCardPurchaseConfirmationPage />} />
          <Route path="/gift-cards/confirmation/:token/print" element={<GiftCardPrintPage />} />
          <Route path="/manage/:token" element={<ManageBookingPage />} />
          <Route path="/account/login" element={<AccountLoginPage />} />
          <Route path="/account/verify" element={<AccountVerifyPage />} />
          <Route path="/account/bookings/:id" element={<AccountBookingDetailsPage />} />
          <Route path="/account" element={<AccountPortalPage />} />
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/leads/*" element={<LegacyLeadsRedirect />} />
          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function LegacyLeadsRedirect() {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/leads/, "");
  return <Navigate to={`/admin/leads${suffix}${location.search}`} replace />;
}

function MoneyFormattingBootstrap() {
  const publicConfig = useQuery({
    queryKey: ["public-config"],
    queryFn: () => apiFetch<PublicClientConfig>("/api/public-config")
  });

  useEffect(() => {
    configureMoneyFormatting({
      currency: publicConfig.data?.currency ?? "USD",
      locale: publicConfig.data?.locale ?? "en-US"
    });
  }, [publicConfig.data?.currency, publicConfig.data?.locale]);

  return null;
}

function HomePage() {
  return (
    <div className="marketing-shell">
      <section className="hero">
        <div>
          <p className="eyebrow">Cloud booking for service companies</p>
          <h1>Booking Seconds turns phone-tag into live availability, payments, and follow-up.</h1>
          <p className="lede">
            Start with cleaning services today, then expand into the rest of your home-service stack without rebuilding
            your back office.
          </p>
          <div className="hero__actions">
            <Link className="button button--primary" to="/book">
              Try the booking flow
            </Link>
            <Link className="button button--secondary" to="/gift-cards/purchase">
              Buy a gift card
            </Link>
            <Link className="button button--secondary" to="/account/login">
              My Account
            </Link>
            <Link className="button button--secondary" to="/admin/login">
              Open admin
            </Link>
          </div>
        </div>
        <Card title="MVP scope" eyebrow="Launch-ready starter">
          <ul className="feature-list">
            <li>Public booking with availability, promos, deposits, and gift cards</li>
            <li>Admin dashboard for customers, bookings, payments, reports, and settings</li>
            <li>Cloudflare Worker, D1, R2, Queues, and Turnstile support</li>
            <li>Stripe, Resend, and Twilio integrations wired into the platform layer</li>
          </ul>
        </Card>
      </section>
    </div>
  );
}

function GiftCardPurchasePage() {
  const navigate = useNavigate();
  const backgroundFileInputRef = useRef<HTMLInputElement | null>(null);
  const latestPaymentFingerprintRef = useRef("");
  const storefrontQuery = useQuery({
    queryKey: ["gift-card-storefront-config"],
    queryFn: () => apiFetch<GiftCardStorefrontConfigResponse>("/api/gift-cards/storefront-config")
  });
  const form = useForm<any>({
    resolver: zodResolver(giftCardPurchaseSessionInputSchema),
    defaultValues: {
      amount: 10000,
      purchaserName: "",
      purchaserEmail: "",
      recipientName: "",
      recipientEmail: "",
      message: "",
      giftCardThemeId: "",
      customBackgroundUrl: "",
      scheduledFor: ""
    }
  });
  const [purchaseSession, setPurchaseSession] = useState<GiftCardPurchaseSession | null>(null);
  const [uploadedBackground, setUploadedBackground] = useState<GiftCardBackgroundUpload | null>(null);
  const [uploadedBackgroundPreviewUrl, setUploadedBackgroundPreviewUrl] = useState("");
  const [purchaseSessionFingerprint, setPurchaseSessionFingerprint] = useState("");
  const [scheduledForInput, setScheduledForInput] = useState("");
  const [deliveryTiming, setDeliveryTiming] = useState<"now" | "later">("now");
  const selectedThemeId = String(form.watch("giftCardThemeId") ?? "");
  const storefront = storefrontQuery.data?.storefront ?? null;
  const themeItems = storefrontQuery.data?.themes ?? [];
  const selectedTheme =
    themeItems.find((item) => item.id === selectedThemeId) ??
    themeItems.find((item) => item.id === storefront?.defaultThemeId) ??
    themeItems.find((item) => item.isDefault) ??
    themeItems[0] ??
    null;
  const presetAmounts = storefront?.presetAmounts?.length ? storefront.presetAmounts : [5000, 10000, 15000, 20000, 25000, 30000, 50000];
  const storefrontTimeZone = storefrontQuery.data?.defaultTimezone || "America/Chicago";
  const previewAmount = Number(form.watch("amount") ?? 0);
  const previewRecipientName = String(form.watch("recipientName") ?? "");
  const previewPurchaserName = String(form.watch("purchaserName") ?? "");
  const previewPurchaserEmail = String(form.watch("purchaserEmail") ?? "").trim();
  const previewMessage = String(form.watch("message") ?? "");
  const previewRecipientEmail = String(form.watch("recipientEmail") ?? "").trim();
  const customBackgroundUrl = String(form.watch("customBackgroundUrl") ?? "").trim();
  const scheduledDeliveryError = String((form.formState.errors as Record<string, any>)?.scheduledFor?.message ?? "");
  const formLocked = false;
  const previewBackgroundUrl = uploadedBackgroundPreviewUrl || customBackgroundUrl || uploadedBackground?.backgroundImageUrl || "";
  const resolvedScheduledFor =
    deliveryTiming === "later" && previewRecipientEmail && scheduledForInput
      ? fromDateTimeInputValue(scheduledForInput, storefrontTimeZone)
      : "";
  const previewTheme = useMemo(() => {
    if (!selectedTheme) {
      return null;
    }

    if (!previewBackgroundUrl) {
      return selectedTheme;
    }

    return {
      ...selectedTheme,
      backgroundImageUrl: previewBackgroundUrl
    };
  }, [previewBackgroundUrl, selectedTheme]);
  const currencySymbol = useMemo(() => {
    try {
      return (
        new Intl.NumberFormat(storefrontQuery.data?.locale || "en-US", {
          style: "currency",
          currency: storefrontQuery.data?.currency || "USD"
        })
          .formatToParts(0)
          .find((part) => part.type === "currency")?.value || "$"
      );
    } catch {
      return "$";
    }
  }, [storefrontQuery.data?.currency, storefrontQuery.data?.locale]);
  const maxAmountLabel = useMemo(() => {
    const amount = Number(storefront?.maxAmount ?? 1000000) / 100;
    try {
      return new Intl.NumberFormat(storefrontQuery.data?.locale || "en-US", {
        style: "currency",
        currency: storefrontQuery.data?.currency || "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch {
      return formatCurrency(Number(storefront?.maxAmount ?? 1000000));
    }
  }, [storefront?.maxAmount, storefrontQuery.data?.currency, storefrontQuery.data?.locale]);
  const createSessionMutation = useMutation({
    mutationFn: (values: any) =>
      apiFetch<GiftCardPurchaseSession>("/api/gift-cards/purchase-session", {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: (session, variables) => {
      const fingerprint = JSON.stringify(variables);
      if (fingerprint !== latestPaymentFingerprintRef.current) {
        return;
      }
      setPurchaseSession(session);
      setPurchaseSessionFingerprint(fingerprint);
    }
  });
  const uploadBackgroundMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiFetch<GiftCardBackgroundUpload>("/api/gift-cards/backgrounds", {
        method: "POST",
        body: formData
      });
    },
    onSuccess: (uploaded) => {
      setUploadedBackground(uploaded);
      setPurchaseSession(null);
      setPurchaseSessionFingerprint("");
      form.setValue("customBackgroundUrl", uploaded.backgroundImageUrl, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    onError: () => {
      setUploadedBackgroundPreviewUrl((current) => {
        if (current.startsWith("blob:")) {
          URL.revokeObjectURL(current);
        }
        return "";
      });
    }
  });
  const minAllowedAmount = Number(storefront?.minAmount ?? 1000);
  const maxAllowedAmount = Number(storefront?.maxAmount ?? 1000000);
  const paymentDraft = useMemo(
    () => ({
      amount: previewAmount,
      purchaserName: previewPurchaserName.trim(),
      purchaserEmail: previewPurchaserEmail,
      recipientName: previewRecipientName.trim(),
      recipientEmail: previewRecipientEmail,
      message: previewMessage,
      giftCardThemeId: selectedThemeId,
      customBackgroundUrl,
      scheduledFor: resolvedScheduledFor
    }),
    [
      customBackgroundUrl,
      previewAmount,
      previewMessage,
      previewPurchaserEmail,
      previewPurchaserName,
      previewRecipientEmail,
      previewRecipientName,
      resolvedScheduledFor,
      selectedThemeId
    ]
  );
  const paymentDraftValidation = useMemo(
    () => giftCardPurchaseSessionInputSchema.safeParse(paymentDraft),
    [paymentDraft]
  );
  const readyForAutomaticPayment =
    storefront?.enabled !== false &&
    previewAmount >= minAllowedAmount &&
    previewAmount <= maxAllowedAmount &&
    !uploadBackgroundMutation.isPending &&
    Boolean(selectedThemeId) &&
    paymentDraftValidation.success &&
    (deliveryTiming === "now" || (Boolean(previewRecipientEmail) && Boolean(scheduledForInput)));
  const currentPaymentFingerprint = readyForAutomaticPayment ? JSON.stringify(paymentDraft) : "";
  const paymentSectionMessage = readyForAutomaticPayment
    ? createSessionMutation.isPending
      ? "Preparing secure payment..."
      : purchaseSession
        ? "Secure payment is ready below."
        : "Refreshing secure payment..."
    : deliveryTiming === "later" && !previewRecipientEmail
      ? "Add a recipient email to unlock secure payment for scheduled delivery."
      : deliveryTiming === "later" && !scheduledForInput
        ? "Choose the delivery date and time to unlock secure payment."
        : "Complete the amount, sender, recipient, and delivery details to load secure payment automatically.";

  latestPaymentFingerprintRef.current = currentPaymentFingerprint;

  useEffect(() => {
    if (!selectedThemeId && themeItems.length) {
      form.setValue("giftCardThemeId", storefront?.defaultThemeId || (themeItems.find((item) => item.isDefault)?.id ?? themeItems[0].id), {
        shouldDirty: false,
        shouldValidate: true
      });
    }
  }, [form, selectedThemeId, storefront?.defaultThemeId, themeItems]);

  useEffect(() => {
    setScheduledForInput(toDateTimeInputValue(String(form.getValues("scheduledFor") ?? ""), storefrontTimeZone));
  }, [form, storefrontTimeZone]);

  useEffect(() => {
    if (deliveryTiming !== "now") {
      return;
    }

    if (!scheduledForInput && !String(form.getValues("scheduledFor") ?? "").trim()) {
      return;
    }

    setScheduledForInput("");
    form.setValue("scheduledFor", "", {
      shouldDirty: true,
      shouldValidate: true
    });
  }, [deliveryTiming, form, scheduledForInput]);

  useEffect(
    () => () => {
      if (uploadedBackgroundPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedBackgroundPreviewUrl);
      }
    },
    [uploadedBackgroundPreviewUrl]
  );

  useEffect(() => {
    if (!readyForAutomaticPayment) {
      setPurchaseSession(null);
      setPurchaseSessionFingerprint("");
      return;
    }

    if (purchaseSessionFingerprint === currentPaymentFingerprint || createSessionMutation.isPending) {
      return;
    }

    setPurchaseSession(null);
    const timeoutId = window.setTimeout(() => {
      createSessionMutation.mutate(paymentDraft);
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [
    createSessionMutation,
    currentPaymentFingerprint,
    paymentDraft,
    purchaseSessionFingerprint,
    readyForAutomaticPayment
  ]);

  return (
    <div className="page-wrap gift-card-storefront-page">
      <section className="section gift-card-storefront-page__copy">
        <div className="stack">
          <p className="eyebrow">{storefront?.heroEyebrow || "Gift Cards"}</p>
          <h1>{storefront?.heroHeadline || "Send a stored-value gift card in minutes."}</h1>
          <p className="lede">{storefront?.heroSubheadline || "Choose an amount, add a note, and we'll deliver the code by email right after payment clears."}</p>
          {storefront?.heroImageUrl ? (
            <img
              alt="Gift card storefront"
              src={storefront.heroImageUrl}
              style={{ width: "100%", maxWidth: "34rem", borderRadius: "1.5rem", objectFit: "cover" }}
            />
          ) : null}
        </div>
      </section>

      <section className="section gift-card-storefront-page__form">
        <div className="stack">
          <Card title="Purchase details" eyebrow={storefront?.enabled === false ? "Unavailable" : "Digital delivery"}>
            <div className="stack">
              <input type="hidden" {...form.register("giftCardThemeId")} />
              <input type="hidden" {...form.register("customBackgroundUrl")} />
              <input type="hidden" {...form.register("scheduledFor")} />
              {storefront?.enabled === false ? (
                <p className="error-text">This website is not currently selling gift cards.</p>
              ) : null}
              <Field label="Choose an amount">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "0.75rem" }}>
                  {presetAmounts.map((amount) => {
                    const checked = Number(form.watch("amount") ?? 0) === amount;
                    return (
                      <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={amount}>
                        <input
                          type="radio"
                          disabled={formLocked}
                          checked={checked}
                          onChange={() => form.setValue("amount", amount, { shouldDirty: true, shouldValidate: true })}
                        />
                        <div className="service-choice-copy">
                          <strong>{formatCurrency(amount)}</strong>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Custom amount">
                <div className="money-input">
                  <span className="money-input__symbol" aria-hidden="true">
                    {currencySymbol}
                  </span>
                  <input
                    type="number"
                    disabled={formLocked}
                    min={Number(storefront?.minAmount ?? 1000) / 100}
                    max={Number(storefront?.maxAmount ?? 1000000) / 100}
                    step="0.01"
                    inputMode="decimal"
                    value={Number((Number(form.watch("amount") ?? 0) / 100).toFixed(2))}
                    onChange={(event) =>
                      form.setValue("amount", Math.max(0, Math.round(Number(event.target.value || 0) * 100)), {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                  />
                </div>
                <p className="card-copy">
                  Enter any amount from {formatCurrency(Number(storefront?.minAmount ?? 1000))} to {maxAmountLabel}
                </p>
              </Field>
              <Field label="Choose a theme">
                {storefrontQuery.isLoading ? <p className="card-copy">Loading occasion themes...</p> : null}
                {storefrontQuery.error ? <p className="error-text">{(storefrontQuery.error as Error).message}</p> : null}
                {!storefrontQuery.isLoading && !storefrontQuery.error ? (
                  themeItems.length ? (
                    <div className="gift-card-theme-grid">
                      {themeItems.map((theme) => {
                        const isSelected = selectedTheme?.id === theme.id;
                        const escapedUrl = String(theme.backgroundImageUrl ?? "").replace(/"/g, '\\"');
                        return (
                          <label className={`gift-card-theme-tile ${isSelected ? "is-selected" : ""}`} key={theme.id}>
                            <input
                              type="radio"
                              disabled={formLocked}
                              checked={isSelected}
                              onChange={() =>
                                form.setValue("giftCardThemeId", theme.id, {
                                  shouldDirty: true,
                                  shouldValidate: true
                                })
                              }
                            />
                            <div
                              className="gift-card-theme-tile__swatch"
                              style={{
                                backgroundColor: theme.accentColor,
                                backgroundImage: escapedUrl
                                  ? `linear-gradient(145deg, rgba(255,255,255,0.1), rgba(21,31,27,0.28)), url("${escapedUrl}")`
                                  : `linear-gradient(145deg, ${theme.accentColor}, rgba(246, 240, 230, 0.94))`
                              }}
                            />
                            <div>
                              <strong>{theme.name}</strong>
                              <span>{theme.subheadline || theme.headline}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="card-copy">No gift card themes are available yet.</p>
                  )
                ) : null}
              </Field>
              <Field label="Upload your own background image (optional)">
                <div className="stack stack--tight">
                  <input
                    ref={backgroundFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                    disabled={formLocked || uploadBackgroundMutation.isPending}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) {
                        return;
                      }
                      const nextPreviewUrl = URL.createObjectURL(file);
                      setUploadedBackgroundPreviewUrl((current) => {
                        if (current.startsWith("blob:")) {
                          URL.revokeObjectURL(current);
                        }
                        return nextPreviewUrl;
                      });
                      setPurchaseSession(null);
                      setPurchaseSessionFingerprint("");
                      void uploadBackgroundMutation.mutateAsync(file).finally(() => {
                        if (backgroundFileInputRef.current) {
                          backgroundFileInputRef.current.value = "";
                        }
                      });
                    }}
                  />
                  <p className="card-copy">Accepted formats: JPG, PNG, WEBP, and GIF.</p>
                  {uploadedBackground ? (
                    <div className="manage-outcome manage-outcome--saved">
                      <strong>{uploadedBackground.name}</strong>
                      <p>Your uploaded image is now the background for this gift card design.</p>
                      <div className="hero__actions">
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={formLocked}
                          onClick={() => {
                            setUploadedBackground(null);
                            setPurchaseSession(null);
                            setPurchaseSessionFingerprint("");
                            setUploadedBackgroundPreviewUrl((current) => {
                              if (current.startsWith("blob:")) {
                                URL.revokeObjectURL(current);
                              }
                              return "";
                            });
                            form.setValue("customBackgroundUrl", "", {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                            if (backgroundFileInputRef.current) {
                              backgroundFileInputRef.current.value = "";
                            }
                          }}
                        >
                          Remove uploaded background
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  {uploadBackgroundMutation.error ? <p className="error-text">{(uploadBackgroundMutation.error as Error).message}</p> : null}
                </div>
              </Field>
              <div className="grid two">
                <Field label="Your name">
                  <input disabled={formLocked} {...form.register("purchaserName")} />
                </Field>
                <Field label="Your email">
                  <input disabled={formLocked} type="email" {...form.register("purchaserEmail")} />
                </Field>
                <Field label="Recipient name">
                  <input disabled={formLocked} {...form.register("recipientName")} />
                </Field>
                <Field label="Recipient email">
                  <input
                    disabled={formLocked}
                    type="email"
                    {...form.register("recipientEmail", {
                      onChange: () => {
                        if (deliveryTiming === "later") {
                          form.clearErrors("scheduledFor");
                        }
                      }
                    })}
                  />
                </Field>
              </div>
              <Field label="Gift message">
                <textarea
                  disabled={formLocked}
                  {...form.register("message")}
                  rows={4}
                  placeholder="Enjoy a little help around the house."
                />
              </Field>
              <Field label="Delivery timing">
                <div className="gift-card-delivery-toggle">
                  <label className={`service-choice-card ${deliveryTiming === "now" ? "is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="gift-card-delivery-timing"
                      disabled={formLocked}
                      checked={deliveryTiming === "now"}
                      onChange={() => {
                        form.clearErrors("scheduledFor");
                        setDeliveryTiming("now");
                      }}
                    />
                    <div className="service-choice-copy">
                      <strong>Send immediately</strong>
                      <span>Deliver as soon as payment clears.</span>
                    </div>
                  </label>
                  <label className={`service-choice-card ${deliveryTiming === "later" ? "is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="gift-card-delivery-timing"
                      disabled={formLocked}
                      checked={deliveryTiming === "later"}
                      onChange={() => {
                        form.clearErrors("scheduledFor");
                        setDeliveryTiming("later");
                      }}
                    />
                    <div className="service-choice-copy">
                      <strong>Schedule for later</strong>
                      <span>Choose the date and time for recipient delivery.</span>
                    </div>
                  </label>
                </div>
              </Field>
              {deliveryTiming === "later" ? (
                <Field label="Schedule recipient delivery">
                  <input
                    type="datetime-local"
                    disabled={formLocked}
                    value={scheduledForInput}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setScheduledForInput(nextValue);
                      form.clearErrors("scheduledFor");
                      form.setValue("scheduledFor", fromDateTimeInputValue(nextValue, storefrontTimeZone), {
                        shouldDirty: true,
                        shouldValidate: true
                      });
                    }}
                  />
                  <p className="card-copy">
                    {previewRecipientEmail
                      ? `Recipient emails are scheduled in ${storefrontTimeZone}.`
                      : scheduledForInput
                        ? "Add a recipient email to use this scheduled delivery time. Without one, the gift card is issued for manual sharing right after payment."
                        : "Pick a date and time now, then add a recipient email to activate the schedule. Without one, the gift card is issued for manual sharing right after payment."}
                  </p>
                  {scheduledDeliveryError ? <p className="error-text">{scheduledDeliveryError}</p> : null}
                </Field>
              ) : (
                <p className="card-copy">The gift card will send as soon as payment clears.</p>
              )}
              {storefront?.legalDisclaimer ? <p className="card-copy">{storefront.legalDisclaimer}</p> : null}
              <div className="stack">
                <GiftCardThemePreview
                  theme={previewTheme ?? purchaseSession?.giftCardTheme}
                  amount={previewAmount}
                  recipientName={previewRecipientName}
                  purchaserName={previewPurchaserName}
                  message={previewMessage}
                />
                <div className="gift-card-payment-brands" aria-label="Accepted cards">
                  <span>Visa</span>
                  <span>Mastercard</span>
                  <span>AmEx</span>
                  <span>Discover</span>
                </div>
                {!readyForAutomaticPayment ? (
                  <p className="card-copy">{paymentSectionMessage}</p>
                ) : createSessionMutation.isPending || !purchaseSession ? (
                  <div className="stripe-payment-shell">
                    <p className="card-copy">Preparing secure payment...</p>
                    <div className="stripe-payment-element stripe-payment-element--placeholder" />
                  </div>
                ) : (
                  <>
                    {purchaseSession.scheduledFor ? (
                      <p className="card-copy">Recipient delivery is scheduled for {formatDateTimeLabel(purchaseSession.scheduledFor)}.</p>
                    ) : null}
                    <StripePaymentCard
                      session={purchaseSession}
                      wrapInCard={false}
                      subtitle="Use Stripe to complete this gift card purchase securely."
                      onPaymentSuccess={(status) =>
                        navigate(`/gift-cards/confirmation/${purchaseSession.confirmationToken}?payment=${encodeURIComponent(status)}`)
                      }
                    />
                  </>
                )}
                {createSessionMutation.error ? <p className="error-text">{(createSessionMutation.error as Error).message}</p> : null}
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}

function GiftCardPurchaseConfirmationPage() {
  const { token = "" } = useParams();
  const confirmationQuery = useQuery({
    queryKey: ["gift-card-confirmation", token],
    queryFn: () => apiFetch<GiftCardOrderConfirmation>(`/api/gift-cards/public/${token}`),
    enabled: Boolean(token),
    refetchInterval: (query) => {
      const state = (query.state.data as GiftCardOrderConfirmation | undefined)?.publicStatus;
      if (state === "ready") {
        return false;
      }
      return state === "paid_waiting_issue" || state === "paid_waiting_send" ? 30000 : 2000;
    },
    refetchOnWindowFocus: false,
    retry: false
  });
  const confirmation = confirmationQuery.data;
  const publicStatus = confirmation?.publicStatus || "waiting_payment";
  const statusHeadline =
    publicStatus === "ready"
      ? "Your gift card is ready."
      : publicStatus === "paid_waiting_send"
        ? "Your gift card is scheduled for delivery."
        : publicStatus === "paid_waiting_issue"
          ? "Your gift card is paid and queued."
          : "Your gift card order is on its way.";
  const statusCopy =
    publicStatus === "ready"
      ? confirmation?.deliverySkipped
        ? "Payment is complete and the gift card is ready for you to print, save, or share manually."
        : "Payment and fulfillment are complete. If this site's reveal policy allows it, your code is shown below."
      : publicStatus === "paid_waiting_send"
        ? "Payment is complete. The gift card is issued and waiting for the scheduled delivery window."
        : publicStatus === "paid_waiting_issue"
          ? "Payment is complete. The gift card itself will be issued when the scheduled delivery time arrives."
          : "We're waiting for payment confirmation and fulfillment. This page will refresh automatically.";

  return (
    <div className="page-wrap">
      <section className="hero">
        <div className="stack">
          <p className="eyebrow">Gift Card Confirmation</p>
          <h1>{statusHeadline}</h1>
          <p className="lede">{statusCopy}</p>
          <div className="hero__actions">
            <Link className="button button--secondary" to="/gift-cards/purchase">
              Buy another gift card
            </Link>
            <Link className="button button--secondary" to="/book">
              Use a gift card
            </Link>
          </div>
        </div>
        <Card title="Status" eyebrow={publicStatus === "ready" ? "Ready to use" : "Processing"}>
          {confirmationQuery.isLoading ? <p className="card-copy">Loading your gift card...</p> : null}
          {confirmationQuery.error ? <p className="error-text">{(confirmationQuery.error as Error).message}</p> : null}
          {confirmation ? (
            <div className="stack">
              <GiftCardThemePreview
                theme={confirmation.giftCardTheme}
                amount={confirmation.amount}
                recipientName={confirmation.recipientName}
                purchaserName={confirmation.purchaserName}
                message={confirmation.message}
                code={confirmation.revealAvailable ? confirmation.giftCardCode : confirmation.giftCardMaskedCode || undefined}
                caption={
                  confirmation.revealAvailable
                    ? "This is the delivered design and current code state."
                    : confirmation.publicStatus === "paid_waiting_send"
                      ? "The code will appear here when the scheduled reveal window opens."
                      : confirmation.publicStatus === "paid_waiting_issue"
                        ? "The code will appear here once the scheduled issuance window arrives."
                        : "The final code will appear here as soon as fulfillment finishes."
                }
              />
              {confirmation.revealAvailable && (confirmation.printPageUrl || confirmation.pdfDownloadUrl) ? (
                <div className="hero__actions gift-card-preview-actions">
                  {confirmation.printPageUrl ? (
                    <Link className="button button--secondary" to={`/gift-cards/confirmation/${token}/print`}>
                      Open print page
                    </Link>
                  ) : null}
                  {confirmation.pdfDownloadUrl ? (
                    <a className="button button--secondary" href={confirmation.pdfDownloadUrl}>
                      Download PDF
                    </a>
                  ) : null}
                </div>
              ) : null}
              <div className="booking-meta-grid">
                <div className="booking-meta-card">
                  <p className="eyebrow">Recipient</p>
                  <strong>{confirmation.recipientName || "Recipient"}</strong>
                  <p>{confirmation.recipientEmail || "No email"}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Amount</p>
                  <strong>{formatCurrency(confirmation.amount)}</strong>
                  <p>{confirmation.giftCardStatus ? `Card ${confirmation.giftCardStatus}` : "Awaiting card"}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Delivery</p>
                  <strong>{confirmation.deliverySkipped ? "Manual share" : confirmation.deliveredAt ? "Delivered" : "Scheduled"}</strong>
                  <p>
                    {confirmation.deliverySkipped
                      ? "No recipient email was provided."
                      : confirmation.deliveredAt
                        ? formatDateTimeLabel(confirmation.deliveredAt)
                        : confirmation.scheduledForLabel || "We'll email the recipient when ready."}
                  </p>
                </div>
              </div>
              {confirmation.message ? (
                <div className="quote-callout">
                  <p className="eyebrow">Gift message</p>
                  <p>{confirmation.message}</p>
                </div>
              ) : null}
              {confirmation.scheduledForLabel ? <p className="card-copy">Scheduled for {confirmation.scheduledForLabel}.</p> : null}
              {confirmation.storefrontLegalDisclaimer ? <p className="card-copy">{confirmation.storefrontLegalDisclaimer}</p> : null}
              {confirmation.revealAvailable ? null : (
                <p className="card-copy">Keep this page open or return later if this site delays the buyer reveal until the scheduled window.</p>
              )}
            </div>
          ) : null}
        </Card>
      </section>
    </div>
  );
}

function GiftCardPrintPage() {
  const { token = "" } = useParams();
  const confirmationQuery = useQuery({
    queryKey: ["gift-card-print", token],
    queryFn: () => apiFetch<GiftCardOrderConfirmation>(`/api/gift-cards/public/${token}`),
    enabled: Boolean(token),
    refetchOnWindowFocus: false,
    retry: false
  });
  const confirmation = confirmationQuery.data;

  return (
    <div className="page-wrap gift-card-print-page">
      <section className="section">
        <div className="stack">
          {confirmationQuery.isLoading ? <p className="card-copy">Loading printable gift card...</p> : null}
          {confirmationQuery.error ? <p className="error-text">{(confirmationQuery.error as Error).message}</p> : null}
          {confirmation ? (
            confirmation.revealAvailable ? (
              <div className="gift-card-print-sheet">
                <GiftCardThemePreview
                  theme={confirmation.giftCardTheme}
                  amount={confirmation.amount}
                  recipientName={confirmation.recipientName}
                  purchaserName={confirmation.purchaserName}
                  message={confirmation.message}
                  code={confirmation.giftCardCode}
                  caption={confirmation.storefrontPrintFooterText || "Present this code during checkout to apply the stored value."}
                />
                <div className="hero__actions gift-card-print-page__actions">
                  <Button type="button" onClick={() => window.print()}>
                    Print this gift card
                  </Button>
                  <Link className="button button--secondary" to={`/gift-cards/confirmation/${token}`}>
                    Back to confirmation
                  </Link>
                  {confirmation.pdfDownloadUrl ? (
                    <a className="button button--secondary" href={confirmation.pdfDownloadUrl}>
                      Download PDF
                    </a>
                  ) : null}
                </div>
                {confirmation.scheduledForLabel ? <p className="card-copy">Scheduled for {confirmation.scheduledForLabel}</p> : null}
              </div>
            ) : (
              <Card title="Printable gift card unavailable" eyebrow="Waiting for reveal">
                <p className="card-copy">This gift card cannot be printed until the current reveal window opens.</p>
              </Card>
            )
          ) : null}
          {confirmation && !confirmation.revealAvailable ? (
            <div className="hero__actions gift-card-print-page__actions">
              <Link className="button button--secondary" to={`/gift-cards/confirmation/${token}`}>
                Back to confirmation
              </Link>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reactQueryClient = useQueryClient();
  const form = useForm<any>({
    resolver: zodResolver(publicBookingCreateInputSchema),
    defaultValues: getBookingFormDefaults()
  });
  const paymentCardRef = useRef<StripePaymentCardHandle | null>(null);
  const appliedRebookRef = useRef<string | null>(null);
  const appliedWaitlistRef = useRef<string | null>(null);
  const smsReminderOptInDefaultAppliedRef = useRef(false);
  const customerSelectedServiceDateRef = useRef(false);
  const autoSelectedFirstAvailableDateRef = useRef<string | null>(null);
  const propertyLookupPrefillRef = useRef<Record<string, boolean>>({});
  const [addressGateValue, setAddressGateValue] = useState("");
  const [bookingGatePostalCode, setBookingGatePostalCode] = useState("");
  const [promoCodeTouched, setPromoCodeTouched] = useState(false);
  const [giftCardCodeTouched, setGiftCardCodeTouched] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const initialStartsAt = String(getBookingFormDefaults().startsAt ?? "");
    return initialStartsAt ? initialStartsAt.slice(0, 10) : "";
  });
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);
  const [waitlistPreferredWindow, setWaitlistPreferredWindow] = useState<WaitlistPreferredWindow>("any");
  const [waitlistNotifyBySms, setWaitlistNotifyBySms] = useState(false);
  const [waitlistFeedback, setWaitlistFeedback] = useState("");
  const [includedTasksOpen, setIncludedTasksOpen] = useState(false);
  const [resolvedAddressGate, setResolvedAddressGate] = useState<{
    line1?: string;
    unitNumber?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  } | null>(null);
  const [confirmedBookingGate, setConfirmedBookingGate] = useState<{
    zip: string;
    locationIds: string[];
  } | null>(null);
  const [confirmedAddressContext, setConfirmedAddressContext] = useState<{
    zip: string;
    locationIds: string[];
  } | null>(null);
  const [pendingPaymentBooking, setPendingPaymentBooking] = useState<null | {
    bookingId: string;
    publicReference: string;
    manageToken: string;
    quote: { total: number; amountDueNow: number };
  }>(null);
  const rebookBookingId = searchParams.get("rebook") ?? "";
  const waitlistToken = searchParams.get("waitlist") ?? "";
  const publicConfig = useQuery({
    queryKey: ["public-config"],
    queryFn: () => apiFetch<PublicClientConfig>("/api/public-config")
  });
  const bookingFormConfig = useQuery({
    queryKey: ["booking-form-config"],
    queryFn: () => apiFetch<BookingFormRuntimeConfig>("/api/booking-form/config")
  });
  const rebookPrefillQuery = useQuery({
    queryKey: ["customer-portal-rebook-prefill", rebookBookingId],
    queryFn: () => apiFetch<CustomerPortalRebookPrefill>(`/api/customer-portal/bookings/${rebookBookingId}/rebook`),
    enabled: Boolean(rebookBookingId),
    retry: false,
    refetchOnWindowFocus: false
  });
  const waitlistPrefillQuery = useQuery({
    queryKey: ["waitlist-prefill", waitlistToken],
    queryFn: () => apiFetch<PublicWaitlistPrefill>(`/api/waitlist/${waitlistToken}/prefill`),
    enabled: Boolean(waitlistToken),
    retry: false,
    refetchOnWindowFocus: false
  });
  const googleMapsApiKey = String(bookingFormConfig.data?.googleMapsApi?.apiKey ?? "").trim();
  const bookingAutocompleteEnabled = bookingFormConfig.data?.googleMapsApi?.enableAddressAutocomplete !== false;
  const hasAddressAutocompleteProvider =
    bookingFormConfig.data?.googleMapsApi?.hasAddressAutocomplete === true || Boolean(googleMapsApiKey);
  const canUseAddressAutocomplete = bookingAutocompleteEnabled && hasAddressAutocompleteProvider;

  const locationId = String(form.watch("locationId") ?? "");
  const serviceId = String(form.watch("serviceId") ?? "");
  const frequencyRuleId = String(form.watch("frequencyRuleId") ?? "");
  const calendarProfileId = String(form.watch("calendarProfileId") ?? "");
  const bookingSpotId = String(form.watch("bookingSpotId") ?? "");
  const startsAtValue = String(form.watch("startsAt") ?? "");
  const dateValue = selectedDate;
  const [visibleCalendarMonth, setVisibleCalendarMonth] = useState(() => getIsoMonth(dateValue || getTodayIsoDate()));
  const optionIds = normalizeStringArray(form.watch("optionIds"));
  const recentRemodeling = String(form.watch("adminDetails.recent_remodeling") ?? "no");
  const promoCode = String(form.watch("promoCode") ?? "");
  const giftCardCode = String(form.watch("giftCardCode") ?? "");
  const depositChoice = String(form.watch("depositChoice") ?? "deposit");
  const addressLine1Value = String(form.watch("customer.address.line1") ?? "");
  const addressUnitNumberValue = String(form.watch("customer.address.unitNumber") ?? "");
  const addressCityValue = String(form.watch("customer.address.city") ?? "");
  const addressStateValue = String(form.watch("customer.address.state") ?? "");
  const customerPhoneValue = String(form.watch("customer.phone") ?? "");
  const zipCodeValue = String(form.watch("customer.address.postalCode") ?? "");
  const gatedPostalCode = normalizePostalCode(zipCodeValue || bookingGatePostalCode || resolvedAddressGate?.postalCode || "");
  const customerTypeValue = String(form.watch("customer.customerType") ?? "Residential");
  const paymentPreferenceEnabled = bookingFormConfig.data?.bookingForm.enablePaymentPreference ?? false;
  const waitlistEmailAlertsEnabled = bookingFormConfig.data?.waitlist?.sendEmailNotifications !== false;
  const waitlistSmsAlertsEnabled = bookingFormConfig.data?.waitlist?.sendSmsNotifications !== false;
  const waitlistFeatureEnabled =
    bookingFormConfig.data?.waitlist?.enabled !== false && (waitlistEmailAlertsEnabled || waitlistSmsAlertsEnabled);
  const configuredDepositRate = bookingFormConfig.data?.bookingForm.depositRate ?? 0.2;
  const { depositChoice: effectiveDepositChoice, depositRate } = resolveCheckoutDepositTerms({
    paymentPreferenceEnabled,
    configuredDepositRate,
    requestedDepositChoice: depositChoice === "none" || depositChoice === "full" ? depositChoice : "deposit"
  });

  const filteredServices = (bookingFormConfig.data?.services ?? []).filter(
    (item) => isActiveRecord(item) && !Boolean(item.staffOnly)
  );
  const postalCodeField = form.register("customer.address.postalCode");
  const promoCodeField = form.register("promoCode", {
    onBlur: (event) => {
      setPromoCodeTouched(Boolean(String(event.target.value ?? "").trim()));
    }
  });
  const giftCardCodeField = form.register("giftCardCode", {
    onBlur: (event) => {
      setGiftCardCodeTouched(Boolean(String(event.target.value ?? "").trim()));
    }
  });
  const currentService = filteredServices.find((item) => item.id === serviceId);
  const adminDetailsRaw = form.watch("adminDetails");
  const adminDetails = normalizeAdminDetailsRecord(adminDetailsRaw);
  const customerSuppliesEquipmentSettings = bookingFormConfig.data?.bookingForm.customerSuppliesEquipment;
  const customerSuppliesEquipmentEnabled = Boolean(customerSuppliesEquipmentSettings?.enabled);
  const customerSuppliesEquipmentDiscountAmount = Math.max(
    0,
    Number(customerSuppliesEquipmentSettings?.discountAmount ?? 0) || 0
  );
  const customerSuppliesEquipmentRequiredItems = getCustomerSuppliesRequiredItems(customerSuppliesEquipmentSettings);
  const customerSuppliesChoice = getCustomerSuppliesChoice(adminDetails);
  const resolvedScenario = resolveServiceScenario(
    bookingFormConfig.data?.serviceScenarios ?? [],
    currentService,
    String(adminDetails.square_footage ?? "")
  );
  const scenarioQuestionProfile = getScenarioQuestionProfile(resolvedScenario, currentService);
  const scenarioRemodelProfile = getScenarioRemodelProfile(resolvedScenario, currentService);
  const scenarioRemodelOptions = getScenarioRemodelOptions(scenarioRemodelProfile);
  const scenarioPartialRemodelOptions = getScenarioPartialRemodelOptions(scenarioRemodelProfile);
  const visibleOptions = getVisibleOptionsForSelection(
    bookingFormConfig.data?.serviceOptions ?? [],
    currentService,
    resolvedScenario
  );
  const scenarioSkipAreaOptions = getScenarioSkipAreaOptions(
    resolvedScenario,
    scenarioQuestionProfile === "residential"
  );
  const selectedSkipAreaCodes = getSelectedSkipAreaCodes(adminDetails);
  const remodelingTypeCode = normalizeRemodelingOptionCode(form.watch("adminDetails.remodeling_type"));
  const selectedPartialRemodelAreaCodes = parseMultiSelectCodes(adminDetails.partial_remodeling_areas);
  const selectedEntryAccessCodes = parseEntryAccessCodes(adminDetails.entry_information_selected);
  const selectedAccessCodeOptionCodes = parseAccessCodeOptionCodes(adminDetails.access_code_selected);
  const selectedEntryAccessCodesKey = selectedEntryAccessCodes.join("|");
  const selectedAccessCodeOptionCodesKey = selectedAccessCodeOptionCodes.join("|");
  const selectedEntryAccessCodeSet = useMemo(
    () => new Set(selectedEntryAccessCodes),
    [selectedEntryAccessCodesKey]
  );
  const selectedAccessCodeOptionSet = useMemo(
    () => new Set(selectedAccessCodeOptionCodes),
    [selectedAccessCodeOptionCodesKey]
  );
  const accessCodesEnabled =
    String(form.watch("adminDetails.access_codes_enabled") ?? "")
      .trim()
      .toLowerCase() === "yes" || selectedAccessCodeOptionCodes.length > 0;
  const skipAreasModeValue = String(form.watch("adminDetails.skip_areas_enabled") ?? "")
    .trim()
    .toLowerCase();
  const skipAreasEnabled =
    scenarioSkipAreaOptions.length > 0 &&
    (skipAreasModeValue === "yes" || (skipAreasModeValue !== "no" && selectedSkipAreaCodes.length > 0));
  const allFrequencyRules = bookingFormConfig.data?.frequencyRules ?? [];
  const availableFrequencyRules = getFrequencyRulesForSelection(
    allFrequencyRules,
    currentService
  );
  const selectedFrequencyRule =
    availableFrequencyRules.find((item) => item.id === frequencyRuleId) ??
    allFrequencyRules.find((item) => item.id === frequencyRuleId);
  const showResidentialHomeDetails =
    scenarioQuestionProfile === "residential" && resolvedScenario?.showHomeDetails !== false;
  const showOfficeCommercialDetails = scenarioQuestionProfile === "office_commercial";
  const showBuildingCommonAreaDetails = scenarioQuestionProfile === "building_common_areas";
  const hasScenarioDetailSection =
    showResidentialHomeDetails || showOfficeCommercialDetails || showBuildingCommonAreaDetails;
  const showScenarioFrequency =
    bookingFormConfig.data?.bookingForm.enableFrequencyStep !== false && resolvedScenario?.showFrequency !== false;
  const isRecurringFrequencySelection =
    showScenarioFrequency &&
    Boolean(frequencyRuleId) &&
    !isOneTimeFrequencyRule(selectedFrequencyRule);
  const recurringExtraScopes = useMemo(
    () => parseRecurringExtraScopes(String(adminDetails.extra_recurring_scopes ?? "")),
    [adminDetails.extra_recurring_scopes]
  );
  const selectedVisibleOptions = useMemo(
    () => visibleOptions.filter((option) => optionIds.includes(option.id)),
    [optionIds, visibleOptions]
  );
  const visibleOptionGroups = useMemo(() => groupVisibleExtraOptions(visibleOptions), [visibleOptions]);
  const standaloneCarpetShampooingOption = useMemo(() => {
    if (scenarioQuestionProfile !== "carpet_standalone") {
      return undefined;
    }

    return (bookingFormConfig.data?.serviceOptions ?? []).find(
      (option) => Boolean(option.carpetShampooingConfig?.enabled) && getActiveCarpetUnits(option).length > 0
    );
  }, [bookingFormConfig.data?.serviceOptions, scenarioQuestionProfile]);
  const selectedCarpetShampooingOption = useMemo(
    () =>
      selectedVisibleOptions.find(
        (option) => Boolean(option.carpetShampooingConfig?.enabled) && getActiveCarpetUnits(option).length > 0
      ),
    [selectedVisibleOptions]
  );
  const effectiveCarpetShampooingOption = selectedCarpetShampooingOption ?? standaloneCarpetShampooingOption;
  const effectiveOptionIds = useMemo(() => {
    if (scenarioQuestionProfile !== "carpet_standalone" || !effectiveCarpetShampooingOption) {
      return optionIds;
    }

    return optionIds.includes(effectiveCarpetShampooingOption.id)
      ? optionIds
      : [...optionIds, effectiveCarpetShampooingOption.id];
  }, [effectiveCarpetShampooingOption, optionIds, scenarioQuestionProfile]);
  const hideEstimateDuration = scenarioQuestionProfile === "carpet_standalone";
  const showScenarioExtras =
    bookingFormConfig.data?.bookingForm.enableExtrasStep !== false && resolvedScenario?.showExtras !== false;
  const showScenarioRemodeling =
    resolvedScenario?.showRemodelingSection !== false && scenarioRemodelProfile !== "none";
  const showScenarioCondition =
    resolvedScenario?.showConditionSection !== false && scenarioQuestionProfile === "residential";
  const showScenarioTip = resolvedScenario?.showTip !== false;
  const requiredScenarioDetailFields = showResidentialHomeDetails
    ? ([
        { key: "bedrooms", label: "Total Bedrooms / offices / dens" },
        { key: "bathrooms", label: "Total Bathrooms" },
        { key: "levels", label: "Levels (floors) to clean" },
        { key: "square_footage", label: "Total square footage" }
      ] as const)
    : showOfficeCommercialDetails
      ? ([
          { key: "workstations", label: "Workstations" },
          { key: "office_private_rooms", label: "Private rooms / offices / conference rooms" },
          { key: "office_breakrooms", label: "Breakrooms / kitchenettes" },
          { key: "bathrooms", label: "Restrooms" },
          { key: "levels", label: "Levels (floors) to clean" },
          { key: "square_footage", label: "Total square footage" }
        ] as const)
      : showBuildingCommonAreaDetails
        ? ([{ key: "building_apartments", label: "How many apartments are in the building?" }] as const)
        : ([] as const);
  const scenarioDetailsComplete =
    !hasScenarioDetailSection ||
    requiredScenarioDetailFields.every((field) => Boolean(String(adminDetails[field.key] ?? "").trim()));
  const quoteDetailsReady = (() => {
    if (!serviceId || !hasScenarioDetailSection) {
      return !(showScenarioRemodeling && recentRemodeling === "yes" && !remodelingTypeCode);
    }

    let scenarioDetailsReady = true;

    if (showResidentialHomeDetails) {
      scenarioDetailsReady = ["bedrooms", "bathrooms", "levels", "square_footage"].every((key) =>
        Boolean(String(adminDetails[key] ?? "").trim())
      );
    } else if (showOfficeCommercialDetails) {
      scenarioDetailsReady = ["workstations", "office_private_rooms", "office_breakrooms", "bathrooms", "levels", "square_footage"].every((key) =>
        Boolean(String(adminDetails[key] ?? "").trim())
      );
    } else if (showBuildingCommonAreaDetails) {
      scenarioDetailsReady = Boolean(String(adminDetails.building_apartments ?? "").trim());
    }

    if (!scenarioDetailsReady) {
      return false;
    }

    if (!showScenarioRemodeling || recentRemodeling !== "yes") {
      return true;
    }

    if (!remodelingTypeCode) {
      return false;
    }

    if (remodelingTypeCode !== "partial_remodeling") {
      return true;
    }

    if (!selectedPartialRemodelAreaCodes.length) {
      return false;
    }

    if (
      selectedPartialRemodelAreaCodes.includes("bedrooms") &&
      !String(adminDetails.partial_remodeling_bedrooms_count ?? "").trim()
    ) {
      return false;
    }

    if (
      selectedPartialRemodelAreaCodes.includes("bathrooms") &&
      !String(adminDetails.partial_remodeling_bathrooms_count ?? "").trim()
    ) {
      return false;
    }

    return true;
  })();
  const selectedRemodelingLabel = remodelingTypeCode
    ? buildRemodelingSummary(scenarioRemodelProfile, adminDetails)
    : "";
  const tipTypeValue = String(form.watch("adminDetails.tip_type") ?? "")
    .trim()
    .toLowerCase();
  const tipValue = String(form.watch("adminDetails.tip") ?? "").trim();
  const selectedTipQuickPick =
    tipTypeValue === "percent" && ["5", "10", "15", "20"].includes(tipValue)
      ? tipValue
      : tipTypeValue || tipValue
        ? "custom"
        : "none";
  const resolvedCalendarProfileId = calendarProfileId || resolveCalendarProfileId(currentService);
  const selectedCalendarProfile = (bookingFormConfig.data?.calendarProfiles ?? []).find(
    (item) => item.id === resolvedCalendarProfileId
  );
  const configuredTaxRate = Math.max(0, Number(bookingFormConfig.data?.general?.taxRate ?? 0) || 0);
  const preferredFrequencyRuleId = useMemo(
    () => getPreferredFrequencyRuleId(availableFrequencyRules),
    [availableFrequencyRules]
  );
  const liveZipCode = gatedPostalCode;
  const liveServiceAreaMatchedLocations = (bookingFormConfig.data?.locations ?? []).filter((location) => {
    if (!isActiveRecord(location) || !liveZipCode) {
      return false;
    }

    const zipCodes = normalizeStringArray(location.serviceArea).map((zip) => normalizePostalCode(zip));
    return zipCodes.includes(liveZipCode);
  });
  const normalizedZipCode = confirmedBookingGate?.zip || confirmedAddressContext?.zip || liveZipCode;
  const serviceAreaMatchedLocations = confirmedBookingGate
    ? (bookingFormConfig.data?.locations ?? []).filter((location) =>
        confirmedBookingGate.locationIds.includes(String(location.id))
      )
    : liveServiceAreaMatchedLocations;
  const baseEligibleLocations = getEligibleLocations(
    bookingFormConfig.data?.locations ?? [],
    selectedCalendarProfile,
    currentService
  );
  const zipMatchedLocations = baseEligibleLocations.filter((location) =>
    serviceAreaMatchedLocations.some((serviceAreaLocation) => serviceAreaLocation.id === location.id)
  );
  const hasCompleteZipCode = normalizedZipCode.length === 5;
  const hasCompleteServiceAddress = Boolean(
    addressLine1Value.trim() &&
      addressCityValue.trim() &&
      addressStateValue.trim() &&
      hasCompleteZipCode
  );
  const confirmedEligibleLocations = confirmedAddressContext
    ? baseEligibleLocations.filter((location) => confirmedAddressContext.locationIds.includes(String(location.id)))
    : [];
  const eligibleLocations = normalizedZipCode
    ? zipMatchedLocations.length
      ? zipMatchedLocations
      : confirmedEligibleLocations
    : [];
  const isZipSupported = Boolean(confirmedBookingGate) || (hasCompleteZipCode && serviceAreaMatchedLocations.length > 0);
  const shouldShowBookingQuestions = Boolean(confirmedBookingGate && hasCompleteServiceAddress);
  const showCheckoutSection = shouldShowBookingQuestions && quoteDetailsReady && Boolean(startsAtValue);
  const isServiceUnavailableForAddress = Boolean(currentService && hasCompleteZipCode && !eligibleLocations.length);
  const propertyLookupAddressKey = [
    addressLine1Value.trim(),
    addressUnitNumberValue.trim(),
    addressCityValue.trim(),
    addressStateValue.trim(),
    normalizedZipCode
  ].join("|");
  const propertyLookupQuery = useQuery({
    queryKey: ["booking-property-lookup", propertyLookupAddressKey],
    queryFn: () =>
      apiFetch<BookingPropertyLookupResponse>("/api/booking-form/property-lookup", {
        method: "POST",
        body: JSON.stringify({
          address: {
            line1: addressLine1Value.trim(),
            unitNumber: addressUnitNumberValue.trim(),
            city: addressCityValue.trim(),
            state: addressStateValue.trim(),
            postalCode: normalizedZipCode,
            country: "US"
          }
        })
      }),
    enabled: Boolean(hasCompleteServiceAddress && isZipSupported),
    staleTime: 1000 * 60 * 20,
    retry: false,
    refetchOnWindowFocus: false
  });
  const propertyLookupSummary = propertyLookupQuery.data?.summary ?? null;
  const publicPropertyBedrooms =
    propertyLookupSummary?.bedrooms != null
      ? `${propertyLookupSummary.bedrooms} ${propertyLookupSummary.bedrooms === 1 ? "bedroom" : "bedrooms"}`
      : "";
  const publicPropertyBathrooms =
    propertyLookupSummary?.bathrooms != null
      ? `${propertyLookupSummary.bathrooms} ${propertyLookupSummary.bathrooms === 1 ? "bathroom" : "bathrooms"}`
      : "";
  const publicPropertySquareFootage = propertyLookupSummary?.squareFeet
    ? `${propertyLookupSummary.squareFeet.toLocaleString()} sq ft`
    : "";
  const squareFootageUnknownSelected = isSquareFootageUnknownSelected(adminDetails);
  const squareFootageExactInputValue = getBookingSquareFootageExactInputValue(adminDetails);
  const squareFootageFallback = getBookingSquareFootageFallback(currentService, adminDetails);
  const squareFootageFallbackNotice = squareFootageFallback?.notice ?? "";
  const adminDetailErrors =
    ((form.formState.errors as any)?.adminDetails as Record<string, { message?: string } | undefined> | undefined) ?? {};
  const setAdminDetailValue = (key: string, value: string) => {
    form.setValue(`adminDetails.${key}`, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };
  const clearSquareFootageFallback = () => {
    setAdminDetailValue("estimated_square_footage_fallback", "");
    setAdminDetailValue("estimated_square_footage_fallback_notice", "");
  };
  const handleSquareFootageExactChange = (value: string) => {
    const normalizedDisplayValue = value.replace(/[^\d,\s]/g, "");
    const squareFeet = parseSquareFeetTextValue(normalizedDisplayValue);
    setAdminDetailValue("square_footage_exact", normalizedDisplayValue);
    setAdminDetailValue("square_footage", squareFeet ? formatSquareFeetInputValue(squareFeet) : "");
    setAdminDetailValue("square_footage_source", squareFeet ? "visitor" : "");
    setAdminDetailValue("square_footage_unknown", "");
    clearSquareFootageFallback();
  };
  const handleSquareFootageUnknownChange = (checked: boolean) => {
    if (!checked) {
      const currentSource = String(form.getValues("adminDetails.square_footage_source") ?? "");
      setAdminDetailValue("square_footage_unknown", "");
      clearSquareFootageFallback();
      if (currentSource === "average_fallback") {
        setAdminDetailValue("square_footage", "");
        setAdminDetailValue("square_footage_source", "");
      }
      return;
    }

    setAdminDetailValue("square_footage_unknown", "yes");
    setAdminDetailValue("square_footage_exact", "");
    if (squareFootageFallback) {
      setAdminDetailValue("square_footage", formatSquareFeetInputValue(squareFootageFallback.squareFeet));
      setAdminDetailValue("square_footage_source", "average_fallback");
      setAdminDetailValue("estimated_square_footage_fallback", formatSquareFeetInputValue(squareFootageFallback.squareFeet));
      setAdminDetailValue("estimated_square_footage_fallback_notice", squareFootageFallback.notice);
      return;
    }

    setAdminDetailValue("square_footage", "");
    setAdminDetailValue("square_footage_source", "");
    clearSquareFootageFallback();
  };
  const renderSquareFootageEntry = ({
    allowUnknown,
    helperText,
    showPublicData
  }: {
    allowUnknown: boolean;
    helperText: string;
    showPublicData: boolean;
  }) => (
    <div className="booking-square-footage-entry">
      {showPublicData ? (
        <PropertyLookupFieldNote value={publicPropertySquareFootage} provider={propertyLookupSummary?.provider} />
      ) : null}
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="1000"
        value={squareFootageExactInputValue}
        onChange={(event) => handleSquareFootageExactChange(event.target.value)}
      />
      <p className="booking-square-footage-helper">{helperText}</p>
      {allowUnknown ? (
        <>
          <label
            className={`checkbox-row checkbox-row--clickable booking-square-footage-unknown${
              squareFootageUnknownSelected ? " is-selected" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={squareFootageUnknownSelected}
              onChange={(event) => handleSquareFootageUnknownChange(event.target.checked)}
            />
            <span>I'm not sure</span>
          </label>
          {squareFootageUnknownSelected && squareFootageFallbackNotice ? (
            <p className="booking-square-footage-helper">{squareFootageFallbackNotice}</p>
          ) : null}
        </>
      ) : null}
      {adminDetailErrors.square_footage?.message ? <p className="error-text">{adminDetailErrors.square_footage.message}</p> : null}
    </div>
  );
  const selectedLocation = eligibleLocations.find((item) => item.id === locationId) ?? eligibleLocations[0];
  const matchedServiceAreaRule = getLocationServiceAreaRule(selectedLocation, normalizedZipCode, dateValue);
  const availableSpots = getEligibleBookingSpots(
    bookingFormConfig.data?.bookingSpots ?? [],
    locationId,
    serviceId,
    selectedCalendarProfile
  );
  const selectedBookingSpot =
    availableSpots.find((item) => item.id === bookingSpotId) ?? availableSpots[0];
  const availabilityLocationTimeZone = selectedLocation?.timezone;
  const quoteTeamSize = Math.max(1, Number(selectedBookingSpot?.teamSize ?? 2));
  const firstAvailableDateAutoSelectKey = useMemo(
    () =>
      [
        normalizedZipCode,
        locationId,
        serviceId,
        resolvedCalendarProfileId,
        bookingSpotId,
        effectiveOptionIds.join(","),
        JSON.stringify(adminDetails ?? {})
      ].join("|"),
    [
      adminDetails,
      bookingSpotId,
      effectiveOptionIds,
      locationId,
      normalizedZipCode,
      resolvedCalendarProfileId,
      serviceId
    ]
  );

  useEffect(() => {
    if (!addressGateValue && addressLine1Value) {
      setAddressGateValue(addressLine1Value);
    }
  }, [addressGateValue, addressLine1Value]);

  useEffect(() => {
    const nextSelectedDate = startsAtValue ? startsAtValue.slice(0, 10) : "";
    if (nextSelectedDate && nextSelectedDate !== selectedDate) {
      setSelectedDate(nextSelectedDate);
    }
  }, [selectedDate, startsAtValue]);

  useEffect(() => {
    if (!customerPhoneValue.trim() && waitlistNotifyBySms) {
      setWaitlistNotifyBySms(false);
    }
  }, [customerPhoneValue, waitlistNotifyBySms]);

  useEffect(() => {
    if (!waitlistFeatureEnabled && waitlistEnabled) {
      setWaitlistEnabled(false);
    }
    if (!waitlistSmsAlertsEnabled && waitlistNotifyBySms) {
      setWaitlistNotifyBySms(false);
    }
  }, [waitlistEnabled, waitlistFeatureEnabled, waitlistNotifyBySms, waitlistSmsAlertsEnabled]);

  useEffect(() => {
    if (!addressGateValue.trim() && !bookingGatePostalCode) {
      setResolvedAddressGate(null);
      setConfirmedAddressContext(null);
      setConfirmedBookingGate(null);
    }
  }, [addressGateValue, bookingGatePostalCode]);

  useEffect(() => {
    if (isZipSupported && gatedPostalCode) {
      const nextLocationIds = serviceAreaMatchedLocations.map((location) => String(location.id));

      setConfirmedAddressContext((current) => {
        if (current?.zip === gatedPostalCode && equalStringSets(current.locationIds, nextLocationIds)) {
          return current;
        }

        return {
          zip: gatedPostalCode,
          locationIds: nextLocationIds
        };
      });

      setConfirmedBookingGate((current) => {
        if (current?.zip === gatedPostalCode && equalStringSets(current.locationIds, nextLocationIds)) {
          return current;
        }

        return {
          zip: gatedPostalCode,
          locationIds: nextLocationIds
        };
      });
    }
  }, [gatedPostalCode, isZipSupported, serviceAreaMatchedLocations]);

  useEffect(() => {
    const summary = propertyLookupSummary;
    if (!summary) {
      return;
    }

    const applyOnce = (
      field: string,
      value: string,
      options: { overwriteAverageFallback?: boolean } = {}
    ) => {
      const key = `${propertyLookupAddressKey}:${field}`;
      if (!value || propertyLookupPrefillRef.current[key]) {
        return false;
      }
      const fieldName = `adminDetails.${field}` as const;
      const currentValue = String(form.getValues(fieldName) ?? "").trim();
      const currentSquareFootageSource = String(form.getValues("adminDetails.square_footage_source") ?? "").trim();
      if (currentValue && !(options.overwriteAverageFallback && currentSquareFootageSource === "average_fallback")) {
        propertyLookupPrefillRef.current[key] = true;
        return false;
      }
      form.setValue(fieldName, value, { shouldDirty: true });
      propertyLookupPrefillRef.current[key] = true;
      return true;
    };

    if (summary.squareFeet) {
      const squareFeetValue = formatSquareFeetInputValue(summary.squareFeet);
      const didApplySquareFeet = applyOnce("square_footage", squareFeetValue, { overwriteAverageFallback: true });
      if (didApplySquareFeet) {
        form.setValue("adminDetails.square_footage_exact", squareFeetValue, { shouldDirty: true });
        form.setValue("adminDetails.square_footage_source", "public", { shouldDirty: true });
        form.setValue("adminDetails.square_footage_unknown", "", { shouldDirty: true });
        form.setValue("adminDetails.estimated_square_footage_fallback", "", { shouldDirty: true });
        form.setValue("adminDetails.estimated_square_footage_fallback_notice", "", { shouldDirty: true });
      }
    }

    if (scenarioQuestionProfile === "residential") {
      applyOnce("bedrooms", findNumericAdminSelectOption("bedrooms", summary.bedrooms));
      applyOnce("bathrooms", findNumericAdminSelectOption("bathrooms", summary.bathrooms));
    }
  }, [
    form,
    propertyLookupAddressKey,
    propertyLookupSummary,
    scenarioQuestionProfile
  ]);

  useEffect(() => {
    if (!squareFootageUnknownSelected) {
      return;
    }

    if (!squareFootageFallback) {
      if (String(form.getValues("adminDetails.square_footage_source") ?? "") === "average_fallback") {
        form.setValue("adminDetails.square_footage", "", { shouldDirty: true });
        form.setValue("adminDetails.square_footage_source", "", { shouldDirty: true });
        form.setValue("adminDetails.estimated_square_footage_fallback", "", { shouldDirty: true });
        form.setValue("adminDetails.estimated_square_footage_fallback_notice", "", { shouldDirty: true });
      }
      return;
    }

    const nextSquareFootage = formatSquareFeetInputValue(squareFootageFallback.squareFeet);
    if (String(form.getValues("adminDetails.square_footage") ?? "") !== nextSquareFootage) {
      form.setValue("adminDetails.square_footage", nextSquareFootage, { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.square_footage_exact") ?? "")) {
      form.setValue("adminDetails.square_footage_exact", "", { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.square_footage_source") ?? "") !== "average_fallback") {
      form.setValue("adminDetails.square_footage_source", "average_fallback", { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.estimated_square_footage_fallback") ?? "") !== nextSquareFootage) {
      form.setValue("adminDetails.estimated_square_footage_fallback", nextSquareFootage, { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.estimated_square_footage_fallback_notice") ?? "") !== squareFootageFallback.notice) {
      form.setValue("adminDetails.estimated_square_footage_fallback_notice", squareFootageFallback.notice, { shouldDirty: true });
    }
  }, [
    form,
    squareFootageFallback?.notice,
    squareFootageFallback?.squareFeet,
    squareFootageUnknownSelected
  ]);

  useEffect(() => {
    if (serviceId && filteredServices.length && !currentService) {
      form.setValue("serviceId", "");
    }
  }, [currentService, filteredServices.length, form, serviceId]);

  useEffect(() => {
    const visibleOptionIds = new Set(visibleOptions.map((option) => option.id));
    const nextOptionIds = optionIds.filter((optionId) => visibleOptionIds.has(optionId));
    if (nextOptionIds.length !== optionIds.length) {
      form.setValue("optionIds", nextOptionIds);
    }
  }, [form, optionIds, visibleOptions]);

  useEffect(() => {
    ENTRY_ACCESS_OPTIONS.forEach((option) => {
      if (!option.detailKey || selectedEntryAccessCodeSet.has(option.value)) {
        return;
      }

      const fieldName = `adminDetails.${option.detailKey}` as const;
      if (String(form.getValues(fieldName) ?? "").trim()) {
        form.setValue(fieldName, "", { shouldDirty: true });
      }
    });
  }, [form, selectedEntryAccessCodeSet]);

  useEffect(() => {
    const currentSerializedScopes = String(form.getValues("adminDetails.extra_recurring_scopes") ?? "");
    const currentSummary = String(form.getValues("adminDetails.extra_recurring_summary") ?? "");

    if (!isRecurringFrequencySelection || !selectedVisibleOptions.length) {
      if (currentSerializedScopes) {
        form.setValue("adminDetails.extra_recurring_scopes", "", { shouldDirty: true });
      }
      if (currentSummary) {
        form.setValue("adminDetails.extra_recurring_summary", "", { shouldDirty: true });
      }
      return;
    }

    const nextScopes = Object.fromEntries(
      selectedVisibleOptions.map((option) => [
        option.id,
        recurringExtraScopes[option.id] ?? getDefaultRecurringExtraScope(option)
      ])
    ) as Record<string, RecurringExtraScopeValue>;

    const nextSerializedScopes = serializeRecurringExtraScopes(nextScopes);
    if (nextSerializedScopes !== currentSerializedScopes) {
      form.setValue("adminDetails.extra_recurring_scopes", nextSerializedScopes, { shouldDirty: true });
    }

    const nextSummary = buildRecurringExtraSummary(selectedVisibleOptions, nextScopes);
    if (nextSummary !== currentSummary) {
      form.setValue("adminDetails.extra_recurring_summary", nextSummary, { shouldDirty: true });
    }
  }, [form, isRecurringFrequencySelection, recurringExtraScopes, selectedVisibleOptions]);

  useEffect(() => {
    if (scenarioQuestionProfile === "residential") {
      if (!String(form.getValues("adminDetails.levels") ?? "").trim()) {
        form.setValue("adminDetails.levels", "1");
      }
      HOME_CONDITION_FIELDS.forEach(({ key }) => {
        if (!String(form.getValues(`adminDetails.${key}`) ?? "").trim()) {
          form.setValue(`adminDetails.${key}`, HOME_CONDITION_DEFAULT_LABEL);
        }
      });
      if (String(form.getValues("adminDetails.workstations") ?? "").trim()) {
        form.setValue("adminDetails.workstations", "");
      }
      if (String(form.getValues("adminDetails.office_private_rooms") ?? "").trim()) {
        form.setValue("adminDetails.office_private_rooms", "");
      }
      if (String(form.getValues("adminDetails.office_breakrooms") ?? "").trim()) {
        form.setValue("adminDetails.office_breakrooms", "");
      }
      if (String(form.getValues("adminDetails.building_apartments") ?? "").trim()) {
        form.setValue("adminDetails.building_apartments", "");
      }
      return;
    }

    HOME_CONDITION_FIELDS.forEach(({ key }) => {
      if (String(form.getValues(`adminDetails.${key}`) ?? "").trim()) {
        form.setValue(`adminDetails.${key}`, "");
      }
    });

    if (scenarioQuestionProfile === "office_commercial") {
      if (!String(form.getValues("adminDetails.levels") ?? "").trim()) {
        form.setValue("adminDetails.levels", "1");
      }
      if (String(form.getValues("adminDetails.square_footage_source") ?? "") === "average_fallback") {
        ["square_footage", "square_footage_exact", "square_footage_unknown", "square_footage_source", "estimated_square_footage_fallback", "estimated_square_footage_fallback_notice"].forEach((key) => {
          if (String(form.getValues(`adminDetails.${key}`) ?? "").trim()) {
            form.setValue(`adminDetails.${key}`, "");
          }
        });
      }
      if (String(form.getValues("adminDetails.bedrooms") ?? "").trim()) {
        form.setValue("adminDetails.bedrooms", "");
      }
      if (String(form.getValues("adminDetails.building_apartments") ?? "").trim()) {
        form.setValue("adminDetails.building_apartments", "");
      }
      return;
    }

    if (scenarioQuestionProfile === "building_common_areas") {
      ["bedrooms", "bathrooms", "levels", "square_footage", "square_footage_exact", "square_footage_unknown", "square_footage_source", "estimated_square_footage_fallback", "estimated_square_footage_fallback_notice", "workstations", "office_private_rooms", "office_breakrooms"].forEach((key) => {
        if (String(form.getValues(`adminDetails.${key}`) ?? "").trim()) {
          form.setValue(`adminDetails.${key}`, "");
        }
      });
      return;
    }

    ["bedrooms", "bathrooms", "levels", "square_footage", "square_footage_exact", "square_footage_unknown", "square_footage_source", "estimated_square_footage_fallback", "estimated_square_footage_fallback_notice", "workstations", "office_private_rooms", "office_breakrooms", "building_apartments"].forEach((key) => {
      if (String(form.getValues(`adminDetails.${key}`) ?? "").trim()) {
        form.setValue(`adminDetails.${key}`, "");
      }
    });
  }, [form, scenarioQuestionProfile]);

  useEffect(() => {
    if (showScenarioExtras || !optionIds.length) {
      return;
    }

    form.setValue("optionIds", [], { shouldDirty: true });
  }, [form, optionIds.length, showScenarioExtras]);

  useEffect(() => {
    if (showScenarioTip) {
      return;
    }

    if (String(form.getValues("adminDetails.tip_type") ?? "").trim()) {
      form.setValue("adminDetails.tip_type", "", { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.tip") ?? "").trim()) {
      form.setValue("adminDetails.tip", "", { shouldDirty: true });
    }
  }, [form, showScenarioTip]);

  useEffect(() => {
    if (!scenarioSkipAreaOptions.length) {
      if (String(form.getValues("adminDetails.skip_areas_enabled") ?? "").trim()) {
        form.setValue("adminDetails.skip_areas_enabled", "");
      }
      if (selectedSkipAreaCodes.length) {
        form.setValue("adminDetails.skip_areas", "");
      }
      if (String(form.getValues("adminDetails.skip_bedrooms_count") ?? "").trim()) {
        form.setValue("adminDetails.skip_bedrooms_count", "");
      }
      if (String(form.getValues("adminDetails.skip_bathrooms_count") ?? "").trim()) {
        form.setValue("adminDetails.skip_bathrooms_count", "");
      }
      return;
    }

    const allowedCodes = new Set<string>(scenarioSkipAreaOptions.map((option) => option.value));
    const filteredCodes = selectedSkipAreaCodes.filter((code) => allowedCodes.has(code));
    if (filteredCodes.length !== selectedSkipAreaCodes.length) {
      form.setValue("adminDetails.skip_areas", serializeBookingMultiSelectValue(filteredCodes), { shouldDirty: true });
    }
    if (!filteredCodes.includes("bedrooms") && String(form.getValues("adminDetails.skip_bedrooms_count") ?? "").trim()) {
      form.setValue("adminDetails.skip_bedrooms_count", "", { shouldDirty: true });
    }
    if (!filteredCodes.includes("bathrooms") && String(form.getValues("adminDetails.skip_bathrooms_count") ?? "").trim()) {
      form.setValue("adminDetails.skip_bathrooms_count", "", { shouldDirty: true });
    }
  }, [form, scenarioSkipAreaOptions, selectedSkipAreaCodes]);

  useEffect(() => {
    if (skipAreasEnabled) {
      return;
    }

    if (String(form.getValues("adminDetails.skip_areas") ?? "").trim()) {
      form.setValue("adminDetails.skip_areas", "", { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.skip_bedrooms_count") ?? "").trim()) {
      form.setValue("adminDetails.skip_bedrooms_count", "", { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.skip_bathrooms_count") ?? "").trim()) {
      form.setValue("adminDetails.skip_bathrooms_count", "", { shouldDirty: true });
    }
  }, [form, skipAreasEnabled]);

  useEffect(() => {
    if (showScenarioRemodeling && recentRemodeling === "yes") {
      return;
    }

    const clearAdminDetail = (key: string, options?: { shouldDirty?: boolean }) => {
      const fieldName = `adminDetails.${key}` as const;
      const currentValue = form.getValues(fieldName);
      if (currentValue !== undefined && currentValue !== "") {
        form.setValue(fieldName, "", options);
      }
    };

    clearAdminDetail("remodeling_type");
    clearAdminDetail("partial_remodeling_areas");
    clearAdminDetail("partial_remodeling_bedrooms_count");
    clearAdminDetail("partial_remodeling_bathrooms_count");
    clearAdminDetail("partial_remodeling_description");
    if (!showScenarioRemodeling) {
      clearAdminDetail("recent_remodeling");
    }
  }, [form, recentRemodeling, showScenarioRemodeling]);

  useEffect(() => {
    const currentRemodelingType = normalizeRemodelingOptionCode(form.getValues("adminDetails.remodeling_type"));
    if (currentRemodelingType && !scenarioRemodelOptions.some((option) => option.value === currentRemodelingType)) {
      form.setValue("adminDetails.remodeling_type", "");
    }
  }, [form, scenarioRemodelOptions]);

  useEffect(() => {
    if (remodelingTypeCode === "partial_remodeling") {
      const allowedCodes = new Set<string>(scenarioPartialRemodelOptions.map((option) => option.value));
      const filteredCodes = selectedPartialRemodelAreaCodes.filter((code) => allowedCodes.has(code));
      if (filteredCodes.length !== selectedPartialRemodelAreaCodes.length) {
        form.setValue("adminDetails.partial_remodeling_areas", serializeBookingMultiSelectValue(filteredCodes), {
          shouldDirty: true
        });
      }
      if (!filteredCodes.includes("bedrooms") && String(form.getValues("adminDetails.partial_remodeling_bedrooms_count") ?? "").trim()) {
        form.setValue("adminDetails.partial_remodeling_bedrooms_count", "", { shouldDirty: true });
      }
      if (!filteredCodes.includes("bathrooms") && String(form.getValues("adminDetails.partial_remodeling_bathrooms_count") ?? "").trim()) {
        form.setValue("adminDetails.partial_remodeling_bathrooms_count", "", { shouldDirty: true });
      }
      return;
    }

    if (String(form.getValues("adminDetails.partial_remodeling_areas") ?? "").trim()) {
      form.setValue("adminDetails.partial_remodeling_areas", "", { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.partial_remodeling_bedrooms_count") ?? "").trim()) {
      form.setValue("adminDetails.partial_remodeling_bedrooms_count", "", { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.partial_remodeling_bathrooms_count") ?? "").trim()) {
      form.setValue("adminDetails.partial_remodeling_bathrooms_count", "", { shouldDirty: true });
    }
    if (String(form.getValues("adminDetails.partial_remodeling_description") ?? "").trim()) {
      form.setValue("adminDetails.partial_remodeling_description", "", { shouldDirty: true });
    }
  }, [form, remodelingTypeCode, scenarioPartialRemodelOptions, selectedPartialRemodelAreaCodes]);

  useEffect(() => {
    if (!showScenarioFrequency) {
      if (frequencyRuleId) {
        form.setValue("frequencyRuleId", "");
      }
      return;
    }

    if (!availableFrequencyRules.length) {
      if (frequencyRuleId) {
        form.setValue("frequencyRuleId", "");
      }
      return;
    }

    if (frequencyRuleId && !availableFrequencyRules.some((item) => item.id === frequencyRuleId)) {
      form.setValue("frequencyRuleId", preferredFrequencyRuleId);
      return;
    }

    if (!frequencyRuleId && preferredFrequencyRuleId) {
      form.setValue("frequencyRuleId", preferredFrequencyRuleId);
    }
  }, [availableFrequencyRules, form, frequencyRuleId, preferredFrequencyRuleId, showScenarioFrequency]);

  useEffect(() => {
    if (resolvedCalendarProfileId !== calendarProfileId) {
      form.setValue("calendarProfileId", resolvedCalendarProfileId);
    }
  }, [calendarProfileId, form, resolvedCalendarProfileId]);

  useEffect(() => {
    if (eligibleLocations.length && !eligibleLocations.some((item) => item.id === locationId)) {
      form.setValue("locationId", eligibleLocations[0].id);
    }
  }, [eligibleLocations, form, locationId]);

  useEffect(() => {
    if (availableSpots.length && !availableSpots.some((item) => item.id === form.getValues("bookingSpotId"))) {
      form.setValue("bookingSpotId", availableSpots[0].id);
    }
  }, [availableSpots, form]);

  const availabilityQuery = useQuery({
    queryKey: [
      "availability",
      locationId,
      serviceId,
      resolvedCalendarProfileId,
      bookingSpotId,
      dateValue,
      effectiveOptionIds,
      JSON.stringify(adminDetails ?? {})
    ],
    queryFn: () =>
      apiFetch<{ items: Array<{ startsAt: string; startsAtLocal: string; remainingCapacity: number }> }>(
        `/api/availability?locationId=${locationId}&serviceId=${serviceId}&bookingSpotId=${bookingSpotId || ""}&calendarProfileId=${resolvedCalendarProfileId || ""}&optionIds=${encodeURIComponent(effectiveOptionIds.join(","))}&adminDetails=${encodeURIComponent(JSON.stringify(adminDetails ?? {}))}&date=${dateValue}`
      ),
    enabled: Boolean(isZipSupported && locationId && serviceId && dateValue)
  });

  const filteredAvailabilityItems = useMemo(() => {
    const slots = availabilityQuery.data?.items ?? [];
    return filterAvailabilitySlotsForServiceArea(slots, selectedLocation, matchedServiceAreaRule, dateValue);
  }, [availabilityQuery.data?.items, dateValue, matchedServiceAreaRule, selectedLocation]);
  const isWaitlistClaimDate = Boolean(
    waitlistToken &&
      waitlistPrefillQuery.data &&
      waitlistPrefillQuery.data.status === "active" &&
      dateValue &&
      dateValue === waitlistPrefillQuery.data.requestedDate
  );
  const visibleAvailabilityItems = useMemo(() => {
    if (!isWaitlistClaimDate || !waitlistPrefillQuery.data) {
      return filteredAvailabilityItems;
    }

    return filterWaitlistSlotsByPreferredWindow(
      filteredAvailabilityItems,
      availabilityLocationTimeZone || "America/Chicago",
      waitlistPrefillQuery.data.preferredWindow
    );
  }, [
    availabilityLocationTimeZone,
    filteredAvailabilityItems,
    isWaitlistClaimDate,
    waitlistPrefillQuery.data
  ]);
  const isArrivalWindowLoading = availabilityQuery.isLoading && !availabilityQuery.data;
  const isArrivalWindowError = availabilityQuery.isError;
  const showWaitlistSubmissionSection =
    waitlistFeatureEnabled &&
    shouldShowBookingQuestions &&
    quoteDetailsReady &&
    Boolean(dateValue) &&
    waitlistEnabled &&
    !isArrivalWindowLoading &&
    !isArrivalWindowError &&
    !visibleAvailabilityItems.length;
  const showWaitlistClaimBanner = Boolean(
    isWaitlistClaimDate &&
      waitlistPrefillQuery.data?.message &&
      !isArrivalWindowLoading &&
      !isArrivalWindowError &&
      !visibleAvailabilityItems.length
  );
  const canJoinWaitlistForSelectedDate =
    waitlistFeatureEnabled &&
    !isArrivalWindowLoading &&
    !isArrivalWindowError &&
    !(
      isWaitlistClaimDate &&
      waitlistPrefillQuery.data &&
      waitlistPrefillQuery.data.status === "active"
    );
  const arrivalTimesHint = visibleAvailabilityItems.length
    ? undefined
    : isArrivalWindowLoading
      ? "We're checking current availability for the selected date."
      : isArrivalWindowError
        ? "We couldn't load live arrival windows for the selected date."
        : isWaitlistClaimDate
          ? "This alert link only shows live openings that match your saved waitlist preference."
          : "Choose a date with open capacity or join the waitlist below.";

  useEffect(() => {
    if (!dateValue) {
      return;
    }
    const nextVisibleMonth = getIsoMonth(dateValue);
    setVisibleCalendarMonth((current) => (current === nextVisibleMonth ? current : nextVisibleMonth));
  }, [dateValue]);

  const monthAvailabilityQuery = useQuery({
    queryKey: [
      "availability-calendar-month",
      locationId,
      serviceId,
      resolvedCalendarProfileId,
      bookingSpotId,
      visibleCalendarMonth,
      effectiveOptionIds,
      JSON.stringify(adminDetails ?? {})
    ],
    queryFn: async () => {
      const monthDates = getIsoDatesForMonth(visibleCalendarMonth);
      const entries = await Promise.all(
        monthDates.map(async (candidateDate) => {
          const candidateResponse = await apiFetch<{ items: AvailabilitySlotSummary[] }>(
            `/api/availability?locationId=${locationId}&serviceId=${serviceId}&bookingSpotId=${bookingSpotId || ""}&calendarProfileId=${resolvedCalendarProfileId || ""}&optionIds=${encodeURIComponent(effectiveOptionIds.join(","))}&adminDetails=${encodeURIComponent(JSON.stringify(adminDetails ?? {}))}&date=${candidateDate}`
          );
          const candidateRule = getLocationServiceAreaRule(selectedLocation, normalizedZipCode, candidateDate);
          const candidateItems = filterAvailabilitySlotsForServiceArea(
            candidateResponse.items ?? [],
            selectedLocation,
            candidateRule,
            candidateDate
          );
          return [
            candidateDate,
            {
              date: candidateDate,
              hasAvailability: candidateItems.length > 0,
              firstStartsAt: candidateItems[0]?.startsAt ?? null
            } satisfies AvailabilityDateSummary
          ] as const;
        })
      );
      return Object.fromEntries(entries) as Record<string, AvailabilityDateSummary>;
    },
    enabled: Boolean(shouldShowBookingQuestions && isZipSupported && locationId && serviceId && visibleCalendarMonth)
  });

  const calendarMonthCells = useMemo(
    () => buildCalendarMonthCells(visibleCalendarMonth, monthAvailabilityQuery.data ?? {}),
    [monthAvailabilityQuery.data, visibleCalendarMonth]
  );
  const firstAvailableDateInVisibleMonth = useMemo(() => {
    const summaries = Object.values(monthAvailabilityQuery.data ?? {});
    const today = getTodayIsoDate();
    return (
      summaries
        .filter((summary) => summary.date >= today && summary.hasAvailability && summary.firstStartsAt)
        .sort((left, right) => left.date.localeCompare(right.date))[0] ?? null
    );
  }, [monthAvailabilityQuery.data]);

  useEffect(() => {
    if (
      !shouldShowBookingQuestions ||
      waitlistToken ||
      customerSelectedServiceDateRef.current ||
      autoSelectedFirstAvailableDateRef.current === firstAvailableDateAutoSelectKey ||
      monthAvailabilityQuery.isLoading ||
      monthAvailabilityQuery.isError ||
      !monthAvailabilityQuery.data
    ) {
      return;
    }

    const selectedDateSummary = dateValue ? monthAvailabilityQuery.data[dateValue] : undefined;
    if (selectedDateSummary?.hasAvailability && selectedDateSummary.firstStartsAt) {
      autoSelectedFirstAvailableDateRef.current = firstAvailableDateAutoSelectKey;
      return;
    }

    if (!firstAvailableDateInVisibleMonth?.date || !firstAvailableDateInVisibleMonth.firstStartsAt) {
      return;
    }

    autoSelectedFirstAvailableDateRef.current = firstAvailableDateAutoSelectKey;
    setSelectedDate(firstAvailableDateInVisibleMonth.date);
    setWaitlistEnabled(false);
    setWaitlistFeedback("");
    form.setValue("startsAt", firstAvailableDateInVisibleMonth.firstStartsAt, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false
    });
  }, [
    dateValue,
    firstAvailableDateAutoSelectKey,
    firstAvailableDateInVisibleMonth,
    form,
    monthAvailabilityQuery.data,
    monthAvailabilityQuery.isError,
    monthAvailabilityQuery.isLoading,
    shouldShowBookingQuestions,
    waitlistToken
  ]);

  useEffect(() => {
    const slots = visibleAvailabilityItems;
    if (!slots.length) {
      return;
    }

    if (waitlistEnabled) {
      setWaitlistEnabled(false);
      setWaitlistFeedback("A matching opening is available right now.");
    }

    if (!slots.some((slot) => slot.startsAt === startsAtValue)) {
      form.setValue("startsAt", slots[0].startsAt);
    }
  }, [form, startsAtValue, visibleAvailabilityItems, waitlistEnabled]);

  const nextAvailableDateQuery = useQuery({
    queryKey: [
      "next-available-date",
      locationId,
      serviceId,
      resolvedCalendarProfileId,
      bookingSpotId,
      dateValue,
      effectiveOptionIds,
      JSON.stringify(adminDetails ?? {})
    ],
    queryFn: async () => {
      for (let offset = 1; offset <= 30; offset += 1) {
        const candidateDate = shiftIsoDate(dateValue, offset);
        const candidateResponse = await apiFetch<{ items: Array<{ startsAt: string; startsAtLocal: string; remainingCapacity: number }> }>(
          `/api/availability?locationId=${locationId}&serviceId=${serviceId}&bookingSpotId=${bookingSpotId || ""}&calendarProfileId=${resolvedCalendarProfileId || ""}&optionIds=${encodeURIComponent(effectiveOptionIds.join(","))}&adminDetails=${encodeURIComponent(JSON.stringify(adminDetails ?? {}))}&date=${candidateDate}`
        );
        const candidateRule = getLocationServiceAreaRule(selectedLocation, normalizedZipCode, candidateDate);
        const candidateItems = filterAvailabilitySlotsForServiceArea(
          candidateResponse.items ?? [],
          selectedLocation,
          candidateRule,
          candidateDate
        );
        if (candidateItems.length) {
          return {
            date: candidateDate,
            startsAt: candidateItems[0].startsAt
          };
        }
      }
      return null;
    },
    enabled: Boolean(
      shouldShowBookingQuestions &&
      isZipSupported &&
      locationId &&
      serviceId &&
      dateValue &&
      !isArrivalWindowLoading &&
      !isArrivalWindowError &&
      !visibleAvailabilityItems.length
    )
  });

  useEffect(() => {
    if (
      !shouldShowBookingQuestions ||
      waitlistToken ||
      customerSelectedServiceDateRef.current ||
      autoSelectedFirstAvailableDateRef.current === firstAvailableDateAutoSelectKey ||
      visibleAvailabilityItems.length ||
      !nextAvailableDateQuery.data?.date ||
      !nextAvailableDateQuery.data.startsAt
    ) {
      return;
    }

    autoSelectedFirstAvailableDateRef.current = firstAvailableDateAutoSelectKey;
    setSelectedDate(nextAvailableDateQuery.data.date);
    setVisibleCalendarMonth(getIsoMonth(nextAvailableDateQuery.data.date));
    setWaitlistEnabled(false);
    setWaitlistFeedback("");
    form.setValue("startsAt", nextAvailableDateQuery.data.startsAt, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false
    });
  }, [
    firstAvailableDateAutoSelectKey,
    form,
    nextAvailableDateQuery.data,
    shouldShowBookingQuestions,
    visibleAvailabilityItems.length,
    waitlistToken
  ]);

  const quoteQuery = useQuery({
    queryKey: [
      "quote",
      locationId,
      serviceId,
      bookingSpotId,
      effectiveOptionIds,
      frequencyRuleId,
      resolvedCalendarProfileId,
      dateValue,
      startsAtValue,
      promoCode,
      giftCardCode,
      depositChoice,
      JSON.stringify(adminDetails ?? {})
    ],
    queryFn: () =>
      apiFetch<QuotePreview>("/api/availability/quote", {
        method: "POST",
        body: JSON.stringify({
          locationId,
          serviceId,
          bookingSpotId: bookingSpotId || undefined,
          optionIds: effectiveOptionIds,
          frequencyRuleId: frequencyRuleId || undefined,
          calendarProfileId: resolvedCalendarProfileId || undefined,
          startsAt: startsAtValue || undefined,
          serviceDate: dateValue || undefined,
          promoCode,
          giftCardCode,
          adminDetails,
          taxRate: configuredTaxRate,
          depositRate
        })
    }),
    enabled: Boolean(isZipSupported && locationId && serviceId)
  });
  const quotePricingMode = String(quoteQuery.data?.pricingDisplayMode ?? "");
  const isRemodelingHourlyQuote = quotePricingMode === "remodeling_hourly";
  const isRemodelingReviewQuote = quotePricingMode === "remodeling_review";
  const isManualReviewQuote = quotePricingMode === "manual_review";
  const isAnyManualReviewQuote = isManualReviewQuote || isRemodelingReviewQuote;
  const isAnyRemodelingQuote = isRemodelingHourlyQuote || isRemodelingReviewQuote;
  const isAnyNonExactQuote = isRemodelingHourlyQuote || isAnyManualReviewQuote;
  const estimateSummaryNote = cleanEstimateSummaryNote(quoteQuery.data?.scenarioCustomerNote);
  const manualReviewQuoteNote =
    estimateSummaryNote ||
    "We'll confirm the final scope, price, and duration after reviewing these details.";
  const arrivalFlexibilitySettings = bookingFormConfig.data?.bookingForm.arrivalFlexibility;
  const arrivalFlexibilityDiscountSummary = getArrivalFlexibilityDiscountSummary(arrivalFlexibilitySettings);
  const currentArrivalFlexibilitySelection = String(adminDetails.arrival_flexibility ?? "").trim();
  const arrivalFlexibilityLabel = String(quoteQuery.data?.arrivalFlexibilityLabel ?? "").trim();
  const arrivalFlexibilityOfferVisible = Boolean(
    quoteDetailsReady &&
      startsAtValue &&
      quoteQuery.data?.arrivalFlexibilityEligible &&
      arrivalFlexibilityLabel &&
      arrivalFlexibilityDiscountSummary &&
      !isAnyNonExactQuote
  );
  const arrivalFlexibilityIsSelected = Boolean(currentArrivalFlexibilitySelection);

  useEffect(() => {
    if (!currentArrivalFlexibilitySelection) {
      return;
    }

    if (!startsAtValue || !arrivalFlexibilityDiscountSummary || (quoteQuery.data && !quoteQuery.data.arrivalFlexibilityEligible)) {
      form.setValue("adminDetails.arrival_flexibility", "", { shouldDirty: true });
      return;
    }

    if (
      quoteQuery.data?.arrivalFlexibilityEligible &&
      arrivalFlexibilityLabel &&
      currentArrivalFlexibilitySelection !== arrivalFlexibilityLabel
    ) {
      form.setValue("adminDetails.arrival_flexibility", arrivalFlexibilityLabel, { shouldDirty: true });
    }
  }, [
    arrivalFlexibilityDiscountSummary,
    arrivalFlexibilityLabel,
    currentArrivalFlexibilitySelection,
    form,
    quoteQuery.data?.arrivalFlexibilityEligible,
    startsAtValue
  ]);

  const setCustomerSuppliesEquipmentChoice = (choice: "Company" | "Customer") => {
    form.clearErrors("adminDetails.supplies_provided_by" as any);
    form.setValue("adminDetails.supplies_provided_by", choice, {
      shouldDirty: true,
      shouldTouch: true
    });
    form.setValue("adminDetails.customer_provides_supplies_equipment", choice === "Customer" ? "yes" : "no", {
      shouldDirty: true,
      shouldTouch: true
    });
    if (choice !== "Customer") {
      form.setValue("adminDetails.customer_supplies_equipment_discount", "", { shouldDirty: true });
      form.setValue("adminDetails.customer_supplies_equipment_items", "", { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (!customerSuppliesEquipmentEnabled || customerSuppliesChoice) {
      return;
    }

    form.setValue("adminDetails.supplies_provided_by", "Company", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true
    });
    form.setValue("adminDetails.customer_provides_supplies_equipment", "no", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true
    });
  }, [customerSuppliesChoice, customerSuppliesEquipmentEnabled, form]);

  const previewPaymentSessionQuery = useQuery({
    queryKey: [
      "preview-payment-session",
      locationId,
      serviceId,
      bookingSpotId,
      effectiveOptionIds,
      frequencyRuleId,
      resolvedCalendarProfileId,
      dateValue,
      startsAtValue,
      promoCode,
      giftCardCode,
      effectiveDepositChoice,
      quoteQuery.data?.amountDueNow,
      JSON.stringify(adminDetails ?? {})
    ],
    queryFn: () =>
      apiFetch<BookingPaymentSession>("/api/availability/payment-session", {
        method: "POST",
        body: JSON.stringify({
          locationId,
          serviceId,
          bookingSpotId: bookingSpotId || undefined,
          optionIds: effectiveOptionIds,
          frequencyRuleId: frequencyRuleId || undefined,
          calendarProfileId: resolvedCalendarProfileId || undefined,
          startsAt: startsAtValue || undefined,
          serviceDate: dateValue || undefined,
          promoCode,
          giftCardCode,
          adminDetails,
          taxRate: configuredTaxRate,
          depositRate,
          depositChoice: effectiveDepositChoice
        })
      }),
    refetchOnWindowFocus: false,
    enabled: Boolean(
      showCheckoutSection &&
        isZipSupported &&
        locationId &&
        serviceId &&
        quoteQuery.data &&
        quoteQuery.data.amountDueNow > 0 &&
        !pendingPaymentBooking
    )
  });

  const selectedQuoteOptions =
    quoteQuery.data?.optionDetails?.length
      ? quoteQuery.data.optionDetails
      : visibleOptions
          .filter((option) => effectiveOptionIds.includes(option.id))
          .map((option) => {
            const quantity = isSimpleQuantityServiceOption(option)
              ? normalizeSimpleQuantityValue(adminDetails[getSimpleQuantityAdminDetailKey(option)], option)
              : 1;
            return {
              id: option.id,
              name: option.name,
              priceDeltaAmount: option.priceDeltaAmount * quantity,
              durationDeltaMinutes: option.durationDeltaMinutes * quantity,
              quantity,
            quantityLabel: isDishesHandWashingServiceOption(option)
              ? "sinkful"
              : isLaundryServiceOption(option)
                ? "load"
                : ""
            };
          });
  const showTaxRowInSummary = bookingFormConfig.data?.general?.showTaxOnBookingForm !== false;
  const hiddenTaxIncludedInTotal = !showTaxRowInSummary && Number(quoteQuery.data?.tax ?? 0) > 0;
  const quoteTotalLabel = hiddenTaxIncludedInTotal ? "Total (incl. tax)" : "Total";
  const quoteAmountDueNow = Math.max(0, Math.round(Number(quoteQuery.data?.amountDueNow ?? 0)));
  const quoteTotalAmount = Math.max(0, Math.round(Number(quoteQuery.data?.total ?? 0)));
  const quoteFrequencyDiscountAmount = Math.max(0, Math.round(Number(quoteQuery.data?.frequencyDiscount ?? 0)));
  const quoteCustomerSuppliesEquipmentDiscountAmount = Math.max(
    0,
    Math.round(Number(quoteQuery.data?.customerSuppliesEquipmentDiscount ?? 0))
  );
  const quoteSkipAreaDeductionAmount = Math.max(0, Math.round(Number(quoteQuery.data?.skipAreaDeductionAmount ?? 0)));
  const quoteSkipAreaVisitMinutesSaved = Math.max(0, Math.round(Number(quoteQuery.data?.skipAreaDurationDeductionMinutes ?? 0)));
  const quoteSkipAreaLaborMinutesSaved =
    quoteQuery.data?.skipAreaDeductionRows?.reduce(
      (total, row) => total + Math.max(0, Number(row.oneMaidMinutes ?? 0)),
      0
    ) || quoteSkipAreaVisitMinutesSaved * Math.max(1, quoteTeamSize);
  const quoteDisplayedSubtotalAmount = Math.max(
    0,
    Math.round(Number(quoteQuery.data?.subtotal ?? 0)) + quoteSkipAreaDeductionAmount
  );
  const showRecurringCleaningTotals = Boolean(
    quoteQuery.data &&
      quoteDetailsReady &&
      !isAnyNonExactQuote &&
      isRecurringFrequencySelection &&
      quoteFrequencyDiscountAmount > 0
  );
  const quoteInitialCleaningTotalAmount = showRecurringCleaningTotals
    ? Math.max(0, quoteTotalAmount + quoteFrequencyDiscountAmount)
    : quoteDisplayedSubtotalAmount;
  const quoteBasePriceAmount = Math.max(
    0,
    Math.round(Number(quoteQuery.data?.basePriceAmount ?? quoteQuery.data?.subtotal ?? 0))
  );
  const quoteBaseDurationMinutes = Math.max(
    0,
    Number(quoteQuery.data?.baseDurationMinutes ?? quoteQuery.data?.durationMinutes ?? 0)
  );
  const quoteBaseLaborHours = (quoteBaseDurationMinutes * Math.max(1, quoteTeamSize)) / 60;
  const quoteHasSelectedExtras = selectedQuoteOptions.length > 0;
  const quoteHourlyRateCentsPerMaidHour =
    Math.max(0, Math.round(Number(quoteQuery.data?.hourlyRateCentsPerMaidHour ?? 0))) ||
    (quoteBaseLaborHours > 0 && quoteBasePriceAmount > 0
      ? Math.round(quoteBasePriceAmount / quoteBaseLaborHours)
      : 0);
  const showQuoteHourlyCalculation =
    Boolean(quoteQuery.data) &&
    quoteDetailsReady &&
    !isAnyNonExactQuote &&
    quoteHourlyRateCentsPerMaidHour > 0 &&
    quoteBaseLaborHours > 0 &&
    quoteBasePriceAmount > 0;
  const quoteRemainingBookingBalance = Math.max(
    0,
    Math.round(Number(quoteQuery.data?.remainingBookingBalance ?? quoteTotalAmount))
  );
  const quoteBalanceDueLater = Math.max(0, quoteRemainingBookingBalance - quoteAmountDueNow);
  const showCompactDueTodayRow = Boolean(
    quoteQuery.data &&
      quoteDetailsReady &&
      !isAnyNonExactQuote &&
      quoteAmountDueNow !== quoteTotalAmount
  );
  const compactDueTodayNote =
    quoteAmountDueNow <= 0
      ? quoteBalanceDueLater > 0
        ? `No payment is due today; ${formatPublicCurrency(quoteBalanceDueLater)} stays due later.`
        : "No payment is due today."
      : quoteBalanceDueLater > 0
        ? `${formatPublicCurrency(quoteBalanceDueLater)} stays due later.`
        : "Today's payment reflects the current checkout balance.";
  useEffect(() => {
    if (!promoCode.trim()) {
      setPromoCodeTouched(false);
    }
  }, [promoCode]);
  useEffect(() => {
    if (!giftCardCode.trim()) {
      setGiftCardCodeTouched(false);
    }
  }, [giftCardCode]);

  const showPromoCodeFeedback =
    promoCodeTouched && Boolean(promoCode.trim()) && Boolean(isZipSupported && locationId && serviceId);
  const showGiftCardFeedback =
    giftCardCodeTouched && Boolean(giftCardCode.trim()) && Boolean(isZipSupported && locationId && serviceId);
  const legalPagesConfig = resolvePublicBookingLegalPagesConfig(bookingFormConfig.data?.other?.legalPages);
  const legalConsentAccepted = isCheckedConsentValue(form.watch("legalConsentAccepted"));
  const bookingConsentMissing = Boolean(legalPagesConfig && !legalConsentAccepted);
  const legalConsentError = String((form.formState.errors as any)?.legalConsentAccepted?.message ?? "");
  const turnstileTokenError = String((form.formState.errors as any)?.turnstileToken?.message ?? "");
  const checkoutSessionMode = previewPaymentSessionQuery.data?.mode ?? "authorization";
  const bookingSubmitLabel =
    legalPagesConfig && quoteQuery.data
      ? getPublicBookingSubmitLabel(Number(quoteQuery.data.amountDueNow ?? 0), formatPublicCurrency, checkoutSessionMode)
      : "Confirm booking";
  const checkoutAuthorizationTitle =
    checkoutSessionMode === "setup"
      ? "Save payment method"
      : checkoutSessionMode === "payment"
        ? `Pay ${formatPublicCurrency(quoteAmountDueNow)}`
        : "Pre-authorization";

  const createBookingMutation = useMutation({
    mutationFn: async (values: any) => {
      const booking = await apiFetch<{
        bookingId: string;
        publicReference: string;
        manageToken: string;
        quote: { total: number; amountDueNow: number };
      }>("/api/bookings", {
        method: "POST",
          body: JSON.stringify(values)
      });
      return booking;
    }
  });
  const createWaitlistMutation = useMutation({
    mutationFn: async (values: any) =>
      apiFetch<PublicWaitlistCreateResponse>("/api/waitlist/public", {
        method: "POST",
        body: JSON.stringify(values)
      })
  });

  const submitWaitlistRequest = async () => {
    try {
      setWaitlistFeedback("");
      form.clearErrors(["customer.firstName", "customer.lastName", "customer.email", "customer.phone", "turnstileToken"] as any);

      if (visibleAvailabilityItems.length) {
        form.setValue("startsAt", visibleAvailabilityItems[0].startsAt, {
          shouldDirty: true,
          shouldTouch: true
        });
        setWaitlistEnabled(false);
        setWaitlistFeedback("A matching opening is already available right now.");
        return;
      }

      const rawValues = form.getValues();
      const submittedCustomer =
        rawValues.customer && typeof rawValues.customer === "object" && !Array.isArray(rawValues.customer)
          ? rawValues.customer
          : {};
      const submittedAdminDetails = normalizeAdminDetailsRecord(rawValues.adminDetails);
      const payload = {
        customer: {
          ...submittedCustomer,
          email: String(submittedCustomer.email ?? "").trim(),
          phone: String(submittedCustomer.phone ?? "").trim()
        },
        locationId,
        serviceId,
        bookingSpotId: bookingSpotId || undefined,
        optionIds: effectiveOptionIds,
        frequencyRuleId: frequencyRuleId || undefined,
        calendarProfileId: resolvedCalendarProfileId || undefined,
        requestedDate: dateValue,
        preferredWindow: waitlistPreferredWindow,
        notifyBySms:
          waitlistSmsAlertsEnabled &&
          waitlistNotifyBySms &&
          Boolean(String(submittedCustomer.phone ?? "").trim()),
        source: "public_waitlist",
        turnstileToken: String(rawValues.turnstileToken ?? "").trim(),
        adminDetails: submittedAdminDetails
      };
      const parsed = publicWaitlistCreateInputSchema.safeParse(payload);
      if (!parsed.success) {
        const flattened = parsed.error.flatten();
        const fieldErrors = flattened.fieldErrors as Record<string, string[] | undefined>;
        const nestedCustomer = (fieldErrors.customer as unknown as string[] | undefined) ?? [];
        const combinedErrors = [...flattened.formErrors, ...nestedCustomer];
        const message = combinedErrors[0] ?? "Please complete the required waitlist details.";

        const customer = payload.customer as Record<string, unknown>;
        if (!String(customer.firstName ?? "").trim()) {
          form.setError("customer.firstName" as any, { type: "required", message: "First name is required." });
        }
        if (!String(customer.lastName ?? "").trim()) {
          form.setError("customer.lastName" as any, { type: "required", message: "Last name is required." });
        }
        if (!String(customer.email ?? "").trim()) {
          form.setError("customer.email" as any, { type: "required", message: "Email is required." });
        }
        if (payload.notifyBySms && !String(customer.phone ?? "").trim()) {
          form.setError("customer.phone" as any, { type: "required", message: "Phone is required for text alerts." });
        }
        if (!payload.turnstileToken) {
          form.setError("turnstileToken" as any, { type: "required", message: "Please complete the verification challenge." });
        }

        setWaitlistFeedback(message);
        return;
      }

      const response = await createWaitlistMutation.mutateAsync(parsed.data);
      setWaitlistFeedback(response.message);

      if (response.status === "available_now" && response.liveSlots.length) {
        setWaitlistEnabled(false);
        form.setValue("startsAt", response.liveSlots[0].startsAt, {
          shouldDirty: true,
          shouldTouch: true
        });
        await reactQueryClient.invalidateQueries({ queryKey: ["availability"] });
      }
    } catch (error) {
      setWaitlistFeedback(error instanceof Error ? error.message : "We couldn't join the waitlist right now.");
    }
  };

  const submitPublicBooking = form.handleSubmit(async (values) => {
    try {
      form.clearErrors([
        ...requiredScenarioDetailFields.map((field) => `adminDetails.${field.key}`),
        "adminDetails.remodeling_type",
        "adminDetails.partial_remodeling_areas",
        "adminDetails.partial_remodeling_bedrooms_count",
        "adminDetails.partial_remodeling_bathrooms_count",
        "adminDetails.lockbox_code",
        "adminDetails.lockbox_location",
        "adminDetails.building_code",
        "adminDetails.gate_code",
        "adminDetails.unit_code",
        "adminDetails.alarm_code",
        "adminDetails.key_hiding_spot",
        "adminDetails.parking_instructions",
        "adminDetails.access_code_selected",
        "adminDetails.entry_information_custom",
        "adminDetails.supplies_provided_by",
        "adminDetails.tip"
      ] as any);
      const submittedAdminDetailsForValidation = normalizeAdminDetailsRecord(values.adminDetails);
      const missingRequiredScenarioFields = requiredScenarioDetailFields.filter(
        (field) => !String(submittedAdminDetailsForValidation[field.key] ?? "").trim()
      );

      if (missingRequiredScenarioFields.length) {
        missingRequiredScenarioFields.forEach((field) => {
          form.setError(`adminDetails.${field.key}` as any, {
            type: "required",
            message: `${field.label} is required.`
          });
        });
        form.setFocus(`adminDetails.${missingRequiredScenarioFields[0].key}` as any);
        return;
      }

      const submittedRemodelingTypeCode = normalizeRemodelingOptionCode(submittedAdminDetailsForValidation.remodeling_type);
      const submittedPartialAreaCodes = parseMultiSelectCodes(submittedAdminDetailsForValidation.partial_remodeling_areas);

      if (showScenarioRemodeling && recentRemodeling === "yes" && !submittedRemodelingTypeCode) {
        form.setError("adminDetails.remodeling_type" as any, {
          type: "required",
          message: "Select a renovation option."
        });
        form.setFocus("adminDetails.remodeling_type" as any);
        return;
      }

      if (submittedRemodelingTypeCode === "partial_remodeling" && !submittedPartialAreaCodes.length) {
        form.setError("adminDetails.partial_remodeling_areas" as any, {
          type: "required",
          message: "Select at least one partial renovation area."
        });
        form.setFocus("adminDetails.partial_remodeling_areas" as any);
        return;
      }

      if (
        submittedPartialAreaCodes.includes("bedrooms") &&
        !String(submittedAdminDetailsForValidation.partial_remodeling_bedrooms_count ?? "").trim()
      ) {
        form.setError("adminDetails.partial_remodeling_bedrooms_count" as any, {
          type: "required",
          message: "Select how many bedrooms were renovated."
        });
        form.setFocus("adminDetails.partial_remodeling_bedrooms_count" as any);
        return;
      }

      if (
        submittedPartialAreaCodes.includes("bathrooms") &&
        !String(submittedAdminDetailsForValidation.partial_remodeling_bathrooms_count ?? "").trim()
      ) {
        form.setError("adminDetails.partial_remodeling_bathrooms_count" as any, {
          type: "required",
          message: "Select how many bathrooms were renovated."
        });
        form.setFocus("adminDetails.partial_remodeling_bathrooms_count" as any);
        return;
      }

      if (
        customerSuppliesEquipmentEnabled &&
        !String(submittedAdminDetailsForValidation.supplies_provided_by ?? "").trim()
      ) {
        form.setError("adminDetails.supplies_provided_by" as any, {
          type: "required",
          message: "Please choose whether you will provide cleaning supplies and equipment."
        });
        return;
      }

      const submittedTipType = String(submittedAdminDetailsForValidation.tip_type ?? "").trim().toLowerCase();
      const submittedTipRaw = String(submittedAdminDetailsForValidation.tip ?? "").trim();
      const submittedTipValue = Number(submittedTipRaw);
      if (submittedTipType && submittedTipRaw) {
        if (!Number.isFinite(submittedTipValue) || submittedTipValue < 0) {
          form.setError("adminDetails.tip" as any, {
            type: "required",
            message: "Enter a valid tip amount."
          });
          form.setFocus("adminDetails.tip" as any);
          return;
        }

        if (submittedTipType === "percent" && submittedTipValue > 100) {
          form.setError("adminDetails.tip" as any, {
            type: "max",
            message: "Percentage tip cannot be more than 100."
          });
          form.setFocus("adminDetails.tip" as any);
          return;
        }

        if (submittedTipType === "flat" && submittedTipValue > 500) {
          form.setError("adminDetails.tip" as any, {
            type: "max",
            message: "Flat amount tip cannot be more than 500."
          });
          form.setFocus("adminDetails.tip" as any);
          return;
        }
      }

      const submittedEntryAccessCodes = new Set(
        parseEntryAccessCodes(submittedAdminDetailsForValidation.entry_information_selected)
      );
      for (const option of ENTRY_ACCESS_OPTIONS) {
        if (!option.detailKey || !submittedEntryAccessCodes.has(option.value)) {
          continue;
        }

        const detailValue = String(submittedAdminDetailsForValidation[option.detailKey] ?? "").trim();
        if (detailValue) {
          continue;
        }

        const fieldName = `adminDetails.${option.detailKey}` as any;
        const detailLabel = option.detailLabel ?? option.label;
        form.setError(fieldName, {
          type: "required",
          message: `Enter ${detailLabel.toLowerCase()}.`
        });
        form.setFocus(fieldName);
        return;
      }

      const submittedAccessCodesEnabled =
        String(submittedAdminDetailsForValidation.access_codes_enabled ?? "").trim().toLowerCase() === "yes";
      const submittedAccessCodeSelections = parseAccessCodeOptionCodes(
        submittedAdminDetailsForValidation.access_code_selected
      );

      if (submittedAccessCodesEnabled && !submittedAccessCodeSelections.length) {
        form.setError("adminDetails.access_code_selected" as any, {
          type: "required",
          message: "Select at least one access code option."
        });
        return;
      }

      for (const option of ACCESS_CODE_OPTIONS) {
        if (!submittedAccessCodeSelections.includes(option.value)) {
          continue;
        }

        for (const field of option.fields) {
          const detailValue = String(submittedAdminDetailsForValidation[field.detailKey] ?? "").trim();
          if (detailValue) {
            continue;
          }

          const fieldName = `adminDetails.${field.detailKey}` as any;
          form.setError(fieldName, {
            type: "required",
            message: `Enter ${field.detailLabel.toLowerCase()}.`
          });
          form.setFocus(fieldName);
          return;
        }
      }

      if (legalPagesConfig && !isCheckedConsentValue(values.legalConsentAccepted)) {
        form.setError("legalConsentAccepted" as any, {
          type: "required",
          message: PUBLIC_BOOKING_LEGAL_CONSENT_ERROR
        });
        form.setFocus("legalConsentAccepted" as any);
        return;
      }

      const normalizedVisibleOptionIds = normalizeStringArray(values.optionIds);
      const normalizedOptionIds =
        scenarioQuestionProfile === "carpet_standalone" && effectiveCarpetShampooingOption
          ? normalizeStringArray([...normalizedVisibleOptionIds, effectiveCarpetShampooingOption.id])
          : normalizedVisibleOptionIds;
      const submittedAdminDetails = normalizeAdminDetailsRecord(values.adminDetails);
      const remodelingSummary = buildRemodelingSummary(scenarioRemodelProfile, submittedAdminDetails);
      const selectedOptionsForSubmit = visibleOptions.filter((option) => normalizedVisibleOptionIds.includes(option.id));
      const submittedFrequencyRule = availableFrequencyRules.find((item) => item.id === String(values.frequencyRuleId ?? ""));

      if (submittedFrequencyRule && !isOneTimeFrequencyRule(submittedFrequencyRule) && selectedOptionsForSubmit.length) {
        const parsedScopes = parseRecurringExtraScopes(String(submittedAdminDetails.extra_recurring_scopes ?? ""));
        const normalizedScopes = Object.fromEntries(
          selectedOptionsForSubmit.map((option) => [
            option.id,
            parsedScopes[option.id] ?? getDefaultRecurringExtraScope(option)
          ])
        ) as Record<string, RecurringExtraScopeValue>;
        submittedAdminDetails.extra_recurring_scopes = serializeRecurringExtraScopes(normalizedScopes);
        submittedAdminDetails.extra_recurring_summary = buildRecurringExtraSummary(selectedOptionsForSubmit, normalizedScopes);
      } else {
        delete submittedAdminDetails.extra_recurring_scopes;
        delete submittedAdminDetails.extra_recurring_summary;
      }

      const entryAccessSummary = buildEntryAccessSummary(submittedAdminDetails);
      if (entryAccessSummary) {
        submittedAdminDetails.entry_information = entryAccessSummary;
      } else {
        delete submittedAdminDetails.entry_information;
      }

      if (!parseEntryAccessCodes(submittedAdminDetails.entry_information_selected).length) {
        delete submittedAdminDetails.entry_information_selected;
      }
      if (
        String(submittedAdminDetails.access_codes_enabled ?? "").trim().toLowerCase() !== "yes" &&
        !parseAccessCodeOptionCodes(submittedAdminDetails.access_code_selected).length
      ) {
        delete submittedAdminDetails.access_codes_enabled;
        delete submittedAdminDetails.access_code_selected;
      }

      if (remodelingSummary) {
        submittedAdminDetails.remodeling_summary = remodelingSummary;
      } else {
        delete submittedAdminDetails.remodeling_summary;
      }

      const appointmentNotifications = parseAppointmentNotificationLabels(submittedAdminDetails.appointment_notifications);
      const reminderOptInSelected = Boolean(values.customer?.emailPreferences?.smsReminderOptInStatus);
      const nextAppointmentNotifications = reminderOptInSelected
        ? [...appointmentNotifications.filter((entry) => entry !== SMS_REMINDER_APPOINTMENT_NOTIFICATION_LABEL), SMS_REMINDER_APPOINTMENT_NOTIFICATION_LABEL]
        : appointmentNotifications.filter((entry) => entry !== SMS_REMINDER_APPOINTMENT_NOTIFICATION_LABEL);
      if (nextAppointmentNotifications.length) {
        submittedAdminDetails.appointment_notifications = nextAppointmentNotifications.join(", ");
      } else {
        delete submittedAdminDetails.appointment_notifications;
      }

      const booking =
        pendingPaymentBooking ??
        (await createBookingMutation.mutateAsync({
          ...values,
          source: waitlistToken ? "waitlist_claim" : values.source,
          depositChoice: effectiveDepositChoice,
          promoCode: String(values.promoCode ?? "").trim(),
          giftCardCode: String(values.giftCardCode ?? "").trim(),
          optionIds: normalizedOptionIds,
          adminDetails: submittedAdminDetails,
          waitlistRequestToken: waitlistToken || undefined,
          paymentIntentId:
            quoteQuery.data && quoteQuery.data.amountDueNow > 0 ? previewPaymentSessionQuery.data?.paymentIntentId || undefined : undefined,
          setupIntentId:
            quoteQuery.data && quoteQuery.data.amountDueNow > 0 ? previewPaymentSessionQuery.data?.setupIntentId || undefined : undefined
        }));

      let paymentStatus: string | null = null;
      if (booking.quote.amountDueNow > 0) {
        setPendingPaymentBooking(booking);
        if (!paymentCardRef.current) {
          throw new Error("Stripe payment form is not ready yet.");
        }
        paymentStatus = await paymentCardRef.current.confirmPayment();
      }

      const confirmationSearchParams = new URLSearchParams({
        reference: booking.publicReference,
        token: booking.manageToken
      });
      if (paymentStatus) {
        confirmationSearchParams.set("payment", paymentStatus);
      }

      navigate(`/book/confirmation/${booking.bookingId}?${confirmationSearchParams.toString()}`);
    } catch (error) {
      if (!pendingPaymentBooking && error instanceof Error && !createBookingMutation.error) {
        console.error("Public booking submit failed", error);
      }
    }
  });

  const showSmsReminderOptIn = bookingFormConfig.data?.textMessages?.offerReminderOptInOnBookingForm === true;
  const preselectSmsReminderOptIn =
    bookingFormConfig.data?.textMessages?.preselectReminderOptInOnBookingForm === true;
  const smsReminderDisclosure = buildSmsReminderOptInDisclosure().replace(/\s*Reply STOP to opt out\.?/i, "");

  const zillowAddressQuery = [
    String(form.watch("customer.address.line1") ?? addressGateValue ?? "").trim(),
    String(form.watch("customer.address.city") ?? "").trim(),
    String(form.watch("customer.address.state") ?? "").trim(),
    String(form.watch("customer.address.postalCode") ?? "").trim()
  ]
    .filter(Boolean)
    .join(", ");
  const zillowSearchUrl = zillowAddressQuery
    ? `https://www.zillow.com/homes/${encodeURIComponent(zillowAddressQuery)}_rb/`
    : "https://www.zillow.com/";
  const showBookingDebugPanel =
    typeof window !== "undefined" &&
    window.location.hostname.includes("booking-seconds-staging");
  const smsReminderOptInDirty = Boolean(
    (form.formState.dirtyFields as any)?.customer?.emailPreferences?.smsReminderOptInStatus
  );

  useEffect(() => {
    if (!showSmsReminderOptIn || !preselectSmsReminderOptIn || smsReminderOptInDefaultAppliedRef.current) {
      return;
    }

    const currentValue = Boolean(form.getValues("customer.emailPreferences.smsReminderOptInStatus"));
    if (smsReminderOptInDirty || currentValue) {
      smsReminderOptInDefaultAppliedRef.current = true;
      return;
    }

    form.setValue("customer.emailPreferences.smsReminderOptInStatus", true, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false
    });
    smsReminderOptInDefaultAppliedRef.current = true;
  }, [form, preselectSmsReminderOptIn, showSmsReminderOptIn, smsReminderOptInDirty]);

  useEffect(() => {
    if (!rebookBookingId) {
      appliedRebookRef.current = null;
      return;
    }

    if (!rebookPrefillQuery.data || appliedRebookRef.current === rebookBookingId) {
      return;
    }

    const defaults = getBookingFormDefaults();
    form.reset({
      ...defaults,
      customer: {
        ...defaults.customer,
        ...rebookPrefillQuery.data.customer,
        address: {
          ...defaults.customer.address,
          ...(rebookPrefillQuery.data.customer.address ?? {})
        },
        emailPreferences: {
          ...defaults.customer.emailPreferences,
          ...(rebookPrefillQuery.data.customer.emailPreferences ?? {})
        }
      },
      locationId: rebookPrefillQuery.data.locationId || defaults.locationId,
      serviceId: rebookPrefillQuery.data.serviceId || defaults.serviceId,
      bookingSpotId: rebookPrefillQuery.data.bookingSpotId || defaults.bookingSpotId,
      optionIds: rebookPrefillQuery.data.optionIds ?? defaults.optionIds,
      frequencyRuleId: rebookPrefillQuery.data.frequencyRuleId || defaults.frequencyRuleId,
      calendarProfileId: rebookPrefillQuery.data.calendarProfileId || defaults.calendarProfileId,
      adminDetails: {
        ...defaults.adminDetails,
        ...normalizeAdminDetailsRecord(rebookPrefillQuery.data.adminDetails)
      },
      startsAt: defaults.startsAt,
      promoCode: "",
      depositChoice: defaults.depositChoice,
      turnstileToken: ""
    });
    const rebookPostalCode = normalizePostalCode(rebookPrefillQuery.data.customer.address?.postalCode ?? "");
    setAddressGateValue(rebookPrefillQuery.data.customer.address?.line1 ?? "");
    setBookingGatePostalCode(rebookPostalCode);
    setResolvedAddressGate({
      line1: rebookPrefillQuery.data.customer.address?.line1 ?? "",
      unitNumber: rebookPrefillQuery.data.customer.address?.unitNumber ?? "",
      city: rebookPrefillQuery.data.customer.address?.city ?? "",
      state: rebookPrefillQuery.data.customer.address?.state ?? "",
      postalCode: rebookPostalCode
    });
    setConfirmedBookingGate(null);
    setConfirmedAddressContext(null);
    setPromoCodeTouched(false);
    setPendingPaymentBooking(null);
    setSelectedDate("");
    customerSelectedServiceDateRef.current = false;
    autoSelectedFirstAvailableDateRef.current = null;
    setWaitlistEnabled(false);
    setWaitlistPreferredWindow("any");
    setWaitlistNotifyBySms(false);
    setWaitlistFeedback("");
    appliedRebookRef.current = rebookBookingId;
  }, [form, rebookBookingId, rebookPrefillQuery.data]);

  useEffect(() => {
    if (!waitlistToken) {
      appliedWaitlistRef.current = null;
      return;
    }

    if (!waitlistPrefillQuery.data || appliedWaitlistRef.current === waitlistToken) {
      return;
    }

    const defaults = getBookingFormDefaults();
    const waitlistCustomer =
      waitlistPrefillQuery.data.customer &&
      typeof waitlistPrefillQuery.data.customer === "object" &&
      !Array.isArray(waitlistPrefillQuery.data.customer)
        ? (waitlistPrefillQuery.data.customer as Record<string, any>)
        : {};
    form.reset({
      ...defaults,
      customer: {
        ...defaults.customer,
        ...waitlistCustomer,
        address: {
          ...defaults.customer.address,
          ...(waitlistCustomer.address ?? {})
        },
        emailPreferences: {
          ...defaults.customer.emailPreferences,
          ...(waitlistCustomer.emailPreferences ?? {})
        }
      },
      locationId: waitlistPrefillQuery.data.locationId || defaults.locationId,
      serviceId: waitlistPrefillQuery.data.serviceId || defaults.serviceId,
      bookingSpotId: waitlistPrefillQuery.data.bookingSpotId || defaults.bookingSpotId,
      optionIds: waitlistPrefillQuery.data.optionIds ?? defaults.optionIds,
      frequencyRuleId: waitlistPrefillQuery.data.frequencyRuleId || defaults.frequencyRuleId,
      calendarProfileId: waitlistPrefillQuery.data.calendarProfileId || defaults.calendarProfileId,
      adminDetails: {
        ...defaults.adminDetails,
        ...normalizeAdminDetailsRecord(waitlistPrefillQuery.data.adminDetails)
      },
      startsAt: waitlistPrefillQuery.data.liveSlots[0]?.startsAt ?? defaults.startsAt,
      promoCode: "",
      depositChoice: defaults.depositChoice,
      turnstileToken: ""
    });
    const waitlistPostalCode = normalizePostalCode(String(waitlistCustomer.address?.postalCode ?? ""));
    setAddressGateValue(String(waitlistCustomer.address?.line1 ?? ""));
    setBookingGatePostalCode(waitlistPostalCode);
    setResolvedAddressGate({
      line1: String(waitlistCustomer.address?.line1 ?? ""),
      unitNumber: String(waitlistCustomer.address?.unitNumber ?? ""),
      city: String(waitlistCustomer.address?.city ?? ""),
      state: String(waitlistCustomer.address?.state ?? ""),
      postalCode: waitlistPostalCode
    });
    setConfirmedBookingGate(null);
    setConfirmedAddressContext(null);
    setPromoCodeTouched(false);
    setPendingPaymentBooking(null);
    setSelectedDate(waitlistPrefillQuery.data.requestedDate);
    customerSelectedServiceDateRef.current = true;
    autoSelectedFirstAvailableDateRef.current = null;
    setWaitlistEnabled(false);
    setWaitlistPreferredWindow(waitlistPrefillQuery.data.preferredWindow);
    setWaitlistNotifyBySms(false);
    setWaitlistFeedback(waitlistPrefillQuery.data.message || "");
    appliedWaitlistRef.current = waitlistToken;
  }, [form, waitlistPrefillQuery.data, waitlistToken]);

  return (
    <div className="marketing-shell">
      <section className="booking-shell">
        <div className="booking-grid">
          <Card eyebrow="Instant Booking">
            <form className="stack" onSubmit={submitPublicBooking}>
              {rebookPrefillQuery.data ? (
                <div className="portal-info-banner">
                  <strong>We prefilled your last visit.</strong>
                  <span>
                    Review the details from {rebookPrefillQuery.data.sourcePublicReference} and choose a new appointment time.
                  </span>
                </div>
              ) : null}
              {rebookPrefillQuery.error ? (
                <p className="error-text">We couldn't prefill that visit, but you can still book normally.</p>
              ) : null}
              {showWaitlistClaimBanner ? (
                <div className="portal-info-banner">
                  <strong>That opening is no longer available.</strong>
                  <span>{waitlistPrefillQuery.data?.message}</span>
                </div>
              ) : null}
              <BookingFlowSection title="Cleaning address">
                <div className="field">
                {canUseAddressAutocomplete ? (
                  <AddressAutocompleteField
                    value={addressGateValue}
                    enabled={canUseAddressAutocomplete}
                    onChange={(nextValue) => {
                      setConfirmedBookingGate(null);
                      setConfirmedAddressContext(null);
                      setBookingGatePostalCode("");
                      setAddressGateValue(nextValue);
                      form.setValue("customer.address.line1", nextValue, { shouldDirty: true });
                    }}
                    onResolved={(address) => {
                      setResolvedAddressGate(address);
                      if (address.line1) {
                        form.setValue("customer.address.line1", address.line1, { shouldDirty: true });
                      }
                      if (address.unitNumber !== undefined) {
                        form.setValue("customer.address.unitNumber", address.unitNumber, { shouldDirty: true });
                      }
                      if (address.city) {
                        form.setValue("customer.address.city", address.city, { shouldDirty: true });
                      }
                      if (address.state) {
                        form.setValue("customer.address.state", address.state, { shouldDirty: true });
                      }
                      if (address.postalCode !== undefined) {
                        const nextPostalCode = normalizePostalCode(address.postalCode);
                        setBookingGatePostalCode(nextPostalCode);
                        form.setValue("customer.address.postalCode", nextPostalCode, {
                          shouldDirty: true
                        });
                      }
                    }}
                  />
                ) : (
                  <input
                    value={addressLine1Value}
                    aria-label="Street address"
                    autoComplete="street-address"
                    placeholder="Street address"
                    onChange={(event) => {
                      setConfirmedBookingGate(null);
                      setConfirmedAddressContext(null);
                      setAddressGateValue(event.target.value);
                      form.setValue("customer.address.line1", event.target.value, { shouldDirty: true });
                    }}
                  />
                )}
                </div>

                <Field label="Unit number">
                  <input {...form.register("customer.address.unitNumber")} placeholder="Unit, apt, suite" />
                </Field>

                <div className="grid three">
                  <Field label="City">
                    <input {...form.register("customer.address.city")} autoComplete="address-level2" />
                  </Field>
                  <Field label="State">
                    <input {...form.register("customer.address.state")} autoComplete="address-level1" />
                  </Field>
                  <Field label="ZIP">
                    <input
                      name={postalCodeField.name}
                      ref={postalCodeField.ref}
                      onBlur={postalCodeField.onBlur}
                      value={zipCodeValue}
                      inputMode="numeric"
                      maxLength={5}
                      autoComplete="postal-code"
                      onChange={(event) => {
                        const nextPostalCode = normalizePostalCode(event.target.value);
                        setConfirmedBookingGate(null);
                        setConfirmedAddressContext(null);
                        setBookingGatePostalCode(nextPostalCode);
                        setResolvedAddressGate((current) => ({
                          ...(current ?? {}),
                          postalCode: nextPostalCode
                        }));
                        postalCodeField.onChange({
                          target: {
                            name: postalCodeField.name,
                            value: nextPostalCode
                          }
                        });
                      }}
                    />
                  </Field>
                </div>

                {propertyLookupSummary ? (
                  <div className="property-lookup-note">
                    <strong>
                      Found {propertyLookupSummary.squareFeet ? `${propertyLookupSummary.squareFeet.toLocaleString()} sq ft` : "property details"}
                      {propertyLookupSummary.provider ? ` from ${propertyLookupSummary.provider}` : ""}.
                    </strong>
                    <span>You can edit the cleaning details below before the quote is finalized.</span>
                  </div>
                ) : null}
              </BookingFlowSection>

              {hasCompleteZipCode && !isZipSupported ? (
                <p className="error-text">Sorry, we don't service your area yet.</p>
              ) : null}

              {shouldShowBookingQuestions ? (
                <RenderErrorBoundary
                  resetKey={[
                    serviceId,
                    locationId,
                    bookingSpotId,
                    frequencyRuleId,
                    normalizedZipCode,
                    recentRemodeling,
                    optionIds.join("|")
                  ].join("::")}
                  fallbackTitle="Booking form"
                >
                  <fieldset
                    key={`${serviceId || "no-service"}::${locationId || "no-location"}::${normalizedZipCode || "no-zip"}`}
                    disabled={Boolean(pendingPaymentBooking)}
                    style={{ border: 0, margin: 0, padding: 0 }}
                  >
              <Card title="Type">
                <div className="stack">
                  <div className="service-choice-grid" role="radiogroup" aria-label="Service type">
                    {filteredServices.map((service) => {
                      const checked = serviceId === service.id;
                      return (
                        <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={service.id}>
                          <input type="radio" value={service.id} checked={checked} {...form.register("serviceId")} />
                          <div className="service-choice-copy">
                            <strong>{service.name}</strong>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {isServiceUnavailableForAddress ? (
                    <p className="error-text">This service is not available for the selected address.</p>
                  ) : null}
                </div>
              </Card>

              {showScenarioFrequency && availableFrequencyRules.length && serviceId ? (
                <Card title="Frequency">
                  <div className="stack">
                    <Field
                      label="How often should we clean?"
                      hint={isRecurringFrequencySelection ? "Discounts apply from the 2nd regular cleaning." : undefined}
                    >
                      <select
                        key={`${serviceId || "no-service"}::${locationId || "no-location"}::frequency`}
                        value={frequencyRuleId || ""}
                        autoComplete="off"
                        onChange={(event) =>
                          form.setValue("frequencyRuleId", event.target.value, {
                            shouldDirty: true,
                            shouldTouch: true
                          })
                        }
                      >
                        <option value="">Select frequency</option>
                        {availableFrequencyRules.map((rule) => (
                          <option key={rule.id} value={rule.id}>
                            {formatFrequencyRuleLabel(rule)}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </Card>
              ) : null}

              {showResidentialHomeDetails ? (
                <Card title="Home details">
                  <div className="stack">
                    <Field
                      label={
                        <span className="booking-inline-label">
                          Total Bedrooms / offices / dens
                          <InfoTooltip text="Living areas and hallways are included automatically." />
                        </span>
                      }
                    >
                      <PropertyLookupFieldNote value={publicPropertyBedrooms} provider={propertyLookupSummary?.provider} />
                      <select {...form.register("adminDetails.bedrooms")}>
                        <option value="">Select</option>
                        {getBookingAdminSelectOptions("bedrooms").map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.bedrooms?.message ? <p className="error-text">{adminDetailErrors.bedrooms.message}</p> : null}
                    </Field>
                    <Field
                      label={
                        <span className="booking-inline-label">
                          Total Bathrooms
                          <InfoTooltip>
                            Full bathroom includes a toilet, sink, shower, or bathtub.
                            <br />
                            Half bathroom (0.5) includes only two fixtures: toilet and sink.
                          </InfoTooltip>
                        </span>
                      }
                    >
                      <PropertyLookupFieldNote value={publicPropertyBathrooms} provider={propertyLookupSummary?.provider} />
                      <select {...form.register("adminDetails.bathrooms")}>
                        <option value="">Select</option>
                        {getBookingAdminSelectOptions("bathrooms").map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.bathrooms?.message ? <p className="error-text">{adminDetailErrors.bathrooms.message}</p> : null}
                    </Field>
                    <Field
                      label={
                        <span className="booking-inline-label">
                          Levels (floors) to clean
                          <InfoTooltip text="Number of floors that needs cleaning for multi-level homes" />
                        </span>
                      }
                    >
                      <select {...form.register("adminDetails.levels")}>
                        <option value="">Select</option>
                        {getBookingAdminSelectOptions("levels").map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.levels?.message ? <p className="error-text">{adminDetailErrors.levels.message}</p> : null}
                    </Field>
                    <Field
                      label={
                        <span className="booking-inline-label">
                          Total square footage
                          <InfoTooltip>
                            <a href={zillowSearchUrl} target="_blank" rel="noreferrer">
                              Click here to view your home's square footage on Zillow
                            </a>{" "}
                            (if available).
                          </InfoTooltip>
                        </span>
                      }
                    >
                      {renderSquareFootageEntry({
                        allowUnknown: true,
                        helperText:
                          "Enter the approximate finished living area. Do not include the whole building size for apartments or condos.",
                        showPublicData: true
                      })}
                    </Field>
                  </div>
                </Card>
              ) : null}

              {showOfficeCommercialDetails ? (
                <Card title="Office details">
                  <div className="stack">
                    <Field label="Workstations">
                      <select {...form.register("adminDetails.workstations")}>
                        <option value="">Select</option>
                        {OFFICE_WORKSTATION_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.workstations?.message ? <p className="error-text">{adminDetailErrors.workstations.message}</p> : null}
                    </Field>
                    <Field label="Private rooms / offices / conference rooms">
                      <select {...form.register("adminDetails.office_private_rooms")}>
                        <option value="">Select</option>
                        {OFFICE_ROOM_COUNT_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.office_private_rooms?.message ? <p className="error-text">{adminDetailErrors.office_private_rooms.message}</p> : null}
                    </Field>
                    <Field label="Breakrooms / kitchenettes">
                      <select {...form.register("adminDetails.office_breakrooms")}>
                        <option value="">Select</option>
                        {OFFICE_ROOM_COUNT_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.office_breakrooms?.message ? <p className="error-text">{adminDetailErrors.office_breakrooms.message}</p> : null}
                    </Field>
                    <Field label="Restrooms">
                      <select {...form.register("adminDetails.bathrooms")}>
                        <option value="">Select</option>
                        {getBookingAdminSelectOptions("bathrooms").map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.bathrooms?.message ? <p className="error-text">{adminDetailErrors.bathrooms.message}</p> : null}
                    </Field>
                    <Field
                      label={
                        <span className="booking-inline-label">
                          Levels (floors) to clean
                          <InfoTooltip text="Number of floors that needs cleaning for multi-level homes" />
                        </span>
                      }
                    >
                      <select {...form.register("adminDetails.levels")}>
                        <option value="">Select</option>
                        {getBookingAdminSelectOptions("levels").map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.levels?.message ? <p className="error-text">{adminDetailErrors.levels.message}</p> : null}
                    </Field>
                    <Field label="Total square footage">
                      {renderSquareFootageEntry({
                        allowUnknown: false,
                        helperText: "Enter the approximate finished square footage for the area to clean.",
                        showPublicData: false
                      })}
                    </Field>
                  </div>
                </Card>
              ) : null}

              {showBuildingCommonAreaDetails ? (
                <Card title="Building common areas details">
                  <div className="stack">
                    <Field label="How many apartments are in the building?">
                      <select {...form.register("adminDetails.building_apartments")}>
                        <option value="">Select</option>
                        {BUILDING_APARTMENT_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {adminDetailErrors.building_apartments?.message ? <p className="error-text">{adminDetailErrors.building_apartments.message}</p> : null}
                    </Field>
                  </div>
                </Card>
              ) : null}

              {scenarioSkipAreaOptions.length ? (
                <Card
                  title={
                    <span className="booking-inline-label">
                      Areas to skip
                      <InfoTooltip text="Choose Yes only if there are areas you do not want cleaned." />
                    </span>
                  }
                >
                  <div className="stack">
                    <div className="grid two">
                      {[
                        { value: "no", label: "No" },
                        { value: "yes", label: "Yes" }
                      ].map((option) => {
                        const checked = option.value === "yes" ? skipAreasEnabled : !skipAreasEnabled;
                        return (
                          <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={option.value}>
                            <input
                              type="radio"
                              value={option.value}
                              checked={checked}
                              onChange={() => {
                                form.setValue("adminDetails.skip_areas_enabled", option.value, { shouldDirty: true });
                                if (option.value === "no") {
                                  form.setValue("adminDetails.skip_areas", "", { shouldDirty: true });
                                  form.setValue("adminDetails.skip_bedrooms_count", "", { shouldDirty: true });
                                  form.setValue("adminDetails.skip_bathrooms_count", "", { shouldDirty: true });
                                }
                              }}
                            />
                            <div className="service-choice-copy">
                              <strong>{option.label}</strong>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {skipAreasEnabled ? (
                      <>
                        <div className="extra-task-grid">
                          {scenarioSkipAreaOptions.map((option) => {
                            const checked = selectedSkipAreaCodes.includes(option.value);
                            return (
                              <div className="stack" key={option.value}>
                                <label className="extra-task-card">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(event) => {
                                      const nextCodes = toggleStringSelection(selectedSkipAreaCodes, option.value, event.target.checked);
                                      form.setValue("adminDetails.skip_areas", serializeBookingMultiSelectValue(nextCodes), {
                                        shouldDirty: true
                                      });
                                      if (!event.target.checked && option.value === "bedrooms") {
                                        form.setValue("adminDetails.skip_bedrooms_count", "", { shouldDirty: true });
                                      }
                                      if (!event.target.checked && option.value === "bathrooms") {
                                        form.setValue("adminDetails.skip_bathrooms_count", "", { shouldDirty: true });
                                      }
                                    }}
                                  />
                                  <div className="extra-task-copy">
                                    <strong>{option.label}</strong>
                                  </div>
                                </label>

                                {option.value === "bedrooms" && checked ? (
                                  <Field label="How many bedrooms / offices / dens should we skip?">
                                    <select {...form.register("adminDetails.skip_bedrooms_count")}>
                                      <option value="">Select</option>
                                      {getLimitedSkipCountOptions("bedrooms", String(form.watch("adminDetails.bedrooms") ?? "")).map((countOption) => (
                                        <option key={countOption} value={countOption}>
                                          {countOption}
                                        </option>
                                      ))}
                                    </select>
                                  </Field>
                                ) : null}

                                {option.value === "bathrooms" && checked ? (
                                  <Field label="How many bathrooms should we skip?">
                                    <select {...form.register("adminDetails.skip_bathrooms_count")}>
                                      <option value="">Select</option>
                                      {getLimitedSkipCountOptions("bathrooms", String(form.watch("adminDetails.bathrooms") ?? "")).map((countOption) => (
                                        <option key={countOption} value={countOption}>
                                          {countOption}
                                        </option>
                                      ))}
                                    </select>
                                  </Field>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              {showScenarioRemodeling ? (
                <Card>
                  <div className="stack">
                    <div className="booking-inline-label">
                      <span>{getScenarioRemodelPrompt(scenarioRemodelProfile)}</span>
                      <InfoTooltip text="Answer Yes if there is post-renovation dust on surfaces." />
                    </div>
                    <div className="grid two">
                      {[
                        { value: "no", label: "No" },
                        { value: "yes", label: "Yes" }
                      ].map((option) => {
                        const checked = recentRemodeling === option.value;
                        return (
                          <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={option.value}>
                            <input type="radio" value={option.value} checked={checked} {...form.register("adminDetails.recent_remodeling")} />
                            <div className="service-choice-copy">
                              <strong>{option.label}</strong>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {recentRemodeling === "yes" ? (
                      <div className="stack">
                        <div className="grid two">
                          {scenarioRemodelOptions.map((option) => {
                            const checked = remodelingTypeCode === option.value;
                            return (
                              <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={option.value}>
                                <input
                                  type="radio"
                                  value={option.value}
                                  checked={checked}
                                  {...form.register("adminDetails.remodeling_type")}
                                />
                                <div className="service-choice-copy">
                                  <strong>{option.label}</strong>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        {adminDetailErrors.remodeling_type?.message ? (
                          <p className="error-text">{adminDetailErrors.remodeling_type.message}</p>
                        ) : null}

                        {remodelingTypeCode === "partial_remodeling" && scenarioPartialRemodelOptions.length ? (
                          <div className="stack">
                            <p className="card-copy">Choose renovated areas.</p>
                            <div className="extra-task-grid">
                              {scenarioPartialRemodelOptions.map((option) => {
                                const checked = selectedPartialRemodelAreaCodes.includes(option.value);
                                return (
                                  <div className="stack" key={option.value}>
                                    <label className="extra-task-card">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(event) => {
                                          const nextCodes = toggleStringSelection(
                                            selectedPartialRemodelAreaCodes,
                                            option.value,
                                            event.target.checked
                                          );
                                          form.setValue(
                                            "adminDetails.partial_remodeling_areas",
                                            serializeBookingMultiSelectValue(nextCodes),
                                            { shouldDirty: true }
                                          );
                                          if (!event.target.checked && option.value === "bedrooms") {
                                            form.setValue("adminDetails.partial_remodeling_bedrooms_count", "", {
                                              shouldDirty: true
                                            });
                                          }
                                          if (!event.target.checked && option.value === "bathrooms") {
                                            form.setValue("adminDetails.partial_remodeling_bathrooms_count", "", {
                                              shouldDirty: true
                                            });
                                          }
                                        }}
                                      />
                                      <div className="extra-task-copy">
                                        <strong>{option.label}</strong>
                                      </div>
                                    </label>

                                    {option.value === "bedrooms" && checked ? (
                                      <Field label="How many bedrooms were renovated?">
                                        <select {...form.register("adminDetails.partial_remodeling_bedrooms_count")}>
                                          <option value="">Select</option>
                                          {getLimitedSkipCountOptions("bedrooms", String(form.watch("adminDetails.bedrooms") ?? "")).map(
                                            (countOption) => (
                                              <option key={countOption} value={countOption}>
                                                {countOption}
                                              </option>
                                            )
                                          )}
                                        </select>
                                        {adminDetailErrors.partial_remodeling_bedrooms_count?.message ? (
                                          <p className="error-text">{adminDetailErrors.partial_remodeling_bedrooms_count.message}</p>
                                        ) : null}
                                      </Field>
                                    ) : null}

                                    {option.value === "bathrooms" && checked ? (
                                      <Field label="How many bathrooms were renovated?">
                                        <select {...form.register("adminDetails.partial_remodeling_bathrooms_count")}>
                                          <option value="">Select</option>
                                          {getLimitedSkipCountOptions("bathrooms", String(form.watch("adminDetails.bathrooms") ?? "")).map(
                                            (countOption) => (
                                              <option key={countOption} value={countOption}>
                                                {countOption}
                                              </option>
                                            )
                                          )}
                                        </select>
                                        {adminDetailErrors.partial_remodeling_bathrooms_count?.message ? (
                                          <p className="error-text">{adminDetailErrors.partial_remodeling_bathrooms_count.message}</p>
                                        ) : null}
                                      </Field>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                            {adminDetailErrors.partial_remodeling_areas?.message ? (
                              <p className="error-text">{adminDetailErrors.partial_remodeling_areas.message}</p>
                            ) : null}
                            <Field label="Describe">
                              <textarea
                                rows={3}
                                placeholder="Describe the partial renovation work."
                                {...form.register("adminDetails.partial_remodeling_description")}
                              />
                            </Field>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              {showScenarioCondition ? (
                <Card title="Home condition">
                  <div className="stack">
                    <p className="card-copy">Slide to the left if dirty, slide to the right if clean.</p>
                    <div className="stack">
                      {HOME_CONDITION_FIELDS.map((field) => {
                        const options = getBookingAdminSelectOptions(field.key);
                        const currentValue = String(form.watch(`adminDetails.${field.key}`) ?? "");
                        const currentIndex = getConditionIndex(options, currentValue);
                        const currentLabel = options[currentIndex] ?? HOME_CONDITION_DEFAULT_LABEL;

                        return (
                          <div className="condition-slider-card" key={field.key}>
                            <div className="condition-slider-head">
                              <strong>{field.label}</strong>
                              <span className="condition-slider-value">{currentLabel}</span>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={Math.max(options.length - 1, 0)}
                              step={1}
                              value={currentIndex}
                              onChange={(event) => {
                                const nextIndex = Number(event.target.value);
                                form.setValue(`adminDetails.${field.key}`, options[nextIndex] ?? HOME_CONDITION_DEFAULT_LABEL, {
                                  shouldDirty: true
                                });
                              }}
                            />
                            <div className="condition-slider-ends">
                              <span>Dirty</span>
                              <span>Clean</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
              ) : null}

              {showScenarioExtras && visibleOptions.length ? (
                <Card title="Additional tasks">
                  <div className="stack">
                    {showScenarioExtras && visibleOptions.length ? (
                      <>
                        <div>
                          <div className="included-cleaning-summary">
                            <div>
                              <strong>Your cleaning already includes basic cleaning tasks.</strong>
                              <p>Use optional add-ons only when you want extra detail beyond the standard checklist.</p>
                            </div>
                            <Button
                              type="button"
                              variant="secondary"
                              className="included-cleaning-summary__button"
                              aria-expanded={includedTasksOpen}
                              aria-controls="booking-included-cleaning-accordion"
                              onClick={() => setIncludedTasksOpen((current) => !current)}
                            >
                              {includedTasksOpen ? "Hide what's included" : "See what's included"}
                            </Button>
                          </div>

                          {includedTasksOpen ? (
                            <section
                              id="booking-included-cleaning-accordion"
                              className="included-cleaning-accordion"
                              aria-labelledby="booking-included-cleaning-title"
                            >
                              <div className="included-cleaning-accordion__header">
                                <p className="included-cleaning-accordion__eyebrow">Basic cleaning checklist</p>
                                <h3 id="booking-included-cleaning-title">What's already included?</h3>
                              </div>
                              <p className="included-cleaning-accordion__intro">
                                Your estimate includes the basic cleaning checklist below. Optional add-ons are for tasks
                                that go beyond this standard scope.
                              </p>
                              {skipAreasEnabled && selectedSkipAreaCodes.length ? (
                                <p className="included-cleaning-accordion__notice">
                                  Some checklist sections are marked excluded because you chose not to clean those areas.
                                </p>
                              ) : null}
                              <div className="included-cleaning-accordion__groups">
                                {INCLUDED_BASIC_CLEANING_GROUPS.map((group) => {
                                  const status = getIncludedChecklistStatus(
                                    group.key,
                                    selectedSkipAreaCodes,
                                    adminDetails,
                                    skipAreasEnabled
                                  );
                                  return (
                                    <section
                                      className="included-cleaning-group"
                                      data-included-status={status}
                                      key={group.key}
                                    >
                                      <div className="included-cleaning-group__header">
                                        <h4>{group.title}</h4>
                                        <div className="included-cleaning-group__badges">
                                          <span className="included-cleaning-group__meta">{group.meta}</span>
                                          <span className="included-cleaning-group__status">
                                            {labelForIncludedChecklistStatus(status)}
                                          </span>
                                        </div>
                                      </div>
                                      <p>{group.body}</p>
                                      <ul>
                                        {group.points.map((point) => (
                                          <li key={point}>{point}</li>
                                        ))}
                                      </ul>
                                    </section>
                                  );
                                })}
                              </div>
                            </section>
                          ) : null}
                        </div>
                        {isRecurringFrequencySelection ? (
                          <p className="card-copy">
                            For recurring service, each selected extra can be applied to the initial cleaning only or on an
                            ongoing schedule.
                          </p>
                        ) : null}
                        <div className="extra-task-area-groups">
                          {visibleOptionGroups.map((group) => (
                            <div className="extra-task-area-group" key={group.key}>
                              <h3 className="booking-section-title booking-section-title--small">{group.title}</h3>
                              <div className="extra-task-grid">
                                {group.options.map((option) => {
                                  const isSelected = optionIds.includes(option.id);
                                  const scopeValue = recurringExtraScopes[option.id] ?? getDefaultRecurringExtraScope(option);
                                  const tooltipText = getExtraTooltipText(String(option.name ?? ""), option.description);
                                  const showSimpleQuantity = isSelected && isSimpleQuantityServiceOption(option);
                                  const simpleQuantityFieldName = `adminDetails.${getSimpleQuantityAdminDetailKey(option)}` as const;
                                  const simpleQuantityValue = normalizeSimpleQuantityValue(form.watch(simpleQuantityFieldName), option);
                                  const simpleQuantityRange = getSimpleQuantityRange(option);
                                  const extraMetaText = formatBookingExtraCardMeta(option);
                                  return (
                                    <div className="stack" key={option.id} style={{ gap: "0.5rem" }}>
                                      <label className="extra-task-card">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(event) => {
                                            form.setValue(
                                              "optionIds",
                                              toggleStringSelection(optionIds, option.id, event.target.checked),
                                              { shouldDirty: true }
                                            );
                                            if (!event.target.checked && option.carpetShampooingConfig?.enabled) {
                                              for (const unit of getActiveCarpetUnits(option)) {
                                                form.setValue(`adminDetails.${getCarpetAdminDetailKey(unit.code)}`, "", {
                                                  shouldDirty: true
                                                });
                                              }
                                            }
                                            if (!event.target.checked && isSimpleQuantityServiceOption(option)) {
                                              form.setValue(simpleQuantityFieldName, "", { shouldDirty: true });
                                            }
                                            if (event.target.checked && isSimpleQuantityServiceOption(option)) {
                                              const existingQuantity = String(form.getValues(simpleQuantityFieldName) ?? "").trim();
                                              if (!existingQuantity) {
                                                form.setValue(simpleQuantityFieldName, String(simpleQuantityValue), {
                                                  shouldDirty: true
                                                });
                                              }
                                            }
                                          }}
                                        />
                                        <div className="extra-task-copy">
                                          <strong>{option.name}</strong>
                                          {extraMetaText ? <span className="extra-task-card__meta">{extraMetaText}</span> : null}
                                        </div>
                                        {tooltipText ? <InfoTooltip text={tooltipText} /> : null}
                                      </label>
                                      {showSimpleQuantity ? (
                                        <Field label={getSimpleQuantityLabel(option)}>
                                          <select
                                            value={String(simpleQuantityValue)}
                                            onChange={(event) =>
                                              form.setValue(
                                                simpleQuantityFieldName,
                                                String(normalizeSimpleQuantityValue(event.target.value, option)),
                                                { shouldDirty: true }
                                              )
                                            }
                                          >
                                            {Array.from(
                                              { length: simpleQuantityRange.maxQuantity - simpleQuantityRange.minQuantity + 1 },
                                              (_, index) => simpleQuantityRange.minQuantity + index
                                            ).map((value) => (
                                              <option key={value} value={value}>
                                                {value}
                                              </option>
                                            ))}
                                          </select>
                                        </Field>
                                      ) : null}
                                      {isRecurringFrequencySelection &&
                                      isSelected &&
                                      !Boolean(option.carpetShampooingConfig?.enabled) ? (
                                        <Field label={`${option.name} recurrence`}>
                                          <select
                                            value={scopeValue}
                                            onChange={(event) => {
                                              applyRecurringExtraScopeUpdate(
                                                form,
                                                selectedVisibleOptions,
                                                recurringExtraScopes,
                                                option.id,
                                                event.target.value as RecurringExtraScopeValue
                                              );
                                            }}
                                          >
                                            {RECURRING_EXTRA_SCOPE_OPTIONS.map((scopeOption) => (
                                              <option key={scopeOption.value} value={scopeOption.value}>
                                                {scopeOption.label}
                                              </option>
                                            ))}
                                          </select>
                                        </Field>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : null}

                    {selectedCarpetShampooingOption ? (
                      <div className="stack">
                        <div>
                          <h3 className="booking-section-title">Carpet shampooing details</h3>
                          <p className="card-copy">Tell us how many carpet areas should be cleaned.</p>
                        </div>
                        {isRecurringFrequencySelection ? (
                          <Field label="Carpet shampooing recurrence">
                            <select
                              value={
                                recurringExtraScopes[selectedCarpetShampooingOption.id] ??
                                getDefaultRecurringExtraScope(selectedCarpetShampooingOption)
                              }
                              onChange={(event) =>
                                applyRecurringExtraScopeUpdate(
                                  form,
                                  selectedVisibleOptions,
                                  recurringExtraScopes,
                                  selectedCarpetShampooingOption.id,
                                  event.target.value as RecurringExtraScopeValue
                                )
                              }
                            >
                              {RECURRING_EXTRA_SCOPE_OPTIONS.map((scopeOption) => (
                                <option key={scopeOption.value} value={scopeOption.value}>
                                  {scopeOption.label}
                                </option>
                              ))}
                            </select>
                          </Field>
                        ) : null}
                        <div className="grid two">
                          {getActiveCarpetUnits(selectedCarpetShampooingOption).map((unit) => {
                            const fieldName = `adminDetails.${getCarpetAdminDetailKey(unit.code)}` as const;
                            const currentValue = normalizeCarpetUnitQuantity(form.watch(fieldName));
                            const maxQuantity = Math.max(0, Number(unit.maxQuantity ?? 0));
                            return (
                              <Field key={unit.code} label={getCarpetUnitQuestionLabel(unit)}>
                                <select
                                  value={String(currentValue)}
                                  onChange={(event) =>
                                    form.setValue(fieldName, String(normalizeCarpetUnitQuantity(event.target.value)), {
                                      shouldDirty: true
                                    })
                                  }
                                >
                                  {Array.from({ length: maxQuantity + 1 }, (_, index) => index).map((value) => (
                                    <option key={value} value={value}>
                                      {value}
                                    </option>
                                  ))}
                                </select>
                              </Field>
                            );
                          })}
                        </div>
                        {getCarpetOptionTotals(selectedCarpetShampooingOption, adminDetails).totalQuantity < 1 ? (
                          <p className="card-copy">
                            Choose at least one room, hallway, step group, or area rug so we can price carpet shampooing.
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              {scenarioQuestionProfile === "carpet_standalone" && effectiveCarpetShampooingOption ? (
                <Card title="Carpet shampooing details">
                  <div className="stack">
                    <div>
                      <p className="card-copy">Tell us how many carpet areas should be cleaned.</p>
                    </div>
                    <div className="grid two">
                      {getActiveCarpetUnits(effectiveCarpetShampooingOption).map((unit) => {
                        const fieldName = `adminDetails.${getCarpetAdminDetailKey(unit.code)}` as const;
                        const currentValue = normalizeCarpetUnitQuantity(form.watch(fieldName));
                        const maxQuantity = Math.max(0, Number(unit.maxQuantity ?? 0));
                        return (
                          <Field key={unit.code} label={getCarpetUnitQuestionLabel(unit)}>
                            <select
                              value={String(currentValue)}
                              onChange={(event) =>
                                form.setValue(fieldName, String(normalizeCarpetUnitQuantity(event.target.value)), {
                                  shouldDirty: true
                                })
                              }
                            >
                              {Array.from({ length: maxQuantity + 1 }, (_, index) => index).map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                            </select>
                          </Field>
                        );
                      })}
                    </div>
                    {getCarpetOptionTotals(effectiveCarpetShampooingOption, adminDetails).totalQuantity < 1 ? (
                      <p className="card-copy">
                        Choose at least one room, hallway, step group, or area rug so we can price carpet shampooing.
                      </p>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              {customerSuppliesEquipmentEnabled ? (
                <Card title="Supplies & equipment">
                  <div className="stack">
                    <p className="card-copy">
                      AccessMaids normally brings professional cleaning supplies and equipment. If you prefer to provide
                      everything listed below, your estimate qualifies for a{" "}
                      {formatPublicCurrency(Math.round(customerSuppliesEquipmentDiscountAmount * 100))} discount.
                    </p>
                    <div className="grid two">
                      {[
                        { value: "Company", label: "No, AccessMaids should bring supplies and equipment" },
                        { value: "Customer", label: "Yes, I will provide supplies and equipment" }
                      ].map((option) => {
                        const checked = customerSuppliesChoice === option.value;
                        return (
                          <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={option.value}>
                            <input
                              type="radio"
                              name="customerSuppliesEquipmentChoice"
                              checked={checked}
                              onChange={() => setCustomerSuppliesEquipmentChoice(option.value as "Company" | "Customer")}
                            />
                            <div className="service-choice-copy">
                              <strong>{option.label}</strong>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {adminDetailErrors.supplies_provided_by?.message ? (
                      <p className="error-text">{adminDetailErrors.supplies_provided_by.message}</p>
                    ) : null}
                    {customerSuppliesChoice === "Customer" ? (
                      <div className="quote-callout">
                        <p className="eyebrow">Required supplies and equipment</p>
                        <p>Please have these ready before the cleaning team arrives.</p>
                        <ul>
                          {customerSuppliesEquipmentRequiredItems.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                        <p className="card-copy">
                          Discount applies only if all required supplies and equipment are available when the team arrives.
                          If anything is missing, AccessMaids may adjust the final price.
                        </p>
                      </div>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              {quoteDetailsReady ? (
                <Card title="Scheduling">
                <div className="stack">
                  <Field
                    label="Service date"
                    hint={!monthAvailabilityQuery.isLoading && !monthAvailabilityQuery.isError ? "Unavailable dates are greyed out." : undefined}
                  >
                    {monthAvailabilityQuery.isLoading && !monthAvailabilityQuery.data ? (
                      <div className="service-choice-card" aria-busy="true">
                        <div className="service-choice-copy">
                          <strong>Loading available dates...</strong>
                          <p className="card-copy">We’re checking this month for bookable arrival windows.</p>
                        </div>
                      </div>
                    ) : monthAvailabilityQuery.isError ? (
                      <div className="service-choice-card" aria-disabled="true">
                        <div className="service-choice-copy">
                          <strong>We couldn’t load the calendar right now.</strong>
                          <p className="card-copy">Please try another moment or refresh the page.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="booking-calendar">
                        <div className="booking-calendar__header">
                          <button
                            type="button"
                            className="booking-calendar__nav"
                            onClick={() => setVisibleCalendarMonth((current) => shiftIsoMonth(current, -1))}
                            aria-label="Show previous month"
                          >
                            ‹
                          </button>
                          <strong className="booking-calendar__month">{formatMonthLabel(visibleCalendarMonth)}</strong>
                          <button
                            type="button"
                            className="booking-calendar__nav"
                            onClick={() => setVisibleCalendarMonth((current) => shiftIsoMonth(current, 1))}
                            aria-label="Show next month"
                          >
                            ›
                          </button>
                        </div>

                        <div className="booking-calendar__weekdays" aria-hidden="true">
                          {CALENDAR_WEEKDAY_LABELS.map((label) => (
                            <span className="booking-calendar__weekday" key={label}>
                              {label}
                            </span>
                          ))}
                        </div>

                        <div className="booking-calendar__grid">
                          {calendarMonthCells.map((cell) =>
                            cell.date ? (
                              <button
                                type="button"
                                key={cell.key}
                                className={[
                                  "booking-calendar__day",
                                  cell.hasAvailability ? "" : "is-unavailable",
                                  cell.date === dateValue ? "is-selected" : "",
                                  cell.isToday ? "is-today" : ""
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                disabled={cell.date < getTodayIsoDate()}
                                aria-pressed={cell.date === dateValue}
                                aria-label={
                                  cell.hasAvailability
                                    ? `${formatLongDateLabel(cell.date)} is available`
                                    : `${formatLongDateLabel(cell.date)} is unavailable`
                                }
                                onClick={() => {
                                  const selectedCellDate = cell.date ?? "";
                                  customerSelectedServiceDateRef.current = true;
                                  setSelectedDate(selectedCellDate);
                                  setWaitlistFeedback("");
                                  if (cell.hasAvailability && cell.firstStartsAt) {
                                    setWaitlistEnabled(false);
                                    form.setValue("startsAt", cell.firstStartsAt, {
                                      shouldDirty: true,
                                      shouldTouch: true
                                    });
                                    return;
                                  }

                                  form.setValue("startsAt", "", {
                                    shouldDirty: true,
                                    shouldTouch: true
                                  });
                                }}
                              >
                                <span>{cell.dayNumber}</span>
                              </button>
                            ) : (
                              <span className="booking-calendar__day booking-calendar__day--placeholder" key={cell.key} aria-hidden="true" />
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </Field>

                  <Field label="Available arrival times" hint={arrivalTimesHint}>
                    {isArrivalWindowLoading ? (
                      <div className="service-choice-card" aria-busy="true">
                        <div className="service-choice-copy">
                          <strong>Loading live arrival windows...</strong>
                          <p className="card-copy">We're checking current availability for the selected date.</p>
                        </div>
                      </div>
                    ) : isArrivalWindowError ? (
                      <div className="service-choice-card" aria-disabled="true">
                        <div className="service-choice-copy">
                          <strong>We couldn't load arrival windows right now.</strong>
                          <p className="card-copy">Please try another date or refresh the page.</p>
                        </div>
                      </div>
                    ) : visibleAvailabilityItems.length ? (
                      <div className="arrival-window-grid">
                        {visibleAvailabilityItems.map((slot: AvailabilitySlotSummary) => {
                          const checked = !arrivalFlexibilityIsSelected && startsAtValue === slot.startsAt;
                          return (
                            <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={slot.startsAt}>
                              <input
                                type="radio"
                                name="startsAt"
                                value={slot.startsAt}
                                checked={checked}
                                onChange={() => {
                                  form.setValue("startsAt", slot.startsAt, {
                                    shouldDirty: true,
                                    shouldTouch: true
                                  });
                                  if (currentArrivalFlexibilitySelection) {
                                    form.setValue("adminDetails.arrival_flexibility", "", {
                                      shouldDirty: true,
                                      shouldTouch: true
                                    });
                                  }
                                }}
                              />
                              <div className="service-choice-copy">
                                <strong>{getAvailabilityOptionLabel(slot, availabilityLocationTimeZone)}</strong>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="service-choice-card">
                        <div className="stack" style={{ gap: "0.75rem" }}>
                          <div className="service-choice-copy">
                            <strong>No arrival windows are available for this date.</strong>
                            <p className="card-copy">
                              {nextAvailableDateQuery.isLoading
                                ? "Checking the next available date..."
                                : nextAvailableDateQuery.data?.date
                                  ? `Next available date: ${formatLongDateLabel(nextAvailableDateQuery.data.date)}.`
                                  : "Try another date to see available arrival windows."}
                            </p>
                          </div>
                          {nextAvailableDateQuery.data?.date ? (
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => {
                                customerSelectedServiceDateRef.current = true;
                                setSelectedDate(nextAvailableDateQuery.data!.date);
                                setWaitlistEnabled(false);
                                form.setValue("startsAt", nextAvailableDateQuery.data!.startsAt, {
                                  shouldDirty: true,
                                  shouldTouch: true
                                });
                              }}
                            >
                              Use {formatLongDateLabel(nextAvailableDateQuery.data.date)}
                            </Button>
                          ) : null}
                          {canJoinWaitlistForSelectedDate && dateValue ? (
                            <div className="waitlist-panel">
                              <p className="waitlist-panel__eyebrow">Desired spot unavailable?</p>
                              <label className={`service-choice-card ${waitlistEnabled ? "is-selected" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={waitlistEnabled}
                                  onChange={(event) => {
                                    setWaitlistEnabled(event.target.checked);
                                    setWaitlistFeedback("");
                                  }}
                                />
                                <div className="service-choice-copy">
                                  <strong>Join the waitlist</strong>
                                </div>
                              </label>
                              {waitlistEnabled ? (
                                <div className="waitlist-panel__details">
                                  <p className="card-copy">
                                    We'll watch this date using your current service details and let you know when an exact-fit
                                    opening appears.
                                  </p>
                                  <div className="waitlist-window-grid">
                                    {([
                                      { value: "any", label: "Any time" },
                                      { value: "morning", label: "Morning" },
                                      { value: "midday", label: "Midday" },
                                      { value: "afternoon", label: "Afternoon" }
                                    ] as Array<{ value: WaitlistPreferredWindow; label: string }>).map((option) => {
                                      const checked = waitlistPreferredWindow === option.value;
                                      return (
                                        <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={option.value}>
                                          <input
                                            type="radio"
                                            checked={checked}
                                            onChange={() => setWaitlistPreferredWindow(option.value)}
                                          />
                                          <div className="service-choice-copy">
                                            <strong>{option.label}</strong>
                                          </div>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </Field>

                  {arrivalFlexibilityOfferVisible ? (
                    <Field
                      label="Flexible arrival discount"
                      hint={`Let us arrive any time during ${arrivalFlexibilityLabel} and save ${arrivalFlexibilityDiscountSummary}.`}
                    >
                      <label className={`service-choice-card ${arrivalFlexibilityIsSelected ? "is-selected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={arrivalFlexibilityIsSelected}
                          onChange={(event) => {
                            form.setValue(
                              "adminDetails.arrival_flexibility",
                              event.target.checked ? arrivalFlexibilityLabel : "",
                              {
                                shouldDirty: true,
                                shouldTouch: true
                              }
                            );
                          }}
                        />
                        <div className="service-choice-copy">
                          <strong>I am flexible: {arrivalFlexibilityLabel}</strong>
                        </div>
                      </label>
                    </Field>
                  ) : null}
                </div>
                </Card>
              ) : null}

              {quoteDetailsReady ? (
                <Card title="Contact details">
                <div className="stack">
                  <div className="grid two">
                    <Field label="First name">
                      <input {...form.register("customer.firstName")} />
                    </Field>
                    <Field label="Last name">
                      <input {...form.register("customer.lastName")} />
                    </Field>
                  </div>

                  <div className="grid two">
                    <Field label="Email">
                      <input {...form.register("customer.email")} type="email" />
                    </Field>
                    <Field label={showWaitlistSubmissionSection ? "Phone (optional)" : "Phone"}>
                      <input {...form.register("customer.phone")} />
                    </Field>
                  </div>

                  {showWaitlistSubmissionSection && waitlistSmsAlertsEnabled && customerPhoneValue.trim() ? (
                    <div className="stack">
                      <label className={`service-choice-card ${waitlistNotifyBySms ? "is-selected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={waitlistNotifyBySms}
                          onChange={(event) => setWaitlistNotifyBySms(event.target.checked)}
                        />
                        <div className="service-choice-copy">
                          <p className="card-copy">Text me if a matching waitlist opening becomes available.</p>
                        </div>
                      </label>
                    </div>
                  ) : showSmsReminderOptIn && !showWaitlistSubmissionSection ? (
                    <div className="stack">
                      <label
                        className={`service-choice-card ${form.watch("customer.emailPreferences.smsReminderOptInStatus") ? "is-selected" : ""}`}
                      >
                        <input type="checkbox" {...form.register("customer.emailPreferences.smsReminderOptInStatus")} />
                        <div className="service-choice-copy">
                          <p className="card-copy">{smsReminderDisclosure}</p>
                        </div>
                      </label>
                    </div>
                  ) : null}
                </div>
                </Card>
              ) : null}

              {quoteDetailsReady ? (
                <Card title="Entry details">
                <div className="stack">
                  <div className="stack" style={{ gap: "0.75rem" }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: "0.75rem"
                      }}
                    >
                      {ENTRY_ACCESS_OPTIONS.map((option) => {
                        const checked = selectedEntryAccessCodeSet.has(option.value);
                        const fieldKey = option.detailKey;
                        const fieldName = fieldKey ? (`adminDetails.${fieldKey}` as const) : null;
                        return (
                          <div key={option.value} className="stack" style={{ gap: "0.75rem" }}>
                            <label className={`service-choice-card ${checked ? "is-selected" : ""}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  const nextValues = toggleStringSelection(
                                    selectedEntryAccessCodes,
                                    option.value,
                                    event.target.checked
                                  );
                                  form.setValue(
                                    "adminDetails.entry_information_selected",
                                    serializeBookingMultiSelectValue(nextValues),
                                    { shouldDirty: true }
                                  );
                                }}
                              />
                              <div className="service-choice-copy">
                                <strong>{option.label}</strong>
                              </div>
                            </label>

                            {checked && fieldKey && fieldName ? (
                              <Field label={option.detailLabel ?? option.label}>
                                {option.multiline ? (
                                  <textarea {...form.register(fieldName)} rows={3} />
                                ) : (
                                  <input {...form.register(fieldName)} />
                                )}
                                {adminDetailErrors[fieldKey]?.message ? (
                                  <p className="error-text">{adminDetailErrors[fieldKey]?.message}</p>
                                ) : null}
                              </Field>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    {adminDetailErrors.entry_information_selected?.message ? (
                      <p className="error-text">{adminDetailErrors.entry_information_selected?.message}</p>
                    ) : null}
                  </div>

                  <Field label="Access codes">
                    <div className="stack" style={{ gap: "0.75rem" }}>
                      <label className={`service-choice-card ${accessCodesEnabled ? "is-selected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={accessCodesEnabled}
                          onChange={(event) => {
                            if (!event.target.checked) {
                              form.setValue("adminDetails.access_codes_enabled", "", { shouldDirty: true });
                              form.setValue("adminDetails.access_code_selected", "", { shouldDirty: true });
                              return;
                            }

                            form.setValue("adminDetails.access_codes_enabled", "yes", { shouldDirty: true });
                          }}
                        />
                        <div className="service-choice-copy">
                          <strong>I would like to provide access codes</strong>
                        </div>
                      </label>

                      {accessCodesEnabled ? (
                        <div className="stack" style={{ gap: "0.75rem" }}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                              gap: "0.75rem"
                            }}
                          >
                            {ACCESS_CODE_OPTIONS.map((option) => {
                              const checked = selectedAccessCodeOptionSet.has(option.value);
                              return (
                                <div key={option.value} className="stack" style={{ gap: "0.75rem" }}>
                                  <label className={`service-choice-card ${checked ? "is-selected" : ""}`}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(event) => {
                                        const nextValues = toggleStringSelection(
                                          selectedAccessCodeOptionCodes,
                                          option.value,
                                          event.target.checked
                                        );
                                        form.setValue(
                                          "adminDetails.access_code_selected",
                                          serializeBookingMultiSelectValue(nextValues),
                                          { shouldDirty: true }
                                        );
                                      }}
                                    />
                                    <div className="service-choice-copy">
                                      <strong>{option.label}</strong>
                                    </div>
                                  </label>

                                  {checked
                                    ? option.fields.map((field) => {
                                        const fieldName = `adminDetails.${field.detailKey}` as const;
                                        return (
                                          <Field key={field.detailKey} label={field.detailLabel}>
                                            {field.multiline ? (
                                              <textarea {...form.register(fieldName)} rows={3} />
                                            ) : (
                                              <input {...form.register(fieldName)} />
                                            )}
                                            {adminDetailErrors[field.detailKey]?.message ? (
                                              <p className="error-text">{adminDetailErrors[field.detailKey]?.message}</p>
                                            ) : null}
                                          </Field>
                                        );
                                      })
                                    : null}
                                </div>
                              );
                            })}
                          </div>
                          {adminDetailErrors.access_code_selected?.message ? (
                            <p className="error-text">{adminDetailErrors.access_code_selected?.message}</p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </Field>
                </div>
                </Card>
              ) : null}

              {quoteDetailsReady ? (
                <Card title="Checkout options">
                <div className="stack">
                  <div className={bookingFormConfig.data?.bookingForm.enablePromoCodes !== false ? "grid two" : "stack"}>
                    {bookingFormConfig.data?.bookingForm.enablePromoCodes !== false ? (
                      <Field label="Promo code">
                        <input {...promoCodeField} placeholder="Enter discount code" />
                        {showPromoCodeFeedback && quoteQuery.isFetching ? (
                          <p className="card-copy">Checking promo code...</p>
                        ) : null}
                        {showPromoCodeFeedback && !quoteQuery.isFetching && quoteQuery.data?.promoMessage ? (
                          <p className={quoteQuery.data.promoStatus === "applied" ? "success-text" : "error-text"}>
                            {quoteQuery.data.promoMessage}
                          </p>
                        ) : null}
                      </Field>
                    ) : null}
                    <Field label="Gift card">
                      <input {...giftCardCodeField} placeholder="Enter gift card code" />
                      {showGiftCardFeedback && quoteQuery.isFetching ? (
                        <p className="card-copy">Checking gift card...</p>
                      ) : null}
                      {showGiftCardFeedback && !quoteQuery.isFetching && quoteQuery.data?.giftCardMessage ? (
                        <p className={quoteQuery.data.giftCardStatus === "applied" ? "success-text" : "error-text"}>
                          {quoteQuery.data.giftCardMessage}
                        </p>
                      ) : null}
                    </Field>
                  </div>

                  {showScenarioTip ? (
                    <div className="stack">
                      <Field
                        label="Optional tip"
                        hint="Your tip goes directly to your hardworking cleaning team and is always appreciated but never required."
                      >
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.75rem" }}>
                          {TIP_QUICK_PICK_OPTIONS.map((option) => {
                            const checked = selectedTipQuickPick === option.value;
                            return (
                              <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={option.value}>
                                <input
                                  type="radio"
                                  value={option.value}
                                  checked={checked}
                                  onChange={() => {
                                    if (option.value === "none") {
                                      form.setValue("adminDetails.tip_type", "", { shouldDirty: true });
                                      form.setValue("adminDetails.tip", "", { shouldDirty: true });
                                      return;
                                    }

                                    if (option.value === "custom") {
                                      form.setValue("adminDetails.tip_type", tipTypeValue === "percent" ? "percent" : "flat", {
                                        shouldDirty: true
                                      });
                                      if (selectedTipQuickPick !== "custom") {
                                        form.setValue("adminDetails.tip", "", { shouldDirty: true });
                                      }
                                      return;
                                    }

                                    form.setValue("adminDetails.tip_type", "percent", { shouldDirty: true });
                                    form.setValue("adminDetails.tip", option.value, { shouldDirty: true });
                                  }}
                                />
                                <div className="service-choice-copy">
                                  <strong>{option.label}</strong>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </Field>
                      {selectedTipQuickPick === "custom" ? (
                        <>
                          <Field label="Custom tip type">
                            <div className="grid two">
                              {[
                                { value: "percent", label: "Percentage" },
                                { value: "flat", label: "Flat amount" }
                              ].map((option) => {
                                const checked = (tipTypeValue || "flat") === option.value;
                                return (
                                  <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={option.value}>
                                    <input
                                      type="radio"
                                      value={option.value}
                                      checked={checked}
                                      onChange={() => form.setValue("adminDetails.tip_type", option.value, { shouldDirty: true })}
                                    />
                                    <div className="service-choice-copy">
                                      <strong>{option.label}</strong>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </Field>
                          <Field label={(tipTypeValue || "flat") === "percent" ? "Tip (%)" : "Tip amount ($)"}>
                            <input
                              type="number"
                              min="0"
                              max={(tipTypeValue || "flat") === "percent" ? "100" : "500"}
                              step={(tipTypeValue || "flat") === "percent" ? "1" : "0.01"}
                              {...form.register("adminDetails.tip")}
                              placeholder={(tipTypeValue || "flat") === "percent" ? "Enter tip percent" : "Enter tip amount"}
                            />
                            {adminDetailErrors.tip?.message ? <p className="error-text">{adminDetailErrors.tip.message}</p> : null}
                          </Field>
                        </>
                      ) : null}
                    </div>
                  ) : null}

                  {bookingFormConfig.data?.bookingForm.enablePaymentPreference ? (
                    <Field label="Payment preference">
                      <select {...form.register("depositChoice")}>
                        <option value="deposit">Pay deposit now</option>
                        <option value="full">Pay in full now</option>
                        <option value="none">Reserve without payment</option>
                      </select>
                    </Field>
                  ) : null}

                  <Field label="Your special requests or comments">
                    <textarea {...form.register("notes")} rows={4} />
                  </Field>

                </div>
                </Card>
              ) : null}
                  </fieldset>
                </RenderErrorBoundary>
              ) : null}

              {showWaitlistSubmissionSection ? (
                <Card eyebrow="Waitlist" title={`Join the ${formatWaitlistPreferredWindowLabel(waitlistPreferredWindow).toLowerCase()} waitlist`}>
                  <div className="stack">
                    <p className="card-copy">
                      We'll notify you at {String(form.watch("customer.email") ?? "").trim() || "your email"} when an exact-fit
                      opening appears on {formatLongDateLabel(dateValue)}.
                    </p>
                    {waitlistFeedback ? (
                      <p className={createWaitlistMutation.error ? "error-text" : "success-text"}>{waitlistFeedback}</p>
                    ) : null}
                    {createWaitlistMutation.error && !waitlistFeedback ? (
                      <p className="error-text">{(createWaitlistMutation.error as Error).message}</p>
                    ) : null}
                    <Button type="button" onClick={submitWaitlistRequest} disabled={createWaitlistMutation.isPending || !dateValue}>
                      {createWaitlistMutation.isPending ? "Joining waitlist..." : "Join the waitlist"}
                    </Button>
                  </div>
                </Card>
              ) : null}

              {showCheckoutSection ? (
                <RenderErrorBoundary
                  resetKey={[
                    serviceId,
                    locationId,
                    frequencyRuleId,
                    dateValue,
                    effectiveDepositChoice,
                    String(quoteQuery.data?.amountDueNow ?? 0),
                    previewPaymentSessionQuery.data?.paymentIntentId ?? "no-payment-intent"
                  ].join("::")}
                  fallbackTitle="Authorization and confirmation"
                  showDebugDetails={showBookingDebugPanel}
                >
                  {quoteQuery.data && quoteQuery.data.amountDueNow > 0 ? (
                    previewPaymentSessionQuery.isLoading ? (
                      <Card eyebrow="Authorization" title="Credit card details">
                        <p className="card-copy">Preparing your secure Stripe card form...</p>
                      </Card>
                    ) : previewPaymentSessionQuery.error ? (
                      <Card eyebrow="Authorization" title="Credit card details">
                        <p className="error-text">{(previewPaymentSessionQuery.error as Error).message}</p>
                      </Card>
                    ) : (
                      <StripePaymentCard
                        key={previewPaymentSessionQuery.data?.paymentIntentId || `${serviceId || "service"}-${quoteQuery.data?.amountDueNow || 0}`}
                        ref={paymentCardRef}
                        session={previewPaymentSessionQuery.data}
                        title={checkoutAuthorizationTitle}
                        hideConfirmButton
                      />
                    )
                  ) : (
                    <Card eyebrow="Payment" title="Credit card details">
                      <p className="card-copy">No payment is due for this booking right now.</p>
                    </Card>
                  )}

                  <div className="booking-verification-compact" aria-label="Verification">
                    <TurnstileField
                      siteKey={publicConfig.data?.turnstileSiteKey}
                      token={form.watch("turnstileToken") ?? ""}
                      onTokenChange={(token) => form.setValue("turnstileToken", token, { shouldValidate: true })}
                      appearance="interaction-only"
                      compact
                    />
                    {turnstileTokenError ? <p className="error-text">{turnstileTokenError}</p> : null}
                  </div>

                  {legalPagesConfig ? (
                    <Field label="">
                      <label className={`service-choice-card booking-consent-row ${legalConsentAccepted ? "is-selected" : ""}`}>
                        <input type="checkbox" {...form.register("legalConsentAccepted")} />
                        <div className="service-choice-copy">
                          <strong>
                            I agree to the{" "}
                            <a className="booking-legal-link" href={legalPagesConfig.termsUrl} target="_blank" rel="noreferrer">
                              Terms of Service
                            </a>{" "}
                            and acknowledge the{" "}
                            <a className="booking-legal-link" href={legalPagesConfig.privacyUrl} target="_blank" rel="noreferrer">
                              Privacy Policy
                            </a>
                            .
                          </strong>
                        </div>
                      </label>
                      {legalConsentError ? <p className="error-text">{legalConsentError}</p> : null}
                    </Field>
                  ) : null}

                  {createBookingMutation.error ? <p className="error-text">{(createBookingMutation.error as Error).message}</p> : null}
                  <Button
                    type="submit"
                    aria-disabled={bookingConsentMissing}
                    disabled={
                      createBookingMutation.isPending ||
                      bookingConsentMissing ||
                      !visibleAvailabilityItems.length ||
                      ((quoteQuery.data?.amountDueNow ?? 0) > 0 &&
                        !Boolean(previewPaymentSessionQuery.data?.clientSecret || pendingPaymentBooking))
                    }
                  >
                    {createBookingMutation.isPending ? "Booking..." : bookingSubmitLabel}
                  </Button>
                </RenderErrorBoundary>
              ) : null}

              {showBookingDebugPanel ? (
                <details style={{ marginTop: "1rem" }}>
                  <summary>Staging debug</summary>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      background: "#f7f3ed",
                      border: "1px solid #ddd5c8",
                      borderRadius: "12px",
                      padding: "0.75rem"
                    }}
                  >
                    {JSON.stringify(
                      {
                        addressGateValue,
                        zipCodeValue,
                        gatedPostalCode,
                        confirmedBookingGate,
                        confirmedAddressContext,
                        shouldShowBookingQuestions,
                        showCheckoutSection,
                        isZipSupported,
                        serviceId,
                        currentServiceName: currentService?.name ?? null,
                        frequencyRuleId,
                        locationId,
                        eligibleLocationIds: eligibleLocations.map((item) => item.id),
                        availableSpotIds: availableSpots.map((item) => item.id),
                        visibleOptionCount: visibleOptions.length,
                        showResidentialHomeDetails,
                        resolvedScenarioCode: resolvedScenario?.code ?? null,
                        resolvedScenarioName: resolvedScenario?.name ?? null,
                        resolvedScenarioPricingMode: resolvedScenario?.pricingMode ?? null,
                        resolvedScenarioCustomerNote: resolvedScenario?.customerNote ?? null
                      },
                      null,
                      2
                    )}
                  </pre>
                </details>
              ) : null}
            </form>
          </Card>

          <Card title="Estimate summary" className="quote-card">
            {quoteQuery.isLoading && quoteDetailsReady ? <p className="card-copy">Refreshing pricing...</p> : null}
            {quoteQuery.error && quoteDetailsReady ? <p className="error-text">{(quoteQuery.error as Error).message}</p> : null}
            {quoteQuery.data ? (
              <div className="stack">
                <div className="quote-line">
                  <span>Service</span>
                  <strong>{quoteQuery.data.serviceName}</strong>
                </div>
                {frequencyRuleId && selectedFrequencyRule ? (
                  <div className="quote-line">
                    <span>Frequency</span>
                    <strong>{normalizeFrequencyDisplayText(selectedFrequencyRule.name)}</strong>
                  </div>
                ) : null}
                {isAnyRemodelingQuote && selectedRemodelingLabel ? (
                  <div className="quote-line">
                    <span>Renovation</span>
                    <strong>{selectedRemodelingLabel}</strong>
                  </div>
                ) : null}
                {!quoteDetailsReady ? (
                  <p className="card-copy">
                    Complete the property details to see your estimated duration, subtotal, discounts, and total.
                  </p>
                ) : null}
                {quoteDetailsReady && isRemodelingHourlyQuote ? (
                  <>
                    <div className="quote-line">
                      <span>Hourly rate</span>
                      <strong>
                        {formatPublicCurrency(Number(quoteQuery.data.remodelingHourlyRatePerMaidHour ?? 0))} per maid / hour
                      </strong>
                    </div>
                    <div className="quote-line">
                      <span>Minimum</span>
                      <strong>
                        {formatRemodelingMinimumSummary(
                          Number(quoteQuery.data.remodelingMinimumMaidCount ?? 0),
                          Number(quoteQuery.data.remodelingMinimumHours ?? 0)
                        )}
                      </strong>
                    </div>
                    <div className="quote-line quote-line--total">
                      <span>Starting at</span>
                      <strong>{formatPublicCurrency(Number(quoteQuery.data.remodelingStartingAtAmount ?? 0))}</strong>
                    </div>
                    <div className="quote-callout">
                      <p className="eyebrow">Custom quote</p>
                      <h3>No payment due now</h3>
                      <p>
                        Starting at {formatPublicCurrency(Number(quoteQuery.data.remodelingStartingAtAmount ?? 0))}. We'll
                        confirm the final scope and price after reviewing the renovation details.
                      </p>
                    </div>
                  </>
                ) : quoteDetailsReady && isAnyManualReviewQuote ? (
                  <div className="quote-callout">
                    <p className="eyebrow">{isManualReviewQuote ? "Manual review" : "Custom quote"}</p>
                    <h3>No payment due now</h3>
                    <p>{manualReviewQuoteNote}</p>
                  </div>
                ) : quoteDetailsReady ? (
                  <>
                    {!hideEstimateDuration ? (
                      <div className="quote-line quote-line--duration">
                        <span>Estimated duration</span>
                        {(() => {
                          const durationSummary = formatEstimatedDurationSummary(
                            quoteQuery.data.durationMinutes,
                            quoteTeamSize
                          );
                          const laborHoursExplanationRows = showQuoteHourlyCalculation
                            ? buildLaborHoursExplanationRows(quoteBaseLaborHours, quoteBasePriceAmount)
                            : [];
                          return (
                            <strong
                              className="quote-duration-details"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: "0.1rem",
                                textAlign: "right"
                              }}
                            >
                              <span className="quote-labor-summary">
                                <span>{durationSummary.primary}</span>
                                <span>{durationSummary.secondary}</span>
                                {showQuoteHourlyCalculation ? (
                                  <span
                                    className="quote-labor-tooltip"
                                    tabIndex={0}
                                    aria-label="Show labor hours examples"
                                  >
                                    <span className="quote-labor-tooltip__icon" aria-hidden="true">i</span>
                                    <span className="quote-labor-tooltip__bubble" role="tooltip">
                                      {laborHoursExplanationRows.map((row) => (
                                        <span key={row}>{row}</span>
                                      ))}
                                    </span>
                                  </span>
                                ) : null}
                              </span>
                              {showQuoteHourlyCalculation ? (
                                <span style={{ fontSize: "0.82rem", fontWeight: 500, color: "var(--muted)" }}>
                                  {quoteHasSelectedExtras ? "Base cleaning: " : ""}
                                  {formatPublicCurrency(quoteHourlyRateCentsPerMaidHour)} per maid/hour x{" "}
                                  {formatLaborHours(quoteBaseLaborHours)} = {formatPublicCurrency(quoteBasePriceAmount)}
                                </span>
                              ) : null}
                            </strong>
                          );
                        })()}
                      </div>
                    ) : null}
                    {selectedQuoteOptions.length ? (
                      <div className="quote-addon-list">
                        <p className="eyebrow">Extras</p>
                        {selectedQuoteOptions.map((option) => (
                          (() => {
                            const visibleOption = visibleOptions.find((item) => item.id === option.id);
                            const carpetBreakdown = getCarpetQuoteBreakdownRows(visibleOption, adminDetails);
                            const showOptionDuration = !Boolean(visibleOption?.carpetShampooingConfig?.enabled);
                            const simpleQuantitySummary = formatSimpleQuantitySummary(option.quantity, option.quantityLabel);
                            const carpetMinimumAmount = Number(visibleOption?.carpetShampooingConfig?.minimumOrderAmount ?? 0);
                            const carpetBreakdownAmount = carpetBreakdown.reduce((total, row) => total + row.amount, 0);
                            const carpetMinimumApplied = Boolean(
                              carpetBreakdown.length &&
                                carpetMinimumAmount > 0 &&
                                carpetBreakdownAmount < carpetMinimumAmount &&
                                Number(option.priceDeltaAmount ?? 0) === carpetMinimumAmount
                            );
                            return (
                              <div className="quote-addon-line" key={option.id}>
                                <div>
                                  <strong>{option.name}</strong>
                                  {isRecurringFrequencySelection && optionIds.includes(option.id) ? (
                                    <div className="card-copy">
                                      {getRecurringExtraScopeLabel(
                                        recurringExtraScopes[option.id] ?? getDefaultRecurringExtraScope(visibleOption)
                                      )}
                                    </div>
                                  ) : null}
                                  {simpleQuantitySummary ? <div className="card-copy">{simpleQuantitySummary}</div> : null}
                                  {carpetBreakdown.length ? (
                                    <div className="quote-addon-breakdown">
                                      {carpetBreakdown.map((row) => (
                                        <div className="quote-addon-breakdown-line" key={`${option.id}-${row.code}`}>
                                          <span>
                                            {row.label}: {row.quantity}
                                          </span>
                                          <strong>{formatPublicCurrency(row.amount)}</strong>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                                <div className="quote-addon-meta">
                                  {showOptionDuration ? (
                                    <span>{option.durationDeltaMinutes > 0 ? `+${option.durationDeltaMinutes} min` : "0 min"}</span>
                                  ) : null}
                                  <strong>
                                    {carpetMinimumApplied
                                      ? `Minimum ${formatPublicCurrency(option.priceDeltaAmount)}`
                                      : formatPublicCurrency(option.priceDeltaAmount)}
                                  </strong>
                                </div>
                              </div>
                            );
                          })()
                        ))}
                      </div>
                    ) : null}
                    {quoteSkipAreaLaborMinutesSaved > 0 ? (
                      <div className="quote-line">
                        <span>Skipped area time saving</span>
                        <strong>- {formatMinuteQuantity(quoteSkipAreaLaborMinutesSaved)}</strong>
                      </div>
                    ) : null}
                    <div className="quote-line">
                      <span>{showRecurringCleaningTotals ? "Initial cleaning total" : "Subtotal"}</span>
                      <strong>{formatPublicCurrency(quoteInitialCleaningTotalAmount)}</strong>
                    </div>
                    {quoteSkipAreaDeductionAmount > 0 ? (
                      <div className="quote-line">
                        <span>Areas to skip deduction</span>
                        <strong>-{formatPublicCurrency(quoteSkipAreaDeductionAmount)}</strong>
                      </div>
                    ) : null}
                    {quoteFrequencyDiscountAmount > 0 ? (
                      <div className="quote-line">
                        <span>{showRecurringCleaningTotals ? "2nd cleaning discount" : "Frequency discount"}</span>
                        <strong>-{formatPublicCurrency(quoteFrequencyDiscountAmount)}</strong>
                      </div>
                    ) : null}
                    {Number(quoteQuery.data.arrivalFlexibilityDiscount ?? 0) > 0 ? (
                      <div className="quote-line">
                        <span>Flexible arrival discount</span>
                        <strong>-{formatPublicCurrency(Number(quoteQuery.data.arrivalFlexibilityDiscount ?? 0))}</strong>
                      </div>
                    ) : null}
                    {quoteCustomerSuppliesEquipmentDiscountAmount > 0 ? (
                      <div className="quote-line">
                        <span>Customer supplies discount</span>
                        <strong>-{formatPublicCurrency(quoteCustomerSuppliesEquipmentDiscountAmount)}</strong>
                      </div>
                    ) : null}
                    {Number(quoteQuery.data.promoDiscount ?? 0) > 0 ? (
                      <div className="quote-line">
                        <span>Promo code discount</span>
                        <strong>-{formatPublicCurrency(Number(quoteQuery.data.promoDiscount ?? 0))}</strong>
                      </div>
                    ) : null}
                    {showTaxRowInSummary ? (
                      <div className="quote-line">
                        <span>Tax</span>
                        <strong>{formatPublicCurrency(quoteQuery.data.tax)}</strong>
                      </div>
                    ) : null}
                    {Number(quoteQuery.data.tip ?? 0) > 0 ? (
                      <div className="quote-line">
                        <span>Tip</span>
                        <strong>{formatPublicCurrency(quoteQuery.data.tip ?? 0)}</strong>
                      </div>
                    ) : null}
                    {Number(quoteQuery.data.giftCardAppliedAmount ?? 0) > 0 ? (
                      <div className="quote-line">
                        <span>Gift card applied</span>
                        <strong>-{formatPublicCurrency(Number(quoteQuery.data.giftCardAppliedAmount ?? 0))}</strong>
                      </div>
                    ) : null}
                    <div className="quote-line quote-line--total">
                      <span>{showRecurringCleaningTotals ? "2nd regular cleaning total" : quoteTotalLabel}</span>
                      <strong>{formatPublicCurrency(quoteQuery.data.total)}</strong>
                    </div>
                    {showCompactDueTodayRow ? (
                      <>
                        <div className="quote-line">
                          <span>Due today</span>
                          <strong>{formatPublicCurrency(quoteQuery.data.amountDueNow)}</strong>
                        </div>
                        <p className="card-copy quote-balance-note">{compactDueTodayNote}</p>
                      </>
                    ) : null}
                  </>
                ) : null}
                {quoteQuery.data.promotionCode ? (
                  <div>
                    <p className="eyebrow">Promotion applied</p>
                    <p className="card-copy">{quoteQuery.data.promotionCode}</p>
                  </div>
                ) : null}
                {quoteDetailsReady && estimateSummaryNote && !isAnyManualReviewQuote ? (
                  <div>
                    <p className="eyebrow">Estimate note</p>
                    <p className="card-copy">{estimateSummaryNote}</p>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="card-copy">Choose a service to preview pricing and the amount due at booking.</p>
            )}
          </Card>
        </div>
      </section>
    </div>
  );
}

function ConfirmationPage() {
  const { bookingId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const manageToken = searchParams.get("token") ?? "";
  const reference = searchParams.get("reference") ?? bookingId;
  const [paymentStatusHint, setPaymentStatusHint] = useState<string | null>(() => searchParams.get("payment"));
  const [showPaymentCard, setShowPaymentCard] = useState(false);
  const [shouldPollPaymentStatus, setShouldPollPaymentStatus] = useState(Boolean(searchParams.get("payment")));
  const bookingQuery = useQuery({
    queryKey: ["confirmation-booking", manageToken],
    queryFn: () => apiFetch<BookingDetail>(`/api/bookings/public/${manageToken}`),
    enabled: Boolean(manageToken),
    refetchOnWindowFocus: false,
    refetchInterval: shouldPollPaymentStatus ? 2000 : false
  });

  const booking = bookingQuery.data;
  const latestSucceededPayment = booking?.payments.find(
    (payment) => String(payment.provider ?? "").toLowerCase() === "stripe" && String(payment.status ?? "").toLowerCase() === "succeeded"
  );
  const latestAuthorizedPayment = booking?.payments.find(
    (payment) => String(payment.provider ?? "").toLowerCase() === "stripe" && String(payment.status ?? "").toLowerCase() === "authorized"
  );
  const latestSavedCardPayment = booking?.payments.find(
    (payment) =>
      String(payment.provider ?? "").toLowerCase() === "stripe" &&
      String(payment.status ?? "").toLowerCase() === "card_saved_pending_authorization"
  );
  const latestPendingPayment = booking?.payments.find(
    (payment) =>
      String(payment.provider ?? "").toLowerCase() === "stripe" &&
      ["pending", "pending_authorization"].includes(String(payment.status ?? "").toLowerCase())
  );
  const outstandingBalance = Math.max(0, Number(booking?.amountDue ?? 0));
  const paidAmount = Math.max(0, Number(latestSucceededPayment?.amount ?? 0));
  const bookingIsConfirmed = String(booking?.status ?? "").toLowerCase() === "confirmed";
  const bookingIsPending = String(booking?.status ?? "").toLowerCase() === "pending";
  const paymentIsProcessing =
    Boolean(paymentStatusHint) &&
    !latestSucceededPayment &&
    !latestAuthorizedPayment &&
    !latestSavedCardPayment &&
    Boolean(latestPendingPayment || bookingIsPending);
  const authorizationApproved = Boolean(latestAuthorizedPayment);
  const cardSavedPendingAuthorization = Boolean(latestSavedCardPayment);
  const paymentReceivedInFull = Boolean(latestSucceededPayment) && outstandingBalance <= 0;
  const depositReceived = Boolean(latestSucceededPayment) && outstandingBalance > 0;
  const nothingDueNow =
    !latestSucceededPayment && !latestAuthorizedPayment && !latestSavedCardPayment && !latestPendingPayment && bookingIsConfirmed;
  const canManuallyCompletePayment =
    Boolean(manageToken) &&
    !paymentReceivedInFull &&
    !depositReceived &&
    !authorizationApproved &&
    !cardSavedPendingAuthorization &&
    Boolean(latestPendingPayment || bookingIsPending);

  useEffect(() => {
    if (latestSucceededPayment || !paymentIsProcessing) {
      setShouldPollPaymentStatus(false);
    }
  }, [latestSucceededPayment, paymentIsProcessing]);

  useEffect(() => {
    if (paymentReceivedInFull || depositReceived || authorizationApproved || cardSavedPendingAuthorization || nothingDueNow) {
      setShowPaymentCard(false);
    }
  }, [authorizationApproved, cardSavedPendingAuthorization, depositReceived, nothingDueNow, paymentReceivedInFull]);

  const paymentSummary = paymentReceivedInFull
    ? {
        eyebrow: "Payment received",
        title: `Reference ${reference}`,
        copy: `Your booking is confirmed and we received ${formatCurrency(paidAmount)}.`,
        emphasis: latestSucceededPayment?.receiptUrl ? "Your Stripe receipt is ready below." : "A receipt will be emailed if receipt emails are enabled."
      }
    : authorizationApproved
      ? {
          eyebrow: "Authorization approved",
          title: `Reference ${reference}`,
          copy: `Your booking is confirmed and your card is authorized for ${formatCurrency(Number(latestAuthorizedPayment?.amount ?? 0))}.`,
          emphasis: "No payment has been captured yet. We will capture the approved amount after service."
        }
      : cardSavedPendingAuthorization
        ? {
            eyebrow: "Card saved",
            title: `Reference ${reference}`,
            copy: "Your booking is confirmed and your card is securely saved.",
            emphasis: "We will authorize the card closer to the service date, then capture the approved amount after service."
          }
        : depositReceived
      ? {
          eyebrow: "Deposit received",
          title: `Reference ${reference}`,
          copy: `Your booking is confirmed. We received ${formatCurrency(paidAmount)} today, and the remaining ${formatCurrency(outstandingBalance)} is due later.`,
          emphasis: "You do not need to pay again right now."
        }
      : paymentIsProcessing
        ? {
            eyebrow: "Payment processing",
            title: `Reference ${reference}`,
            copy:
              paymentStatusHint === "succeeded"
                ? "Stripe confirmed your card. We're updating the booking record now."
                : paymentStatusHint === "requires_capture"
                  ? "Stripe approved the authorization. We're updating the booking record now."
                  : "Your booking is confirmed and Stripe is still finishing the card update.",
            emphasis: "No need to pay again. This page refreshes automatically."
          }
        : nothingDueNow
          ? {
              eyebrow: "Booking confirmed",
              title: `Reference ${reference}`,
              copy: "Your booking is confirmed.",
              emphasis:
                outstandingBalance > 0
                  ? `Nothing is due right now. The remaining ${formatCurrency(outstandingBalance)} will be collected later.`
                  : "Nothing is due right now."
            }
          : {
              eyebrow: "Booking confirmed",
              title: `Reference ${reference}`,
              copy: "Your booking is saved, but the payment due now has not been completed yet.",
              emphasis: "Use the payment option below only if you still need to finish checkout."
            };
  const balanceSummary =
    paymentReceivedInFull
      ? "Paid in full"
      : authorizationApproved
        ? `Authorized ${formatCurrency(Number(latestAuthorizedPayment?.amount ?? 0))}`
        : cardSavedPendingAuthorization
          ? "Card saved for later authorization"
      : depositReceived || nothingDueNow
        ? `Remaining balance ${formatCurrency(outstandingBalance)} due later`
        : paymentIsProcessing
          ? "Payment update in progress"
          : outstandingBalance > 0
            ? `${formatCurrency(outstandingBalance)} still due now`
            : "Nothing due right now";

  return (
    <div className="marketing-shell">
      <div className="stack">
        <Card eyebrow={paymentSummary.eyebrow} title={paymentSummary.title}>
          <p>{paymentSummary.copy}</p>
          <p className="card-copy">{paymentSummary.emphasis}</p>
          {booking ? (
            <div className="stack" style={{ gap: "0.35rem" }}>
              <p className="card-copy">
                Appointment: <strong>{formatDateTimeLabel(booking.startsAt)}</strong>
              </p>
              <p className="card-copy">
                Service address: <strong>{booking.serviceAddress || "Address provided at booking"}</strong>
              </p>
              <p className="card-copy">
                Booking total: <strong>{formatCurrency(Number(booking.totalAmount ?? 0))}</strong>
                {` • ${balanceSummary}`}
              </p>
            </div>
          ) : null}
          <div className="hero__actions">
            <Link className="button button--primary" to={`/manage/${manageToken}`}>
              Manage booking
            </Link>
            <Link className="button button--secondary" to="/account/login">
              My Account
            </Link>
            {latestSucceededPayment?.receiptUrl ? (
              <a className="button button--secondary" href={latestSucceededPayment.receiptUrl} target="_blank" rel="noreferrer">
                View receipt
              </a>
            ) : null}
            {paymentIsProcessing ? (
              <Button variant="secondary" onClick={() => bookingQuery.refetch()} disabled={bookingQuery.isFetching}>
                {bookingQuery.isFetching ? "Refreshing..." : "Refresh status"}
              </Button>
            ) : null}
            {canManuallyCompletePayment && !showPaymentCard ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setShowPaymentCard(true);
                  setPaymentStatusHint(null);
                  setShouldPollPaymentStatus(false);
                }}
              >
                {paymentIsProcessing ? "Show payment options again" : "Complete payment"}
              </Button>
            ) : null}
            <Link className="button button--secondary" to="/">
              Back home
            </Link>
          </div>
        </Card>
        {bookingQuery.isLoading && manageToken ? (
          <Card eyebrow="Status" title="Checking payment">
            <p className="card-copy">Refreshing the latest booking and payment status...</p>
          </Card>
        ) : null}
        {bookingQuery.error && manageToken ? (
          <Card eyebrow="Status" title="Unable to refresh">
            <p className="error-text">We couldn't refresh the latest payment status right now.</p>
          </Card>
        ) : null}
        {manageToken && showPaymentCard && canManuallyCompletePayment ? (
          <StripePaymentCard
            token={manageToken}
            onPaymentSuccess={(status) => {
              setPaymentStatusHint(status);
              setShowPaymentCard(false);
              setShouldPollPaymentStatus(true);
              bookingQuery.refetch();
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function FeedbackPage() {
  const { token = "" } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [overallRating, setOverallRating] = useState(0);
  const [kitchenRating, setKitchenRating] = useState(3);
  const [bathroomsRating, setBathroomsRating] = useState(3);
  const [bedroomsRating, setBedroomsRating] = useState(3);
  const [livingAreasRating, setLivingAreasRating] = useState(3);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState<BookingFeedbackPhoto[]>([]);
  const [saveMessage, setSaveMessage] = useState("");

  const feedbackQuery = useQuery({
    queryKey: ["public-booking-feedback", token],
    queryFn: () => apiFetch<PublicBookingFeedbackContext>(`/api/feedback/public/${token}`),
    enabled: Boolean(token),
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (!feedbackQuery.data) {
      return;
    }

    const nextOverallRating = Number(feedbackQuery.data.feedback?.overallRating ?? feedbackQuery.data.feedback?.clickedRating ?? 0);
    const roomFallback = nextOverallRating || 3;
    setOverallRating(nextOverallRating);
    setKitchenRating(Number(feedbackQuery.data.feedback?.kitchenRating ?? roomFallback));
    setBathroomsRating(Number(feedbackQuery.data.feedback?.bathroomsRating ?? roomFallback));
    setBedroomsRating(Number(feedbackQuery.data.feedback?.bedroomsRating ?? roomFallback));
    setLivingAreasRating(Number(feedbackQuery.data.feedback?.livingAreasRating ?? roomFallback));
    setComment(String(feedbackQuery.data.feedback?.comment ?? ""));
    setPhotos(feedbackQuery.data.feedback?.photos ?? []);
  }, [feedbackQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = bookingFeedbackUpdateInputSchema.parse({
        overallRating,
        kitchenRating: feedbackQuery.data?.showAreaRatings ? kitchenRating : null,
        bathroomsRating: feedbackQuery.data?.showAreaRatings ? bathroomsRating : null,
        bedroomsRating: feedbackQuery.data?.showAreaRatings ? bedroomsRating : null,
        livingAreasRating: feedbackQuery.data?.showAreaRatings ? livingAreasRating : null,
        comment,
        photos
      });
      return apiFetch<BookingFeedback>(`/api/feedback/public/${token}`, {
        method: "PUT",
        body: JSON.stringify(payload)
      });
    },
    onSuccess: async (feedback) => {
      setSaveMessage("Your feedback is saved. Thank you.");
      queryClient.setQueryData<PublicBookingFeedbackContext | undefined>(["public-booking-feedback", token], (current) =>
        current ? { ...current, feedback } : current
      );
      await queryClient.invalidateQueries({ queryKey: ["public-booking-feedback", token] });
    }
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`/api/feedback/public/${token}/photos`, {
        method: "POST",
        body: formData,
        credentials: "include"
      });
      const payload = (await response.json()) as { error?: string; feedback?: BookingFeedback };
      if (!response.ok) {
        throw new Error(payload.error ?? "Photo upload failed.");
      }
      return payload;
    },
    onSuccess: async (payload) => {
      const nextPhotos = payload.feedback?.photos ?? photos;
      setPhotos(nextPhotos);
      queryClient.setQueryData<PublicBookingFeedbackContext | undefined>(["public-booking-feedback", token], (current) =>
        current && payload.feedback ? { ...current, feedback: payload.feedback } : current
      );
      await queryClient.invalidateQueries({ queryKey: ["public-booking-feedback", token] });
    }
  });

  const booking = feedbackQuery.data?.booking;
  const submittedAt = feedbackQuery.data?.feedback?.submittedAt;
  const invalidRating = searchParams.get("error") === "rating";

  return (
    <div className="marketing-shell">
      <div className="stack">
        <Card
          eyebrow="Customer feedback"
          title={booking?.publicReference ? `Rate ${booking.publicReference}` : "Rate your appointment"}
          actions={
            <div className="hero__actions">
              <Link className="button button--secondary" to="/">
                Back home
              </Link>
            </div>
          }
        >
          {feedbackQuery.isLoading ? (
            <p className="card-copy">Loading your feedback page...</p>
          ) : feedbackQuery.error || !feedbackQuery.data ? (
            <p className="error-text">This feedback link is invalid or has expired.</p>
          ) : (
            <div className="stack">
              <div className="feedback-summary-grid">
                <div className="booking-meta-card">
                  <p className="eyebrow">Appointment</p>
                  <strong>{booking?.startsAt ? formatDateTimeLabel(booking.startsAt) : "Appointment time unavailable"}</strong>
                  <p>{booking?.serviceName || "Cleaning service"}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Location</p>
                  <strong>{booking?.locationName || "Service location"}</strong>
                  <p>{booking?.customerName || "Customer"}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Status</p>
                  <strong>{submittedAt ? "Feedback submitted" : "Feedback in progress"}</strong>
                  <p>{submittedAt ? `Saved ${formatDateTimeLabel(submittedAt)}` : "We save one editable feedback record for this booking."}</p>
                </div>
              </div>

              {invalidRating ? <p className="error-text">Choose a rating from 1 to 5 stars.</p> : null}
              {saveMutation.error ? <p className="error-text">{(saveMutation.error as Error).message}</p> : null}
              {uploadPhotoMutation.error ? <p className="error-text">{(uploadPhotoMutation.error as Error).message}</p> : null}
              {saveMessage ? <p className="success-text">{saveMessage}</p> : null}

              <Field label="Overall rating" hint="Tap a number to rate the visit from 1 to 5 stars.">
                <div className="feedback-rating-buttons">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      className={`feedback-rating-button ${overallRating === rating ? "is-active" : ""}`}
                      onClick={() => {
                        setSaveMessage("");
                        setOverallRating(rating);
                      }}
                    >
                      <span>{rating}</span>
                      <small>star{rating === 1 ? "" : "s"}</small>
                    </button>
                  ))}
                </div>
              </Field>

              {feedbackQuery.data.showAreaRatings ? (
                <div className="grid two">
                  <Field label={`Kitchen: ${kitchenRating}/5`}>
                    <input type="range" min="1" max="5" step="1" value={kitchenRating} onChange={(event) => setKitchenRating(Number(event.target.value))} />
                  </Field>
                  <Field label={`Bathrooms: ${bathroomsRating}/5`}>
                    <input type="range" min="1" max="5" step="1" value={bathroomsRating} onChange={(event) => setBathroomsRating(Number(event.target.value))} />
                  </Field>
                  <Field label={`Bedrooms: ${bedroomsRating}/5`}>
                    <input type="range" min="1" max="5" step="1" value={bedroomsRating} onChange={(event) => setBedroomsRating(Number(event.target.value))} />
                  </Field>
                  <Field label={`Living areas: ${livingAreasRating}/5`}>
                    <input type="range" min="1" max="5" step="1" value={livingAreasRating} onChange={(event) => setLivingAreasRating(Number(event.target.value))} />
                  </Field>
                </div>
              ) : null}

              <Field label="Comments" hint="Tell us what went well or what we should improve next time.">
                <textarea rows={6} value={comment} onChange={(event) => setComment(event.target.value)} />
              </Field>

              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  uploadPhotoMutation.mutate(file);
                  event.currentTarget.value = "";
                }}
              />

              <Card
                title="Photos"
                eyebrow="Optional"
                actions={
                  <Button
                    variant="secondary"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadPhotoMutation.isPending}
                  >
                    {uploadPhotoMutation.isPending ? "Uploading..." : "Add photo"}
                  </Button>
                }
              >
                {photos.length ? (
                  <div className="feedback-photo-grid">
                    {photos.map((photo) => (
                      <a
                        key={photo.key}
                        href={`/api/feedback/public/${token}/photos/download?key=${encodeURIComponent(photo.key)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="feedback-photo-card"
                      >
                        <img src={`/api/feedback/public/${token}/photos/download?key=${encodeURIComponent(photo.key)}`} alt={photo.name} />
                        <span>{photo.name}</span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="card-copy">No photos uploaded yet.</p>
                )}
              </Card>

              <div className="hero__actions">
                <Button
                  variant="primary"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending || overallRating < 1}
                >
                  {saveMutation.isPending ? "Saving..." : "Save feedback"}
                </Button>
                {feedbackQuery.data.feedback?.lowRatingFlaggedAt ? <Badge tone="warning">Office follow-up flagged</Badge> : null}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function MyFeedbackPage() {
  const feedbackQuery = useQuery({
    queryKey: ["staff-my-feedback"],
    queryFn: () => apiFetch<{ items: StaffFeedbackFeedItem[] }>("/api/staff/me/feedback")
  });

  const items = feedbackQuery.data?.items ?? [];

  return (
    <div className="stack payments-page">
      <SectionHeader
        title="My Feedback"
        description="Ratings, comments, and photos from bookings that are currently assigned to you."
      />
      {feedbackQuery.isLoading ? (
        <div className="page-loading">Loading your feedback...</div>
      ) : feedbackQuery.error ? (
        <Card title="Unable to load feedback" eyebrow="My Feedback">
          <p className="error-text">{(feedbackQuery.error as Error).message}</p>
        </Card>
      ) : items.length ? (
        items.map((item) => {
          const photoBaseUrl = `/api/staff/me/feedback/photos/download?bookingId=${encodeURIComponent(item.bookingId)}&key=`;
          const ratingLabel = item.effectiveRating == null ? "No rating yet" : `${item.effectiveRating}/5 stars`;
          const statusLabel = item.submittedAt
            ? `Submitted ${formatDateTimeLabel(item.submittedAt)}`
            : item.clickedAt
              ? `Clicked ${formatDateTimeLabel(item.clickedAt)}`
              : "Awaiting feedback";

          return (
            <Card
              key={`${item.bookingId}-${item.submittedAt ?? item.clickedAt ?? "feedback"}`}
              title={item.bookingPublicReference ? `${item.bookingPublicReference} · ${item.serviceName}` : item.serviceName}
              eyebrow="Booking feedback"
              actions={item.lowRatingFlaggedAt ? <Badge tone="warning">Low rating</Badge> : undefined}
            >
              <div className="stack">
                <div className="feedback-summary-grid">
                  <div className="booking-meta-card">
                    <p className="eyebrow">Visit</p>
                    <strong>{item.startsAt ? formatDateTimeLabel(item.startsAt) : "Visit time unavailable"}</strong>
                    <p>{item.locationLabel || "Assigned location"}</p>
                  </div>
                  <div className="booking-meta-card">
                    <p className="eyebrow">Rating</p>
                    <strong>{ratingLabel}</strong>
                    <p>{item.overallRating == null && item.clickedRating != null ? "Email click only so far." : "Detailed feedback saved."}</p>
                  </div>
                  <div className="booking-meta-card">
                    <p className="eyebrow">Status</p>
                    <strong>{item.submittedAt ? "Submitted" : item.clickedAt ? "Started" : "Pending"}</strong>
                    <p>{statusLabel}</p>
                  </div>
                </div>

                {item.showAreaRatings ? (
                  <DataTable
                    columns={["Kitchen", "Bathrooms", "Bedrooms", "Living areas"]}
                    rows={[
                      [
                        item.kitchenRating == null ? "" : `${item.kitchenRating}/5`,
                        item.bathroomsRating == null ? "" : `${item.bathroomsRating}/5`,
                        item.bedroomsRating == null ? "" : `${item.bedroomsRating}/5`,
                        item.livingAreasRating == null ? "" : `${item.livingAreasRating}/5`
                      ]
                    ]}
                  />
                ) : null}

                <Card title="Comment" eyebrow="Customer notes">
                  <p className="card-copy">{item.comment || "No written comment was included."}</p>
                </Card>

                <Card title="Photos" eyebrow="Customer uploads">
                  {item.photos.length ? (
                    <div className="feedback-photo-grid">
                      {item.photos.map((photo) => (
                        <a
                          key={photo.key}
                          href={`${photoBaseUrl}${encodeURIComponent(photo.key)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="feedback-photo-card"
                        >
                          <img src={`${photoBaseUrl}${encodeURIComponent(photo.key)}`} alt={photo.name} />
                          <span>{photo.name}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="card-copy">No photos were uploaded for this feedback.</p>
                  )}
                </Card>
              </div>
            </Card>
          );
        })
      ) : (
        <Card title="No feedback yet" eyebrow="My Feedback">
          <p className="card-copy">Feedback will appear here once a currently assigned booking receives a rating or comment.</p>
        </Card>
      )}
    </div>
  );
}

function ManageBookingPage() {
  const { token = "" } = useParams();
  const [searchParams] = useSearchParams();
  const manageMode = (() => {
    const rawMode = String(searchParams.get("mode") ?? "").trim().toLowerCase();
    if (rawMode === "extras" || rawMode === "payment" || rawMode === "reschedule") {
      return rawMode;
    }
    return "manage";
  })();
  const bookingQuery = useQuery({
    queryKey: ["manage-booking", token],
    queryFn: () => apiFetch<ManageBookingData>(`/api/bookings/public/${token}`)
  });
  const booking = bookingQuery.data;
  const bookingTimeZone = String(booking?.timezone ?? "").trim() || "America/Chicago";
  const serviceId = String(booking?.items?.[0]?.serviceId ?? "");
  const bookingSpotId = String(booking?.bookingSpotId ?? "");
  const calendarProfileId = String(booking?.calendarProfileId ?? "");
  const locationId = String(booking?.locationId ?? "");
  const optionIds = useMemo(
    () => parseManageBookingOptionIds(Array.isArray(booking?.items) ? booking.items : []),
    [booking?.items]
  );
  const bookingFormConfig = useQuery({
    queryKey: ["manage-booking-form-config", token],
    queryFn: () => apiFetch<BookingFormRuntimeConfig>("/api/booking-form/config"),
    enabled: manageMode === "extras" && Boolean(serviceId)
  });
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [visibleCalendarMonth, setVisibleCalendarMonth] = useState(getIsoMonth(getTodayIsoDate()));
  const [selectedStartsAt, setSelectedStartsAt] = useState("");
  const [extrasSaved, setExtrasSaved] = useState(false);
  const [extrasSaveOutcome, setExtrasSaveOutcome] = useState<ExtrasSaveOutcome | null>(null);
  const bookingDateValue = booking?.startsAt ? toDateTimeInputValue(String(booking.startsAt), bookingTimeZone).slice(0, 10) : "";
  const isExtrasMode = manageMode === "extras";
  const resetExtrasSaveState = () => {
    setExtrasSaved(false);
    setExtrasSaveOutcome(null);
  };

  useEffect(() => {
    setSelectedOptionIds(optionIds);
  }, [token, optionIds]);

  useEffect(() => {
    resetExtrasSaveState();
  }, [manageMode, token]);

  const currentService = useMemo(
    () => (bookingFormConfig.data?.services ?? []).find((service) => service.id === serviceId),
    [bookingFormConfig.data?.services, serviceId]
  );
  const currentScenario = useMemo(
    () =>
      resolveServiceScenario(
        bookingFormConfig.data?.serviceScenarios ?? [],
        currentService,
        String(booking?.adminDetails?.square_footage ?? "")
      ),
    [booking?.adminDetails?.square_footage, bookingFormConfig.data?.serviceScenarios, currentService]
  );
  const visibleOptions = useMemo(
    () => getVisibleOptionsForSelection(bookingFormConfig.data?.serviceOptions ?? [], currentService, currentScenario),
    [bookingFormConfig.data?.serviceOptions, currentScenario, currentService]
  );
  const editableOptions = useMemo(
    () =>
      visibleOptions.filter(
        (option) => !Boolean(option.quantityBased) && !Boolean(option.carpetShampooingConfig?.enabled)
      ),
    [visibleOptions]
  );
  const editableOptionIds = useMemo(() => editableOptions.map((option) => option.id), [editableOptions]);
  const optionById = useMemo(
    () =>
      new Map(
        (bookingFormConfig.data?.serviceOptions ?? []).map((option) => [option.id, option] as const)
      ),
    [bookingFormConfig.data?.serviceOptions]
  );
  const currentExtras = useMemo(
    () => parsePortalSummaryLines(String(booking?.adminDetails?.extras ?? "")),
    [booking?.adminDetails?.extras]
  );
  const currentExtrasDisplay = useMemo(() => {
    if (currentExtras.length) {
      return currentExtras;
    }
    return optionIds
      .map((optionId) => String(optionById.get(optionId)?.name ?? "").trim())
      .filter(Boolean);
  }, [currentExtras, optionById, optionIds]);
  const currentRecurringExtras = useMemo(
    () => parsePortalSummaryLines(String(booking?.adminDetails?.extra_recurring_summary ?? "")),
    [booking?.adminDetails?.extra_recurring_summary]
  );
  const selectedOptionsFromConfig = useMemo(
    () =>
      selectedOptionIds
        .map((optionId) => optionById.get(optionId))
        .filter((option): option is BookingFormServiceOption => Boolean(option)),
    [optionById, selectedOptionIds]
  );
  const selectedExtrasSummary = useMemo(
    () => selectedOptionsFromConfig.map((option) => option.name).filter(Boolean).join(" | "),
    [selectedOptionsFromConfig]
  );
  const currentRecurringScopes = useMemo(
    () => parseRecurringExtraScopes(String(booking?.adminDetails?.extra_recurring_scopes ?? "")),
    [booking?.adminDetails?.extra_recurring_scopes]
  );
  const nextRecurringScopes = useMemo(
    () =>
      Object.fromEntries(
        selectedOptionsFromConfig.map((option) => [
          option.id,
          currentRecurringScopes[option.id] ?? getDefaultRecurringExtraScope(option)
        ])
      ) as Record<string, RecurringExtraScopeValue>,
    [currentRecurringScopes, selectedOptionsFromConfig]
  );
  const editableAdminDetails = useMemo(() => {
    const nextAdminDetails = {
      ...(booking?.adminDetails ?? {})
    } as Record<string, string>;

    if (selectedExtrasSummary) {
      nextAdminDetails.extras = selectedExtrasSummary;
    } else {
      delete nextAdminDetails.extras;
    }

    if (Object.keys(nextRecurringScopes).length) {
      nextAdminDetails.extra_recurring_scopes = serializeRecurringExtraScopes(nextRecurringScopes);
      nextAdminDetails.extra_recurring_summary = buildRecurringExtraSummary(selectedOptionsFromConfig, nextRecurringScopes);
    } else {
      delete nextAdminDetails.extra_recurring_scopes;
      delete nextAdminDetails.extra_recurring_summary;
    }

    return nextAdminDetails;
  }, [booking?.adminDetails, nextRecurringScopes, selectedExtrasSummary, selectedOptionsFromConfig]);
  const extrasChanged = useMemo(() => !equalStringSets(selectedOptionIds, optionIds), [optionIds, selectedOptionIds]);
  const addedExtraNames = useMemo(
    () =>
      selectedOptionIds
        .filter((optionId) => !optionIds.includes(optionId))
        .map((optionId) => String(optionById.get(optionId)?.name ?? "").trim())
        .filter(Boolean),
    [optionById, optionIds, selectedOptionIds]
  );
  const removedExtraNames = useMemo(
    () =>
      optionIds
        .filter((optionId) => !selectedOptionIds.includes(optionId))
        .map((optionId) => String(optionById.get(optionId)?.name ?? "").trim())
        .filter(Boolean),
    [optionById, optionIds, selectedOptionIds]
  );
  const lockedSelectedOptionNames = useMemo(
    () =>
      selectedOptionIds
        .filter((optionId) => !editableOptionIds.includes(optionId))
        .map((optionId) => String(optionById.get(optionId)?.name ?? "").trim())
        .filter(Boolean),
    [editableOptionIds, optionById, selectedOptionIds]
  );
  const availabilityOptionIds = isExtrasMode ? selectedOptionIds : optionIds;
  const availabilityAdminDetails = isExtrasMode ? editableAdminDetails : booking?.adminDetails ?? {};
  const shouldCheckAvailability = !isExtrasMode || extrasChanged;

  useEffect(() => {
    if (!bookingDateValue) {
      return;
    }
    setSelectedDate((current) => current || bookingDateValue);
    setVisibleCalendarMonth((current) => (current === getIsoMonth(bookingDateValue) ? current : getIsoMonth(bookingDateValue)));
  }, [bookingDateValue]);

  useEffect(() => {
    if (!selectedDate) {
      return;
    }
    const nextVisibleMonth = getIsoMonth(selectedDate);
    setVisibleCalendarMonth((current) => (current === nextVisibleMonth ? current : nextVisibleMonth));
  }, [selectedDate]);

  const availabilityQuery = useQuery({
    queryKey: [
      "manage-availability",
      token,
      locationId,
      serviceId,
      bookingSpotId,
      calendarProfileId,
      selectedDate,
      availabilityOptionIds,
      JSON.stringify(availabilityAdminDetails ?? {})
    ],
    queryFn: () =>
      apiFetch<{ items: AvailabilitySlotSummary[] }>(
        `/api/availability?locationId=${locationId}&serviceId=${serviceId}&bookingSpotId=${bookingSpotId || ""}&calendarProfileId=${calendarProfileId || ""}&optionIds=${encodeURIComponent(availabilityOptionIds.join(","))}&adminDetails=${encodeURIComponent(JSON.stringify(availabilityAdminDetails ?? {}))}&date=${selectedDate}`
      ),
    enabled: Boolean(locationId && serviceId && bookingSpotId && selectedDate && shouldCheckAvailability)
  });

  const monthAvailabilityQuery = useQuery({
    queryKey: [
      "manage-availability-calendar-month",
      token,
      locationId,
      serviceId,
      bookingSpotId,
      calendarProfileId,
      visibleCalendarMonth,
      availabilityOptionIds,
      JSON.stringify(availabilityAdminDetails ?? {})
    ],
    queryFn: async () => {
      const monthDates = getIsoDatesForMonth(visibleCalendarMonth);
      const entries = await Promise.all(
        monthDates.map(async (candidateDate) => {
          const candidateResponse = await apiFetch<{ items: AvailabilitySlotSummary[] }>(
            `/api/availability?locationId=${locationId}&serviceId=${serviceId}&bookingSpotId=${bookingSpotId || ""}&calendarProfileId=${calendarProfileId || ""}&optionIds=${encodeURIComponent(availabilityOptionIds.join(","))}&adminDetails=${encodeURIComponent(JSON.stringify(availabilityAdminDetails ?? {}))}&date=${candidateDate}`
          );
          return [
            candidateDate,
            {
              date: candidateDate,
              hasAvailability: (candidateResponse.items ?? []).length > 0,
              firstStartsAt: candidateResponse.items?.[0]?.startsAt ?? null
            } satisfies AvailabilityDateSummary
          ] as const;
        })
      );
      return Object.fromEntries(entries) as Record<string, AvailabilityDateSummary>;
    },
    enabled: Boolean(locationId && serviceId && bookingSpotId && visibleCalendarMonth && shouldCheckAvailability)
  });

  const calendarMonthCells = useMemo(
    () => buildCalendarMonthCells(visibleCalendarMonth, monthAvailabilityQuery.data ?? {}),
    [monthAvailabilityQuery.data, visibleCalendarMonth]
  );

  useEffect(() => {
    if (!shouldCheckAvailability) {
      setSelectedStartsAt(String(booking?.startsAt ?? ""));
      return;
    }

    const slots = availabilityQuery.data?.items ?? [];
    if (!slots.length) {
      setSelectedStartsAt("");
      return;
    }

    const currentStartsAt = String(booking?.startsAt ?? "");
    if (selectedDate === bookingDateValue && slots.some((slot) => slot.startsAt === currentStartsAt)) {
      setSelectedStartsAt((current) =>
        current && slots.some((slot) => slot.startsAt === current) ? current : currentStartsAt
      );
      return;
    }

    setSelectedStartsAt((current) => (current && slots.some((slot) => slot.startsAt === current) ? current : slots[0].startsAt));
  }, [availabilityQuery.data, booking?.startsAt, bookingDateValue, selectedDate, shouldCheckAvailability]);

  const extrasQuoteQuery = useQuery({
    queryKey: [
      "manage-extras-quote",
      token,
      locationId,
      serviceId,
      bookingSpotId,
      selectedOptionIds,
      selectedStartsAt || booking?.startsAt,
      selectedDate || bookingDateValue,
      JSON.stringify(editableAdminDetails ?? {})
    ],
    queryFn: () =>
      apiFetch<QuotePreview>("/api/availability/quote", {
        method: "POST",
        body: JSON.stringify({
          locationId,
          serviceId,
          bookingSpotId: bookingSpotId || undefined,
          optionIds: selectedOptionIds,
          calendarProfileId: calendarProfileId || undefined,
          startsAt: selectedStartsAt || booking?.startsAt || undefined,
          serviceDate: selectedDate || bookingDateValue || undefined,
          adminDetails: editableAdminDetails,
          taxRate: Math.max(0, Number(bookingFormConfig.data?.general?.taxRate ?? 0) || 0),
          depositRate:
            Number(booking?.totalAmount ?? 0) > 0 && Number(booking?.depositAmount ?? 0) > 0
              ? Math.max(0, Math.min(1, Number(booking?.depositAmount ?? 0) / Number(booking?.totalAmount ?? 0)))
              : Math.max(0, Number(bookingFormConfig.data?.bookingForm?.depositRate ?? 0.2) || 0.2)
        })
      }),
    enabled: Boolean(isExtrasMode && bookingFormConfig.data && locationId && serviceId)
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiFetch(`/api/bookings/public/${token}/cancel`, { method: "POST" }),
    onSuccess: () => bookingQuery.refetch()
  });
  const rescheduleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedStartsAt) {
        throw new Error("Choose one of the available arrival windows.");
      }
      return apiFetch(`/api/bookings/public/${token}/reschedule`, {
        method: "POST",
        body: JSON.stringify({ startsAt: selectedStartsAt })
      });
    },
    onSuccess: () => bookingQuery.refetch()
  });
  const extrasMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; booking: ManageBookingData }>(`/api/bookings/public/${token}/update-extras`, {
        method: "POST",
        body: JSON.stringify({
          optionIds: selectedOptionIds,
          startsAt:
            isExtrasMode && extrasChanged && currentArrivalWindowStillAvailable
              ? booking?.startsAt
              : selectedStartsAt || booking?.startsAt
        })
      }),
    onSuccess: async (response) => {
      setExtrasSaveOutcome(buildExtrasSaveOutcome(booking, response.booking));
      setExtrasSaved(true);
      await bookingQuery.refetch();
    }
  });

  if (bookingQuery.isLoading) {
    return <div className="page-loading">Loading booking...</div>;
  }

  if (bookingQuery.error) {
    return <div className="page-loading">Unable to load that booking link.</div>;
  }

  const bookingData = bookingQuery.data as ManageBookingData;

  const currentArrivalWindowStillAvailable = !shouldCheckAvailability
    ? true
    : Boolean((availabilityQuery.data?.items ?? []).some((slot) => slot.startsAt === bookingData.startsAt));
  const shouldShowExtrasArrivalWindowPicker = isExtrasMode && extrasChanged && !currentArrivalWindowStillAvailable;
  const updatedPaymentSummary = summarizePortalPayment({
    totalAmount: Number(extrasQuoteQuery.data?.total ?? bookingData.totalAmount ?? 0),
    amountPaid: Number(bookingData.amountPaid ?? 0),
    amountDue: Math.max(
      0,
      Number(extrasQuoteQuery.data?.total ?? bookingData.totalAmount ?? 0) - Number(bookingData.amountPaid ?? 0)
    )
  });
  const shouldShowExtrasPaymentCard = Boolean(
    isExtrasMode && extrasSaved && (extrasSaveOutcome?.updatedAmountDue ?? Number(bookingData.amountDue ?? 0)) > 0
  );

  if (isExtrasMode) {
    return (
      <div className="marketing-shell">
        <div className="stack">
          <Card eyebrow="Self-service" title={bookingData.publicReference}>
            <p>
              {bookingData.customerName} is currently booked for{" "}
              <strong>{formatDateLabelInTimeZone(bookingData.startsAt, bookingTimeZone)}</strong> with arrival window{" "}
              <strong>{formatPortalArrivalWindow(bookingData.startsAt, bookingTimeZone)}</strong>.
            </p>
            <p className="card-copy">
              Start with the extras already on this appointment, then add or remove simple extras. We only ask you to
              choose a new arrival window if the updated visit no longer fits the current one.
            </p>
            <div className="hero__actions">
              <Link className="button button--secondary" to={`/manage/${token}`}>
                Back to manage booking
              </Link>
              <Button variant="secondary" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>
                {cancelMutation.isPending ? "Cancelling..." : "Cancel booking"}
              </Button>
            </div>
          </Card>

          <Card eyebrow="Current extras" title="What is already selected">
            {currentExtrasDisplay.length ? (
              <div className="portal-pill-list">
                {currentExtrasDisplay.map((entry) => (
                  <span key={entry} className="portal-pill">
                    {entry}
                  </span>
                ))}
              </div>
            ) : (
              <p className="card-copy">No extras are currently selected on this appointment.</p>
            )}
            {currentRecurringExtras.length ? (
              <div className="stack stack--tight">
                <p className="eyebrow">Recurring extras</p>
                <div className="portal-pill-list">
                  {currentRecurringExtras.map((entry) => (
                    <span key={entry} className="portal-pill">
                      {entry}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </Card>

          <Card eyebrow="Modify extras" title="Choose extras">
            {bookingFormConfig.isLoading ? (
              <p className="card-copy">Loading available extras...</p>
            ) : bookingFormConfig.error ? (
              <p className="error-text">{(bookingFormConfig.error as Error).message}</p>
            ) : editableOptions.length ? (
              <div className="stack">
                <div className="booking-checkbox-grid">
                  {editableOptions.map((option) => {
                    const isSelected = selectedOptionIds.includes(option.id);
                    const meta = formatManageBookingOptionMeta(option);
                    return (
                      <label key={option.id} className={`service-choice-card ${isSelected ? "is-selected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(event) => {
                            resetExtrasSaveState();
                            setSelectedOptionIds((current) => toggleStringSelection(current, option.id, event.target.checked));
                          }}
                        />
                        <div className="service-choice-copy">
                          <strong>{option.name}</strong>
                          {option.description ? <p className="card-copy">{option.description}</p> : null}
                          {meta ? <p className="card-copy">{meta}</p> : null}
                        </div>
                      </label>
                    );
                  })}
                </div>
                {lockedSelectedOptionNames.length ? (
                  <p className="card-copy">
                    Some extras on this appointment still need support to edit: <strong>{lockedSelectedOptionNames.join(", ")}</strong>.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="card-copy">This service does not currently have editable online extras.</p>
            )}
          </Card>

          <Card eyebrow="Review" title="Updated visit summary">
            <div className="stack">
                <div className="booking-meta-grid">
                <div className="booking-meta-card">
                  <p className="eyebrow">Arrival window</p>
                  <strong>
                    {shouldShowExtrasArrivalWindowPicker
                      ? "Choose a new arrival window below"
                      : formatPortalArrivalWindow(bookingData.startsAt, bookingTimeZone)}
                  </strong>
                  <p>{shouldShowExtrasArrivalWindowPicker ? "The updated extras no longer fit the current visit slot." : "Your current arrival window still works."}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Updated total</p>
                  <strong>
                    {extrasQuoteQuery.data ? formatCurrency(extrasQuoteQuery.data.total) : formatCurrency(bookingData.totalAmount)}
                  </strong>
                  <p>{updatedPaymentSummary.detail}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Estimated duration</p>
                  <strong>
                    {extrasQuoteQuery.data ? `${extrasQuoteQuery.data.durationMinutes} minutes` : `${bookingData.items?.[0]?.durationMinutes ?? 0} minutes`}
                  </strong>
                </div>
              </div>

              {addedExtraNames.length || removedExtraNames.length ? (
                <div className="stack stack--tight">
                  {addedExtraNames.length ? (
                    <p className="card-copy">
                      Added: <strong>{addedExtraNames.join(", ")}</strong>
                    </p>
                  ) : null}
                  {removedExtraNames.length ? (
                    <p className="card-copy">
                      Removed: <strong>{removedExtraNames.join(", ")}</strong>
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="card-copy">No extras changes selected yet.</p>
              )}

              {extrasQuoteQuery.data?.optionNames?.length ? (
                <div className="portal-pill-list">
                  {extrasQuoteQuery.data.optionNames.map((entry) => (
                    <span key={entry} className="portal-pill">
                      {entry}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </Card>

          {shouldShowExtrasArrivalWindowPicker ? (
            <Card eyebrow="Arrival window" title="Choose a new arrival window">
              <Field label="Service date" hint="Only dates with bookable arrival windows are enabled.">
                {monthAvailabilityQuery.isLoading && !monthAvailabilityQuery.data ? (
                  <div className="service-choice-card" aria-busy="true">
                    <div className="service-choice-copy">
                      <strong>Loading available dates...</strong>
                      <p className="card-copy">We're checking this month for live arrival windows.</p>
                    </div>
                  </div>
                ) : monthAvailabilityQuery.isError ? (
                  <div className="service-choice-card" aria-disabled="true">
                    <div className="service-choice-copy">
                      <strong>We couldn't load the calendar right now.</strong>
                      <p className="card-copy">Please try again in a moment.</p>
                    </div>
                  </div>
                ) : (
                  <div className="booking-calendar">
                    <div className="booking-calendar__header">
                      <button
                        type="button"
                        className="booking-calendar__nav"
                        onClick={() => setVisibleCalendarMonth((current) => shiftIsoMonth(current, -1))}
                        aria-label="Show previous month"
                      >
                        ‹
                      </button>
                      <strong className="booking-calendar__month">{formatMonthLabel(visibleCalendarMonth)}</strong>
                      <button
                        type="button"
                        className="booking-calendar__nav"
                        onClick={() => setVisibleCalendarMonth((current) => shiftIsoMonth(current, 1))}
                        aria-label="Show next month"
                      >
                        ›
                      </button>
                    </div>

                    <div className="booking-calendar__weekdays" aria-hidden="true">
                      {CALENDAR_WEEKDAY_LABELS.map((label) => (
                        <span className="booking-calendar__weekday" key={label}>
                          {label}
                        </span>
                      ))}
                    </div>

                    <div className="booking-calendar__grid">
                      {calendarMonthCells.map((cell) =>
                        cell.date ? (
                          <button
                            type="button"
                            key={cell.key}
                            className={[
                              "booking-calendar__day",
                              cell.hasAvailability ? "" : "is-unavailable",
                              cell.date === selectedDate ? "is-selected" : "",
                              cell.isToday ? "is-today" : ""
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={() => {
                              if (cell.hasAvailability) {
                                resetExtrasSaveState();
                                setSelectedDate(cell.date ?? "");
                              }
                            }}
                            disabled={!cell.hasAvailability}
                            aria-pressed={cell.date === selectedDate}
                          >
                            <span>{cell.dayNumber}</span>
                          </button>
                        ) : (
                          <span className="booking-calendar__day booking-calendar__day--blank" key={cell.key} aria-hidden="true" />
                        )
                      )}
                    </div>
                  </div>
                )}
              </Field>

              <Field label="Available arrival windows" hint="Only live bookable arrival windows appear here.">
                {availabilityQuery.isLoading && !availabilityQuery.data ? (
                  <div className="service-choice-card" aria-busy="true">
                    <div className="service-choice-copy">
                      <strong>Loading live arrival windows...</strong>
                      <p className="card-copy">We're checking current availability for the selected date.</p>
                    </div>
                  </div>
                ) : availabilityQuery.isError ? (
                  <div className="service-choice-card" aria-disabled="true">
                    <div className="service-choice-copy">
                      <strong>We couldn't load arrival windows right now.</strong>
                      <p className="card-copy">Please try another date or refresh the page.</p>
                    </div>
                  </div>
                ) : availabilityQuery.data?.items?.length ? (
                  <div className="stack">
                    {availabilityQuery.data.items.map((slot) => {
                      const checked = selectedStartsAt === slot.startsAt;
                      return (
                        <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={slot.startsAt}>
                          <input
                            type="radio"
                            value={slot.startsAt}
                            checked={checked}
                            onChange={(event) => {
                              resetExtrasSaveState();
                              setSelectedStartsAt(event.target.value);
                            }}
                          />
                          <div className="service-choice-copy">
                            <strong>{getAvailabilityOptionLabel(slot, bookingTimeZone)}</strong>
                            <p className="card-copy">Live availability</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="service-choice-card" aria-disabled="true">
                    <div className="service-choice-copy">
                      <strong>No arrival windows are currently available for this date.</strong>
                      <p className="card-copy">Pick another available date from the calendar above.</p>
                    </div>
                  </div>
                )}
              </Field>
            </Card>
          ) : null}

          <Card eyebrow="Confirm" title="Save these extras changes">
            <div className="stack">
              <p className="card-copy">
                {shouldShowExtrasArrivalWindowPicker
                  ? "The updated extras require a new arrival window before we can save your changes."
                  : "We'll keep the current arrival window and update this appointment with the extras you selected."}
              </p>
              <Button
                onClick={() => extrasMutation.mutate()}
                disabled={
                  !extrasChanged ||
                  editableOptions.length === 0 ||
                  (shouldShowExtrasArrivalWindowPicker && !selectedStartsAt) ||
                  extrasMutation.isPending
                }
              >
                {extrasMutation.isPending
                  ? "Saving..."
                  : shouldShowExtrasArrivalWindowPicker
                    ? "Save extras and reschedule"
                    : "Save extras"}
              </Button>
              {extrasSaveOutcome ? (
                <div className={`manage-outcome manage-outcome--${extrasSaveOutcome.kind}`}>
                  <strong>{extrasSaveOutcome.title}</strong>
                  <p>{extrasSaveOutcome.body}</p>
                  <p className="manage-outcome__detail">{extrasSaveOutcome.detail}</p>
                </div>
              ) : null}
              {extrasMutation.error ? <p className="error-text">{(extrasMutation.error as Error).message}</p> : null}
            </div>
          </Card>

          {shouldShowExtrasPaymentCard ? <StripePaymentCard token={token} onPaymentSuccess={() => bookingQuery.refetch()} /> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="marketing-shell">
      <div className="stack">
        <Card eyebrow="Self-service" title={bookingData.publicReference}>
          <p>
            {bookingData.customerName} is currently booked for{" "}
            <strong>{formatDateLabelInTimeZone(bookingData.startsAt, bookingTimeZone)}</strong> with arrival window{" "}
            <strong>{formatPortalArrivalWindow(bookingData.startsAt, bookingTimeZone)}</strong>.
          </p>
          <p className="card-copy">Choose a new date and then pick from live arrival windows shown in {bookingTimeZone}.</p>
          <div className="hero__actions">
            <Link className="button button--secondary" to={`/manage/${token}?mode=extras`}>
              Modify extras
            </Link>
            <Button variant="secondary" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>
              {cancelMutation.isPending ? "Cancelling..." : "Cancel booking"}
            </Button>
          </div>
          <Field label="Service date" hint="Only dates with bookable arrival windows are enabled.">
            {monthAvailabilityQuery.isLoading && !monthAvailabilityQuery.data ? (
              <div className="service-choice-card" aria-busy="true">
                <div className="service-choice-copy">
                  <strong>Loading available dates...</strong>
                  <p className="card-copy">We’re checking this month for live arrival windows.</p>
                </div>
              </div>
            ) : monthAvailabilityQuery.isError ? (
              <div className="service-choice-card" aria-disabled="true">
                <div className="service-choice-copy">
                  <strong>We couldn’t load the reschedule calendar right now.</strong>
                  <p className="card-copy">Please try again in a moment.</p>
                </div>
              </div>
            ) : (
              <div className="booking-calendar">
                <div className="booking-calendar__header">
                  <button
                    type="button"
                    className="booking-calendar__nav"
                    onClick={() => setVisibleCalendarMonth((current) => shiftIsoMonth(current, -1))}
                    aria-label="Show previous month"
                  >
                    ‹
                  </button>
                  <strong className="booking-calendar__month">{formatMonthLabel(visibleCalendarMonth)}</strong>
                  <button
                    type="button"
                    className="booking-calendar__nav"
                    onClick={() => setVisibleCalendarMonth((current) => shiftIsoMonth(current, 1))}
                    aria-label="Show next month"
                  >
                    ›
                  </button>
                </div>

                <div className="booking-calendar__weekdays" aria-hidden="true">
                  {CALENDAR_WEEKDAY_LABELS.map((label) => (
                    <span className="booking-calendar__weekday" key={label}>
                      {label}
                    </span>
                  ))}
                </div>

                <div className="booking-calendar__grid">
                  {calendarMonthCells.map((cell) =>
                    cell.date ? (
                      <button
                        type="button"
                        key={cell.key}
                        className={[
                          "booking-calendar__day",
                          cell.hasAvailability ? "" : "is-unavailable",
                          cell.date === selectedDate ? "is-selected" : "",
                          cell.isToday ? "is-today" : ""
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => {
                          if (cell.hasAvailability) {
                            setSelectedDate(cell.date ?? "");
                          }
                        }}
                        disabled={!cell.hasAvailability}
                        aria-pressed={cell.date === selectedDate}
                      >
                        <span>{cell.dayNumber}</span>
                      </button>
                    ) : (
                      <span className="booking-calendar__day booking-calendar__day--blank" key={cell.key} aria-hidden="true" />
                    )
                  )}
                </div>
              </div>
            )}
          </Field>

          <Field label="Available arrival windows" hint={availabilityQuery.data?.items?.length ? undefined : "Only live bookable arrival windows appear here."}>
            {availabilityQuery.isLoading && !availabilityQuery.data ? (
              <div className="service-choice-card" aria-busy="true">
                <div className="service-choice-copy">
                  <strong>Loading live arrival windows...</strong>
                  <p className="card-copy">We’re checking current availability for the selected date.</p>
                </div>
              </div>
            ) : availabilityQuery.isError ? (
              <div className="service-choice-card" aria-disabled="true">
                <div className="service-choice-copy">
                  <strong>We couldn’t load arrival windows right now.</strong>
                  <p className="card-copy">Please try another date or refresh the page.</p>
                </div>
              </div>
            ) : availabilityQuery.data?.items?.length ? (
              <div className="stack">
                {availabilityQuery.data.items.map((slot) => {
                  const checked = selectedStartsAt === slot.startsAt;
                  return (
                    <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={slot.startsAt}>
                      <input
                        type="radio"
                        value={slot.startsAt}
                        checked={checked}
                        onChange={(event) => setSelectedStartsAt(event.target.value)}
                      />
                      <div className="service-choice-copy">
                        <strong>{getAvailabilityOptionLabel(slot, bookingTimeZone)}</strong>
                        <p className="card-copy">
                          {slot.startsAt === bookingData.startsAt ? "Current arrival window" : "Live availability"}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="service-choice-card" aria-disabled="true">
                <div className="service-choice-copy">
                  <strong>No arrival windows are currently available for this date.</strong>
                  <p className="card-copy">Pick another available date from the calendar above.</p>
                </div>
              </div>
            )}
          </Field>
          <div className="stack stack--tight">
            <p className="card-copy">Need to change the extras on this visit instead? Open the extras editor from here.</p>
            <div className="hero__actions">
              <Button
                onClick={() => rescheduleMutation.mutate()}
                disabled={!selectedStartsAt || selectedStartsAt === bookingData.startsAt || rescheduleMutation.isPending}
              >
                {rescheduleMutation.isPending ? "Rescheduling..." : "Reschedule"}
              </Button>
              <Link className="button button--secondary" to={`/manage/${token}?mode=extras`}>
                Modify extras
              </Link>
            </div>
          </div>
          {rescheduleMutation.error ? <p className="error-text">{(rescheduleMutation.error as Error).message}</p> : null}
        </Card>
        <StripePaymentCard token={token} onPaymentSuccess={() => bookingQuery.refetch()} />
      </div>
    </div>
  );
}

function buildCustomerPortalProfileDefaults(overview: CustomerPortalOverview) {
  return {
    firstName: overview.customer.firstName,
    lastName: overview.customer.lastName,
    phone: overview.customer.phone,
    secondaryPhone: overview.customer.secondaryPhone ?? "",
    emailPreferences: {
      smsOptInStatus: overview.customer.emailPreferences?.smsOptInStatus ?? false,
      smsReminderOptInStatus: overview.customer.emailPreferences?.smsReminderOptInStatus ?? false,
      emailOptInStatus: overview.customer.emailPreferences?.emailOptInStatus ?? true,
      bookingConfirmation: overview.customer.emailPreferences?.bookingConfirmation ?? true,
      reminders: overview.customer.emailPreferences?.reminders ?? true,
      receipts: overview.customer.emailPreferences?.receipts ?? true,
      marketing: overview.customer.emailPreferences?.marketing ?? true
    },
    address: {
      label: overview.customer.address?.label ?? "Service address",
      line1: overview.customer.address?.line1 ?? "",
      line2: overview.customer.address?.line2 ?? "",
      unitNumber: overview.customer.address?.unitNumber ?? "",
      city: overview.customer.address?.city ?? "",
      state: overview.customer.address?.state ?? "",
      postalCode: overview.customer.address?.postalCode ?? "",
      country: overview.customer.address?.country ?? "US",
      instructions: overview.customer.address?.instructions ?? "",
      isPrimary: true
    }
  };
}

function AccountLoginPage() {
  const [submittedEmail, setSubmittedEmail] = useState("");
  const form = useForm({
    resolver: zodResolver(customerMagicLinkRequestSchema),
    defaultValues: {
      email: "",
      turnstileToken: ""
    }
  });
  const publicConfig = useQuery({
    queryKey: ["public-config"],
    queryFn: () => apiFetch<PublicClientConfig>("/api/public-config")
  });
  const requestLinkMutation = useMutation({
    mutationFn: (values: { email: string; turnstileToken?: string }) =>
      apiFetch<{ ok: boolean; message: string }>("/api/customer-auth/request-link", {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: (_result, values) => {
      setSubmittedEmail(values.email);
    }
  });

  return (
    <div className="auth-shell">
      <Card eyebrow="My Account" title="Sign in with email">
        {requestLinkMutation.isSuccess ? (
          <div className="stack">
            <p>
              If an account exists for <strong>{submittedEmail}</strong>, we sent a secure sign-in link. Check your inbox
              and open the latest email to continue.
            </p>
            <div className="hero__actions">
              <Button variant="secondary" onClick={() => requestLinkMutation.reset()}>
                Send another link
              </Button>
              <Link className="button button--secondary" to="/">
                Back home
              </Link>
            </div>
          </div>
        ) : (
          <form className="stack" onSubmit={form.handleSubmit((values) => requestLinkMutation.mutate(values))}>
            <Field label="Email">
              <input type="email" {...form.register("email")} />
            </Field>
            <Field label="Verification" hint="Complete the Turnstile check to receive your secure sign-in link.">
              <TurnstileField
                siteKey={publicConfig.data?.turnstileSiteKey}
                token={form.watch("turnstileToken") ?? ""}
                onTokenChange={(token) => form.setValue("turnstileToken", token, { shouldValidate: true })}
              />
            </Field>
            {requestLinkMutation.error ? <p className="error-text">{(requestLinkMutation.error as Error).message}</p> : null}
            <Button type="submit">{requestLinkMutation.isPending ? "Sending..." : "Email me a sign-in link"}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}

function AccountVerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const startedRef = useRef(false);
  const consumeLinkMutation = useMutation({
    mutationFn: (value: { token: string }) =>
      apiFetch<{ customer: CustomerPortalSession }>("/api/customer-auth/consume-link", {
        method: "POST",
        body: JSON.stringify(value)
      }),
    onSuccess: () => {
      navigate("/account", { replace: true });
    }
  });

  useEffect(() => {
    if (!token || startedRef.current) {
      return;
    }

    startedRef.current = true;
    consumeLinkMutation.mutate({ token });
  }, [consumeLinkMutation, token]);

  return (
    <div className="auth-shell">
      <Card eyebrow="My Account" title="Signing you in">
        {!token ? (
          <div className="stack">
            <p className="error-text">That sign-in link is missing a token.</p>
            <Link className="button button--secondary" to="/account/login">
              Request a new link
            </Link>
          </div>
        ) : consumeLinkMutation.isError ? (
          <div className="stack">
            <p className="error-text">{(consumeLinkMutation.error as Error).message}</p>
            <Link className="button button--secondary" to="/account/login">
              Request a new link
            </Link>
          </div>
        ) : (
          <p>Please wait while we open your account.</p>
        )}
      </Card>
    </div>
  );
}

function AccountPortalPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authQuery = useQuery({
    queryKey: ["customer-auth"],
    queryFn: () => apiFetch<{ customer: CustomerPortalSession }>("/api/customer-auth/me")
  });
  const overviewQuery = useQuery({
    queryKey: ["customer-portal-overview"],
    queryFn: () => apiFetch<CustomerPortalOverview>("/api/customer-portal/overview"),
    enabled: authQuery.isSuccess
  });
  const profileForm = useForm({
    resolver: zodResolver(customerPortalProfileUpdateSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      secondaryPhone: "",
      emailPreferences: {
        smsOptInStatus: false,
        smsReminderOptInStatus: false,
        emailOptInStatus: true,
        bookingConfirmation: true,
        reminders: true,
        receipts: true,
        marketing: true
      },
      address: {
        label: "Service address",
        line1: "",
        line2: "",
        unitNumber: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
        instructions: "",
        isPrimary: true
      }
    }
  });

  useEffect(() => {
    if (!overviewQuery.data) {
      return;
    }

    profileForm.reset(buildCustomerPortalProfileDefaults(overviewQuery.data));
  }, [overviewQuery.data, profileForm]);

  const updateProfileMutation = useMutation({
    mutationFn: (values: any) =>
      apiFetch<CustomerPortalOverview>("/api/customer-portal/profile", {
        method: "PUT",
        body: JSON.stringify(values)
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["customer-portal-overview"], data);
      profileForm.reset(buildCustomerPortalProfileDefaults(data));
    }
  });
  const manageLinkMutation = useMutation({
    mutationFn: (bookingId: string) =>
      apiFetch<{ manageToken: string; manageUrl: string }>(`/api/customer-portal/bookings/${bookingId}/manage-link`, {
        method: "POST"
      }),
    onSuccess: (result) => {
      navigate(result.manageUrl);
    }
  });
  const logoutMutation = useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>("/api/customer-auth/logout", { method: "POST" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer-auth"] });
      navigate("/account/login", { replace: true });
    }
  });

  if (authQuery.isLoading || (authQuery.isSuccess && overviewQuery.isLoading)) {
    return <div className="page-loading">Loading your account...</div>;
  }

  if (authQuery.error) {
    return <Navigate to="/account/login" replace />;
  }

  if (overviewQuery.error || !overviewQuery.data) {
    return (
      <div className="marketing-shell">
        <Card eyebrow="My Account" title="We couldn't load your account">
          <p className="error-text">{overviewQuery.error ? (overviewQuery.error as Error).message : "Please try again."}</p>
        </Card>
      </div>
    );
  }

  const profile = overviewQuery.data.customer;
  const support = overviewQuery.data.support;
  const watchedEmailPreferences = profileForm.watch("emailPreferences") ?? {};

  return (
    <div className="marketing-shell">
      <div className="stack">
        <Card eyebrow="My Account" title={profile.name || "Customer account"}>
          <div className="hero__actions">
            <Link className="button button--secondary" to="/book">
              Book another service
            </Link>
            <Button variant="secondary" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
              {logoutMutation.isPending ? "Signing out..." : "Sign out"}
            </Button>
          </div>
          <p className="card-copy">{profile.email}</p>
        </Card>

        <Card eyebrow="Upcoming" title="Upcoming appointments">
          {overviewQuery.data.upcomingBookings.length ? (
            <div className="stack">
              {overviewQuery.data.upcomingBookings.map((booking) => (
                <div key={booking.id} className="card card--inset">
                  <div className="stack" style={{ gap: "0.75rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <p className="eyebrow">{booking.publicReference}</p>
                        <h3 style={{ margin: 0 }}>{booking.serviceNames || "Service visit"}</h3>
                        <p className="card-copy">{formatDateLabel(booking.startsAt)}</p>
                        <p className="card-copy">Arrival window {formatPortalArrivalWindow(booking.startsAt, booking.timeZone)}</p>
                      </div>
                      <Badge tone={badgeToneForStatus(booking.status)}>{booking.status}</Badge>
                    </div>
                    <p className="card-copy">
                      {booking.locationName ? `${booking.locationName} - ` : ""}
                      {booking.serviceAddress || "Address on file"}
                    </p>
                    <div className="portal-summary-row">
                      <div>
                        <strong>{summarizePortalPayment(booking).label}</strong>
                        <p className="card-copy">{summarizePortalPayment(booking).detail}</p>
                      </div>
                      <div className="hero__actions">
                        <Button onClick={() => manageLinkMutation.mutate(booking.id)} disabled={manageLinkMutation.isPending}>
                          {manageLinkMutation.isPending && manageLinkMutation.variables === booking.id ? "Opening..." : "Manage booking"}
                        </Button>
                        <Link className="button button--secondary" to={`/account/bookings/${booking.id}`}>
                          View details
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {manageLinkMutation.error ? <p className="error-text">{(manageLinkMutation.error as Error).message}</p> : null}
            </div>
          ) : (
            <p className="card-copy">No upcoming appointments right now.</p>
          )}
        </Card>

        <Card eyebrow="History" title="Past appointments">
          {overviewQuery.data.pastBookings.length ? (
            <div className="stack">
              {overviewQuery.data.pastBookings.map((booking) => (
                <div key={booking.id} className="card card--inset">
                  <div className="stack" style={{ gap: "0.75rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <p className="eyebrow">{booking.publicReference}</p>
                        <h3 style={{ margin: 0 }}>{booking.serviceNames || "Service visit"}</h3>
                        <p className="card-copy">{formatDateLabel(booking.startsAt)}</p>
                        <p className="card-copy">Arrival window {formatPortalArrivalWindow(booking.startsAt, booking.timeZone)}</p>
                      </div>
                      <Badge tone={badgeToneForStatus(booking.status)}>{booking.status}</Badge>
                    </div>
                    <p className="card-copy">
                      {booking.locationName ? `${booking.locationName} - ` : ""}
                      {booking.serviceAddress || "Address on file"}
                    </p>
                    <div className="hero__actions">
                      <Link className="button button--primary" to={`/book?rebook=${booking.id}`}>
                        Book same service
                      </Link>
                      <Link className="button button--secondary" to={`/account/bookings/${booking.id}`}>
                        View details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="card-copy">No appointment history yet.</p>
          )}
        </Card>

        <form className="stack" onSubmit={profileForm.handleSubmit((values) => updateProfileMutation.mutate(values))}>
          <Card eyebrow="Profile" title="Contact and service address">
            <div className="stack">
              <div className="grid two">
                <Field label="First name">
                  <input {...profileForm.register("firstName")} />
                </Field>
                <Field label="Last name">
                  <input {...profileForm.register("lastName")} />
                </Field>
              </div>
              <div className="grid two">
                <Field label="Email">
                  <input value={profile.email} readOnly />
                </Field>
                <Field label="Phone">
                  <input {...profileForm.register("phone")} />
                </Field>
              </div>
              <div className="grid two">
                <Field label="Secondary phone">
                  <input {...profileForm.register("secondaryPhone")} />
                </Field>
                <Field label="Address label">
                  <input {...profileForm.register("address.label")} />
                </Field>
              </div>
              <Field label="Street address">
                <input {...profileForm.register("address.line1")} />
              </Field>
              <div className="grid two">
                <Field label="Line 2">
                  <input {...profileForm.register("address.line2")} />
                </Field>
                <Field label="Unit number">
                  <input {...profileForm.register("address.unitNumber")} />
                </Field>
              </div>
              <div className="grid three">
                <Field label="City">
                  <input {...profileForm.register("address.city")} />
                </Field>
                <Field label="State">
                  <input {...profileForm.register("address.state")} />
                </Field>
                <Field label="ZIP code">
                  <input {...profileForm.register("address.postalCode")} />
                </Field>
              </div>
              <Field label="Entry instructions">
                <textarea rows={4} {...profileForm.register("address.instructions")} />
              </Field>
            </div>
          </Card>

          <Card eyebrow="Notifications" title="Notification preferences">
            <div className="stack" style={{ gap: "0.75rem" }}>
              {[
                ["emailOptInStatus", "Allow email contact"],
                ["bookingConfirmation", "Booking confirmation emails"],
                ["reminders", "Reminder emails"],
                ["receipts", "Receipt emails"],
                ["marketing", "Marketing emails"],
                ["smsOptInStatus", "Allow text messages"],
                ["smsReminderOptInStatus", "Text reminder messages"]
              ].map(([key, label]) => (
                <label key={key} className={`service-choice-card ${watchedEmailPreferences[key as keyof typeof watchedEmailPreferences] ? "is-selected" : ""}`}>
                  <input
                    type="checkbox"
                    checked={Boolean(watchedEmailPreferences[key as keyof typeof watchedEmailPreferences])}
                    onChange={(event) =>
                      profileForm.setValue(`emailPreferences.${key}` as any, event.target.checked, { shouldDirty: true })
                    }
                  />
                  <div className="service-choice-copy">
                    <strong>{label}</strong>
                  </div>
                </label>
              ))}
              {updateProfileMutation.error ? <p className="error-text">{(updateProfileMutation.error as Error).message}</p> : null}
              <Button type="submit" disabled={updateProfileMutation.isPending}>
                {updateProfileMutation.isPending ? "Saving..." : "Save profile"}
              </Button>
            </div>
          </Card>
        </form>

        <Card eyebrow="More" title="More ways we can help">
          <div className="stack stack--tight">
            <div className="hero__actions">
              {support.email ? (
                <a className="button button--secondary" href={`mailto:${support.email}`}>
                  Contact support
                </a>
              ) : null}
              {support.phone ? (
                <a className="button button--secondary" href={`tel:${support.phone}`}>
                  Call support
                </a>
              ) : null}
            </div>
            <div className="portal-coming-soon-list">
              <Link className="portal-coming-soon" to="/gift-cards/purchase">
                Buy a gift card
              </Link>
              <span className="portal-coming-soon">Refer a friend coming soon</span>
            </div>
            {!support.email && !support.phone ? (
              <p className="card-copy">Support contact links will appear here once they are configured.</p>
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  );
}

function AccountBookingDetailsPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const authQuery = useQuery({
    queryKey: ["customer-auth"],
    queryFn: () => apiFetch<{ customer: CustomerPortalSession }>("/api/customer-auth/me")
  });
  const detailQuery = useQuery({
    queryKey: ["customer-portal-booking", id],
    queryFn: () => apiFetch<CustomerPortalBookingDetail>(`/api/customer-portal/bookings/${id}`),
    enabled: authQuery.isSuccess && Boolean(id)
  });
  const manageLinkMutation = useMutation({
    mutationFn: (action: "manage" | "reschedule" | "cancel" | "modify" | "payment") =>
      apiFetch<{ manageToken: string; manageUrl: string }>(`/api/customer-portal/bookings/${id}/manage-link`, {
        method: "POST",
        body: JSON.stringify({ action })
      }),
    onSuccess: (result) => {
      navigate(result.manageUrl);
    }
  });

  if (authQuery.isLoading || (authQuery.isSuccess && detailQuery.isLoading)) {
    return <div className="page-loading">Loading appointment details...</div>;
  }

  if (authQuery.error) {
    return <Navigate to="/account/login" replace />;
  }

  if (detailQuery.error || !detailQuery.data) {
    return (
      <div className="marketing-shell">
        <div className="stack">
          <Card eyebrow="My Account" title="We couldn't load this appointment">
            <p className="error-text">{detailQuery.error ? (detailQuery.error as Error).message : "Please try again."}</p>
            <div className="hero__actions">
              <Link className="button button--secondary" to="/account">
                Back to account
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const detail = detailQuery.data;
  const paymentSummary = summarizePortalPayment(detail);
  const policyMessage = buildPortalPolicyMessage(detail);
  const extras = parsePortalSummaryLines(detail.extrasSummary);
  const recurringExtras = parsePortalSummaryLines(detail.recurringExtrasSummary);
  const support = detail.policy.support;

  return (
    <div className="marketing-shell">
      <div className="stack">
        <Card eyebrow="Appointment" title={detail.publicReference}>
          <div className="hero__actions">
            <Link className="button button--secondary" to="/account">
              Back to account
            </Link>
            <Link className="button button--primary" to={`/book?rebook=${detail.id}`}>
              Book same service
            </Link>
            {detail.actions.canManageOnline ? (
              <Button onClick={() => manageLinkMutation.mutate("manage")} disabled={manageLinkMutation.isPending}>
                {manageLinkMutation.isPending && manageLinkMutation.variables === "manage" ? "Opening..." : "Manage booking"}
              </Button>
            ) : null}
          </div>
          <div className="booking-meta-grid">
            <div className="booking-meta-card">
              <p className="eyebrow">Appointment</p>
              <strong>{formatDateLabel(detail.startsAt)}</strong>
              <p>Arrival window {formatPortalArrivalWindow(detail.startsAt, detail.timeZone)}</p>
              <p>{detail.frequencyLabel}</p>
              <p>{detail.status}</p>
            </div>
            <div className="booking-meta-card">
              <p className="eyebrow">Address</p>
              <strong>{detail.serviceAddress || "Address on file"}</strong>
              <p>{detail.locationName || "Location on file"}</p>
            </div>
            <div className="booking-meta-card">
              <p className="eyebrow">Payment</p>
              <strong>{paymentSummary.label}</strong>
              <p>{paymentSummary.detail}</p>
            </div>
          </div>
        </Card>

        <Card eyebrow="Service" title="Visit details">
          <div className="stack">
            <div className="booking-meta-grid">
              <div className="booking-meta-card">
                <p className="eyebrow">Service</p>
                <strong>{detail.serviceNames || "Service visit"}</strong>
                <p>{detail.frequencyLabel}</p>
              </div>
              <div className="booking-meta-card">
                <p className="eyebrow">Total</p>
                <strong>{formatCurrency(detail.totalAmount)}</strong>
                <p>{detail.paymentStatus}</p>
              </div>
              <div className="booking-meta-card">
                <p className="eyebrow">Balance</p>
                <strong>{detail.amountDue > 0 ? formatCurrency(detail.amountDue) : "Paid"}</strong>
                <p>{detail.amountDue > 0 ? `${formatCurrency(detail.amountPaid)} received` : "Nothing due right now"}</p>
              </div>
            </div>
            {detail.items.length ? (
              <div className="portal-detail-list">
                {detail.items.map((item) => (
                  <div key={item.id} className="portal-detail-list__item">
                    <div>
                      <strong>{item.name}</strong>
                      <p className="card-copy">{item.durationMinutes} minutes</p>
                    </div>
                    <strong>{formatCurrency(item.totalPriceAmount)}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="card-copy">We'll show service line items here as they become available.</p>
            )}
            {extras.length ? (
              <div className="stack stack--tight">
                <p className="eyebrow">Extras</p>
                <div className="portal-pill-list">
                  {extras.map((entry) => (
                    <span key={entry} className="portal-pill">
                      {entry}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {recurringExtras.length ? (
              <div className="stack stack--tight">
                <p className="eyebrow">Recurring extras</p>
                <div className="portal-pill-list">
                  {recurringExtras.map((entry) => (
                    <span key={entry} className="portal-pill">
                      {entry}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </Card>

        <Card eyebrow="Property" title="Address and access">
          <div className="stack">
            <div className="booking-meta-grid">
              <div className="booking-meta-card">
                <p className="eyebrow">Service address</p>
                <strong>{detail.serviceAddress || "Address on file"}</strong>
                <p>{detail.locationName || "Location on file"}</p>
              </div>
              <div className="booking-meta-card">
                <p className="eyebrow">Entry instructions</p>
                <strong>{detail.addressInstructions || "No entry instructions on file"}</strong>
              </div>
              <div className="booking-meta-card">
                <p className="eyebrow">Access details</p>
                <strong>{detail.entryInformation || "No special access details on file"}</strong>
              </div>
            </div>
            {detail.notes ? (
              <div className="stack stack--tight">
                <p className="eyebrow">Appointment notes</p>
                <p>{detail.notes}</p>
              </div>
            ) : null}
          </div>
        </Card>

        <Card eyebrow="Self-service" title="Manage this appointment">
          <div className="stack">
            <p className="card-copy">{policyMessage}</p>
            {detail.policy.cancellationPolicy ? <p className="card-copy">Cancellation policy: {detail.policy.cancellationPolicy}</p> : null}
            <div className="hero__actions">
              <Button
                variant="secondary"
                onClick={() => manageLinkMutation.mutate("reschedule")}
                disabled={!detail.actions.canModifyOnline || manageLinkMutation.isPending}
              >
                {manageLinkMutation.isPending && manageLinkMutation.variables === "reschedule" ? "Opening..." : "Reschedule"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => manageLinkMutation.mutate("cancel")}
                disabled={!detail.actions.canModifyOnline || manageLinkMutation.isPending}
              >
                {manageLinkMutation.isPending && manageLinkMutation.variables === "cancel" ? "Opening..." : "Cancel"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => manageLinkMutation.mutate("modify")}
                disabled={!detail.actions.canModifyOnline || manageLinkMutation.isPending}
              >
                {manageLinkMutation.isPending && manageLinkMutation.variables === "modify" ? "Opening..." : "Modify extras"}
              </Button>
              {detail.actions.canCompletePayment ? (
                <Button onClick={() => manageLinkMutation.mutate("payment")} disabled={manageLinkMutation.isPending}>
                  {manageLinkMutation.isPending && manageLinkMutation.variables === "payment" ? "Opening..." : "Complete payment"}
                </Button>
              ) : null}
            </div>
            {manageLinkMutation.error ? <p className="error-text">{(manageLinkMutation.error as Error).message}</p> : null}
            <div className="hero__actions">
              {support.email ? (
                <a className="button button--secondary" href={`mailto:${support.email}`}>
                  Contact support
                </a>
              ) : null}
              {support.phone ? (
                <a className="button button--secondary" href={`tel:${support.phone}`}>
                  Call support
                </a>
              ) : null}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const [pendingMfa, setPendingMfa] = useState<LoginMfaChallengeResponse | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const form = useForm({ resolver: zodResolver(loginInputSchema), defaultValues: { email: "", password: "", turnstileToken: "" } });
  const publicConfig = useQuery({
    queryKey: ["public-config"],
    queryFn: () => apiFetch<PublicClientConfig>("/api/public-config")
  });
  const mutation = useMutation({
    mutationFn: (values: any) => apiFetch<LoginResponse>("/api/auth/login", { method: "POST", body: JSON.stringify(values) }),
    onSuccess: (payload) => {
      if ("mfaRequired" in payload) {
        setPendingMfa(payload);
        setMfaCode("");
        return;
      }
      navigate(isMaidSafeUser(payload.user) ? "/admin/my-feedback" : "/admin/dashboard");
    }
  });
  const verifyMfaMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ user: SessionUser }>("/api/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({
          challengeToken: pendingMfa?.challengeToken ?? "",
          code: mfaCode.trim()
        })
      }),
    onSuccess: (payload) => navigate(isMaidSafeUser(payload.user) ? "/admin/my-feedback" : "/admin/dashboard")
  });
  const resendMfaMutation = useMutation({
    mutationFn: () =>
      apiFetch<LoginMfaChallengeResponse>("/api/auth/mfa/resend", {
        method: "POST",
        body: JSON.stringify({ challengeToken: pendingMfa?.challengeToken ?? "" })
      }),
    onSuccess: (payload) => {
      setPendingMfa(payload);
      setMfaCode("");
    }
  });

  return (
    <div className="auth-shell">
      <Card eyebrow="Admin sign in" title="Welcome back">
        {pendingMfa ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              verifyMfaMutation.mutate();
            }}
          >
            <p className="card-copy">Enter the 6-digit code sent to {pendingMfa.phoneHint || "your MFA phone"}.</p>
            <Field label="SMS code">
              <input
                inputMode="numeric"
                maxLength={6}
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </Field>
            {verifyMfaMutation.error ? <p className="error-text">{(verifyMfaMutation.error as Error).message}</p> : null}
            {resendMfaMutation.error ? <p className="error-text">{(resendMfaMutation.error as Error).message}</p> : null}
            <div className="hero__actions">
              <Button type="submit" disabled={verifyMfaMutation.isPending || mfaCode.trim().length !== 6}>
                {verifyMfaMutation.isPending ? "Verifying..." : "Verify and sign in"}
              </Button>
              <Button type="button" variant="secondary" onClick={() => resendMfaMutation.mutate()} disabled={resendMfaMutation.isPending}>
                {resendMfaMutation.isPending ? "Sending..." : "Resend code"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setPendingMfa(null)}>
                Use a different login
              </Button>
            </div>
          </form>
        ) : (
          <form className="stack" onSubmit={form.handleSubmit((values) => mutation.mutate(values))}>
            <Field label="Email">
              <input type="email" {...form.register("email")} />
            </Field>
            <Field label="Password">
              <input type="password" {...form.register("password")} />
            </Field>
            <Field label="Verification" hint="Complete the Turnstile check to sign in.">
              <TurnstileField
                siteKey={publicConfig.data?.turnstileSiteKey}
                token={form.watch("turnstileToken") ?? ""}
                onTokenChange={(token) => form.setValue("turnstileToken", token, { shouldValidate: true })}
              />
            </Field>
            {mutation.error ? <p className="error-text">{(mutation.error as Error).message}</p> : null}
            <Button type="submit">{mutation.isPending ? "Signing in..." : "Sign in"}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}

function AdminApp() {
  const auth = useQuery({ queryKey: ["auth"], queryFn: () => apiFetch<{ user: SessionUser }>("/api/auth/me"), retry: false });
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logoutMutation = useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
    onSettled: () => {
      queryClient.removeQueries({ queryKey: ["auth"] });
      navigate("/admin/login", { replace: true });
    }
  });
  if (auth.isLoading) {
    return <div className="page-loading">Loading admin...</div>;
  }
  if (auth.error) {
    return <Navigate to="/admin/login" replace />;
  }

  const currentUser = auth.data?.user;
  const maidSafeMode = isMaidSafeUser(currentUser);

  const staffSidebarSections = [
    { routeKey: "staff", label: "Directory" },
    { routeKey: "teams", label: "Teams" },
    { routeKey: "staff-time-off", label: "Vacations" },
    { routeKey: "salary-methods", label: "Salary Methods" },
    { routeKey: "staff-types", label: "Staff Types" }
  ];
  const staffSettingsPaths = staffSidebarSections.map((section) => `/admin/settings/${section.routeKey}`);
  const settingsSidebarSectionsBase: Array<{ routeKey: string; label: string }> = [
    ...singletonSettingsSections
      .filter((section) => !("hidden" in section) || !section.hidden)
      .map((section) => ({ routeKey: section.routeKey, label: section.label })),
    ...relationalSettingsSections
      .filter((section) => !["staff", "staff-time-off", "salary-methods", "staff-types", "booking-spots", "calendar-profiles"].includes(section.routeKey))
      .map((section) => ({ routeKey: section.routeKey, label: section.label }))
  ];
  const settingsSidebarSections = (() => {
    const unique = new Map<string, { routeKey: string; label: string }>(
      settingsSidebarSectionsBase.map((section) => [section.routeKey, section])
    );
    if (!unique.has("service-scenarios")) {
      unique.set("service-scenarios", { routeKey: "service-scenarios", label: "Service Scenarios" });
    }
    if (!unique.has("scheduling-overview")) {
      unique.set("scheduling-overview", { routeKey: "scheduling-overview", label: "Scheduling" });
    }
    if (!unique.has("customer-portal")) {
      unique.set("customer-portal", { routeKey: "customer-portal", label: "Customer Portal" });
    }
    if (!unique.has("problem-accounts")) {
      unique.set("problem-accounts", { routeKey: "problem-accounts", label: "Problem Accounts" });
    }
    const orderedRouteKeys = [
      "general",
      "pricebook",
      "customer-portal",
      "invoices",
      "booking-form",
      "emails",
      "text-messages",
      "waitlist",
      "leads",
      "marketing",
      "lead-sources",
      "other",
      "scheduling-overview",
      "locations",
      "services",
      "service-scenarios",
      "extras",
      "frequency-rules",
      "promotions",
      "notification-rules",
      "tags",
      "problem-accounts"
    ];
    const ordered: Array<{ routeKey: string; label: string }> = orderedRouteKeys
      .map((routeKey) => unique.get(routeKey))
      .filter((section): section is { routeKey: string; label: string } => section != null);
    for (const section of unique.values()) {
      if (!ordered.some((item) => item.routeKey === section.routeKey)) {
        ordered.push(section);
      }
    }
    return ordered;
  })();
  const isStaffMenuOpen = staffSettingsPaths.some((path) => location.pathname.startsWith(path));
  const isSettingsMenuOpen =
    location.pathname.startsWith("/admin/settings/") &&
    !staffSettingsPaths.some((path) => location.pathname.startsWith(path));

  const sidebar = maidSafeMode ? (
    <div className="sidebar">
      <Link className="sidebar__brand" to="/">
        Booking Seconds
      </Link>
      <nav className="sidebar__nav">
        <NavLink className={({ isActive }) => `sidebar__link ${isActive ? "is-active" : ""}`} to="/admin/my-feedback">
          My Feedback
        </NavLink>
      </nav>
    </div>
  ) : (
    <div className="sidebar">
      <Link className="sidebar__brand" to="/">Booking Seconds</Link>
      <nav className="sidebar__nav">
        {[
          ["dashboard", "Dashboard"],
          ["dispatcher", "Dispatcher"],
          ["leads", "Leads"],
          ["customers", "Customers"],
          ["bookings", "Bookings"],
          ["payments", "Payments"],
          ["gift-cards", "Gift Cards"],
          ["reports", "Reports"],
          ["communications", "Communications"]
        ].map(([path, label]) => (
          <NavLink className={({ isActive }) => `sidebar__link ${isActive ? "is-active" : ""}`} key={path} to={`/admin/${path}`}>
            {label}
          </NavLink>
        ))}
        <div className={`sidebar__group ${isStaffMenuOpen ? "is-open" : ""}`}>
          <NavLink className={({ isActive }) => `sidebar__link ${isActive ? "is-active" : ""}`} to="/admin/settings/staff">
            Staff
          </NavLink>
          {isStaffMenuOpen ? (
            <div className="sidebar__submenu">
              {staffSidebarSections.map((section) => (
                <NavLink
                  key={section.routeKey}
                  className={({ isActive }) => `sidebar__sublink ${isActive ? "is-active" : ""}`}
                  to={`/admin/settings/${section.routeKey}`}
                >
                  {section.label}
                </NavLink>
              ))}
            </div>
          ) : null}
        </div>
        <div className={`sidebar__group ${isSettingsMenuOpen ? "is-open" : ""}`}>
          <NavLink className={({ isActive }) => `sidebar__link ${isActive ? "is-active" : ""}`} to="/admin/settings/general">
            Settings
          </NavLink>
          {isSettingsMenuOpen ? (
            <div className="sidebar__submenu">
              {settingsSidebarSections.map((section) => (
                <Fragment key={section.routeKey}>
                  {section.routeKey === "scheduling-overview" ? (
                    <Link
                      className={`sidebar__sublink ${location.pathname.startsWith("/admin/settings/scheduling-") ? "is-active" : ""}`}
                      to="/admin/settings/scheduling-overview"
                    >
                      {section.label}
                    </Link>
                  ) : (
                    <NavLink
                      className={({ isActive }) => `sidebar__sublink ${isActive ? "is-active" : ""}`}
                      to={`/admin/settings/${section.routeKey}`}
                    >
                      {section.label}
                    </NavLink>
                  )}
                  {section.routeKey === "emails" ? (
                    <NavLink
                      className={({ isActive }) => `sidebar__sublink ${isActive ? "is-active" : ""}`}
                      to="/admin/communications/templates"
                    >
                      Email Templates
                    </NavLink>
                  ) : null}
                </Fragment>
              ))}
            </div>
          ) : null}
        </div>
      </nav>
    </div>
  );

  return (
    <AppShell
      sidebar={sidebar}
      header={
        <div className="admin-header">
          <p>{currentUser?.name}</p>
          <Badge tone="info">{currentUser?.roles.join(", ")}</Badge>
          <Button variant="secondary" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
            {logoutMutation.isPending ? "Signing out..." : "Sign out"}
          </Button>
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<Navigate to={maidSafeMode ? "my-feedback" : "dashboard"} replace />} />
        <Route path="my-feedback" element={<MyFeedbackPage />} />
        {maidSafeMode ? (
          <Route path="*" element={<Navigate to="/admin/my-feedback" replace />} />
        ) : (
          <>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id" element={<CustomersPage />} />
            <Route path="problem-accounts" element={<Navigate to="/admin/settings/problem-accounts" replace />} />
            <Route path="problem-accounts/:id" element={<LegacyProblemAccountsRedirect />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="bookings/:id" element={<BookingsPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="gift-cards" element={<GiftCardsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="dispatcher" element={<DispatcherPage />} />
            <Route path="scheduler" element={<Navigate to="/admin/settings/teams" replace />} />
            <Route path="leads/*" element={<LeadsPage />} />
            <Route path="communications/*" element={<CommunicationsPage />} />
            <Route path="settings/teams" element={<SchedulerPage />} />
            <Route path="settings/problem-accounts" element={<CustomersPage problemOnly />} />
            <Route path="settings/problem-accounts/:id" element={<CustomersPage problemOnly />} />
            <Route path="settings/:section" element={<SettingsSectionPage />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </>
        )}
      </Routes>
    </AppShell>
  );
}

function LegacyProblemAccountsRedirect() {
  const { id = "" } = useParams();
  return <Navigate to={id ? `/admin/settings/problem-accounts/${id}` : "/admin/settings/problem-accounts"} replace />;
}

function CustomersPage({ problemOnly = false }: { problemOnly?: boolean }) {
  const navigate = useNavigate();
  const { id: customerRouteId = "" } = useParams();
  const [customerSearchParams, setCustomerSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const customerBasePath = problemOnly ? "/admin/settings/problem-accounts" : "/admin/customers";
  const customerListEndpoint = problemOnly ? "/api/problem-accounts" : "/api/customers";
  const customerImportEndpoint = problemOnly ? "/api/problem-accounts/import" : "/api/customers/import";
  const customerExportEndpoint = problemOnly ? "/api/problem-accounts/export" : "/api/customers/export";
  const customerSectionLabel = problemOnly ? "Problem Accounts" : "Customers";
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [search, setSearch] = useState("");
  const [customerStatusFilter, setCustomerStatusFilter] = useState(problemOnly ? "problem" : "active");
  const [showCreateCustomerForm, setShowCreateCustomerForm] = useState(false);
  const [importFeedback, setImportFeedback] = useState("");
  const [portalActionFeedback, setPortalActionFeedback] = useState("");
  const [customerMessageOpen, setCustomerMessageOpen] = useState(false);
  const [customerMessageTemplateId, setCustomerMessageTemplateId] = useState("");
  const [customerMessageSubject, setCustomerMessageSubject] = useState("");
  const [customerMessageBody, setCustomerMessageBody] = useState("");
  const [customerMessageFeedback, setCustomerMessageFeedback] = useState("");
  const [customerSmsOpen, setCustomerSmsOpen] = useState(false);
  const [customerSmsTemplateKey, setCustomerSmsTemplateKey] = useState("");
  const [customerSmsBody, setCustomerSmsBody] = useState("");
  const [customerSmsFeedback, setCustomerSmsFeedback] = useState("");
  const [activeCustomerTab, setActiveCustomerTab] = useState<CustomerTabKey>("overview");
  const [pendingCustomerSectionId, setPendingCustomerSectionId] = useState<string | null>(null);
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [historyVisitTypeFilter, setHistoryVisitTypeFilter] = useState("all");
  const [issueDraft, setIssueDraft] = useState<CustomerIssueEntryDraft>({ bookingId: "", description: "" });
  const [damageDraft, setDamageDraft] = useState<CustomerIssueEntryDraft>({ bookingId: "", description: "" });
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const customerDocumentInputRef = useRef<HTMLInputElement | null>(null);
  const isCustomerProfilePage = Boolean(customerRouteId);

  const customers = useQuery({
    queryKey: ["customers", problemOnly ? "problem" : "all"],
    queryFn: () => apiFetch<{ items: CustomerListItem[] }>(customerListEndpoint)
  });
  const customerTags = useQuery({
    queryKey: ["customer-tags"],
    queryFn: () => apiFetch<{ items: Array<CustomerTag & { entityType?: string; active?: number | boolean }> }>("/api/settings/tags")
  });
  const leadSources = useQuery({
    queryKey: ["customer-lead-sources"],
    queryFn: () => apiFetch<{ items: LeadSourceOption[] }>("/api/customers/lead-sources")
  });
  const staff = useQuery({
    queryKey: ["customer-staff"],
    queryFn: () => apiFetch<{ items: StaffSettingsItem[] }>("/api/settings/staff")
  });

  const createForm = useForm<any>({
    resolver: zodResolver(customerInputSchema),
    defaultValues: getCustomerFormDefaults()
  });
  const profileForm = useForm<any>({
    resolver: zodResolver(customerInputSchema),
    defaultValues: getCustomerFormDefaults()
  });

  const filteredCustomers = (customers.data?.items ?? []).filter((item) => {
    const normalizedStatus = String(item.account_status || (item.blocked_until ? "Problem account" : "Active")).toLowerCase();

    const matchesStatus = problemOnly
      ? true
      : customerStatusFilter === "all"
        ? true
        : customerStatusFilter === "problem"
          ? Boolean(item.blocked_until) || normalizedStatus.includes("problem") || normalizedStatus.includes("blocked")
          : customerStatusFilter === "inactive"
            ? normalizedStatus.includes("inactive")
            : !item.blocked_until && !normalizedStatus.includes("inactive");

    const haystack = [
      item.first_name,
      item.last_name,
      item.company_name ?? "",
      item.email ?? "",
      item.phone,
      item.secondary_phone ?? "",
      item.lead_source_name ?? ""
    ]
      .join(" ")
      .toLowerCase();
    return matchesStatus && haystack.includes(search.trim().toLowerCase());
  });

  useEffect(() => {
    setSelectedCustomerId(customerRouteId);
  }, [customerRouteId]);

  useEffect(() => {
    setCustomerStatusFilter(problemOnly ? "problem" : "active");
    if (problemOnly) {
      setShowCreateCustomerForm(false);
    }
  }, [problemOnly]);

  useEffect(() => {
    const requestedTab = customerSearchParams.get("tab");
    const nextTab = ["overview", "internal-notes", "history", "properties", "payments", "files", "issues"].includes(String(requestedTab))
      ? (requestedTab as CustomerTabKey)
      : "overview";
    setActiveCustomerTab(nextTab);
    setPendingCustomerSectionId(null);
    setHistoryStatusFilter("all");
    setHistoryVisitTypeFilter("all");
    setIssueDraft({ bookingId: "", description: "" });
    setDamageDraft({ bookingId: "", description: "" });
    setPortalActionFeedback("");
    setCustomerMessageOpen(false);
    setCustomerMessageTemplateId("");
    setCustomerMessageSubject("");
    setCustomerMessageBody("");
    setCustomerMessageFeedback("");
    setCustomerSmsOpen(false);
    setCustomerSmsTemplateKey("");
    setCustomerSmsBody("");
    setCustomerSmsFeedback("");
  }, [customerSearchParams, selectedCustomerId]);

  const customerDetail = useQuery({
    queryKey: ["customer", selectedCustomerId],
    queryFn: () => apiFetch<CustomerDetail>(`/api/customers/${selectedCustomerId}`),
    enabled: Boolean(selectedCustomerId)
  });
  const customerMarketingSummary = useQuery({
    queryKey: ["customer-marketing-summary", selectedCustomerId],
    queryFn: () => apiFetch<CustomerMarketingSummary>(`/api/communications/customers/${selectedCustomerId}/marketing-summary`),
    enabled: Boolean(selectedCustomerId)
  });
  const customerMessageTemplates = useQuery({
    queryKey: ["customer-message-templates"],
    queryFn: () => apiFetch<{ items: EmailTemplateRecord[] }>("/api/communications/templates"),
    enabled: customerMessageOpen
  });

  useEffect(() => {
    if (!problemOnly || !customerRouteId || !customerDetail.data) {
      return;
    }

    if (
      !isProblemAccountRecord({
        accountStatus: customerDetail.data.accountStatus,
        blockedUntil: customerDetail.data.blockedUntil
      })
    ) {
      navigate(`/admin/customers/${customerRouteId}`, { replace: true });
    }
  }, [customerDetail.data, customerRouteId, navigate, problemOnly]);

  useEffect(() => {
    if (!customerDetail.data) {
      return;
    }

    const primaryAddress = customerDetail.data.addresses[0];
    profileForm.reset({
      firstName: customerDetail.data.firstName,
      lastName: customerDetail.data.lastName,
      companyName: customerDetail.data.companyName ?? "",
      profilePhotoUrl: customerDetail.data.profilePhotoUrl ?? "",
      customerType: customerDetail.data.customerType ?? "Residential",
      accountStatus: customerDetail.data.accountStatus ?? "Active",
      email: customerDetail.data.email ?? "",
      phone: customerDetail.data.phone,
      secondaryPhone: customerDetail.data.secondaryPhone ?? "",
      pets: customerDetail.data.pets ?? "",
      sensitivities: customerDetail.data.sensitivities ?? "",
      documents: customerDetail.data.documents ?? [],
      notes: customerDetail.data.notes ?? "",
      serviceFrequency: customerDetail.data.serviceFrequency ?? "",
      blockedUntil: customerDetail.data.blockedUntil ?? "",
      blockedReason: customerDetail.data.blockedReason ?? "",
      preferredStaffIds: customerDetail.data.preferredStaffIds ?? [],
      blockedStaffIds: customerDetail.data.blockedStaffIds ?? [],
      preferredContactMethod: customerDetail.data.preferredContactMethod ?? "Phone",
      englishSpeakingStaff: Boolean(customerDetail.data.englishSpeakingStaff),
      leadSourceId: customerDetail.data.leadSourceId ?? "",
      stripeCustomerId: customerDetail.data.stripeCustomerId ?? "",
      paymentMethods: customerDetail.data.paymentMethods ?? [],
      propertyPhotos: customerDetail.data.propertyPhotos ?? [],
      refundsDisputes: customerDetail.data.refundsDisputes ?? [],
      problemAreas: customerDetail.data.problemAreas ?? [],
      damageReports: customerDetail.data.damageReports ?? [],
      specialIncidents: customerDetail.data.specialIncidents ?? [],
      clientRating: customerDetail.data.clientRating ?? undefined,
      customerSatisfactionScore: customerDetail.data.customerSatisfactionScore ?? undefined,
      rewards: {
        points: customerDetail.data.rewards?.points ?? 0,
        tier: customerDetail.data.rewards?.tier ?? "",
        notes: customerDetail.data.rewards?.notes ?? ""
      },
      emailPreferences: {
        smsOptInStatus: customerDetail.data.emailPreferences?.smsOptInStatus ?? false,
        smsReminderOptInStatus: customerDetail.data.emailPreferences?.smsReminderOptInStatus ?? false,
        emailOptInStatus: customerDetail.data.emailPreferences?.emailOptInStatus ?? true,
        bookingConfirmation: customerDetail.data.emailPreferences?.bookingConfirmation ?? true,
        reminders: customerDetail.data.emailPreferences?.reminders ?? true,
        receipts: customerDetail.data.emailPreferences?.receipts ?? true,
        marketing: customerDetail.data.emailPreferences?.marketing ?? true
      },
      taxExempt: Boolean(customerDetail.data.taxExempt),
      specialTaxRate: customerDetail.data.specialTaxRate ?? undefined,
      tagIds: customerDetail.data.tags.map((tag) => tag.id),
      addresses: customerDetail.data.addresses.length
        ? customerDetail.data.addresses.map((address, index) => ({
            label: address.label ?? `Service address ${index + 1}`,
            line1: address.line1 ?? "",
            line2: address.line2 ?? "",
            unitNumber: address.unitNumber ?? "",
            city: address.city ?? "",
            state: address.state ?? "",
            postalCode: address.postalCode ?? "",
            country: address.country ?? "US",
            instructions: address.instructions ?? "",
            isPrimary: index === 0 || Boolean(address.isPrimary)
          }))
        : [createEmptyCustomerAddress(0)],
      address: {
        label: primaryAddress?.label ?? "Primary service address",
        line1: primaryAddress?.line1 ?? "",
        line2: primaryAddress?.line2 ?? "",
        unitNumber: primaryAddress?.unitNumber ?? "",
        city: primaryAddress?.city ?? "",
        state: primaryAddress?.state ?? "",
        postalCode: primaryAddress?.postalCode ?? "",
        country: primaryAddress?.country ?? "US",
        instructions: primaryAddress?.instructions ?? "",
        isPrimary: true
      }
    });
  }, [customerDetail.data, profileForm]);

  const createMutation = useMutation({
    mutationFn: (values: any) => apiFetch<{ id: string }>("/api/customers", { method: "POST", body: JSON.stringify(normalizeCustomerValues(values)) }),
    onSuccess: async (customer) => {
      createForm.reset(getCustomerFormDefaults());
      setShowCreateCustomerForm(false);
      navigate(`/admin/customers/${customer.id}`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customers"] }),
        queryClient.invalidateQueries({ queryKey: ["customer", customer.id] })
      ]);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (values: any) =>
      apiFetch(`/api/customers/${selectedCustomerId}`, {
        method: "PUT",
        body: JSON.stringify(normalizeCustomerValues(values))
      }),
    onSuccess: async (_result, values) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customers"] }),
        queryClient.invalidateQueries({ queryKey: ["customer", selectedCustomerId] }),
        queryClient.invalidateQueries({ queryKey: ["customer-marketing-summary", selectedCustomerId] })
      ]);
      if (
        problemOnly &&
        !isProblemAccountRecord({
          accountStatus: values.accountStatus,
          blockedUntil: values.blockedUntil
        })
      ) {
        navigate(`/admin/customers/${selectedCustomerId}`);
      }
    }
  });
  const deleteMutation = useMutation({
    mutationFn: () => apiFetch<{ id: string; mode: "deleted" | "archived" }>(`/api/customers/${selectedCustomerId}`, { method: "DELETE" }),
    onSuccess: async (result) => {
      setImportFeedback(
        result.mode === "archived"
          ? "Customer was archived because booking history is attached."
          : "Customer deleted."
      );
      setSelectedCustomerId("");
      navigate(customerBasePath);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customers"] }),
        queryClient.invalidateQueries({ queryKey: ["customer", selectedCustomerId] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] })
      ]);
    }
  });
  const portalSignInLinkMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; message: string }>(`/api/customers/${selectedCustomerId}/portal/sign-in-link`, {
        method: "POST"
      }),
    onMutate: () => {
      setPortalActionFeedback("");
    },
    onSuccess: async (result) => {
      setPortalActionFeedback(result.message || "Portal sign-in link sent.");
      await queryClient.invalidateQueries({ queryKey: ["customer", selectedCustomerId] });
    },
    onError: (error) => {
      setPortalActionFeedback(error instanceof Error ? error.message : "Portal sign-in link could not be sent.");
    }
  });
  const sendCustomerEmailMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; id?: string; messageLogId?: string }>(`/api/customers/${selectedCustomerId}/messages/email`, {
        method: "POST",
        body: JSON.stringify({
          subject: customerMessageSubject,
          bodyText: customerMessageBody,
          templateId: customerMessageTemplateId || undefined,
          sendMode: customerMessageTemplateId ? "template" : "freeform"
        })
      }),
    onMutate: () => {
      setCustomerMessageFeedback("");
    },
    onSuccess: async () => {
      setCustomerMessageFeedback("Email sent to the customer.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customer", selectedCustomerId] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity", "full"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    },
    onError: (error) => {
      setCustomerMessageFeedback(error instanceof Error ? error.message : "Customer email could not be sent.");
    }
  });
  const sendCustomerSmsMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; sid?: string; messageLogId?: string }>(`/api/customers/${selectedCustomerId}/messages/sms`, {
        method: "POST",
        body: JSON.stringify({
          bodyText: customerSmsBody,
          templateKey: customerSmsTemplateKey || undefined,
          sendMode: customerSmsTemplateKey ? "template" : "freeform"
        })
      }),
    onMutate: () => {
      setCustomerSmsFeedback("");
    },
    onSuccess: async () => {
      setCustomerSmsFeedback("SMS sent to the customer.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customer", selectedCustomerId] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity", "full"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    },
    onError: (error) => {
      setCustomerSmsFeedback(error instanceof Error ? error.message : "Customer SMS could not be sent.");
    }
  });
  const importMutation = useMutation({
    mutationFn: (items: any[]) =>
      apiFetch<CustomerImportResult>(customerImportEndpoint, {
        method: "POST",
        body: JSON.stringify({ items })
      }),
    onSuccess: async (result) => {
      const details = [
        result.unsubscribers ? `${result.unsubscribers} unsubscribers suppressed` : "",
        result.alreadySuppressed ? `${result.alreadySuppressed} already suppressed` : "",
        result.marketingEligible !== undefined ? `${result.marketingEligible} marketing eligible` : "",
        result.marketingBlocked !== undefined ? `${result.marketingBlocked} marketing blocked` : "",
        result.duplicateEmails ? `${result.duplicateEmails} duplicate emails` : ""
      ].filter(Boolean);
      setImportFeedback(
        problemOnly
          ? `Imported ${result.imported} problem accounts with all customer fields.${details.length ? ` ${details.join(", ")}.` : ""}`
          : `Imported ${result.imported} customers with all customer fields.${details.length ? ` ${details.join(", ")}.` : ""}`
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customers"] }),
        queryClient.invalidateQueries({ queryKey: ["customer"] })
      ]);
    }
  });
  const uploadCustomerDocumentMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`/api/customers/${selectedCustomerId}/documents`, {
        method: "POST",
        body: formData,
        credentials: "same-origin",
        headers: {
          "x-csrf-token": getCookie("bs_csrf") ?? ""
        }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Document upload failed.");
      }
      return payload as { uploaded: boolean; documents: string[] };
    },
    onSuccess: async (result) => {
      profileForm.setValue("documents", result.documents ?? [], { shouldDirty: true });
      await queryClient.invalidateQueries({ queryKey: ["customer", selectedCustomerId] });
    }
  });

  const activeCustomerTags = (customerTags.data?.items ?? []).filter(
    (tag) => (tag.entityType ?? "customer") === "customer" && tag.active !== 0 && tag.active !== false
  );
  const leadSourceOptions = leadSources.data?.items ?? [];
  const activeStaff = (staff.data?.items ?? []).filter((item) => item.active !== 0 && item.active !== false);
  const totalCustomers = customers.data?.items.length ?? 0;
  const activeCustomerCount = (customers.data?.items ?? []).filter((item) => {
    const status = String(item.account_status || (item.blocked_until ? "Problem account" : "Active")).toLowerCase();
    return !item.blocked_until && !status.includes("inactive") && !status.includes("problem") && !status.includes("blocked");
  }).length;
  const problemCustomerCount = (customers.data?.items ?? []).filter((item) => {
    const status = String(item.account_status || "").toLowerCase();
    return Boolean(item.blocked_until) || status.includes("problem") || status.includes("blocked");
  }).length;
  const inactiveCustomerCount = (customers.data?.items ?? []).filter((item) =>
    String(item.account_status || "").toLowerCase().includes("inactive")
  ).length;
  const watchedProfileTagIds = profileForm.watch("tagIds") ?? [];
  const watchedProfilePreferredStaffIds = profileForm.watch("preferredStaffIds") ?? [];
  const watchedProfileBlockedStaffIds = profileForm.watch("blockedStaffIds") ?? [];
  const watchedProfileAddresses = (profileForm.watch("addresses") ?? []) as CustomerAddressDraft[];
  const watchedProfilePaymentMethods = profileForm.watch("paymentMethods") ?? [];
  const watchedProfileDocuments = profileForm.watch("documents") ?? [];
  const uploadedProfileDocuments = watchedProfileDocuments.filter((document: string) => parseCustomerDocumentEntry(document).isUploadedFile);
  const manualProfileDocuments = watchedProfileDocuments.filter((document: string) => !parseCustomerDocumentEntry(document).isUploadedFile);
  const watchedProfilePropertyPhotos = profileForm.watch("propertyPhotos") ?? [];
  const watchedProfileRefundsDisputes = profileForm.watch("refundsDisputes") ?? [];
  const watchedProfileProblemAreas = profileForm.watch("problemAreas") ?? [];
  const watchedProfileDamageReports = profileForm.watch("damageReports") ?? [];
  const watchedProfileRewards = profileForm.watch("rewards") ?? {};
  const watchedProfilePhotoUrl = String(profileForm.watch("profilePhotoUrl") ?? "");
  const watchedProfileCustomerType = String(profileForm.watch("customerType") ?? "Residential");
  const watchedProfileAccountStatus = String(profileForm.watch("accountStatus") ?? "Active");
  const watchedProfilePets = String(profileForm.watch("pets") ?? "");
  const watchedProfileSensitivities = String(profileForm.watch("sensitivities") ?? "");
  const watchedCreateAddresses = (createForm.watch("addresses") ?? []) as CustomerAddressDraft[];
  const watchedEmailPreferences = profileForm.watch("emailPreferences") ?? {};

  const currentCustomer = customerDetail.data;
  const customerPortalLoginUrl =
    typeof window === "undefined" ? "/account/login" : `${window.location.origin}/account/login`;
  const displayName = currentCustomer
    ? [currentCustomer.firstName, currentCustomer.lastName].filter(Boolean).join(" ") || currentCustomer.companyName || "Customer"
    : "Customer";
  const profileInitials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  const primaryAddress =
    currentCustomer?.addresses.find((address) => Boolean(address.isPrimary)) ?? currentCustomer?.addresses[0] ?? null;
  const upcomingBookings = (currentCustomer?.bookings ?? [])
    .filter((booking) => new Date(booking.startsAt).getTime() >= Date.now() && !booking.status.toLowerCase().includes("cancel"))
    .slice(0, 4);
  const paymentRecords = currentCustomer?.payments ?? [];
  const lastPayment = paymentRecords[0];
  const portalStatusLabel = currentCustomer?.portalActivatedAt
    ? "Activated"
    : currentCustomer?.portalInvitedAt
      ? "Invited"
      : "Not invited yet";
  const portalStatusTone = currentCustomer?.portalActivatedAt ? "success" : currentCustomer?.portalInvitedAt ? "info" : "warning";
  const getStaffDisplayName = (member: StaffSettingsItem) =>
    String(member.fullName ?? "").trim() || [member.firstName, member.lastName].filter(Boolean).join(" ").trim() || member.code || "Staff member";
  const matchesPreferredStaffSelection = (member: StaffSettingsItem, values: string[]) =>
    values.includes(member.id) || (member.code ? values.includes(member.code) : false);
  const preferredStaffLabel =
    activeStaff
      .filter((member) => matchesPreferredStaffSelection(member, watchedProfilePreferredStaffIds))
      .map((member) => getStaffDisplayName(member))
      .join(", ") || "No preferred staff saved";
  const blockedStaffLabel =
    activeStaff
      .filter((member) => matchesPreferredStaffSelection(member, watchedProfileBlockedStaffIds))
      .map((member) => getStaffDisplayName(member))
      .join(", ") || "No blocked staff saved";
  const derivedCustomerFrequency = describeBookingFrequency(currentCustomer?.bookings ?? [], "");
  const pastCustomerBookings = (currentCustomer?.bookings ?? []).filter((booking) => new Date(booking.startsAt).getTime() <= Date.now());
  const inferredVisitType =
    currentCustomer && (Boolean(currentCustomer.serviceFrequency?.trim()) || currentCustomer.bookingCount > 1)
      ? "recurring"
      : "one-time";
  const filteredHistory = (currentCustomer?.bookings ?? []).filter((booking) => {
    const normalizedStatus = booking.status.toLowerCase();
    const matchesStatus =
      historyStatusFilter === "all" ||
      (historyStatusFilter === "completed" && normalizedStatus.includes("complete")) ||
      (historyStatusFilter === "canceled" && normalizedStatus.includes("cancel")) ||
      (historyStatusFilter === "no-show" && normalizedStatus.includes("no-show"));
    const matchesVisitType = historyVisitTypeFilter === "all" || historyVisitTypeFilter === inferredVisitType;
    return matchesStatus && matchesVisitType;
  });

  const updateAddresses = (formApi: typeof profileForm | typeof createForm, nextAddresses: CustomerAddressDraft[]) => {
    const nonEmpty = nextAddresses.length ? nextAddresses : [createEmptyCustomerAddress(0)];
    const sorted = [...nonEmpty].sort((left, right) => (Boolean(right.isPrimary) ? 1 : 0) - (Boolean(left.isPrimary) ? 1 : 0));
    const normalized = sorted.map((address, index) => ({
      ...createEmptyCustomerAddress(index),
      ...address,
      label: String(address.label ?? (index === 0 ? "Primary service address" : `Additional address ${index}`)),
      isPrimary: index === 0
    }));

    formApi.setValue("addresses", normalized, { shouldDirty: true });
    formApi.setValue("address", normalized[0], { shouldDirty: true });
  };

  const renderAddressFields = (
    formApi: typeof profileForm | typeof createForm,
    addresses: CustomerAddressDraft[],
    editable: boolean
  ) => (
    <div className="stack">
      {addresses.map((address, index) => (
        <div key={index} className="booking-meta-card">
          <div className="booking-list-item__top">
            <div>
              <p className="eyebrow">{index === 0 ? "Primary address" : `Additional address ${index}`}</p>
              <strong>{String(address.label ?? "") || "Service address"}</strong>
              <AddressLinkButtons address={address} compact />
            </div>
            {editable ? (
              <div className="booking-list-item__meta">
                {index > 0 ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const next = addresses.filter((_, addressIndex) => addressIndex !== index);
                      updateAddresses(formApi, next);
                    }}
                  >
                    Remove
                  </Button>
                ) : null}
                {index > 0 ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const next = addresses.map((entry, addressIndex) => ({
                        ...entry,
                        isPrimary: addressIndex === index
                      }));
                      updateAddresses(formApi, next);
                    }}
                  >
                    Make primary
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="grid two">
            <Field label="Address">
              <input
                value={String(address.line1 ?? "")}
                onChange={(event) => {
                  const next = [...addresses];
                  next[index] = { ...next[index], line1: event.target.value };
                  updateAddresses(formApi, next);
                }}
              />
            </Field>
            <Field label="Unit number">
              <input
                value={String(address.unitNumber ?? "")}
                onChange={(event) => {
                  const next = [...addresses];
                  next[index] = { ...next[index], unitNumber: event.target.value };
                  updateAddresses(formApi, next);
                }}
              />
            </Field>
            <Field label="City">
              <input
                value={String(address.city ?? "")}
                onChange={(event) => {
                  const next = [...addresses];
                  next[index] = { ...next[index], city: event.target.value };
                  updateAddresses(formApi, next);
                }}
              />
            </Field>
            <Field label="State">
              <input
                value={String(address.state ?? "")}
                onChange={(event) => {
                  const next = [...addresses];
                  next[index] = { ...next[index], state: event.target.value };
                  updateAddresses(formApi, next);
                }}
              />
            </Field>
            <Field label="Postal / zip code">
              <input
                value={String(address.postalCode ?? "")}
                onChange={(event) => {
                  const next = [...addresses];
                  next[index] = { ...next[index], postalCode: event.target.value };
                  updateAddresses(formApi, next);
                }}
              />
            </Field>
            <Field label="Country">
              <input
                value={String(address.country ?? "US")}
                onChange={(event) => {
                  const next = [...addresses];
                  next[index] = { ...next[index], country: event.target.value };
                  updateAddresses(formApi, next);
                }}
              />
            </Field>
            <Field label="Address instructions">
              <textarea
                rows={3}
                value={String(address.instructions ?? "")}
                onChange={(event) => {
                  const next = [...addresses];
                  next[index] = { ...next[index], instructions: event.target.value };
                  updateAddresses(formApi, next);
                }}
              />
            </Field>
          </div>
        </div>
      ))}
      {editable ? (
        <Button
          variant="secondary"
          onClick={() => {
            updateAddresses(formApi, [...addresses, createEmptyCustomerAddress(addresses.length)]);
          }}
        >
          Add another address
        </Button>
      ) : null}
    </div>
  );

  const openHref = (href: string | null | undefined) => {
    if (!href || typeof window === "undefined") {
      return;
    }

    window.location.href = href;
  };

  const copyCustomerPortalLoginLink = async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      setPortalActionFeedback(`Customer portal link: ${customerPortalLoginUrl}`);
      return;
    }

    await navigator.clipboard.writeText(customerPortalLoginUrl);
    setPortalActionFeedback("Customer portal login link copied.");
  };

  const confirmDeleteCustomer = () => {
    if (typeof window !== "undefined" && !window.confirm(`Delete ${displayName}? Customers with booking history will be archived instead.`)) {
      return;
    }
    setImportFeedback("");
    deleteMutation.mutate();
  };

  const openCustomerMessageComposer = () => {
    setCustomerMessageTemplateId("");
    setCustomerMessageSubject("");
    setCustomerMessageBody("");
    setCustomerMessageFeedback("");
    setCustomerMessageOpen(true);
  };

  const customerTemplateVariables = () => {
    const firstName = String(currentCustomer?.firstName ?? "").trim();
    const lastName = String(currentCustomer?.lastName ?? "").trim();
    const email = String(currentCustomer?.email ?? "").trim();
    const phone = String(currentCustomer?.phone ?? "").trim();
    const companyName = String(currentCustomer?.companyName ?? "").trim();
    const customerName = [firstName, lastName].filter(Boolean).join(" ") || companyName || email || "Customer";
    return {
      customerName,
      customer: {
        firstName,
        lastName,
        name: customerName,
        email,
        phone,
        companyName
      },
      client: {
        firstName,
        lastName,
        name: customerName,
        email,
        phone
      }
    };
  };

  const applyCustomerMessageTemplate = (templateId: string) => {
    setCustomerMessageTemplateId(templateId);
    setCustomerMessageFeedback("");
    if (!templateId) {
      return;
    }
    const template = (customerMessageTemplates.data?.items ?? []).find((item) => item.id === templateId);
    if (!template) {
      return;
    }
    setCustomerMessageSubject(renderSimpleTemplate(template.subjectTemplate ?? "", customerTemplateVariables()));
    setCustomerMessageBody(
      renderSimpleTemplate(String(template.bodyText ?? "").trim() || emailTemplateHtmlToText(template.bodyHtml ?? ""), customerTemplateVariables())
    );
  };

  const openCustomerSmsComposer = () => {
    setCustomerSmsTemplateKey("");
    setCustomerSmsBody("");
    setCustomerSmsFeedback("");
    setCustomerSmsOpen(true);
  };

  const applyCustomerSmsTemplate = (templateKey: string) => {
    setCustomerSmsTemplateKey(templateKey);
    setCustomerSmsFeedback("");
    if (!templateKey) {
      return;
    }
    const template = customerSmsTemplates.find((item) => item.key === templateKey);
    if (!template) {
      return;
    }
    setCustomerSmsBody(renderSimpleTemplate(template.bodyText, customerTemplateVariables()));
  };

  useEffect(() => {
    if (!pendingCustomerSectionId || typeof window === "undefined") {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (scrollToSectionTarget(pendingCustomerSectionId)) {
        setPendingCustomerSectionId(null);
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeCustomerTab, currentCustomer, pendingCustomerSectionId]);

  const handleCustomerSectionJump = (item: SectionJumpItem<CustomerTabKey>) => {
    if (item.tab && item.tab !== activeCustomerTab) {
      setPendingCustomerSectionId(item.id);
      setActiveCustomerTab(item.tab);
      return;
    }

    if (!scrollToSectionTarget(item.id)) {
      setPendingCustomerSectionId(item.id);
    }
  };

  const exportCustomers = async () => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const payload = await apiFetch<CustomerTransferPayload>(customerExportEndpoint);
      const csv = [
        customerTransferHeaders.join(","),
        ...payload.items.map((item) =>
          customerTransferHeaders
            .map((header) => escapeCsvValue(flattenCustomerTransferRecord(item)[header] ?? ""))
            .join(",")
        )
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = `${problemOnly ? "problem-accounts" : "customers"}-export-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      setImportFeedback(
        problemOnly
          ? `Exported ${payload.items.length} problem accounts with all customer fields to CSV.`
          : `Exported ${payload.items.length} customers with all customer fields to CSV.`
      );
    } catch (error) {
      setImportFeedback(error instanceof Error ? error.message : "Customer export failed.");
    }
  };

  const renderCustomerMessageComposer = () => {
    if (!customerMessageOpen || !currentCustomer) {
      return null;
    }

    const templates = customerMessageTemplates.data?.items ?? [];
    const hasRecipientEmail = Boolean(currentCustomer.email);
    const canSendCustomerEmail =
      hasRecipientEmail &&
      customerMessageSubject.trim().length > 0 &&
      customerMessageBody.trim().length > 0 &&
      !sendCustomerEmailMutation.isPending;

    return (
      <div
        className="customer-message-modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setCustomerMessageOpen(false);
          }
        }}
      >
        <div className="customer-message-modal" role="dialog" aria-modal="true" aria-labelledby="customer-message-title">
          <div className="customer-message-modal__header">
            <div>
              <p className="eyebrow">Direct email</p>
              <h3 id="customer-message-title">Message customer</h3>
              <p className="card-copy">
                {currentCustomer.email
                  ? `Sending to ${currentCustomer.email}`
                  : "Add an email address to this customer before sending a message."}
              </p>
            </div>
            <Button variant="ghost" onClick={() => setCustomerMessageOpen(false)}>
              Close
            </Button>
          </div>

          <div className="stack">
            <Field label="Start from template" hint="Optional. You can edit the subject and message before sending.">
              <select
                value={customerMessageTemplateId}
                onChange={(event) => applyCustomerMessageTemplate(event.target.value)}
                disabled={customerMessageTemplates.isLoading}
              >
                <option value="">Freeform message</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.templateType})
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Subject">
              <input
                value={customerMessageSubject}
                onChange={(event) => setCustomerMessageSubject(event.target.value)}
                placeholder="Subject"
                disabled={!hasRecipientEmail}
              />
            </Field>

            <Field label="Message">
              <textarea
                rows={10}
                value={customerMessageBody}
                onChange={(event) => setCustomerMessageBody(event.target.value)}
                placeholder="Write your message..."
                disabled={!hasRecipientEmail}
              />
            </Field>

            {customerMessageFeedback ? (
              <p className={sendCustomerEmailMutation.isError ? "error-text" : "success-text"}>{customerMessageFeedback}</p>
            ) : null}

            <div className="customer-message-modal__actions">
              <Button variant="secondary" onClick={() => setCustomerMessageOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => sendCustomerEmailMutation.mutate()} disabled={!canSendCustomerEmail}>
                {sendCustomerEmailMutation.isPending ? "Sending..." : "Send email"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerSmsComposer = () => {
    if (!customerSmsOpen || !currentCustomer) {
      return null;
    }

    const hasRecipientPhone = Boolean(currentCustomer.phone);
    const smsOptIn = Boolean(currentCustomer.emailPreferences?.smsOptInStatus || currentCustomer.emailPreferences?.smsReminderOptInStatus);
    const canSendCustomerSms =
      hasRecipientPhone &&
      customerSmsBody.trim().length > 0 &&
      !sendCustomerSmsMutation.isPending;

    return (
      <div
        className="customer-message-modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setCustomerSmsOpen(false);
          }
        }}
      >
        <div className="customer-message-modal" role="dialog" aria-modal="true" aria-labelledby="customer-sms-title">
          <div className="customer-message-modal__header">
            <div>
              <p className="eyebrow">Direct SMS</p>
              <h3 id="customer-sms-title">Send SMS</h3>
              <p className="card-copy">
                {currentCustomer.phone
                  ? `Sending to ${currentCustomer.phone}`
                  : "Add a phone number to this customer before sending a text message."}
              </p>
            </div>
            <Button variant="ghost" onClick={() => setCustomerSmsOpen(false)}>
              Close
            </Button>
          </div>

          <div className="stack">
            {!smsOptIn && hasRecipientPhone ? (
              <p className="card-copy">
                This customer is not marked as opted in for SMS. Use direct SMS only for appropriate service communication.
              </p>
            ) : null}

            <Field label="Start from template" hint="Optional. Freeform messages are always available.">
              <select
                value={customerSmsTemplateKey}
                onChange={(event) => applyCustomerSmsTemplate(event.target.value)}
              >
                <option value="">Freeform SMS</option>
                {customerSmsTemplates.map((template) => (
                  <option key={template.key} value={template.key}>
                    {template.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Text message" hint={`${customerSmsBody.length} characters`}>
              <textarea
                rows={7}
                value={customerSmsBody}
                onChange={(event) => setCustomerSmsBody(event.target.value)}
                placeholder="Write your SMS..."
                disabled={!hasRecipientPhone}
              />
            </Field>

            {customerSmsFeedback ? (
              <p className={sendCustomerSmsMutation.isError ? "error-text" : "success-text"}>{customerSmsFeedback}</p>
            ) : null}

            <div className="customer-message-modal__actions">
              <Button variant="secondary" onClick={() => setCustomerSmsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => sendCustomerSmsMutation.mutate()} disabled={!canSendCustomerSms}>
                {sendCustomerSmsMutation.isPending ? "Sending..." : "Send SMS"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerWorkspace = () => {
    if (!selectedCustomerId) {
      return (
        <Card title="Customer profile" eyebrow="Select a customer">
          <p className="card-copy">
            {problemOnly
              ? "Select a problem account to review profile details, properties, payments, and service issues."
              : "Select a customer to review profile details, properties, payments, and service issues."}
          </p>
        </Card>
      );
    }

    if (customerDetail.isLoading) {
      return (
        <Card title="Customer profile" eyebrow="Loading">
          <div className="page-loading">Loading customer profile...</div>
        </Card>
      );
    }

    if (customerDetail.error) {
      return (
        <Card title="Customer profile" eyebrow="Error">
          <p className="error-text">{(customerDetail.error as Error).message}</p>
        </Card>
      );
    }

    if (!currentCustomer) {
      return (
        <Card title="Customer profile" eyebrow="Unavailable">
          <p className="card-copy">Customer details are unavailable for this record.</p>
        </Card>
      );
    }

    return (
      <div className="stack">
        <SectionJumpNav items={customerSectionJumpItems} onSelect={handleCustomerSectionJump} />

        <div className="customer-summary-bar">
          <div className="customer-summary-bar__identity">
            <div className="customer-summary-bar__avatar">
              {watchedProfilePhotoUrl ? <img src={watchedProfilePhotoUrl} alt={displayName} /> : <span>{profileInitials || "C"}</span>}
            </div>
            <div>
              <p className="eyebrow">Customer summary</p>
              <h3>{displayName}</h3>
              <p className="card-copy">
                {currentCustomer.companyName ? `${currentCustomer.companyName} • ` : ""}
                {currentCustomer.email || currentCustomer.phone}
              </p>
              <div className="booking-list-item__meta">
                <Badge tone="info">{watchedProfileCustomerType}</Badge>
                <Badge tone={badgeToneForStatus(watchedProfileAccountStatus)}>{watchedProfileAccountStatus}</Badge>
                {currentCustomer.blockedUntil ? <Badge tone="danger">Problem account</Badge> : null}
              </div>
            </div>
          </div>
          <div className="customer-summary-bar__actions">
            <Button variant="secondary" onClick={() => openHref(currentCustomer.phone ? `tel:${currentCustomer.phone}` : null)} disabled={!currentCustomer.phone}>Call</Button>
            <Button variant="secondary" onClick={() => openCustomerMessageComposer()}>Message</Button>
            <Button variant="secondary" onClick={() => openCustomerSmsComposer()}>Send SMS</Button>
            <Button variant="secondary" onClick={() => navigate("/admin/bookings")}>Book new service</Button>
            <Button onClick={() => setActiveCustomerTab("internal-notes")}>Edit customer</Button>
          </div>
        </div>

        <form className="customer-detail-layout" onSubmit={profileForm.handleSubmit((values) => updateMutation.mutate(values))}>
          <div className="customer-sidebar">
            <Card title="Customer info" eyebrow="Quick profile">
              <div className="stack">
                <div className="booking-meta-card">
                  <p className="eyebrow">Primary contact</p>
                  <strong>{currentCustomer.phone}</strong>
                  <p>{currentCustomer.secondaryPhone || "No secondary phone saved"}</p>
                  <p>{currentCustomer.email || "No email saved"}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Default service address</p>
                  <strong>{primaryAddress?.label || "Primary service address"}</strong>
                  <p>{[primaryAddress?.line1, primaryAddress?.unitNumber ? `Unit ${primaryAddress.unitNumber}` : null].filter(Boolean).join(", ") || "No default address saved"}</p>
                  <p>{[primaryAddress?.city, primaryAddress?.state, primaryAddress?.postalCode].filter(Boolean).join(", ")}</p>
                  <AddressLinkButtons address={primaryAddress} compact />
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Pets & sensitivities</p>
                  <strong>{watchedProfilePets || "No pet notes"}</strong>
                  <p>{watchedProfileSensitivities || "No sensitivities or allergies saved."}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Files</p>
                  <strong>{watchedProfileDocuments.length} documents</strong>
                  <p>{watchedProfilePropertyPhotos.length} property photo links</p>
                </div>
              </div>
            </Card>

            <Card title="Account snapshot" eyebrow="Always visible" actions={<Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? "Saving..." : "Save customer"}</Button>}>
              <div className="stack">
                <div className="booking-meta-card">
                  <p className="eyebrow">Customer ID</p>
                  <strong>{currentCustomer.id}</strong>
                  <p>Created {formatDateLabel(currentCustomer.createdAt)}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Bookings</p>
                  <strong>{currentCustomer.bookingCount} total</strong>
                  <p>{currentCustomer.activeBookingCount} active • {currentCustomer.cancelledBookingCount} cancelled</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Inactive for</p>
                  <strong>{formatDaysInactive(currentCustomer.lastBookingAt, currentCustomer.createdAt)}</strong>
                  <p>{currentCustomer.blockedUntil ? `Blocked until ${formatDateLabel(currentCustomer.blockedUntil)}` : "Account active"}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Financials</p>
                  <strong>{formatCurrency(Number(currentCustomer.lifetimeValueAmount ?? 0))}</strong>
                  <p>{formatCurrency(Number(currentCustomer.outstandingBalanceAmount ?? 0))} outstanding</p>
                </div>
                {updateMutation.error ? <p className="error-text">{(updateMutation.error as Error).message}</p> : null}
              </div>
            </Card>
          </div>

          <div className="customer-tab-panel">
            <div className="customer-tab-bar">
              {[
                ["overview", "Overview"],
                ["internal-notes", "Customer Profile"],
                ["history", "Booking History"],
                ["properties", "Property Details"],
                ["payments", "Payments"],
                ["files", "Pictures and Docs"],
                ["issues", "Issues / Incidents"]
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`customer-tab-button ${activeCustomerTab === key ? "is-active" : ""}`}
                  onClick={() => {
                    setPendingCustomerSectionId(null);
                    setActiveCustomerTab(key as CustomerTabKey);
                    const nextParams = new URLSearchParams(customerSearchParams);
                    if (key === "overview") {
                      nextParams.delete("tab");
                    } else {
                      nextParams.set("tab", key);
                    }
                    setCustomerSearchParams(nextParams, { replace: true });
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeCustomerTab === "overview" ? (
              <div className="customer-card-grid">
                <SectionJumpTarget id="customer-overview-upcoming-bookings">
                  <Card title="Upcoming bookings" eyebrow="Overview">
                  {upcomingBookings.length ? (
                    <div className="booking-list">
                      {upcomingBookings.map((booking) => (
                        <div key={booking.id} className="booking-meta-card">
                          <p className="eyebrow">{booking.publicReference}</p>
                          <strong>{booking.serviceNames || "Service visit"}</strong>
                          <p>{formatDateTimeLabel(booking.startsAt)}</p>
                          <p>{booking.locationName || "Primary property"} • {booking.teamNames || "Team pending"}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="card-copy">No upcoming bookings are scheduled right now.</p>
                  )}
                  </Card>
                </SectionJumpTarget>

                <SectionJumpTarget id="customer-overview-service-profile">
                  <Card title="Service profile" eyebrow="Operations">
                  <div className="stack">
                    <div className="booking-meta-card">
                      <p className="eyebrow">Preferred contact</p>
                      <strong>{String(profileForm.watch("preferredContactMethod") ?? "Phone")}</strong>
                      <p>{derivedCustomerFrequency}</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">Preferred staff</p>
                      <strong>{preferredStaffLabel}</strong>
                      <p>{Boolean(profileForm.watch("englishSpeakingStaff")) ? "English speaking staff requested" : "No language preference saved"}</p>
                    </div>
                  </div>
                  </Card>
                </SectionJumpTarget>

                <SectionJumpTarget id="customer-overview-account-health">
                  <Card title="Account health" eyebrow="Alerts">
                  <div className="stack">
                    <div className="booking-meta-card">
                      <p className="eyebrow">Problem account</p>
                      <strong>{currentCustomer.blockedUntil ? `Blocked until ${formatDateLabel(currentCustomer.blockedUntil)}` : "Account active"}</strong>
                      <p>{currentCustomer.blockedReason || "No blocked reason saved."}</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">Tax handling</p>
                      <strong>
                        {Boolean(profileForm.watch("taxExempt"))
                          ? "Tax exempt"
                          : profileForm.watch("specialTaxRate") != null
                            ? `Special ${(Number(profileForm.watch("specialTaxRate")) * 100).toFixed(2)}%`
                            : "Standard tax"}
                      </strong>
                      <p>{formatCurrency(Number(currentCustomer.outstandingBalanceAmount ?? 0))} outstanding</p>
                    </div>
                  </div>
                  </Card>
                </SectionJumpTarget>

                <SectionJumpTarget id="customer-overview-rewards-follow-up">
                  <Card title="Rewards & follow-up" eyebrow="Retention">
                  <div className="stack">
                    <div className="booking-meta-card">
                      <p className="eyebrow">Rewards</p>
                      <strong>{Number(watchedProfileRewards.points ?? 0)} points</strong>
                      <p>{String(watchedProfileRewards.tier ?? "") || "No tier assigned"}</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">Tags & notes</p>
                      <strong>{currentCustomer.tags.length ? currentCustomer.tags.map((tag) => tag.name).join(", ") : "No tags yet"}</strong>
                      <p>{currentCustomer.notes || "No internal notes saved."}</p>
                    </div>
                  </div>
                  </Card>
                </SectionJumpTarget>

                <SectionJumpTarget id="customer-overview-marketing">
                  <Card title="Marketing & re-engagement" eyebrow="Communications">
                  <div className="stack">
                    <div className="booking-meta-card">
                      <p className="eyebrow">Marketing consent</p>
                      <strong>{customerMarketingSummary.data?.marketingEnabled ? "Enabled" : "Not enabled"}</strong>
                      <p>{customerMarketingSummary.data ? `${customerMarketingSummary.data.activeAutomationCount} active automation${customerMarketingSummary.data.activeAutomationCount === 1 ? "" : "s"}` : "Loading automation status..."}</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">Last completed booking</p>
                      <strong>{customerMarketingSummary.data?.lastCompletedBookingAt ? formatDateTimeLabel(customerMarketingSummary.data.lastCompletedBookingAt) : "No completed visit yet"}</strong>
                      <p>{customerMarketingSummary.data?.lastMarketingEmailAt ? `Last marketing email ${formatDateTimeLabel(customerMarketingSummary.data.lastMarketingEmailAt)}` : "No marketing email logged yet."}</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">Next automation</p>
                      <strong>{customerMarketingSummary.data?.nextAutomationName || "No queued automation"}</strong>
                      <p>{customerMarketingSummary.data?.nextAutomationDueAt ? `Due ${formatDateTimeLabel(customerMarketingSummary.data.nextAutomationDueAt)}` : "This customer is not currently queued for a re-engagement send."}</p>
                    </div>
                    {customerMarketingSummary.error ? <p className="error-text">{(customerMarketingSummary.error as Error).message}</p> : null}
                  </div>
                  </Card>
                </SectionJumpTarget>
              </div>
            ) : null}

            {activeCustomerTab === "history" ? (
              <SectionJumpTarget id="customer-history-bookings">
                <Card title="Booking history" eyebrow="Visit log">
                  <div className="stack">
                  <div className="grid two">
                    <Field label="Status filter">
                      <select value={historyStatusFilter} onChange={(event) => setHistoryStatusFilter(event.target.value)}>
                        <option value="all">All</option>
                        <option value="completed">Completed</option>
                        <option value="canceled">Canceled</option>
                        <option value="no-show">No-show</option>
                      </select>
                    </Field>
                    <Field label="Visit type filter">
                      <select value={historyVisitTypeFilter} onChange={(event) => setHistoryVisitTypeFilter(event.target.value)}>
                        <option value="all">All</option>
                        <option value="recurring">Recurring</option>
                        <option value="one-time">One-time</option>
                      </select>
                    </Field>
                  </div>

                  {filteredHistory.length ? (
                    <DataTable
                      columns={["Date", "Service", "Property", "Cleaner/team", "Duration", "Amount", "Invoice", "Status", "Notes"]}
                      rows={filteredHistory.map((booking) => [
                        formatDateTimeLabel(booking.startsAt),
                        booking.serviceNames || "Service visit",
                        booking.locationName || "Primary property",
                        booking.teamNames || "Team pending",
                        `${booking.durationMinutes || 0} min`,
                        formatCurrency(Number(booking.totalAmount ?? 0)),
                        booking.invoiceId ? (
                          <span className="stack stack--tight">
                            <Link to={`/admin/bookings/${booking.id}`}>{booking.invoiceNumber || "Invoice"}</Link>
                            <Badge tone={badgeToneForStatus(booking.invoiceStatus || "")}>
                              {(booking.invoiceStatus || "draft").replace(/_/g, " ")}
                            </Badge>
                            {booking.invoiceLastSentAt ? (
                              <a href={`/api/invoices/${booking.invoiceId}/pdf`} target="_blank" rel="noreferrer">
                                Download PDF
                              </a>
                            ) : null}
                          </span>
                        ) : (
                          "No invoice"
                        ),
                        booking.status,
                        booking.notes || "No notes"
                      ])}
                    />
                  ) : (
                    <p className="card-copy">No bookings match the selected filters.</p>
                  )}

                    <SectionJumpTarget id="customer-history-feedback">
                      <Card title="Feedback history" eyebrow="Ratings and photos">
                        {customerDetail.data.feedbackHistory?.length ? (
                          <div className="booking-list">
                            {customerDetail.data.feedbackHistory.map((entry) => (
                              <div key={entry.bookingId} className="booking-meta-card">
                                <p className="eyebrow">{entry.bookingPublicReference || entry.bookingId}</p>
                                <strong>
                                  {entry.effectiveRating == null ? "No rating saved" : `${entry.effectiveRating}/5 stars`}
                                </strong>
                                <p>{entry.serviceName || "Cleaning service"} - {formatDateTimeLabel(entry.startsAt)}</p>
                                <p>{entry.locationName || "Service location"}</p>
                                <p>{entry.feedback.comment || "No comment provided."}</p>
                                {entry.feedback.photos?.length ? (
                                  <div className="feedback-photo-grid">
                                    {entry.feedback.photos.map((photo) => (
                                      <a
                                        key={photo.key}
                                        href={`/api/bookings/${entry.bookingId}/feedback/photos/download?key=${encodeURIComponent(photo.key)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="feedback-photo-card"
                                      >
                                        <img src={`/api/bookings/${entry.bookingId}/feedback/photos/download?key=${encodeURIComponent(photo.key)}`} alt={photo.name} />
                                        <span>{photo.name}</span>
                                      </a>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="card-copy">No feedback history is available for this customer yet.</p>
                        )}
                      </Card>
                    </SectionJumpTarget>
                  </div>
                </Card>
              </SectionJumpTarget>
            ) : null}

            {activeCustomerTab === "properties" ? (
              <div className="stack">
                <SectionJumpTarget id="customer-properties-details">
                  <Card title="Property details" eyebrow={currentCustomer.addresses.length > 1 ? "Multiple properties" : "Single property"}>
                    <div className="customer-card-grid">
                      {currentCustomer.addresses.map((address, index) => (
                        <div key={address.id || `${address.label}-${index}`} className="booking-meta-card">
                          <p className="eyebrow">{address.isPrimary ? "Primary property" : "Additional property"}</p>
                          <strong>{address.line1 || `Property ${index + 1}`}</strong>
                          <p>{address.unitNumber ? `Unit ${address.unitNumber}` : "No unit number saved"}</p>
                          <p>{[address.city, address.state, address.postalCode].filter(Boolean).join(", ")}</p>
                          <p>{address.instructions || "No property notes saved."}</p>
                          <AddressLinkButtons address={address} compact />
                          {address.id ? (
                            <ZillowPropertyEnrichmentPanel
                              customerId={currentCustomer.id}
                              address={address}
                              onFetched={() => {
                                void queryClient.invalidateQueries({ queryKey: ["booking"] });
                              }}
                            />
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </Card>
                </SectionJumpTarget>
                <SectionJumpTarget id="customer-properties-manage">
                  <Card title="Manage properties" eyebrow="Editable addresses">
                    {renderAddressFields(profileForm, watchedProfileAddresses, true)}
                  </Card>
                </SectionJumpTarget>
              </div>
            ) : null}

            {activeCustomerTab === "payments" ? (
              <div className="stack">
                <div className="booking-meta-grid">
                  <div className="booking-meta-card">
                    <p className="eyebrow">Outstanding balance</p>
                    <strong>{formatCurrency(Number(currentCustomer.outstandingBalanceAmount ?? 0))}</strong>
                  </div>
                  <div className="booking-meta-card">
                    <p className="eyebrow">Lifetime spend</p>
                    <strong>{formatCurrency(Number(currentCustomer.lifetimeValueAmount ?? 0))}</strong>
                  </div>
                  <div className="booking-meta-card">
                    <p className="eyebrow">Last payment</p>
                    <strong>{lastPayment ? formatCurrency(Number(lastPayment.amount ?? 0)) : "No payments yet"}</strong>
                    <p>{lastPayment ? formatDateTimeLabel(lastPayment.createdAt) : "No payment history yet."}</p>
                  </div>
                  <div className="booking-meta-card">
                    <p className="eyebrow">Recurring plan</p>
                    <strong>{derivedCustomerFrequency}</strong>
                    <p>Based on appointment history.</p>
                  </div>
                </div>

                <SectionJumpTarget id="customer-payments-card">
                  <Card
                    title="Payments"
                    eyebrow="Finance summary"
                    actions={
                      <div className="customer-summary-bar__actions">
                        <Button variant="secondary" onClick={() => openHref(currentCustomer.email ? `mailto:${currentCustomer.email}` : null)} disabled={!currentCustomer.email}>Send invoice</Button>
                        <Button variant="secondary" onClick={() => navigate("/admin/payments")}>Collect payment</Button>
                        <Button variant="secondary" onClick={() => navigate("/admin/payments")}>View receipts</Button>
                      </div>
                    }
                  >
                    <div className="stack">
                      <Field label="Payment methods" hint="Comma-separated labels like Visa, ACH, Cash.">
                        <input
                          value={watchedProfilePaymentMethods.join(", ")}
                          onChange={(event) =>
                            profileForm.setValue(
                              "paymentMethods",
                              event.target.value
                                .split(",")
                                .map((value) => value.trim())
                                .filter(Boolean),
                              { shouldDirty: true }
                            )
                          }
                        />
                      </Field>

                      {paymentRecords.length ? (
                        <DataTable
                          columns={["Invoice #", "Date", "Amount", "Method", "Status"]}
                          rows={paymentRecords.map((payment) => [
                            payment.invoiceId ? (
                              <a href={`/api/invoices/${payment.invoiceId}/pdf`} target="_blank" rel="noreferrer">
                                {payment.invoiceNumber || payment.id}
                              </a>
                            ) : (
                              payment.invoiceNumber || payment.id
                            ),
                            formatDateTimeLabel(payment.createdAt),
                            formatCurrency(Number(payment.amount ?? 0)),
                            payment.paymentType || payment.provider || "Manual",
                            payment.status
                          ])}
                        />
                      ) : (
                        <p className="card-copy">No payment records are attached to this customer yet.</p>
                      )}
                    </div>
                  </Card>
                </SectionJumpTarget>
              </div>
            ) : null}

            {activeCustomerTab === "files" ? (
              <div className="stack">
                <div className="customer-card-grid">
                  <SectionJumpTarget id="customer-files-profile-photo">
                    <Card title="Profile photo" eyebrow="Core customer profile">
                      <div className="stack">
                        <div className="customer-photo-frame">
                          {watchedProfilePhotoUrl ? <img src={watchedProfilePhotoUrl} alt={`${displayName} profile`} /> : <span>{profileInitials || "C"}</span>}
                        </div>
                        <Field label="Profile photo URL">
                          <input {...profileForm.register("profilePhotoUrl")} placeholder="https://..." />
                        </Field>
                      </div>
                    </Card>
                  </SectionJumpTarget>

                  <SectionJumpTarget id="customer-files-property-photos">
                    <Card title="Property photos" eyebrow="Admin-only">
                      <div className="stack">
                        <Field label="Photo links" hint="One URL per line for property, before/after, or issue photos.">
                          <textarea
                            rows={6}
                            value={watchedProfilePropertyPhotos.join("\n")}
                            onChange={(event) =>
                              profileForm.setValue("propertyPhotos", parseLineSeparatedValues(event.target.value), {
                                shouldDirty: true
                              })
                            }
                          />
                        </Field>
                        <div className="customer-card-grid">
                          {watchedProfilePropertyPhotos.length ? (
                            watchedProfilePropertyPhotos.map((photo: string, index: number) => (
                              <div key={`${photo}-${index}`} className="customer-photo-frame">
                                <img src={photo} alt={`Property ${index + 1}`} />
                              </div>
                            ))
                          ) : (
                            <p className="card-copy">No property photo links saved yet.</p>
                          )}
                        </div>
                      </div>
                    </Card>
                  </SectionJumpTarget>
                </div>

                <SectionJumpTarget id="customer-files-documents">
                  <Card
                    title="Documents"
                    eyebrow="Customer-visible files"
                    actions={
                      <div className="customer-summary-bar__actions">
                        <input
                          ref={customerDocumentInputRef}
                          type="file"
                          style={{ display: "none" }}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (!file || !selectedCustomerId) {
                              return;
                            }
                            uploadCustomerDocumentMutation.mutate(file);
                            event.currentTarget.value = "";
                          }}
                        />
                        <Button
                          variant="secondary"
                          onClick={() => customerDocumentInputRef.current?.click()}
                          disabled={!selectedCustomerId || uploadCustomerDocumentMutation.isPending}
                        >
                          {uploadCustomerDocumentMutation.isPending ? "Uploading..." : "Upload document"}
                        </Button>
                      </div>
                    }
                  >
                    <div className="stack">
                      <Field label="Documents" hint="Add links manually or upload files directly.">
                        <textarea
                          rows={6}
                          value={manualProfileDocuments.join("\n")}
                          onChange={(event) =>
                            profileForm.setValue("documents", [...uploadedProfileDocuments, ...parseLineSeparatedValues(event.target.value)], {
                              shouldDirty: true
                            })
                          }
                        />
                      </Field>
                        {uploadCustomerDocumentMutation.error ? (
                          <p className="error-text">{(uploadCustomerDocumentMutation.error as Error).message}</p>
                        ) : null}
                      {watchedProfileDocuments.length ? (
                        <div className="booking-list">
                          {watchedProfileDocuments.map((document: string, index: number) => (
                            <div key={`${document}-${index}`} className="booking-meta-card">
                              <p className="eyebrow">Document {index + 1}</p>
                              {(() => {
                                const entry = parseCustomerDocumentEntry(document);
                                return (
                                  <div className="stack">
                                    <strong>{entry.name}</strong>
                                    {entry.isUploadedFile && entry.key && selectedCustomerId ? (
                                      <Button
                                        variant="secondary"
                                        onClick={() => window.open(`/api/customers/${selectedCustomerId}/documents/download?key=${encodeURIComponent(entry.key!)}`, "_blank", "noopener")}
                                      >
                                        Download
                                      </Button>
                                    ) : document.startsWith("http") ? (
                                      <a href={document} target="_blank" rel="noreferrer">
                                        Open link
                                      </a>
                                    ) : null}
                                  </div>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="card-copy">No documents are linked to this customer yet.</p>
                      )}
                    </div>
                  </Card>
                </SectionJumpTarget>
              </div>
            ) : null}

            {activeCustomerTab === "issues" ? (
              <div className="stack">
                <div className="booking-meta-grid">
                  <div className="booking-meta-card">
                    <p className="eyebrow">Refunds / disputes</p>
                    <strong>{watchedProfileRefundsDisputes.length}</strong>
                  </div>
                  <div className="booking-meta-card">
                    <p className="eyebrow">Issues / incidents</p>
                    <strong>{watchedProfileProblemAreas.length}</strong>
                  </div>
                  <div className="booking-meta-card">
                    <p className="eyebrow">Damages</p>
                    <strong>{watchedProfileDamageReports.length}</strong>
                  </div>
                </div>

                <div className="customer-card-grid">
                  <SectionJumpTarget id="customer-issues-incidents">
                    <Card title="Issues / incidents" eyebrow="Admin-only">
                      <div className="stack">
                        <Field label="Refunds / disputes" hint="One entry per line.">
                          <textarea
                            rows={5}
                            value={watchedProfileRefundsDisputes.join("\n")}
                            onChange={(event) =>
                              profileForm.setValue("refundsDisputes", parseLineSeparatedValues(event.target.value), {
                                shouldDirty: true
                              })
                            }
                          />
                        </Field>
                        <Field label="Past appointment">
                          <select value={issueDraft.bookingId} onChange={(event) => setIssueDraft((current) => ({ ...current, bookingId: event.target.value }))}>
                            <option value="">Select appointment</option>
                            {pastCustomerBookings.map((booking) => (
                              <option key={booking.id} value={booking.id}>
                                {booking.publicReference} - {formatDateTimeLabel(booking.startsAt)} - {booking.serviceNames || "Service visit"}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Issue description">
                          <textarea
                            rows={4}
                            value={issueDraft.description}
                            onChange={(event) => setIssueDraft((current) => ({ ...current, description: event.target.value }))}
                          />
                        </Field>
                        <div className="customer-summary-bar__actions">
                          <Button
                            variant="secondary"
                            onClick={() => {
                              const booking = pastCustomerBookings.find((item) => item.id === issueDraft.bookingId);
                              const entry = formatCustomerIssueEntry(booking, issueDraft.description);
                              if (!entry) {
                                return;
                              }
                              profileForm.setValue("problemAreas", [...watchedProfileProblemAreas, entry], { shouldDirty: true });
                              setIssueDraft({ bookingId: "", description: "" });
                            }}
                          >
                            Add issue
                          </Button>
                        </div>
                        {watchedProfileProblemAreas.length ? (
                          <div className="booking-list">
                            {watchedProfileProblemAreas.map((entry: string, index: number) => (
                              <div key={`${entry}-${index}`} className="booking-meta-card">
                                <strong>{entry}</strong>
                                <Button
                                  variant="ghost"
                                  onClick={() =>
                                    profileForm.setValue(
                                      "problemAreas",
                                      watchedProfileProblemAreas.filter((_: string, itemIndex: number) => itemIndex !== index),
                                      { shouldDirty: true }
                                    )
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="card-copy">No issues saved yet.</p>
                        )}
                      </div>
                    </Card>
                  </SectionJumpTarget>

                  <SectionJumpTarget id="customer-issues-damages">
                    <Card title="Damages" eyebrow="Service follow-up">
                      <div className="stack">
                        <Field label="Past appointment">
                          <select value={damageDraft.bookingId} onChange={(event) => setDamageDraft((current) => ({ ...current, bookingId: event.target.value }))}>
                            <option value="">Select appointment</option>
                            {pastCustomerBookings.map((booking) => (
                              <option key={booking.id} value={booking.id}>
                                {booking.publicReference} - {formatDateTimeLabel(booking.startsAt)} - {booking.serviceNames || "Service visit"}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Damage description">
                          <textarea
                            rows={4}
                            value={damageDraft.description}
                            onChange={(event) => setDamageDraft((current) => ({ ...current, description: event.target.value }))}
                          />
                        </Field>
                        <div className="customer-summary-bar__actions">
                          <Button
                            variant="secondary"
                            onClick={() => {
                              const booking = pastCustomerBookings.find((item) => item.id === damageDraft.bookingId);
                              const entry = formatCustomerIssueEntry(booking, damageDraft.description);
                              if (!entry) {
                                return;
                              }
                              profileForm.setValue("damageReports", [...watchedProfileDamageReports, entry], { shouldDirty: true });
                              setDamageDraft({ bookingId: "", description: "" });
                            }}
                          >
                            Add damage
                          </Button>
                        </div>
                        {watchedProfileDamageReports.length ? (
                          <div className="booking-list">
                            {watchedProfileDamageReports.map((entry: string, index: number) => (
                              <div key={`${entry}-${index}`} className="booking-meta-card">
                                <strong>{entry}</strong>
                                <Button
                                  variant="ghost"
                                  onClick={() =>
                                    profileForm.setValue(
                                      "damageReports",
                                      watchedProfileDamageReports.filter((_: string, itemIndex: number) => itemIndex !== index),
                                      { shouldDirty: true }
                                    )
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="card-copy">No damages saved yet.</p>
                        )}
                      </div>
                    </Card>
                  </SectionJumpTarget>
                </div>

                <SectionJumpTarget id="customer-issues-staff-quality">
                  <Card title="Staff blocks & quality" eyebrow="Admin controls">
                    <div className="stack">
                      <div>
                        <p className="eyebrow">Blocked staff</p>
                        <p className="card-copy">Prevent specific cleaners or teams from being assigned to this customer.</p>
                        <p className="card-copy">{blockedStaffLabel}</p>
                      </div>
                      <div className="tag-grid">
                        {activeStaff.length ? (
                          activeStaff.map((member) => {
                            const active = matchesPreferredStaffSelection(member, watchedProfileBlockedStaffIds);
                            return (
                              <label key={member.id} className={`tag-choice ${active ? "is-active" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={active}
                                  onChange={(event) => {
                                    const next = event.target.checked
                                      ? [...watchedProfileBlockedStaffIds.filter((value: string) => value !== member.code), member.id]
                                      : watchedProfileBlockedStaffIds.filter((value: string) => value !== member.id && value !== member.code);
                                    profileForm.setValue("blockedStaffIds", next, { shouldDirty: true });
                                  }}
                                />
                                <span>{getStaffDisplayName(member)}</span>
                              </label>
                            );
                          })
                        ) : (
                          <p className="card-copy">No active staff records are available yet.</p>
                        )}
                      </div>

                      <div className="grid two">
                        <Field label="Client rating" hint="0 to 5">
                          <input
                            type="number"
                            min="0"
                            max="5"
                            step="0.1"
                            value={profileForm.watch("clientRating") ?? ""}
                            onChange={(event) =>
                              profileForm.setValue(
                                "clientRating",
                                event.target.value === "" ? undefined : Number(event.target.value),
                                { shouldDirty: true }
                              )
                            }
                          />
                        </Field>
                        <Field label="Customer satisfaction score" hint="0 to 100">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={profileForm.watch("customerSatisfactionScore") ?? ""}
                            onChange={(event) =>
                              profileForm.setValue(
                                "customerSatisfactionScore",
                                event.target.value === "" ? undefined : Number(event.target.value),
                                { shouldDirty: true }
                              )
                            }
                          />
                        </Field>
                      </div>
                    </div>
                  </Card>
                </SectionJumpTarget>
              </div>
            ) : null}

            {activeCustomerTab === "internal-notes" ? (
              <div className="stack">
                <SectionJumpTarget id="customer-profile-main">
                  <Card title="Customer profile" eyebrow="Customer admin record">
                  <div className="grid two">
                    <Field label="First name"><input {...profileForm.register("firstName")} /></Field>
                    <Field label="Last name"><input {...profileForm.register("lastName")} /></Field>
                    <Field label="Company name"><input {...profileForm.register("companyName")} /></Field>
                    <Field label="Email"><input {...profileForm.register("email")} /></Field>
                    <Field label="Primary phone"><input {...profileForm.register("phone")} /></Field>
                    <Field label="Secondary phone"><input {...profileForm.register("secondaryPhone")} /></Field>
                    <Field label="Customer type">
                      <select {...profileForm.register("customerType")}>
                        {["Residential", "Commercial", "Airbnb", "VIP"].map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Account status">
                      <select {...profileForm.register("accountStatus")}>
                        {["Active", "Inactive", "Problem account"].map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Preferred contact method">
                      <select {...profileForm.register("preferredContactMethod")}>
                        {["Phone", "Text", "Email"].map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Stripe ID"><input {...profileForm.register("stripeCustomerId")} placeholder="cus_..." /></Field>
                    <Field label="Blocked date"><DateInput {...profileForm.register("blockedUntil")} /></Field>
                    <Field label="Blocked reason"><input {...profileForm.register("blockedReason")} placeholder="Collections hold, service issue, etc." /></Field>
                    <Field label="Lead source">
                      <select {...profileForm.register("leadSourceId")}>
                        <option value="">Direct / not set</option>
                        {leadSourceOptions.map((option) => (
                          <option key={option.id} value={option.id}>{option.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Pets"><input {...profileForm.register("pets")} placeholder="Dogs, cats, crated pet, etc." /></Field>
                    <Field label="Sensitivities / allergies"><input {...profileForm.register("sensitivities")} placeholder="Fragrance free, bleach sensitivity, etc." /></Field>
                    <Field label="Tax exempt">
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={Boolean(profileForm.watch("taxExempt"))}
                          onChange={(event) => profileForm.setValue("taxExempt", event.target.checked, { shouldDirty: true })}
                        />
                        <span>Exclude this customer from standard sales tax.</span>
                      </label>
                    </Field>
                    <Field label="Special sales tax" hint="Use a decimal rate such as 0.0825 for 8.25%.">
                      <input
                        type="number"
                        step="0.0001"
                        value={profileForm.watch("specialTaxRate") ?? ""}
                        onChange={(event) =>
                          profileForm.setValue(
                            "specialTaxRate",
                            event.target.value === "" ? undefined : Number(event.target.value),
                            { shouldDirty: true }
                          )
                        }
                      />
                    </Field>
                  </div>

                  <Field label="Customer profile notes">
                    <textarea {...profileForm.register("notes")} rows={4} />
                  </Field>
                  </Card>
                </SectionJumpTarget>

                <SectionJumpTarget id="customer-profile-preferences">
                  <Card title="Staff, tags, and preferences" eyebrow="Admin-only settings">
                    <div className="stack">
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={Boolean(profileForm.watch("englishSpeakingStaff"))}
                        onChange={(event) => profileForm.setValue("englishSpeakingStaff", event.target.checked, { shouldDirty: true })}
                      />
                      <span>Requires English speaking staff</span>
                    </label>

                    <div>
                      <p className="eyebrow">Preferred staff</p>
                      <p className="card-copy">Mark staff this customer prefers by name.</p>
                    </div>
                    <div className="tag-grid">
                      {activeStaff.length ? (
                        activeStaff.map((member) => {
                          const active = matchesPreferredStaffSelection(member, watchedProfilePreferredStaffIds);
                          return (
                            <label key={member.id} className={`tag-choice ${active ? "is-active" : ""}`}>
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? [...watchedProfilePreferredStaffIds.filter((value: string) => value !== member.code), member.id]
                                    : watchedProfilePreferredStaffIds.filter((value: string) => value !== member.id && value !== member.code);
                                  profileForm.setValue("preferredStaffIds", next, { shouldDirty: true });
                                }}
                              />
                              <span>{getStaffDisplayName(member)}</span>
                            </label>
                          );
                        })
                      ) : (
                        <p className="card-copy">No active staff records are available yet.</p>
                      )}
                    </div>

                    <div>
                      <p className="eyebrow">Communication opt-in status</p>
                      <p className="card-copy">Track whether this customer has opted in to SMS and email communication.</p>
                    </div>
                    <div className="tag-grid">
                      {[ 
                        ["smsOptInStatus", "SMS opt-in status"],
                        ["smsReminderOptInStatus", "SMS reminder opt-in"],
                        ["emailOptInStatus", "Email opt-in status"]
                      ].map(([key, label]) => (
                        <label key={key} className={`tag-choice ${watchedEmailPreferences[key as keyof typeof watchedEmailPreferences] ? "is-active" : ""}`}>
                          <input
                            type="checkbox"
                            checked={Boolean(watchedEmailPreferences[key as keyof typeof watchedEmailPreferences])}
                            onChange={(event) => profileForm.setValue(`emailPreferences.${key}` as any, event.target.checked, { shouldDirty: true })}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>

                    <div>
                      <p className="eyebrow">Email preferences</p>
                      <p className="card-copy">Control operational and marketing email categories for this customer profile.</p>
                    </div>
                    <div className="tag-grid">
                      {[
                        ["bookingConfirmation", "Booking confirmations"],
                        ["reminders", "Reminders"],
                        ["receipts", "Receipts"],
                        ["marketing", "Marketing"]
                      ].map(([key, label]) => (
                        <label key={key} className={`tag-choice ${watchedEmailPreferences[key as keyof typeof watchedEmailPreferences] ? "is-active" : ""}`}>
                          <input
                            type="checkbox"
                            checked={Boolean(watchedEmailPreferences[key as keyof typeof watchedEmailPreferences])}
                            onChange={(event) => profileForm.setValue(`emailPreferences.${key}` as any, event.target.checked, { shouldDirty: true })}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>

                    <div>
                      <p className="eyebrow">Tags</p>
                      <p className="card-copy">Use reusable customer tags to mark recurring clients, VIPs, and follow-up segments.</p>
                    </div>
                    <div className="tag-grid">
                      {activeCustomerTags.map((tag) => {
                        const active = watchedProfileTagIds.includes(tag.id);
                        return (
                          <label key={tag.id} className={`tag-choice ${active ? "is-active" : ""}`}>
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={(event) => {
                                const next = event.target.checked
                                  ? [...watchedProfileTagIds, tag.id]
                                  : watchedProfileTagIds.filter((value: string) => value !== tag.id);
                                profileForm.setValue("tagIds", next, { shouldDirty: true });
                              }}
                            />
                            <span className="tag-swatch" style={{ backgroundColor: tag.color }} />
                            <span>{tag.name}</span>
                          </label>
                        );
                      })}
                    </div>
                    </div>
                  </Card>
                </SectionJumpTarget>
              </div>
            ) : null}
          </div>

          <div className="customer-profile-bottom-actions">
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save customer"}
            </Button>
            {updateMutation.error ? <p className="error-text">{(updateMutation.error as Error).message}</p> : null}
          </div>

          <SectionJumpTarget id="customer-profile-portal" className="customer-portal-bottom">
            <Card title="Customer portal" eyebrow="My Account access">
              <div className="stack">
                <div className="grid two">
                  <div className="booking-meta-card">
                    <p className="eyebrow">Portal status</p>
                    <strong><Badge tone={portalStatusTone}>{portalStatusLabel}</Badge></strong>
                    <p>{currentCustomer.email || "Add an email before sending portal access."}</p>
                  </div>
                  <div className="booking-meta-card">
                    <p className="eyebrow">Access history</p>
                    <strong>{currentCustomer.portalLastLoginAt ? `Last login ${formatDateTimeLabel(currentCustomer.portalLastLoginAt)}` : "No portal login yet"}</strong>
                    <p>{currentCustomer.portalInvitedAt ? `Invited ${formatDateTimeLabel(currentCustomer.portalInvitedAt)}` : "No invite sent from the portal system yet."}</p>
                    <p>{currentCustomer.portalActivatedAt ? `Activated ${formatDateTimeLabel(currentCustomer.portalActivatedAt)}` : "Not activated yet."}</p>
                  </div>
                </div>
                <Field label="Website login link" hint="Use this same link from accessmaids.com or support messages.">
                  <input value={customerPortalLoginUrl} readOnly />
                </Field>
                <div className="customer-directory-actions">
                  <Button
                    variant="secondary"
                    onClick={() => portalSignInLinkMutation.mutate()}
                    disabled={!currentCustomer.email || portalSignInLinkMutation.isPending}
                  >
                    {portalSignInLinkMutation.isPending ? "Sending..." : "Send sign-in link"}
                  </Button>
                  <Button variant="secondary" onClick={() => copyCustomerPortalLoginLink()}>
                    Copy portal link
                  </Button>
                </div>
                {portalActionFeedback ? (
                  <p className={portalSignInLinkMutation.isError ? "error-text" : "card-copy"}>{portalActionFeedback}</p>
                ) : null}
              </div>
            </Card>
          </SectionJumpTarget>
        </form>
      </div>
    );
  };

  if (isCustomerProfilePage) {
    return (
      <OpsPageShell>
        <OpsHeader
          eyebrow={problemOnly ? "Problem account" : "Customer profile"}
          title={problemOnly ? "Problem account profile" : "Customer profile"}
          description={
            problemOnly
              ? "Review profile details, booking history, payments, files, and internal service notes for this problem account."
              : "Review profile details, booking history, payments, files, and internal service notes."
          }
          actions={
            <div className="customer-directory-actions">
              <Button variant="secondary" onClick={() => navigate(customerBasePath)}>
                {problemOnly ? "Back to problem accounts" : "Back to customers"}
              </Button>
              <Button variant="ghost" onClick={() => confirmDeleteCustomer()} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? "Deleting..." : "Delete customer"}
              </Button>
            </div>
          }
        />
        {renderCustomerWorkspace()}
        {renderCustomerMessageComposer()}
        {renderCustomerSmsComposer()}
      </OpsPageShell>
    );
  }

  return (
      <OpsPageShell>
        <OpsHeader
          eyebrow={problemOnly ? "Risk review" : "Customer operations"}
          title={`${customerSectionLabel} (${totalCustomers})`}
          description={
            problemOnly
              ? "Search your problem-account directory, import or export records, and open a full profile when you need it."
              : "Search your customer directory, import or export records, and open a full customer profile when you need it."
          }
          actions={
            <div className="customer-directory-actions">
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,.json,text/csv,application/json"
              style={{ display: "none" }}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }

                try {
                  const contents = await file.text();
                  const items = parseCustomerTransferFile(file.name, contents);
                  const preview = buildCustomerImportPreview(items);
                  const confirmed = window.confirm(
                    formatCustomerImportPreviewMessage(preview, problemOnly ? "problem accounts" : "customers")
                  );
                  if (!confirmed) {
                    setImportFeedback("Customer import cancelled.");
                    event.currentTarget.value = "";
                    return;
                  }
                  setImportFeedback(
                    problemOnly
                      ? `Importing ${items.length} problem accounts from ${file.name}. ${preview.unsubscribers} unsubscribers will be suppressed.`
                      : `Importing ${items.length} customers from ${file.name}. ${preview.unsubscribers} unsubscribers will be suppressed.`
                  );
                  importMutation.mutate(items);
                } catch (error) {
                  setImportFeedback(error instanceof Error ? error.message : "Customer import failed.");
                }

                event.currentTarget.value = "";
              }}
            />
            <Button variant="secondary" onClick={() => importInputRef.current?.click()}>
              <span className="tw:inline-flex tw:items-center tw:gap-2">
                <Upload className="tw:size-4" aria-hidden="true" />
                {problemOnly ? "Import problem accounts" : "Import customers"}
              </span>
            </Button>
            <Button variant="secondary" onClick={() => void exportCustomers()}>
              <span className="tw:inline-flex tw:items-center tw:gap-2">
                <Download className="tw:size-4" aria-hidden="true" />
                {problemOnly ? "Export problem accounts" : "Export customers"}
              </span>
            </Button>
            {!problemOnly ? (
              <Button
                onClick={() => {
                  setImportFeedback("");
                  setShowCreateCustomerForm((current) => !current);
                }}
              >
                <span className="tw:inline-flex tw:items-center tw:gap-2">
                  <UserPlus className="tw:size-4" aria-hidden="true" />
                  {showCreateCustomerForm ? "Close new" : "Add new"}
                </span>
              </Button>
            ) : null}
          </div>
        }
      />
      <OpsMetricGrid>
        <OpsMetricCard
          label={problemOnly ? "Problem accounts" : "Total customers"}
          value={totalCustomers}
          detail={problemOnly ? "Flagged customer records" : "Directory records"}
          icon={Users}
          tone={problemOnly ? "rose" : "blue"}
        />
        <OpsMetricCard
          label="Visible matches"
          value={filteredCustomers.length}
          detail={search.trim() ? "After search and filters" : "Current filter result"}
          icon={Search}
          tone="emerald"
        />
        <OpsMetricCard
          label="Active"
          value={activeCustomerCount}
          detail="Available for normal booking"
          icon={CheckCircle2}
          tone="emerald"
        />
        <OpsMetricCard
          label={problemOnly ? "Inactive" : "Problem / inactive"}
          value={problemOnly ? inactiveCustomerCount : problemCustomerCount + inactiveCustomerCount}
          detail={problemOnly ? "Inactive records in this data set" : "Needs review before service"}
          icon={AlertCircle}
          tone="amber"
        />
      </OpsMetricGrid>
      <Card
        title={problemOnly ? "Problem account directory" : "Customer directory"}
        eyebrow={problemOnly ? "Search, import, export, and open a problem account profile" : "Search, import, export, and open a profile"}
        className="customer-directory-card"
      >
        <div className="customer-directory-toolbar">
          {!problemOnly ? (
            <select value={customerStatusFilter} onChange={(event) => setCustomerStatusFilter(event.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="problem">Problem account</option>
              <option value="all">All customers</option>
            </select>
          ) : null}
          <input
            className="customer-directory-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={problemOnly ? "Search problem accounts by name, company, phone, or email" : "Search customers by name, company, phone, or email"}
          />
        </div>

        {importFeedback ? <p className="card-copy">{importFeedback}</p> : null}
        {importMutation.error ? <p className="error-text">{(importMutation.error as Error).message}</p> : null}
        {deleteMutation.error ? <p className="error-text">{(deleteMutation.error as Error).message}</p> : null}

        {!problemOnly && showCreateCustomerForm ? (
          <Card title="New customer" eyebrow="Create profile">
            <form className="grid two" onSubmit={createForm.handleSubmit((values) => createMutation.mutate(values))}>
              <Field label="First name"><input {...createForm.register("firstName")} /></Field>
              <Field label="Last name"><input {...createForm.register("lastName")} /></Field>
              <Field label="Company name"><input {...createForm.register("companyName")} /></Field>
              <Field label="Email"><input {...createForm.register("email")} /></Field>
              <Field label="Primary phone"><input {...createForm.register("phone")} /></Field>
              <Field label="Secondary phone"><input {...createForm.register("secondaryPhone")} /></Field>
              <Field label="Lead source">
                <select {...createForm.register("leadSourceId")}>
                  <option value="">Direct / not set</option>
                  {leadSourceOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="SMS opt-in status">
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(createForm.watch("emailPreferences.smsOptInStatus"))}
                    onChange={(event) => createForm.setValue("emailPreferences.smsOptInStatus", event.target.checked, { shouldDirty: true })}
                  />
                  <span>Customer can receive SMS</span>
                </label>
              </Field>
              <Field label="SMS reminder opt-in">
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(createForm.watch("emailPreferences.smsReminderOptInStatus"))}
                    onChange={(event) =>
                      createForm.setValue("emailPreferences.smsReminderOptInStatus", event.target.checked, { shouldDirty: true })
                    }
                  />
                  <span>Customer wants SMS reminders for appointments</span>
                </label>
              </Field>
              <Field label="Email opt-in status">
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(createForm.watch("emailPreferences.emailOptInStatus"))}
                    onChange={(event) => createForm.setValue("emailPreferences.emailOptInStatus", event.target.checked, { shouldDirty: true })}
                  />
                  <span>Customer can receive email</span>
                </label>
              </Field>
              <Field label="Address"><input value={String(watchedCreateAddresses[0]?.line1 ?? "")} onChange={(event) => updateAddresses(createForm, [{ ...createEmptyCustomerAddress(0), ...(watchedCreateAddresses[0] ?? {}), line1: event.target.value }])} /></Field>
              <Field label="Unit number"><input value={String(watchedCreateAddresses[0]?.unitNumber ?? "")} onChange={(event) => updateAddresses(createForm, [{ ...createEmptyCustomerAddress(0), ...(watchedCreateAddresses[0] ?? {}), unitNumber: event.target.value }])} /></Field>
              <Field label="City"><input value={String(watchedCreateAddresses[0]?.city ?? "")} onChange={(event) => updateAddresses(createForm, [{ ...createEmptyCustomerAddress(0), ...(watchedCreateAddresses[0] ?? {}), city: event.target.value }])} /></Field>
              <Field label="State"><input value={String(watchedCreateAddresses[0]?.state ?? "")} onChange={(event) => updateAddresses(createForm, [{ ...createEmptyCustomerAddress(0), ...(watchedCreateAddresses[0] ?? {}), state: event.target.value }])} /></Field>
              <Field label="Postal / zip code"><input value={String(watchedCreateAddresses[0]?.postalCode ?? "")} onChange={(event) => updateAddresses(createForm, [{ ...createEmptyCustomerAddress(0), ...(watchedCreateAddresses[0] ?? {}), postalCode: event.target.value }])} /></Field>
              <Field label="Notes">
                <textarea {...createForm.register("notes")} rows={3} />
              </Field>
              <div className="span-two">
                {createMutation.error ? <p className="error-text">{(createMutation.error as Error).message}</p> : null}
                <div className="customer-summary-bar__actions">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      createForm.reset(getCustomerFormDefaults());
                      setShowCreateCustomerForm(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? "Creating..." : "Create customer"}
                  </Button>
                </div>
              </div>
            </form>
          </Card>
        ) : null}

        <div className="table-wrap">
          <table className="table customer-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Default address</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Type</th>
                <th>Status</th>
                <th>Bookings</th>
                <th>Next / Last booking</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length ? (
                filteredCustomers.map((item) => {
                  const rowStatus = item.account_status || (item.blocked_until ? "Problem account" : "Active");
                  return (
                    <tr
                      key={item.id}
                      onClick={() => navigate(`${customerBasePath}/${item.id}`)}
                    >
                      <td>
                        <div className="customer-table__name">
                          <strong>{`${item.first_name} ${item.last_name}`.trim()}</strong>
                          {item.secondary_phone || item.lead_source_name ? (
                            <span>{item.secondary_phone || item.lead_source_name}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>{item.company_name || "—"}</td>
                      <td>{item.default_service_address || "—"}</td>
                      <td>{item.email || "—"}</td>
                      <td>{item.phone || "—"}</td>
                      <td>{item.customer_type || "Residential"}</td>
                      <td>
                        <Badge tone={badgeToneForStatus(rowStatus)}>{rowStatus}</Badge>
                      </td>
                      <td>{item.booking_count}</td>
                      <td>{formatDateLabel(item.next_booking_at || item.last_booking_at)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9}>
                    <div className="empty-state">
                      <h3>{problemOnly ? "No problem accounts found" : "No customers found"}</h3>
                      <p>
                        {problemOnly
                          ? "Try a different search or import a problem-account file."
                          : "Try a different search or filter, or add a new customer."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </OpsPageShell>
  );
}

function AdminBookingCreateCard({ onCreated }: { onCreated: (bookingId: string) => void }) {
  const queryClient = useQueryClient();
  const [selectedExistingCustomerId, setSelectedExistingCustomerId] = useState("");

  const form = useForm<any>({
    resolver: zodResolver(bookingCreateInputSchema),
    defaultValues: {
      ...getBookingFormDefaults(),
      source: "admin"
    }
  });

  const customers = useQuery({
    queryKey: ["customers"],
    queryFn: () => apiFetch<{ items: CustomerListItem[] }>("/api/customers")
  });
  const bookingFormConfig = useQuery({
    queryKey: ["booking-form-config"],
    queryFn: () => apiFetch<BookingFormRuntimeConfig>("/api/booking-form/config")
  });

  const locationId = form.watch("locationId");
  const serviceId = form.watch("serviceId");
  const frequencyRuleId = form.watch("frequencyRuleId");
  const calendarProfileId = form.watch("calendarProfileId");
  const bookingSpotId = form.watch("bookingSpotId");
  const optionIds = normalizeStringArray(form.watch("optionIds"));
  const startsAtValue = form.watch("startsAt");
  const promoCode = form.watch("promoCode");
  const giftCardCode = form.watch("giftCardCode");
  const depositChoice = form.watch("depositChoice");
  const dateValue = startsAtValue.slice(0, 10);
  const depositRate = depositChoice === "full" ? 1 : depositChoice === "none" ? 0 : 0.2;
  const customerPhoneValue = form.watch("customer.phone") ?? "";
  const customerSecondaryPhoneValue = form.watch("customer.secondaryPhone") ?? "";
  const customerTypeValue = form.watch("customer.customerType") ?? "";
  const filteredServices = (bookingFormConfig.data?.services ?? []).filter((item) => isActiveRecord(item));
  const currentService = filteredServices.find((item) => item.id === serviceId);
  const visibleOptions = getVisibleOptionsForSelection(
    bookingFormConfig.data?.serviceOptions ?? [],
    currentService,
    undefined
  );
  const availableFrequencyRules = getFrequencyRulesForSelection(
    bookingFormConfig.data?.frequencyRules ?? [],
    currentService
  );
  const resolvedCalendarProfileId = calendarProfileId || resolveCalendarProfileId(currentService);
  const selectedCalendarProfile = (bookingFormConfig.data?.calendarProfiles ?? []).find(
    (item) => item.id === resolvedCalendarProfileId
  );
  const activeLocations = getEligibleLocations(
    bookingFormConfig.data?.locations ?? [],
    selectedCalendarProfile,
    currentService
  );
  const selectedAdminLocation = activeLocations.find((item) => item.id === locationId) ?? activeLocations[0];
  const availableSpots = getEligibleBookingSpots(
    bookingFormConfig.data?.bookingSpots ?? [],
    locationId,
    serviceId,
    selectedCalendarProfile
  );
  const currentAdminDetailsRaw = form.watch("adminDetails");
  const currentAdminDetails =
    currentAdminDetailsRaw && typeof currentAdminDetailsRaw === "object" && !Array.isArray(currentAdminDetailsRaw)
      ? (currentAdminDetailsRaw as Record<string, string>)
      : {};
  const currentAdminScenario = resolveServiceScenario(
    bookingFormConfig.data?.serviceScenarios ?? [],
    serviceId,
    String(currentAdminDetails.square_footage ?? "")
  );
  const currentAdminRemodelProfile = getScenarioRemodelProfile(currentAdminScenario, currentService);
  const adminConfiguredTaxRate = Math.max(0, Number(bookingFormConfig.data?.general?.taxRate ?? 0) || 0);
  const adminCustomerSuppliesEquipmentSettings = bookingFormConfig.data?.bookingForm.customerSuppliesEquipment;
  const adminCustomerSuppliesEquipmentEnabled = Boolean(adminCustomerSuppliesEquipmentSettings?.enabled);
  const adminCustomerSuppliesEquipmentRequiredItems = getCustomerSuppliesRequiredItems(
    adminCustomerSuppliesEquipmentSettings
  );
  const adminCustomerSuppliesChoice = getCustomerSuppliesChoice(currentAdminDetails);
  const adminPreferredFrequencyRuleId = useMemo(
    () => getPreferredFrequencyRuleId(availableFrequencyRules),
    [availableFrequencyRules]
  );

  useEffect(() => {
    if (filteredServices.length && !currentService) {
      form.setValue("serviceId", filteredServices[0].id);
    }
  }, [currentService, filteredServices, form]);

  useEffect(() => {
    if (resolvedCalendarProfileId !== form.getValues("calendarProfileId")) {
      form.setValue("calendarProfileId", resolvedCalendarProfileId);
    }
  }, [form, resolvedCalendarProfileId]);

  useEffect(() => {
    if (activeLocations.length && !activeLocations.some((item) => item.id === locationId)) {
      form.setValue("locationId", activeLocations[0].id);
    }
  }, [activeLocations, form, locationId]);

  useEffect(() => {
    if (availableSpots.length && !availableSpots.some((item) => item.id === bookingSpotId)) {
      form.setValue("bookingSpotId", availableSpots[0].id);
    }
  }, [availableSpots, bookingSpotId, form]);

  useEffect(() => {
    const visibleOptionIds = new Set(visibleOptions.map((option) => option.id));
    const nextOptionIds = optionIds.filter((optionId) => visibleOptionIds.has(optionId));
    if (nextOptionIds.length !== optionIds.length) {
      form.setValue("optionIds", nextOptionIds);
    }
  }, [form, optionIds, visibleOptions]);

  useEffect(() => {
    if (bookingFormConfig.data?.bookingForm.enableFrequencyStep === false) {
      if (frequencyRuleId) {
        form.setValue("frequencyRuleId", "");
      }
      return;
    }

    if (!availableFrequencyRules.length) {
      if (frequencyRuleId) {
        form.setValue("frequencyRuleId", "");
      }
      return;
    }

    if (frequencyRuleId && !availableFrequencyRules.some((item) => item.id === frequencyRuleId)) {
      form.setValue("frequencyRuleId", adminPreferredFrequencyRuleId);
      return;
    }

    if (!frequencyRuleId && adminPreferredFrequencyRuleId) {
      form.setValue("frequencyRuleId", adminPreferredFrequencyRuleId);
    }
  }, [
    adminPreferredFrequencyRuleId,
    availableFrequencyRules,
    bookingFormConfig.data?.bookingForm.enableFrequencyStep,
    form,
    frequencyRuleId
  ]);

  const selectedExistingCustomer = useQuery({
    queryKey: ["customer", selectedExistingCustomerId],
    queryFn: () => apiFetch<CustomerDetail>(`/api/customers/${selectedExistingCustomerId}`),
    enabled: Boolean(selectedExistingCustomerId)
  });

  const renderCreateAdminFields = (fields: BookingAdminFieldConfig[]) => (
    <div className="grid two">
      {fields.map((field) => (
        renderBookingAdminFieldControl({
          field,
          value: String(form.watch(`adminDetails.${field.key}`) ?? ""),
          onChange: (value) => form.setValue(`adminDetails.${field.key}`, value, { shouldDirty: true })
        })
      ))}
    </div>
  );

  useEffect(() => {
    if (!selectedExistingCustomer.data) {
      return;
    }

    const primaryAddress = selectedExistingCustomer.data.addresses[0];
    form.setValue("customer.firstName", selectedExistingCustomer.data.firstName);
    form.setValue("customer.lastName", selectedExistingCustomer.data.lastName);
    form.setValue("customer.companyName", selectedExistingCustomer.data.companyName ?? "");
    form.setValue("customer.email", selectedExistingCustomer.data.email ?? "");
    form.setValue("customer.phone", formatUsPhoneInput(selectedExistingCustomer.data.phone ?? ""));
    form.setValue("customer.secondaryPhone", formatUsPhoneInput(selectedExistingCustomer.data.secondaryPhone ?? ""));
    form.setValue("customer.notes", selectedExistingCustomer.data.notes ?? "");
    form.setValue("customer.tagIds", selectedExistingCustomer.data.tags.map((tag) => tag.id));
    form.setValue("customer.address.label", primaryAddress?.label ?? "Service address");
    form.setValue("customer.address.line1", primaryAddress?.line1 ?? "");
    form.setValue("customer.address.line2", primaryAddress?.line2 ?? "");
    form.setValue("customer.address.unitNumber", primaryAddress?.unitNumber ?? "");
    form.setValue("customer.address.city", primaryAddress?.city ?? "");
    form.setValue("customer.address.state", primaryAddress?.state ?? "");
    form.setValue("customer.address.postalCode", primaryAddress?.postalCode ?? "");
    form.setValue("customer.address.country", primaryAddress?.country ?? "US");
    form.setValue("customer.address.instructions", primaryAddress?.instructions ?? "");
    form.setValue("customer.customerType", selectedExistingCustomer.data.customerType ?? "Residential");
    form.setValue("customer.pets", selectedExistingCustomer.data.pets ?? "");
    form.setValue("customer.sensitivities", selectedExistingCustomer.data.sensitivities ?? "");
    form.setValue("customer.preferredStaffIds", selectedExistingCustomer.data.preferredStaffIds ?? []);
    form.setValue("customer.blockedStaffIds", selectedExistingCustomer.data.blockedStaffIds ?? []);
    form.setValue("customer.englishSpeakingStaff", Boolean(selectedExistingCustomer.data.englishSpeakingStaff));

    form.setValue("adminDetails.company_name", selectedExistingCustomer.data.companyName ?? "");
    form.setValue("adminDetails.email", selectedExistingCustomer.data.email ?? "");
    form.setValue("adminDetails.primary_phone", formatUsPhoneInput(selectedExistingCustomer.data.phone ?? ""));
    form.setValue("adminDetails.secondary_phone", formatUsPhoneInput(selectedExistingCustomer.data.secondaryPhone ?? ""));
    form.setValue("adminDetails.address", primaryAddress?.line1 ?? "");
    form.setValue("adminDetails.unit_number", primaryAddress?.unitNumber ?? "");
    form.setValue("adminDetails.city", primaryAddress?.city ?? "");
    form.setValue("adminDetails.state", primaryAddress?.state ?? "");
    form.setValue("adminDetails.postal_code", primaryAddress?.postalCode ?? "");
    form.setValue("adminDetails.address_instructions", primaryAddress?.instructions ?? "");
    form.setValue("adminDetails.customer_type", selectedExistingCustomer.data.customerType ?? "Residential");
    form.setValue("adminDetails.frequency", selectedExistingCustomer.data.serviceFrequency ?? "");
    form.setValue("adminDetails.preferred_staff", (selectedExistingCustomer.data.preferredStaffIds ?? []).join(" | "));
    form.setValue("adminDetails.blocked_staff", (selectedExistingCustomer.data.blockedStaffIds ?? []).join(" | "));
    form.setValue("adminDetails.english_speaking_staff", Boolean(selectedExistingCustomer.data.englishSpeakingStaff) ? "Yes" : "No");
    form.setValue("adminDetails.pets", selectedExistingCustomer.data.pets ?? "");
    form.setValue("adminDetails.sensitivities_allergies", selectedExistingCustomer.data.sensitivities ?? "");
    form.setValue("adminDetails.property_pictures", (selectedExistingCustomer.data.propertyPhotos ?? []).join("\n"));
    form.setValue("adminDetails.problem_areas", (selectedExistingCustomer.data.problemAreas ?? []).join("\n"));
    form.setValue("adminDetails.special_incidents", (selectedExistingCustomer.data.specialIncidents ?? []).join("\n"));
    form.setValue("adminDetails.required_documents", (selectedExistingCustomer.data.documents ?? []).join("\n"));
    form.setValue("adminDetails.rating_value", selectedExistingCustomer.data.clientRating == null ? "" : String(selectedExistingCustomer.data.clientRating));
    form.setValue("adminDetails.referral_source", selectedExistingCustomer.data.leadSourceName ?? "");
  }, [form, selectedExistingCustomer.data]);

  const availabilityQuery = useQuery({
    queryKey: ["admin-availability", locationId, serviceId, bookingSpotId, resolvedCalendarProfileId, dateValue, optionIds, JSON.stringify(form.watch("adminDetails") ?? {})],
    queryFn: () =>
      apiFetch<{ items: AvailabilitySlotSummary[] }>(
        `/api/availability?locationId=${locationId}&serviceId=${serviceId}&bookingSpotId=${bookingSpotId}&calendarProfileId=${resolvedCalendarProfileId || ""}&optionIds=${encodeURIComponent(optionIds.join(","))}&adminDetails=${encodeURIComponent(JSON.stringify(form.getValues("adminDetails") ?? {}))}&date=${dateValue}`
      ),
    enabled: Boolean(locationId && serviceId && bookingSpotId && dateValue)
  });

  useEffect(() => {
    const slots = availabilityQuery.data?.items ?? [];
    if (!slots.length) {
      return;
    }

    if (!slots.some((slot) => slot.startsAt === startsAtValue)) {
      form.setValue("startsAt", slots[0].startsAt);
    }
  }, [availabilityQuery.data, form, startsAtValue]);

  const quoteQuery = useQuery({
    queryKey: ["admin-quote", locationId, serviceId, bookingSpotId, frequencyRuleId, resolvedCalendarProfileId, optionIds, dateValue, startsAtValue, promoCode, giftCardCode, depositChoice, JSON.stringify(form.watch("adminDetails") ?? {})],
    queryFn: () =>
      apiFetch<QuotePreview>("/api/availability/quote", {
        method: "POST",
        body: JSON.stringify({
          locationId,
          serviceId,
          bookingSpotId: bookingSpotId || undefined,
          optionIds,
          frequencyRuleId: frequencyRuleId || undefined,
          calendarProfileId: resolvedCalendarProfileId || undefined,
          startsAt: startsAtValue || undefined,
          serviceDate: dateValue || undefined,
          promoCode,
          giftCardCode,
          adminDetails: form.getValues("adminDetails") ?? {},
          taxRate: adminConfiguredTaxRate,
          depositRate
        })
      }),
    enabled: Boolean(locationId && serviceId)
  });

  useEffect(() => {
    if (!quoteQuery.data) {
      return;
    }

    const remodelingHourlyMode = quoteQuery.data.pricingDisplayMode === "remodeling_hourly";
    const startingAtAmount = Number(quoteQuery.data.remodelingStartingAtAmount ?? 0);
    const minimumLaborHours = Number(quoteQuery.data.remodelingMinimumLaborHours ?? 0);
    const setQuotedAdminDetail = (key: string, value: string) => {
      const fieldName = `adminDetails.${key}`;
      if (String(form.getValues(fieldName as any) ?? "") !== value) {
        form.setValue(fieldName as any, value);
      }
    };

    form.setValue(
      "adminDetails.service_total",
      remodelingHourlyMode ? formatCurrency(startingAtAmount) : formatCurrency(quoteQuery.data.subtotal)
    );
    form.setValue(
      "adminDetails.final_amount",
      remodelingHourlyMode ? formatCurrency(startingAtAmount) : formatCurrency(quoteQuery.data.total)
    );
    form.setValue(
      "adminDetails.amount_owed_by_customer",
      remodelingHourlyMode ? formatCurrency(startingAtAmount) : formatCurrency(quoteQuery.data.total)
    );
    form.setValue("adminDetails.amount_paid_by_customer", formatCurrency(0));
    form.setValue(
      "adminDetails.quote_amount",
      remodelingHourlyMode ? formatCurrency(startingAtAmount) : formatCurrency(quoteQuery.data.total)
    );
    form.setValue(
      "adminDetails.quote_labor_hours",
      remodelingHourlyMode ? minimumLaborHours.toFixed(2) : (quoteQuery.data.durationMinutes / 60).toFixed(2)
    );
    form.setValue(
      "adminDetails.labor_hours",
      remodelingHourlyMode ? minimumLaborHours.toFixed(2) : (quoteQuery.data.durationMinutes / 60).toFixed(2)
    );
    form.setValue(
      "adminDetails.duration",
      remodelingHourlyMode
        ? formatRemodelingMinimumSummary(
            Number(quoteQuery.data.remodelingMinimumMaidCount ?? 0),
            Number(quoteQuery.data.remodelingMinimumHours ?? 0)
          )
        : `${quoteQuery.data.durationMinutes} min`
    );
    form.setValue("adminDetails.services", quoteQuery.data.serviceName);
    form.setValue("adminDetails.remodeling_summary", buildRemodelingSummary(currentAdminRemodelProfile, currentAdminDetails));
    form.setValue("adminDetails.extras", quoteQuery.data.optionNames.join(" | "));
    if (quoteQuery.data.frequencyRuleName) {
      form.setValue("adminDetails.frequency", quoteQuery.data.frequencyRuleName);
    }
    if (quoteQuery.data.customerSuppliesEquipmentSelected) {
      setQuotedAdminDetail("supplies_provided_by", "Customer");
      setQuotedAdminDetail("customer_provides_supplies_equipment", "yes");
      const discountAmount = Math.max(0, Math.round(Number(quoteQuery.data.customerSuppliesEquipmentDiscount ?? 0)));
      setQuotedAdminDetail("customer_supplies_equipment_discount", discountAmount > 0 ? formatCurrency(discountAmount) : "");
      setQuotedAdminDetail(
        "customer_supplies_equipment_items",
        normalizeStringArray(quoteQuery.data.customerSuppliesEquipmentRequiredItems).join("\n")
      );
    } else if (adminCustomerSuppliesChoice) {
      setQuotedAdminDetail("supplies_provided_by", adminCustomerSuppliesChoice);
      setQuotedAdminDetail(
        "customer_provides_supplies_equipment",
        adminCustomerSuppliesChoice === "Customer" ? "yes" : "no"
      );
      setQuotedAdminDetail("customer_supplies_equipment_discount", "");
      setQuotedAdminDetail("customer_supplies_equipment_items", "");
    }
  }, [adminCustomerSuppliesChoice, currentAdminDetails, currentAdminRemodelProfile, form, quoteQuery.data]);

  const createMutation = useMutation({
    mutationFn: async (values: any) => {
      const startsAtDate = new Date(values.startsAt);
      const appointmentDate = Number.isNaN(startsAtDate.getTime()) ? "" : formatDateLabel(values.startsAt);
      const appointmentTime = Number.isNaN(startsAtDate.getTime())
        ? ""
        : new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).format(startsAtDate);

      const payload = {
        ...values,
        source: "admin",
        promoCode: String(values.promoCode ?? "").trim(),
        giftCardCode: String(values.giftCardCode ?? "").trim(),
        optionIds: normalizeStringArray(values.optionIds),
        frequencyRuleId: values.frequencyRuleId || undefined,
        calendarProfileId: values.calendarProfileId || undefined,
        adminDetails: {
          ...(values.adminDetails ?? {}),
          remodeling_summary: buildRemodelingSummary(
            currentAdminRemodelProfile,
            values.adminDetails && typeof values.adminDetails === "object" && !Array.isArray(values.adminDetails)
              ? (values.adminDetails as Record<string, string>)
              : {}
          ),
          appointment_date: appointmentDate,
          appointment_time: appointmentTime,
          first_name: values.customer.firstName ?? "",
          last_name: values.customer.lastName ?? "",
          company_name: values.customer.companyName ?? values.adminDetails?.company_name ?? "",
          email: values.customer.email ?? "",
          primary_phone: values.customer.phone ?? "",
          secondary_phone: values.customer.secondaryPhone ?? "",
          address: values.customer.address.line1 ?? "",
          unit_number: values.customer.address.unitNumber ?? "",
          city: values.customer.address.city ?? "",
          state: values.customer.address.state ?? "",
          postal_code: values.customer.address.postalCode ?? "",
          address_instructions: values.customer.address.instructions ?? "",
          customer_notes: values.notes ?? "",
          staff_notes: values.internalNotes ?? "",
          customer_type: values.customer.customerType ?? values.adminDetails?.customer_type ?? "",
          frequency: values.adminDetails?.frequency ?? "",
          booking_status: "pending",
          services: values.adminDetails?.services ?? quoteQuery.data?.serviceName ?? "",
          extras: values.adminDetails?.extras ?? quoteQuery.data?.optionNames?.join(" | ") ?? "",
          location: activeLocations.find((item) => item.id === values.locationId)?.name ?? "",
          payment_authorized: values.depositChoice === "none" ? "no" : "pending"
        }
      };
      const booking = await apiFetch<{
        bookingId: string;
        publicReference: string;
        manageToken: string;
        quote: { total: number; amountDueNow: number };
      }>("/api/bookings/admin", {
        method: "POST",
        body: JSON.stringify(payload)
      });

      if (booking.quote.amountDueNow > 0) {
        try {
          await apiFetch("/api/payments/create-intent", {
            method: "POST",
            body: JSON.stringify({
              bookingId: booking.bookingId,
              amount: booking.quote.amountDueNow,
              paymentType: values.depositChoice === "full" ? "full" : "deposit"
            })
          });
        } catch (error) {
          console.warn("Admin payment intent creation deferred", error);
        }
      }

      return booking;
    },
    onSuccess: async (booking) => {
      setSelectedExistingCustomerId("");
      form.reset({
        ...getBookingFormDefaults(),
        source: "admin",
        locationId: activeLocations[0]?.id ?? getBookingFormDefaults().locationId,
        serviceId: filteredServices[0]?.id ?? getBookingFormDefaults().serviceId,
        bookingSpotId: availableSpots[0]?.id ?? ""
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bookings"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["report-bookings"] }),
        queryClient.invalidateQueries({ queryKey: ["payments"] }),
        queryClient.invalidateQueries({ queryKey: ["customers"] })
      ]);
      onCreated(booking.bookingId);
    }
  });

  return (
    <Card title="Create booking" eyebrow="Admin booking flow">
      <form className="stack" onSubmit={form.handleSubmit((values) => createMutation.mutate(values))}>
        <Field label="Existing customer">
          <select value={selectedExistingCustomerId} onChange={(event) => setSelectedExistingCustomerId(event.target.value)}>
            <option value="">New or manual entry</option>
            {(customers.data?.items ?? []).map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.first_name} {customer.last_name} {customer.email ? `(${customer.email})` : `(${customer.phone})`}
              </option>
            ))}
          </select>
        </Field>

        <p className="eyebrow">Core booking</p>
        <div className="grid two">
          <Field label="First name"><input {...form.register("customer.firstName")} /></Field>
          <Field label="Last name"><input {...form.register("customer.lastName")} /></Field>
          <Field label="Company name"><input {...form.register("customer.companyName")} /></Field>
          <Field label="Email"><input {...form.register("customer.email")} /></Field>
          <Field label="Primary phone">
            <input value={customerPhoneValue} onChange={(event) => form.setValue("customer.phone", formatUsPhoneInput(event.target.value), { shouldDirty: true })} />
          </Field>
          <Field label="Secondary phone">
            <input value={customerSecondaryPhoneValue} onChange={(event) => form.setValue("customer.secondaryPhone", formatUsPhoneInput(event.target.value), { shouldDirty: true })} />
          </Field>
          <Field label="Customer type">
            <select value={customerTypeValue} onChange={(event) => form.setValue("customer.customerType", event.target.value, { shouldDirty: true })}>
              {getBookingAdminSelectOptions("customer_type", customerTypeValue, ["Residential", "Airbnb", "VIP"]).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </Field>
          {renderBookingAdminFieldControl({
            field: { key: "frequency", label: "Frequency" },
            value: String(form.watch("adminDetails.frequency") ?? ""),
            onChange: (value) => form.setValue("adminDetails.frequency", value, { shouldDirty: true })
          })}
          {renderBookingAdminFieldControl({
            field: { key: "type", label: "Booking type" },
            value: String(form.watch("adminDetails.type") ?? ""),
            onChange: (value) => form.setValue("adminDetails.type", value, { shouldDirty: true })
          })}
        </div>

        <div className="grid three">
          <Field label="Location">
            <select {...form.register("locationId")}>
              {activeLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Service">
            <select {...form.register("serviceId")}>
              {filteredServices.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Booking spot">
            <select {...form.register("bookingSpotId")}>
              {availableSpots.map((spot) => (
                <option key={spot.id} value={spot.id}>
                  {spot.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {bookingFormConfig.data?.bookingForm.enableExtrasStep !== false && visibleOptions.length ? (
          <Field label="Extras">
            <div className="booking-checkbox-grid">
              {visibleOptions.map((option) => (
                <label key={option.id} className="booking-checkbox-option">
                  <input
                    type="checkbox"
                    checked={optionIds.includes(option.id)}
                    onChange={(event) =>
                      form.setValue(
                        "optionIds",
                        toggleStringSelection(optionIds, option.id, event.target.checked),
                        { shouldDirty: true }
                      )
                    }
                  />
                  <span>{option.name}</span>
                </label>
              ))}
            </div>
          </Field>
        ) : null}

        {bookingFormConfig.data?.bookingForm.enableFrequencyStep !== false && availableFrequencyRules.length ? (
          <Field label="Frequency rule">
            <select {...form.register("frequencyRuleId")}>
              {availableFrequencyRules.map((rule) => (
                <option key={rule.id} value={rule.id}>
                  {normalizeFrequencyDisplayText(rule.name)}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <div className="grid two">
          <Field label="Service date">
            <DateInput value={dateValue} onChange={(event) => form.setValue("startsAt", `${event.target.value}T15:00:00.000Z`)} />
          </Field>
          <Field label="Available arrival times" hint={availabilityQuery.data?.items?.length ? undefined : "No open times for the selected date yet."}>
            {availabilityQuery.data?.items?.length ? (
              <div className="arrival-window-grid">
                {availabilityQuery.data.items.map((slot) => {
                  const checked = startsAtValue === slot.startsAt;
                  return (
                    <label className={`service-choice-card ${checked ? "is-selected" : ""}`} key={slot.startsAt}>
                      <input type="radio" value={slot.startsAt} checked={checked} {...form.register("startsAt")} />
                      <div className="service-choice-copy">
                        <strong>{getAvailabilityOptionLabel(slot, selectedAdminLocation?.timezone)}</strong>
                      </div>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="service-choice-card" aria-disabled="true">
                <div className="service-choice-copy">
                  <strong>No open times</strong>
                </div>
              </div>
            )}
          </Field>

                {bookingFormConfig.data?.bookingForm.enableFrequencyStep !== false && availableFrequencyRules.length ? (
                  <Field label="Frequency">
                    <select {...form.register("frequencyRuleId")}>
                      {availableFrequencyRules.map((rule) => (
                        <option key={rule.id} value={rule.id}>
                          {normalizeFrequencyDisplayText(rule.name)}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
              </div>

        <div className="grid two">
          <Field label="Payment preference">
            <select {...form.register("depositChoice")}>
              <option value="deposit">Deposit at booking</option>
              <option value="full">Full payment at booking</option>
              <option value="none">No payment at booking</option>
            </select>
          </Field>
          <Field label="Promo code">
            <input {...form.register("promoCode")} placeholder="Enter discount code" />
          </Field>
          <Field label="Gift card">
            <input {...form.register("giftCardCode")} placeholder="Enter gift card code" />
          </Field>
        </div>

        <p className="eyebrow">Address & access</p>
        <Field label="Address line 1"><input {...form.register("customer.address.line1")} /></Field>

        <div className="grid three">
          <Field label="Unit #"><input {...form.register("customer.address.unitNumber")} /></Field>
          <Field label="City"><input {...form.register("customer.address.city")} /></Field>
          <Field label="State"><input {...form.register("customer.address.state")} /></Field>
          <Field label="Postal code"><input {...form.register("customer.address.postalCode")} /></Field>
        </div>

        <Field label="Address instructions">
          <textarea {...form.register("customer.address.instructions")} rows={3} />
        </Field>

        {renderCreateAdminFields([
          { key: "entry_information", label: "Entry & access" },
          { key: "lockbox_code", label: "Lockbox combination" },
          { key: "lockbox_location", label: "Lockbox location" },
          { key: "gate_code", label: "Gate code" },
          { key: "building_code", label: "Building code" },
          { key: "unit_code", label: "Unit door code" },
          { key: "alarm_code", label: "Alarm system code" },
          { key: "entry_information_custom", label: "Custom entry notes", multiline: true }
        ])}

        <p className="eyebrow">Pricing & payment</p>
        {renderCreateAdminFields([
          { key: "service_total", label: "Service total" },
          { key: "extras_total", label: "Extras total" },
          { key: "final_amount", label: "Final amount" },
          { key: "amount_paid_by_customer", label: "Amount paid" },
          { key: "amount_owed_by_customer", label: "Amount owed" },
          { key: "tip_type", label: "Tip type" },
          { key: "tip", label: "Tip" },
          { key: "payment_method", label: "Payment method" },
          { key: "frequency_discount_type", label: "Frequency discount type" },
          { key: "customer_supplies_equipment_discount", label: "Customer supplies discount" },
          { key: "discount_code", label: "Discount code" },
          { key: "discount_from_code", label: "Discount from code" },
          { key: "gift_card_number", label: "Gift card number" },
          { key: "payment_authorized", label: "Payment authorized" },
          { key: "quote_received", label: "Quote received" },
          { key: "quote_labor_hours", label: "Quote labor hours" },
          { key: "quote_amount", label: "Quote amount" }
        ])}

        <p className="eyebrow">Service notes</p>
        <Field label="Booking notes">
          <textarea {...form.register("notes")} rows={3} />
        </Field>

        <Field label="Internal notes">
          <textarea {...form.register("internalNotes")} rows={3} />
        </Field>

        {renderCreateAdminFields([
          { key: "extras", label: "Extras" },
          { key: "priority_areas", label: "Priority areas" },
          { key: "skip_areas", label: "Skip areas" },
          { key: "required_documents", label: "Required documents" },
          { key: "consent_received", label: "Consent received" },
          { key: "appointment_notifications", label: "Appointment notifications" },
          { key: "arrival_flexibility", label: "Arrival flexibility" }
        ])}

        <p className="eyebrow">Staff preferences</p>
        {renderCreateAdminFields([
          { key: "preferred_staff", label: "Preferred staff", multiline: true },
          { key: "blocked_staff", label: "Blocked staff", multiline: true },
          { key: "english_speaking_staff", label: "English speaking staff" },
          { key: "team_has_key", label: "Team has key" },
          { key: "teams_assigned_without_ids", label: "Assigned team names", multiline: true }
        ])}

        <p className="eyebrow">Home / property profile</p>
        {renderCreateAdminFields([
          { key: "bedrooms", label: "Bedrooms" },
          { key: "bathrooms", label: "Bathrooms" },
          { key: "levels", label: "Levels" },
          { key: "square_footage", label: "Square footage" },
          { key: "pets", label: "Pets" },
          { key: "sensitivities_allergies", label: "Sensitivities / allergies", multiline: true },
          { key: "green_supplies_only", label: "Green supplies only" },
          { key: "supplies_provided_by", label: "Supplies provided by" },
          { key: "customer_provides_supplies_equipment", label: "Customer provides supplies/equipment" },
          { key: "customer_supplies_equipment_items", label: "Required supplies/equipment", multiline: true },
          { key: "property_pictures", label: "Property pictures", multiline: true }
        ])}

        <p className="eyebrow">Condition / quality</p>
        {renderCreateAdminFields([
          { key: "overall_condition", label: "Overall condition" },
          { key: "kitchen_condition", label: "Kitchen condition" },
          { key: "bathrooms_condition", label: "Bathrooms condition" },
          { key: "bedrooms_condition", label: "Bedrooms condition" },
          { key: "living_areas_condition", label: "Living areas condition" },
          { key: "problem_areas", label: "Problem areas", multiline: true },
          { key: "special_incidents", label: "Special incidents", multiline: true },
          { key: "rating_value", label: "Rating value" },
          { key: "rating_comment", label: "Rating comment", multiline: true },
          { key: "new_or_returning_customer", label: "New or returning customer" },
          { key: "referral_source", label: "Referral source" }
        ])}

        {quoteQuery.data ? (
          <div className="quote-callout">
            <p className="eyebrow">Booking preview</p>
            <h3>{formatCurrency(quoteQuery.data.amountDueNow)}</h3>
            <p>
              {quoteQuery.data.serviceName} totals {formatCurrency(quoteQuery.data.total)} with{" "}
              {quoteQuery.data.durationMinutes} estimated minutes.
            </p>
            {adminCustomerSuppliesEquipmentEnabled && adminCustomerSuppliesChoice === "Customer" ? (
              <p>
                Customer supplies discount:{" "}
                {Number(quoteQuery.data.customerSuppliesEquipmentDiscount ?? 0) > 0
                  ? `-${formatCurrency(Number(quoteQuery.data.customerSuppliesEquipmentDiscount ?? 0))}`
                  : "not applied for this quote mode"}
              </p>
            ) : null}
            {adminCustomerSuppliesChoice === "Customer" && adminCustomerSuppliesEquipmentRequiredItems.length ? (
              <div>
                <p className="eyebrow">Required supplies/equipment</p>
                <p className="card-copy">{adminCustomerSuppliesEquipmentRequiredItems.join(" | ")}</p>
              </div>
            ) : null}
          </div>
        ) : null}

        {createMutation.error ? <p className="error-text">{(createMutation.error as Error).message}</p> : null}
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending ? "Creating..." : "Create booking"}
        </Button>
      </form>
    </Card>
  );
}

function BookingsPage() {
  const navigate = useNavigate();
  const { id: bookingRouteId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const bookingImportInputRef = useRef<HTMLInputElement | null>(null);
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [bookingTransferFeedback, setBookingTransferFeedback] = useState("");
  const [draftStatus, setDraftStatus] = useState<string>("pending");
  const [draftStartsAt, setDraftStartsAt] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [draftInternalNotes, setDraftInternalNotes] = useState("");
  const [draftAdminDetails, setDraftAdminDetails] = useState<Record<string, string>>({});
  const [isEditingBooking, setIsEditingBooking] = useState(false);
  const [invoiceEditorOpen, setInvoiceEditorOpen] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState<InvoiceDraftSnapshot | null>(null);
  const [invoiceIssueDate, setInvoiceIssueDate] = useState("");
  const [invoiceDueDate, setInvoiceDueDate] = useState("");
  const [invoiceRecipientEmail, setInvoiceRecipientEmail] = useState("");
  const [invoiceEmailSubject, setInvoiceEmailSubject] = useState("");
  const [invoiceEmailBody, setInvoiceEmailBody] = useState("");
  const [invoiceFeedback, setInvoiceFeedback] = useState("");
  const [bookingMessageOpen, setBookingMessageOpen] = useState(false);
  const [bookingMessageTemplateId, setBookingMessageTemplateId] = useState("");
  const [bookingMessageSubject, setBookingMessageSubject] = useState("");
  const [bookingMessageBody, setBookingMessageBody] = useState("");
  const [bookingMessageFeedback, setBookingMessageFeedback] = useState("");
  const [bookingSmsOpen, setBookingSmsOpen] = useState(false);
  const [bookingSmsTemplateKey, setBookingSmsTemplateKey] = useState("");
  const [bookingSmsBody, setBookingSmsBody] = useState("");
  const [bookingSmsFeedback, setBookingSmsFeedback] = useState("");
  const isBookingProfilePage = Boolean(bookingRouteId);
  const requestedDate = /^\d{4}-\d{2}-\d{2}$/.test(String(searchParams.get("date") ?? ""))
    ? String(searchParams.get("date"))
    : "";
  const requestedLocationId = String(searchParams.get("locationId") ?? "all").trim() || "all";
  const requestedPageSize = [50, 100].includes(Number(searchParams.get("pageSize"))) ? Number(searchParams.get("pageSize")) : 50;
  const requestedPage = Number.isFinite(Number(searchParams.get("page"))) && Number(searchParams.get("page")) > 0
    ? Math.floor(Number(searchParams.get("page")))
    : 1;
  const requestedSearch = String(searchParams.get("search") ?? "").trim();
  const [bookingSearchInput, setBookingSearchInput] = useState(requestedSearch);
  const isBookingsSearchActive = requestedSearch.length > 0;
  const [visibleCalendarMonth, setVisibleCalendarMonth] = useState(() => getIsoMonth(requestedDate || getTodayIsoDate()));
  const bookingListSearch = searchParams.toString();
  const bookingListSuffix = bookingListSearch ? `?${bookingListSearch}` : "";

  const updateBookingsSearchParams = (mutate: (nextParams: URLSearchParams) => void, replace = true) => {
    const nextParams = new URLSearchParams(searchParams);
    mutate(nextParams);
    if (!String(nextParams.get("date") ?? "").trim()) {
      nextParams.delete("date");
    }
    if (!String(nextParams.get("new") ?? "").trim()) {
      nextParams.delete("new");
    }
    if (!String(nextParams.get("locationId") ?? "").trim() || nextParams.get("locationId") === "all") {
      nextParams.delete("locationId");
    }
    const normalizedSearch = String(nextParams.get("search") ?? "").trim();
    if (normalizedSearch) {
      nextParams.set("search", normalizedSearch);
    } else {
      nextParams.delete("search");
    }
    if (!String(nextParams.get("page") ?? "").trim() || nextParams.get("page") === "1") {
      nextParams.delete("page");
    }
    if (!String(nextParams.get("pageSize") ?? "").trim() || nextParams.get("pageSize") === "50") {
      nextParams.delete("pageSize");
    }
    setSearchParams(nextParams, { replace });
  };

  const bookingLocationsQuery = useQuery({
    queryKey: ["settings", "locations", "bookings-filter"],
    queryFn: () => apiFetch<{ items: Array<Record<string, unknown>> }>("/api/settings/locations"),
    enabled: !isBookingProfilePage
  });

  const bookings = useQuery({
    queryKey: ["bookings", "index", requestedDate || "default", requestedLocationId, requestedPage, requestedPageSize, requestedSearch],
    queryFn: () => {
      const nextParams = new URLSearchParams();
      if (requestedSearch) {
        nextParams.set("search", requestedSearch);
      } else if (requestedDate) {
        nextParams.set("date", requestedDate);
      }
      if (requestedLocationId !== "all") {
        nextParams.set("locationId", requestedLocationId);
      }
      nextParams.set("page", String(requestedPage));
      nextParams.set("pageSize", String(requestedPageSize));
      return apiFetch<BookingsIndexResponse>(`/api/bookings?${nextParams.toString()}`);
    },
    enabled: !isBookingProfilePage
  });

  const calendarSummary = useQuery({
    queryKey: ["bookings", "calendar-summary", visibleCalendarMonth, requestedLocationId],
    queryFn: () => {
      const nextParams = new URLSearchParams({ month: visibleCalendarMonth });
      if (requestedLocationId !== "all") {
        nextParams.set("locationId", requestedLocationId);
      }
      return apiFetch<BookingsCalendarSummaryResponse>(`/api/bookings/calendar-summary?${nextParams.toString()}`);
    },
    enabled: !isBookingProfilePage && !isBookingsSearchActive
  });

  const bookingDetail = useQuery({
    queryKey: ["booking", bookingRouteId],
    queryFn: () => apiFetch<BookingDetail>(`/api/bookings/${bookingRouteId}`),
    enabled: Boolean(bookingRouteId)
  });
  const bookingInvoice = useQuery({
    queryKey: ["booking-invoice", bookingRouteId],
    queryFn: () => apiFetch<{ invoice: InvoiceDetail | null }>(`/api/bookings/${bookingRouteId}/invoice`),
    enabled: Boolean(bookingRouteId)
  });
  const bookingChangeLog = useQuery({
    queryKey: ["booking-change-log", bookingRouteId],
    queryFn: () => apiFetch<{ items: BookingChangeLogEntry[] }>(`/api/bookings/${bookingRouteId}/change-log`),
    enabled: Boolean(bookingRouteId)
  });
  const bookingMessageTemplates = useQuery({
    queryKey: ["booking-message-templates"],
    queryFn: () => apiFetch<{ items: EmailTemplateRecord[] }>("/api/communications/templates"),
    enabled: bookingMessageOpen
  });

  useEffect(() => {
    if (!bookingDetail.data) {
      return;
    }

    setDraftStatus(bookingDetail.data.status);
    setDraftStartsAt(toDateTimeInputValue(bookingDetail.data.startsAt));
    setDraftNotes(bookingDetail.data.notes ?? "");
    setDraftInternalNotes(bookingDetail.data.internalNotes ?? "");
    setDraftAdminDetails(bookingDetail.data.adminDetails ?? {});
    setIsEditingBooking(false);
  }, [bookingDetail.data]);

  useEffect(() => {
    setBookingMessageOpen(false);
    setBookingMessageTemplateId("");
    setBookingMessageSubject("");
    setBookingMessageBody("");
    setBookingMessageFeedback("");
    setBookingSmsOpen(false);
    setBookingSmsTemplateKey("");
    setBookingSmsBody("");
    setBookingSmsFeedback("");
  }, [bookingRouteId]);

  useEffect(() => {
    const invoice = bookingInvoice.data?.invoice;
    if (!invoice) {
      setInvoiceDraft(null);
      setInvoiceIssueDate("");
      setInvoiceDueDate("");
      setInvoiceRecipientEmail("");
      setInvoiceEmailSubject("");
      setInvoiceEmailBody("");
      return;
    }
    setInvoiceDraft(invoice.draft);
    setInvoiceIssueDate(invoice.issueDate);
    setInvoiceDueDate(invoice.dueDate);
    setInvoiceRecipientEmail(invoice.defaultRecipientEmail || invoice.draft.customerEmail || "");
    setInvoiceEmailSubject(invoice.defaultEmailSubject || `Invoice ${invoice.invoiceNumber}`);
    setInvoiceEmailBody(invoice.defaultEmailBody || "");
  }, [bookingInvoice.data?.invoice]);

  const refreshInvoiceQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["booking-invoice", bookingRouteId] }),
      queryClient.invalidateQueries({ queryKey: ["booking", bookingRouteId] }),
      queryClient.invalidateQueries({ queryKey: ["customers"] }),
      queryClient.invalidateQueries({ queryKey: ["payments"] })
    ]);
  };

  const createInvoiceMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ invoice: InvoiceDetail }>(`/api/bookings/${bookingRouteId}/invoice`, {
        method: "POST"
      }),
    onSuccess: async (result) => {
      queryClient.setQueryData(["booking-invoice", bookingRouteId], { invoice: result.invoice });
      setInvoiceEditorOpen(true);
      setInvoiceFeedback("Invoice draft created.");
      await refreshInvoiceQueries();
    },
    onError: (error) => setInvoiceFeedback(error instanceof Error ? error.message : "Invoice could not be created.")
  });

  const saveInvoiceDraftMutation = useMutation({
    mutationFn: () => {
      if (!bookingInvoice.data?.invoice || !invoiceDraft) {
        throw new Error("Open or create an invoice first.");
      }
      return apiFetch<{ invoice: InvoiceDetail }>(`/api/invoices/${bookingInvoice.data.invoice.id}/draft`, {
        method: "PUT",
        body: JSON.stringify({
          issueDate: invoiceIssueDate,
          dueDate: invoiceDueDate,
          snapshot: invoiceDraft
        })
      });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData(["booking-invoice", bookingRouteId], { invoice: result.invoice });
      setInvoiceFeedback("Invoice draft saved.");
      await refreshInvoiceQueries();
    },
    onError: (error) => setInvoiceFeedback(error instanceof Error ? error.message : "Invoice draft could not be saved.")
  });

  const previewInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!bookingInvoice.data?.invoice) {
        throw new Error("Create an invoice first.");
      }
      if (invoiceDraft) {
        await apiFetch<{ invoice: InvoiceDetail }>(`/api/invoices/${bookingInvoice.data.invoice.id}/draft`, {
          method: "PUT",
          body: JSON.stringify({
            issueDate: invoiceIssueDate,
            dueDate: invoiceDueDate,
            snapshot: invoiceDraft
          })
        });
      }
      return apiFetch<{ invoice: InvoiceDetail }>(`/api/invoices/${bookingInvoice.data.invoice.id}/preview`, {
        method: "POST"
      });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData(["booking-invoice", bookingRouteId], { invoice: result.invoice });
      setInvoiceFeedback("PDF preview generated.");
      window.open(`/api/invoices/${result.invoice.id}/pdf`, "_blank", "noopener,noreferrer");
      await refreshInvoiceQueries();
    },
    onError: (error) => setInvoiceFeedback(error instanceof Error ? error.message : "Invoice PDF could not be generated.")
  });

  const sendInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!bookingInvoice.data?.invoice) {
        throw new Error("Create an invoice first.");
      }
      if (invoiceDraft) {
        await apiFetch<{ invoice: InvoiceDetail }>(`/api/invoices/${bookingInvoice.data.invoice.id}/draft`, {
          method: "PUT",
          body: JSON.stringify({
            issueDate: invoiceIssueDate,
            dueDate: invoiceDueDate,
            snapshot: invoiceDraft
          })
        });
      }
      return apiFetch<{ invoice: InvoiceDetail }>(`/api/invoices/${bookingInvoice.data.invoice.id}/send`, {
        method: "POST",
        body: JSON.stringify({
          recipientEmail: invoiceRecipientEmail,
          subject: invoiceEmailSubject,
          body: invoiceEmailBody,
          attachPdf: true
        })
      });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData(["booking-invoice", bookingRouteId], { invoice: result.invoice });
      setInvoiceFeedback("Invoice sent with PDF attachment and pay link.");
      await refreshInvoiceQueries();
    },
    onError: (error) => setInvoiceFeedback(error instanceof Error ? error.message : "Invoice could not be sent.")
  });

  const reviseInvoiceMutation = useMutation({
    mutationFn: () => {
      if (!bookingInvoice.data?.invoice) {
        throw new Error("Create an invoice first.");
      }
      return apiFetch<{ invoice: InvoiceDetail }>(`/api/invoices/${bookingInvoice.data.invoice.id}/revise`, { method: "POST" });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData(["booking-invoice", bookingRouteId], { invoice: result.invoice });
      setInvoiceEditorOpen(true);
      setInvoiceFeedback("Revision draft created.");
      await refreshInvoiceQueries();
    },
    onError: (error) => setInvoiceFeedback(error instanceof Error ? error.message : "Invoice revision could not be created.")
  });

  const voidInvoiceMutation = useMutation({
    mutationFn: () => {
      if (!bookingInvoice.data?.invoice) {
        throw new Error("Create an invoice first.");
      }
      return apiFetch<{ invoice: InvoiceDetail }>(`/api/invoices/${bookingInvoice.data.invoice.id}/void`, { method: "POST" });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData(["booking-invoice", bookingRouteId], { invoice: result.invoice });
      setInvoiceFeedback("Invoice voided.");
      await refreshInvoiceQueries();
    },
    onError: (error) => setInvoiceFeedback(error instanceof Error ? error.message : "Invoice could not be voided.")
  });

  const updateCurrentBooking = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        status: draftStatus,
        notes: draftNotes,
        internalNotes: draftInternalNotes,
        adminDetails: {
          ...draftAdminDetails,
          booking_status: draftStatus,
          customer_notes: draftNotes,
          staff_notes: draftInternalNotes
        }
      };

      if (draftStartsAt) {
        payload.startsAt = new Date(draftStartsAt).toISOString();
        const nextDate = new Date(draftStartsAt);
        if (!Number.isNaN(nextDate.getTime())) {
          (payload.adminDetails as Record<string, string>).appointment_date = formatDateLabel(nextDate.toISOString());
          (payload.adminDetails as Record<string, string>).appointment_time = new Intl.DateTimeFormat("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true
          }).format(nextDate);
        }
      }

      return apiFetch(`/api/bookings/${bookingRouteId}`, {
        method: "PUT",
        body: JSON.stringify(payload)
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bookings"] }),
        queryClient.invalidateQueries({ queryKey: ["booking", bookingRouteId] }),
        queryClient.invalidateQueries({ queryKey: ["booking-change-log", bookingRouteId] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["report-bookings"] })
      ]);
      setIsEditingBooking(false);
    }
  });
  const deleteCurrentBooking = useMutation({
    mutationFn: () => apiFetch<{ id: string; mode: "deleted" }>(`/api/bookings/${bookingRouteId}`, { method: "DELETE" }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bookings"] }),
        queryClient.invalidateQueries({ queryKey: ["booking", bookingRouteId] }),
        queryClient.invalidateQueries({ queryKey: ["booking-change-log", bookingRouteId] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["report-bookings"] }),
        queryClient.invalidateQueries({ queryKey: ["payments"] })
      ]);
      navigate("/admin/bookings");
    }
  });
  const sendBookingCustomerEmailMutation = useMutation({
    mutationFn: () => {
      const customerId = String(bookingDetail.data?.customerId ?? bookingDetail.data?.adminDetails?.customer_id ?? "").trim();
      if (!customerId) {
        throw new Error("This booking is missing a customer record.");
      }
      return apiFetch<{ ok: boolean; id?: string; messageLogId?: string }>(`/api/customers/${customerId}/messages/email`, {
        method: "POST",
        body: JSON.stringify({
          subject: bookingMessageSubject,
          bodyText: bookingMessageBody,
          templateId: bookingMessageTemplateId || undefined,
          sendMode: bookingMessageTemplateId ? "template" : "freeform"
        })
      });
    },
    onMutate: () => {
      setBookingMessageFeedback("");
    },
    onSuccess: async () => {
      setBookingMessageFeedback("Email sent to the customer.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["booking", bookingRouteId] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity", "full"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    },
    onError: (error) => {
      setBookingMessageFeedback(error instanceof Error ? error.message : "Customer email could not be sent.");
    }
  });
  const sendBookingCustomerSmsMutation = useMutation({
    mutationFn: () => {
      const customerId = String(bookingDetail.data?.customerId ?? bookingDetail.data?.adminDetails?.customer_id ?? "").trim();
      if (!customerId) {
        throw new Error("This booking is missing a customer record.");
      }
      return apiFetch<{ ok: boolean; sid?: string; messageLogId?: string }>(`/api/customers/${customerId}/messages/sms`, {
        method: "POST",
        body: JSON.stringify({
          bodyText: bookingSmsBody,
          templateKey: bookingSmsTemplateKey || undefined,
          sendMode: bookingSmsTemplateKey ? "template" : "freeform"
        })
      });
    },
    onMutate: () => {
      setBookingSmsFeedback("");
    },
    onSuccess: async () => {
      setBookingSmsFeedback("SMS sent to the customer.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["booking", bookingRouteId] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity", "full"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    },
    onError: (error) => {
      setBookingSmsFeedback(error instanceof Error ? error.message : "Customer SMS could not be sent.");
    }
  });
  const importBookingsMutation = useMutation({
    mutationFn: (items: BookingTransferRow[]) =>
      apiFetch<{
        imported: number;
        skipped: number;
        errors?: string[];
      }>("/api/bookings/import", {
        method: "POST",
        body: JSON.stringify({ items })
      }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bookings"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["report-bookings"] })
      ]);

      const errorSummary = result.errors?.length ? ` First issue: ${result.errors[0]}` : "";
      setBookingTransferFeedback(
        `Imported ${result.imported} bookings.${result.skipped ? ` Skipped ${result.skipped}.` : ""}${errorSummary}`
      );
    },
    onError: (error) => {
      setBookingTransferFeedback(error instanceof Error ? error.message : "Booking import failed.");
    }
  });

  const exportBookings = async (format: "csv" | "excel") => {
    try {
      const payload = await apiFetch<BookingTransferPayload>("/api/bookings/export");
      const dateStamp = new Date().toISOString().slice(0, 10);

      if (format === "csv") {
        downloadTransferFile(
          `bookings-export-${dateStamp}.csv`,
          "text/csv;charset=utf-8;",
          serializeBookingTransferCsv(payload.headers, payload.items)
        );
      } else {
        downloadTransferFile(
          `bookings-export-${dateStamp}.xls`,
          "application/vnd.ms-excel",
          serializeBookingTransferSpreadsheet(payload.headers, payload.items)
        );
      }

      setBookingTransferFeedback(
        `Exported ${payload.items.length} bookings to ${format === "csv" ? "CSV" : "Excel"}.`
      );
    } catch (error) {
      setBookingTransferFeedback(error instanceof Error ? error.message : "Booking export failed.");
    }
  };

  const handleBookingImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const items = normalizeBookingTransferRows(parseBookingTransferFile(file.name, await file.text()));
      if (!items.length) {
        throw new Error("No importable booking rows were found in that file.");
      }

      importBookingsMutation.mutate(items);
    } catch (error) {
      setBookingTransferFeedback(error instanceof Error ? error.message : "Booking import failed.");
    } finally {
      event.target.value = "";
    }
  };

  const selectedSummary = bookings.data?.items.find((item) => item.id === bookingRouteId);
  const effectiveSelectedDate = bookings.data?.selectedDate ?? requestedDate ?? getTodayIsoDate();
  const calendarCountByDate = useMemo(
    () =>
      new Map(
        (calendarSummary.data?.items ?? []).map((entry) => [entry.date, Number(entry.count ?? 0)])
      ),
    [calendarSummary.data]
  );
  const calendarAvailability = useMemo<Record<string, AvailabilityDateSummary>>(
    () =>
      Object.fromEntries(
        Array.from(calendarCountByDate.entries()).map(([date]) => [
          date,
          {
            date,
            hasAvailability: true,
            firstStartsAt: null
          }
        ])
      ) as Record<string, AvailabilityDateSummary>,
    [calendarCountByDate]
  );
  const calendarMonthCells = useMemo(
    () => buildCalendarMonthCells(visibleCalendarMonth, calendarAvailability),
    [calendarAvailability, visibleCalendarMonth]
  );
  const visibleBookingRows = bookings.data?.items ?? [];
  const visiblePendingBookings = visibleBookingRows.filter((item) => String(item.status ?? "").toLowerCase().includes("pending")).length;
  const visibleUnassignedBookings = visibleBookingRows.filter((item) => !String(item.assignmentSummary ?? "").trim()).length;
  const visibleAmountDue = visibleBookingRows.reduce((sum, item) => sum + Number(item.amountDue ?? 0), 0);
  const currentBooking = bookingDetail.data;
  const bookingCustomerId = String(currentBooking?.customerId ?? currentBooking?.adminDetails?.customer_id ?? "").trim();
  const bookingCustomerFirstName =
    String(currentBooking?.adminDetails?.first_name ?? "").trim() ||
    String(currentBooking?.customerName ?? "").trim().split(/\s+/)[0] ||
    "";
  const bookingCustomerLastName =
    String(currentBooking?.adminDetails?.last_name ?? "").trim() ||
    String(currentBooking?.customerName ?? "").trim().split(/\s+/).slice(1).join(" ");

  const bookingCustomerTemplateVariables = () => {
    const firstName = bookingCustomerFirstName;
    const lastName = bookingCustomerLastName;
    const email = String(currentBooking?.customerEmail ?? "").trim();
    const phone = String(currentBooking?.customerPhone ?? "").trim();
    const companyName = String(currentBooking?.customerCompanyName ?? "").trim();
    const customerName = [firstName, lastName].filter(Boolean).join(" ") || companyName || email || "Customer";
    return {
      customerName,
      bookingReference: currentBooking?.publicReference ?? "",
      appointmentDate: currentBooking?.startsAt ? formatDateLabel(currentBooking.startsAt) : "",
      appointmentTime: currentBooking?.startsAt ? formatDateTimeLabel(currentBooking.startsAt) : "",
      customer: {
        firstName,
        lastName,
        name: customerName,
        email,
        phone,
        companyName
      },
      client: {
        firstName,
        lastName,
        name: customerName,
        email,
        phone
      },
      booking: {
        reference: currentBooking?.publicReference ?? "",
        status: currentBooking?.status ?? "",
        startsAt: currentBooking?.startsAt ?? "",
        appointmentDate: currentBooking?.startsAt ? formatDateLabel(currentBooking.startsAt) : "",
        appointmentTime: currentBooking?.startsAt ? formatDateTimeLabel(currentBooking.startsAt) : "",
        address: currentBooking?.serviceAddress ?? ""
      }
    };
  };

  const openBookingContactHref = (href: string | null | undefined) => {
    if (!href || typeof window === "undefined") {
      return;
    }
    window.location.href = href;
  };

  const openBookingMessageComposer = () => {
    setBookingMessageTemplateId("");
    setBookingMessageSubject("");
    setBookingMessageBody("");
    setBookingMessageFeedback("");
    setBookingMessageOpen(true);
  };

  const applyBookingMessageTemplate = (templateId: string) => {
    setBookingMessageTemplateId(templateId);
    setBookingMessageFeedback("");
    if (!templateId) {
      return;
    }
    const template = (bookingMessageTemplates.data?.items ?? []).find((item) => item.id === templateId);
    if (!template) {
      return;
    }
    const variables = bookingCustomerTemplateVariables();
    setBookingMessageSubject(renderSimpleTemplate(template.subjectTemplate ?? "", variables));
    setBookingMessageBody(
      renderSimpleTemplate(String(template.bodyText ?? "").trim() || emailTemplateHtmlToText(template.bodyHtml ?? ""), variables)
    );
  };

  const openBookingSmsComposer = () => {
    setBookingSmsTemplateKey("");
    setBookingSmsBody("");
    setBookingSmsFeedback("");
    setBookingSmsOpen(true);
  };

  const applyBookingSmsTemplate = (templateKey: string) => {
    setBookingSmsTemplateKey(templateKey);
    setBookingSmsFeedback("");
    if (!templateKey) {
      return;
    }
    const template = customerSmsTemplates.find((item) => item.key === templateKey);
    if (!template) {
      return;
    }
    setBookingSmsBody(renderSimpleTemplate(template.bodyText, bookingCustomerTemplateVariables()));
  };

  const renderBookingMessageComposer = () => {
    if (!bookingMessageOpen || !currentBooking) {
      return null;
    }

    const templates = bookingMessageTemplates.data?.items ?? [];
    const hasRecipientEmail = Boolean(currentBooking.customerEmail);
    const canSendBookingEmail =
      hasRecipientEmail &&
      Boolean(bookingCustomerId) &&
      bookingMessageSubject.trim().length > 0 &&
      bookingMessageBody.trim().length > 0 &&
      !sendBookingCustomerEmailMutation.isPending;

    return (
      <div
        className="customer-message-modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setBookingMessageOpen(false);
          }
        }}
      >
        <div className="customer-message-modal" role="dialog" aria-modal="true" aria-labelledby="booking-message-title">
          <div className="customer-message-modal__header">
            <div>
              <p className="eyebrow">Direct email</p>
              <h3 id="booking-message-title">Message customer</h3>
              <p className="card-copy">
                {currentBooking.customerEmail
                  ? `Sending to ${currentBooking.customerEmail}`
                  : "Add an email address to this customer before sending a message."}
              </p>
            </div>
            <Button variant="ghost" onClick={() => setBookingMessageOpen(false)}>
              Close
            </Button>
          </div>

          <div className="stack">
            <Field label="Start from template" hint="Optional. You can edit the subject and message before sending.">
              <select
                value={bookingMessageTemplateId}
                onChange={(event) => applyBookingMessageTemplate(event.target.value)}
                disabled={bookingMessageTemplates.isLoading}
              >
                <option value="">Freeform message</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.templateType})
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Subject">
              <input
                value={bookingMessageSubject}
                onChange={(event) => setBookingMessageSubject(event.target.value)}
                placeholder="Subject"
                disabled={!hasRecipientEmail}
              />
            </Field>

            <Field label="Message">
              <textarea
                rows={10}
                value={bookingMessageBody}
                onChange={(event) => setBookingMessageBody(event.target.value)}
                placeholder="Write your message..."
                disabled={!hasRecipientEmail}
              />
            </Field>

            {bookingMessageFeedback ? (
              <p className={sendBookingCustomerEmailMutation.isError ? "error-text" : "success-text"}>{bookingMessageFeedback}</p>
            ) : null}

            <div className="customer-message-modal__actions">
              <Button variant="secondary" onClick={() => setBookingMessageOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => sendBookingCustomerEmailMutation.mutate()} disabled={!canSendBookingEmail}>
                {sendBookingCustomerEmailMutation.isPending ? "Sending..." : "Send email"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBookingSmsComposer = () => {
    if (!bookingSmsOpen || !currentBooking) {
      return null;
    }

    const hasRecipientPhone = Boolean(currentBooking.customerPhone);
    const canSendBookingSms =
      hasRecipientPhone &&
      Boolean(bookingCustomerId) &&
      bookingSmsBody.trim().length > 0 &&
      !sendBookingCustomerSmsMutation.isPending;

    return (
      <div
        className="customer-message-modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setBookingSmsOpen(false);
          }
        }}
      >
        <div className="customer-message-modal" role="dialog" aria-modal="true" aria-labelledby="booking-sms-title">
          <div className="customer-message-modal__header">
            <div>
              <p className="eyebrow">Direct SMS</p>
              <h3 id="booking-sms-title">Send SMS</h3>
              <p className="card-copy">
                {currentBooking.customerPhone
                  ? `Sending to ${currentBooking.customerPhone}`
                  : "Add a phone number to this customer before sending a text message."}
              </p>
            </div>
            <Button variant="ghost" onClick={() => setBookingSmsOpen(false)}>
              Close
            </Button>
          </div>

          <div className="stack">
            <Field label="Start from template" hint="Optional. Freeform messages are always available.">
              <select value={bookingSmsTemplateKey} onChange={(event) => applyBookingSmsTemplate(event.target.value)}>
                <option value="">Freeform SMS</option>
                {customerSmsTemplates.map((template) => (
                  <option key={template.key} value={template.key}>
                    {template.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Text message" hint={`${bookingSmsBody.length} characters`}>
              <textarea
                rows={7}
                value={bookingSmsBody}
                onChange={(event) => setBookingSmsBody(event.target.value)}
                placeholder="Write your SMS..."
                disabled={!hasRecipientPhone}
              />
            </Field>

            {bookingSmsFeedback ? (
              <p className={sendBookingCustomerSmsMutation.isError ? "error-text" : "success-text"}>{bookingSmsFeedback}</p>
            ) : null}

            <div className="customer-message-modal__actions">
              <Button variant="secondary" onClick={() => setBookingSmsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => sendBookingCustomerSmsMutation.mutate()} disabled={!canSendBookingSms}>
                {sendBookingCustomerSmsMutation.isPending ? "Sending..." : "Send SMS"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isBookingProfilePage) {
      return;
    }

    setShowCreateBooking(searchParams.get("new") === "1");
  }, [isBookingProfilePage, searchParams]);

  useEffect(() => {
    if (!requestedDate) {
      return;
    }

    const nextMonth = getIsoMonth(requestedDate);
    setVisibleCalendarMonth((current) => (current === nextMonth ? current : nextMonth));
  }, [requestedDate]);

  useEffect(() => {
    setBookingSearchInput(requestedSearch);
  }, [requestedSearch]);

  useEffect(() => {
    const normalizedSearch = bookingSearchInput.trim();
    if (normalizedSearch === requestedSearch) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      updateBookingsSearchParams((nextParams) => {
        if (normalizedSearch) {
          nextParams.set("search", normalizedSearch);
        } else {
          nextParams.delete("search");
        }
        nextParams.delete("page");
      });
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [bookingSearchInput, requestedSearch, searchParams, setSearchParams]);

  const toggleCreateBooking = (nextValue: boolean) => {
    setShowCreateBooking(nextValue);
    const nextParams = new URLSearchParams(searchParams);
    if (nextValue) {
      nextParams.set("new", "1");
    } else {
      nextParams.delete("new");
    }
    setSearchParams(nextParams, { replace: true });
  };

  const bookingEditValue = (key: string, fallback = "") =>
    Object.prototype.hasOwnProperty.call(draftAdminDetails, key) ? String(draftAdminDetails[key] ?? "") : fallback;

  const setBookingEditValue = (key: string, value: string) => {
    setDraftAdminDetails((current) => ({
      ...current,
      [key]: value
    }));
  };

  const renderBookingAdminEditor = (
    fields: Array<BookingAdminFieldConfig & { fallback?: string }>
  ) => (
    <div className="grid two">
      {fields.map((field) => (
        renderBookingAdminFieldControl({
          field,
          value: bookingEditValue(field.key, field.fallback ?? ""),
          onChange: (value) => setBookingEditValue(field.key, value),
          disabled: !isEditingBooking
        })
      ))}
    </div>
  );

  const currentInvoice = bookingInvoice.data?.invoice ?? null;
  const invoiceCanEdit = currentInvoice?.status === "draft";
  const updateInvoiceDraftField = (key: keyof InvoiceDraftSnapshot, value: string | number) => {
    setInvoiceDraft((current) => (current ? recomputeInvoiceDraft({ ...current, [key]: value }) : current));
  };
  const updateInvoiceLineItem = (index: number, patch: Partial<InvoiceDraftSnapshot["lineItems"][number]>) => {
    setInvoiceDraft((current) => {
      if (!current) {
        return current;
      }
      const lineItems = [...(current.lineItems ?? [])];
      lineItems[index] = { ...lineItems[index], ...patch };
      return recomputeInvoiceDraft({ ...current, lineItems });
    });
  };
  const addInvoiceLineItem = () => {
    setInvoiceDraft((current) => {
      if (!current) {
        return current;
      }
      return recomputeInvoiceDraft({
        ...current,
        lineItems: [
          ...(current.lineItems ?? []),
          {
            id: `manual_${Date.now()}`,
            label: "Manual adjustment",
            description: "",
            quantity: 1,
            unitPriceAmount: 0,
            totalAmount: 0
          }
        ]
      });
    });
  };
  const removeInvoiceLineItem = (index: number) => {
    setInvoiceDraft((current) => {
      if (!current) {
        return current;
      }
      return recomputeInvoiceDraft({
        ...current,
        lineItems: (current.lineItems ?? []).filter((_item, itemIndex) => itemIndex !== index)
      });
    });
  };

  if (!isBookingProfilePage) {
    return (
      <OpsPageShell>
        <OpsHeader
          eyebrow="Dispatch operations"
          title="Bookings"
          description="Calendar/list readiness for dispatch, status updates, and payment follow-up."
          actions={
            <div className="customer-directory-actions">
              <input
                ref={bookingImportInputRef}
                type="file"
                accept=".csv,.xls,.xml"
                style={{ display: "none" }}
                onChange={handleBookingImportFile}
              />
              <Button variant="secondary" onClick={() => bookingImportInputRef.current?.click()} disabled={importBookingsMutation.isPending}>
                <span className="tw:inline-flex tw:items-center tw:gap-2">
                  <Upload className="tw:size-4" aria-hidden="true" />
                  {importBookingsMutation.isPending ? "Importing..." : "Import bookings"}
                </span>
              </Button>
              <Button variant="secondary" onClick={() => navigate("/admin/settings/waitlist")}>
                <span className="tw:inline-flex tw:items-center tw:gap-2">
                  <Filter className="tw:size-4" aria-hidden="true" />
                  Waitlist settings
                </span>
              </Button>
              <Button variant="secondary" onClick={() => exportBookings("csv")}>
                <span className="tw:inline-flex tw:items-center tw:gap-2">
                  <FileDown className="tw:size-4" aria-hidden="true" />
                  Export CSV
                </span>
              </Button>
              <Button variant="secondary" onClick={() => exportBookings("excel")}>
                <span className="tw:inline-flex tw:items-center tw:gap-2">
                  <Download className="tw:size-4" aria-hidden="true" />
                  Export Excel
                </span>
              </Button>
              <Button variant={showCreateBooking ? "secondary" : "primary"} onClick={() => toggleCreateBooking(!showCreateBooking)}>
                <span className="tw:inline-flex tw:items-center tw:gap-2">
                  <CalendarPlus className="tw:size-4" aria-hidden="true" />
                  {showCreateBooking ? "Close new booking" : "Add booking"}
                </span>
              </Button>
            </div>
          }
        />

        <OpsMetricGrid>
          <OpsMetricCard
            label={isBookingsSearchActive ? "Matching bookings" : "Selected-day bookings"}
            value={bookings.data?.totalItems ?? 0}
            detail={isBookingsSearchActive ? "Across all booking dates" : formatLongDateLabel(effectiveSelectedDate)}
            icon={CalendarDays}
            tone="blue"
          />
          <OpsMetricCard
            label="Pending"
            value={visiblePendingBookings}
            detail="Visible rows waiting for confirmation"
            icon={AlertCircle}
            tone="amber"
          />
          <OpsMetricCard
            label="Amount due"
            value={formatCurrency(visibleAmountDue)}
            detail="Total due in current view"
            icon={WalletCards}
            tone="violet"
          />
          <OpsMetricCard
            label="Unassigned"
            value={visibleUnassignedBookings}
            detail="Visible rows without assigned staff"
            icon={Users}
            tone="rose"
          />
        </OpsMetricGrid>

        {bookingTransferFeedback ? <p className="card-copy">{bookingTransferFeedback}</p> : null}

        {showCreateBooking ? (
          <AdminBookingCreateCard
            onCreated={(bookingId) => {
              toggleCreateBooking(false);
              navigate(`/admin/bookings/${bookingId}`);
            }}
          />
        ) : null}

        {!showCreateBooking ? (
          <div className="bookings-dispatch-layout">
            <Card
              title={isBookingsSearchActive ? "Search results" : `Bookings for ${formatLongDateLabel(effectiveSelectedDate)}`}
              eyebrow="Dispatch board"
              className="bookings-dispatch-card"
            >
              <div className="bookings-dispatch-toolbar">
                <Field label="Search bookings">
                  <div className="bookings-search-control">
                    <input
                      className="bookings-dispatch-toolbar__search"
                      type="search"
                      value={bookingSearchInput}
                      onChange={(event) => setBookingSearchInput(event.target.value)}
                      placeholder="Search by customer, phone, address, tag, booking ID..."
                      aria-label="Search bookings"
                    />
                    {bookingSearchInput.trim() ? (
                      <button
                        type="button"
                        className="bookings-search-clear"
                        aria-label="Clear booking search"
                        onClick={() => {
                          setBookingSearchInput("");
                          updateBookingsSearchParams((nextParams) => {
                            nextParams.delete("search");
                            nextParams.delete("page");
                          });
                        }}
                      >
                        X
                      </button>
                    ) : null}
                  </div>
                </Field>
                <Field label="Filter bookings by location">
                  <select
                    aria-label="Filter bookings by location"
                    value={requestedLocationId}
                    onChange={(event) =>
                      updateBookingsSearchParams((nextParams) => {
                        nextParams.set("locationId", event.target.value);
                        nextParams.delete("page");
                      })
                    }
                  >
                    <option value="all">All locations</option>
                    {(bookingLocationsQuery.data?.items ?? []).map((location) => (
                      <option key={String(location.id ?? "")} value={String(location.id ?? "")}>
                        {String(location.name ?? "")}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Bookings per page">
                  <select
                    aria-label="Bookings per page"
                    value={requestedPageSize}
                    onChange={(event) =>
                      updateBookingsSearchParams((nextParams) => {
                        nextParams.set("pageSize", event.target.value);
                        nextParams.delete("page");
                      })
                    }
                  >
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </Field>
              </div>
              {bookings.isLoading ? <div className="page-loading">Loading bookings...</div> : null}
              {bookings.error ? <p className="error-text">{(bookings.error as Error).message}</p> : null}
              {!bookings.isLoading && !bookings.error ? (
                <div className="stack">
                  <p className="card-copy">
                    {isBookingsSearchActive
                      ? `${bookings.data?.totalItems ?? 0} matching booking${Number(bookings.data?.totalItems ?? 0) === 1 ? "" : "s"}`
                      : `${bookings.data?.totalItems ?? 0} booking${Number(bookings.data?.totalItems ?? 0) === 1 ? "" : "s"} on ${formatLongDateLabel(effectiveSelectedDate)}`}
                  </p>
                  <div className="table-wrap">
                    <table className="table customer-table bookings-dispatch-table">
                      <thead>
                        <tr>
                          <th>Service date</th>
                          <th>Customer</th>
                          <th>Service address</th>
                          <th>Frequency</th>
                          <th>Assigned to</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(bookings.data?.items ?? []).length ? (
                          (bookings.data?.items ?? []).map((item) => (
                            <tr key={item.id} onClick={() => navigate(`/admin/bookings/${item.id}${bookingListSuffix}`)}>
                              <td>
                                <div className="bookings-dispatch-cell">
                                  <strong>{item.serviceDateLocal}</strong>
                                  <span>{item.serviceTimeLabel}</span>
                                  <span>{item.timeZone}</span>
                                </div>
                              </td>
                              <td>
                                <div className="bookings-dispatch-cell">
                                  <strong>{item.customerName}</strong>
                                  <span>{item.publicReference}</span>
                                </div>
                              </td>
                              <td>
                                <div className="bookings-dispatch-cell">
                                  <strong>{item.serviceAddress || "No address on file"}</strong>
                                  <span>{item.locationName || "No location"}</span>
                                  <AddressLinkButtons address={item.serviceAddress} compact />
                                </div>
                              </td>
                              <td>
                                <div className="bookings-dispatch-cell">
                                  <strong>{item.frequencyLabel || "One-time"}</strong>
                                  <span>{item.customerPhone || item.customerEmail || "No contact on file"}</span>
                                </div>
                              </td>
                              <td>
                                <div className="bookings-dispatch-cell">
                                  <strong>{item.assignmentSummary || "Unassigned"}</strong>
                                  <span>{item.customerEmail || "No email on file"}</span>
                                </div>
                              </td>
                              <td>
                                <div className="bookings-dispatch-cell">
                                  <Badge tone={badgeToneForStatus(item.status)}>{item.status}</Badge>
                                  <span>{formatCurrency(Number(item.amountDue ?? 0))} due</span>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6}>
                              <div className="empty-state">
                                <h3>No bookings found</h3>
                                <p>{isBookingsSearchActive ? "No bookings match this search." : "Try another service day from the calendar."}</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </Card>

            {isBookingsSearchActive ? (
              <Card title="All-date search" eyebrow="Search mode" className="bookings-calendar-card">
                <p className="card-copy">
                  Search scans every booking date. Clear the search box to return to the selected-day calendar.
                </p>
              </Card>
            ) : (
              <Card title="Calendar" eyebrow="Jump to service day" className="bookings-calendar-card">
                <div className="booking-calendar">
                  <div className="booking-calendar__header">
                    <button
                      type="button"
                      className="booking-calendar__nav"
                      aria-label="Go to previous month"
                      onClick={() => setVisibleCalendarMonth((current) => getIsoMonth(shiftIsoDate(`${current}-15`, -31)))}
                    >
                      {"<"}
                    </button>
                    <strong className="booking-calendar__month">{formatMonthLabel(visibleCalendarMonth)}</strong>
                    <button
                      type="button"
                      className="booking-calendar__nav"
                      aria-label="Go to next month"
                      onClick={() => setVisibleCalendarMonth((current) => getIsoMonth(shiftIsoDate(`${current}-15`, 31)))}
                    >
                      {">"}
                    </button>
                  </div>
                  <div className="booking-calendar__weekdays" aria-hidden="true">
                    {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((label) => (
                      <span className="booking-calendar__weekday" key={label}>
                        {label}
                      </span>
                    ))}
                  </div>
                  <div className="booking-calendar__grid">
                    {calendarMonthCells.map((cell) => {
                      if (!cell.date) {
                        return <span className="booking-calendar__day booking-calendar__day--placeholder" key={cell.key} aria-hidden="true" />;
                      }

                      const count = calendarCountByDate.get(cell.date) ?? 0;

                      return (
                        <button
                          type="button"
                          key={cell.key}
                          className={[
                            "booking-calendar__day",
                            cell.date === effectiveSelectedDate ? "is-selected" : "",
                            cell.isToday ? "is-today" : "",
                            count > 0 ? "bookings-calendar__day--has-bookings" : "bookings-calendar__day--empty"
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          aria-label={`${formatLongDateLabel(cell.date)}${count > 0 ? `, ${count} booking${count === 1 ? "" : "s"}` : ""}`}
                          onClick={() =>
                            updateBookingsSearchParams((nextParams) => {
                              nextParams.set("date", cell.date!);
                            }, false)
                          }
                        >
                          <span>{cell.dayNumber}</span>
                          {count > 0 ? <small className="bookings-calendar__day-count">{count}</small> : null}
                        </button>
                      );
                    })}
                  </div>
                  <div className="bookings-calendar__footer">
                    <p className="card-copy">Selected day: {formatLongDateLabel(effectiveSelectedDate)}</p>
                    {calendarSummary.error ? <p className="error-text">{(calendarSummary.error as Error).message}</p> : null}
                    {!calendarSummary.error ? (
                      <p className="card-copy">
                        {(calendarCountByDate.get(effectiveSelectedDate) ?? 0) || 0} booking
                        {(calendarCountByDate.get(effectiveSelectedDate) ?? 0) === 1 ? "" : "s"} on this day
                      </p>
                    ) : null}
                  </div>
                </div>
              </Card>
            )}
          </div>
        ) : null}

      </OpsPageShell>
    );
  }

  return (
    <OpsPageShell>
          <OpsHeader
        eyebrow="Booking detail"
        title={bookingDetail.data?.publicReference ?? selectedSummary?.publicReference ?? "Booking details"}
        description={bookingDetail.data ? bookingDetail.data.customerName : "Review customer details, timing, status, and service scope."}
        actions={
          <div className="customer-directory-actions">
            <Button variant="secondary" onClick={() => navigate(`/admin/bookings${bookingListSuffix}`)}>Back to bookings</Button>
            <Button variant="secondary" onClick={() => navigate("/admin/bookings?new=1")}>Add booking</Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (
                  typeof window !== "undefined" &&
                  window.confirm(
                    `Delete ${bookingDetail.data?.publicReference ?? "this booking"}? Bookings with payments or refunds cannot be deleted.`
                  )
                ) {
                  deleteCurrentBooking.mutate();
                }
              }}
              disabled={!bookingRouteId || deleteCurrentBooking.isPending}
            >
              {deleteCurrentBooking.isPending ? "Deleting..." : "Delete booking"}
            </Button>
            <Button variant="secondary" onClick={() => setIsEditingBooking((value) => !value)}>
              {isEditingBooking ? "Cancel edit" : "Edit booking"}
            </Button>
            {isEditingBooking ? (
              <Button onClick={() => updateCurrentBooking.mutate()} disabled={!bookingRouteId || updateCurrentBooking.isPending}>
                {updateCurrentBooking.isPending ? "Saving..." : "Save changes"}
              </Button>
            ) : null}
          </div>
        }
      />

      {renderBookingMessageComposer()}
      {renderBookingSmsComposer()}

      {bookingDetail.data ? (
        <SectionJumpNav items={bookingSectionJumpItems} onSelect={(item) => scrollToSectionTarget(item.id)} />
      ) : null}

      <SectionJumpTarget id="booking-section-details">
        <Card
          title="Booking details"
          eyebrow={bookingDetail.data ? bookingDetail.data.customerName : "Select a booking"}
        >
        {bookingDetail.isLoading ? (
          <div className="page-loading">Loading booking details...</div>
        ) : bookingDetail.error ? (
          <p className="error-text">{(bookingDetail.error as Error).message}</p>
        ) : bookingDetail.data ? (
          <div className="stack booking-detail-stack">
            <div className="booking-meta-grid">
              <div className="booking-meta-card">
                <p className="eyebrow">Booking</p>
                <strong>{bookingDetail.data.publicReference}</strong>
                <p>{bookingDetailValue(bookingDetail.data.adminDetails, "booking_status", bookingDetail.data.status)}</p>
                <p>{formatDateTimeLabel(bookingDetail.data.startsAt)}</p>
              </div>
              <div className="booking-meta-card">
                <p className="eyebrow">Customer</p>
                <strong>{bookingDetail.data.customerName}</strong>
                <p>{bookingDetail.data.customerCompanyName || bookingDetail.data.customerType || "Customer"}</p>
                <p>{bookingDetail.data.customerPhone || "No phone on file"}</p>
                <p>{bookingDetail.data.customerEmail || "No email on file"}</p>
                <div className="customer-directory-actions">
                  <Button
                    variant="secondary"
                    onClick={() => openBookingContactHref(bookingDetail.data.customerPhone ? `tel:${bookingDetail.data.customerPhone}` : null)}
                    disabled={!bookingDetail.data.customerPhone}
                  >
                    Call
                  </Button>
                  <Button variant="secondary" onClick={() => openBookingMessageComposer()} disabled={!bookingCustomerId}>
                    Message
                  </Button>
                  <Button variant="secondary" onClick={() => openBookingSmsComposer()} disabled={!bookingCustomerId}>
                    Send SMS
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => openBookingContactHref(bookingDetail.data.customerEmail ? `mailto:${bookingDetail.data.customerEmail}` : null)}
                    disabled={!bookingDetail.data.customerEmail}
                  >
                    Email app
                  </Button>
                </div>
              </div>
              <div className="booking-meta-card">
                <p className="eyebrow">Address</p>
                <strong>{bookingDetail.data.serviceAddress || "No address on file"}</strong>
                <p>{bookingDetail.data.locationName}</p>
                <p>{formatCurrency(Number(bookingDetail.data.amountDue ?? 0))} due</p>
                <AddressLinkButtons address={bookingDetail.data.serviceAddress} compact />
              </div>
            </div>

            <SectionJumpTarget id="booking-section-basics">
              <Card title="Booking basics" eyebrow="Appointment">
              <div className="stack">
                <div className="grid two">
                  <Field label="Status">
                    <select value={draftStatus} onChange={(event) => setDraftStatus(event.target.value)} disabled={!isEditingBooking}>
                      {bookingStatusValues.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Start time" hint="Interpreted in your browser timezone when saved.">
                    <input
                      type="datetime-local"
                      value={draftStartsAt}
                      onChange={(event) => setDraftStartsAt(event.target.value)}
                      disabled={!isEditingBooking}
                    />
                  </Field>
                </div>
                {isEditingBooking ? (
                  <Card title="Edit booking basics" eyebrow="Workbook fields">
                    {renderBookingAdminEditor([
                      { key: "customer_type", label: "Customer type", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "customer_type", bookingDetail.data.customerType) },
                      { key: "frequency", label: "Frequency", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "frequency") },
                      { key: "services", label: "Services", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "services", bookingDetail.data.items.map((item) => item.name).join(" | ")) },
                      { key: "extras", label: "Extras", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "extras") },
                      { key: "extra_recurring_summary", label: "Recurring extras", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "extra_recurring_summary"), multiline: true },
                      { key: "labor_hours", label: "Labor hours", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "labor_hours") },
                      { key: "duration", label: "Duration", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "duration") },
                      { key: "type", label: "Booking type", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "type") }
                    ])}
                  </Card>
                ) : null}
                <DataTable
                  columns={["Field", "Value"]}
                  rows={bookingFieldRows([
                    ["Frequency", bookingDetailValue(bookingDetail.data.adminDetails, "frequency")],
                    ["Extras", bookingDetailValue(bookingDetail.data.adminDetails, "extras")],
                    ["Recurring extras", bookingDetailValue(bookingDetail.data.adminDetails, "extra_recurring_summary")],
                    ["Labor hours", bookingDetailValue(bookingDetail.data.adminDetails, "labor_hours")],
                    ["Duration", bookingDetailValue(bookingDetail.data.adminDetails, "duration")],
                    ["Booking type", bookingDetailValue(bookingDetail.data.adminDetails, "type")]
                  ])}
                />
              </div>
              </Card>
            </SectionJumpTarget>

            {isEditingBooking ? (
              <Card title="Edit customer & property" eyebrow="Workbook fields">
                {renderBookingAdminEditor([
                  { key: "first_name", label: "First name", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "first_name", bookingDetail.data.customerName.split(" ")[0] ?? "") },
                  { key: "last_name", label: "Last name", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "last_name", bookingDetail.data.customerName.split(" ").slice(1).join(" ")) },
                  { key: "company_name", label: "Company name", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "company_name", bookingDetail.data.customerCompanyName) },
                  { key: "email", label: "Email", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "email", bookingDetail.data.customerEmail) },
                  { key: "primary_phone", label: "Primary phone", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "primary_phone", bookingDetail.data.customerPhone) },
                  { key: "secondary_phone", label: "Secondary phone", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "secondary_phone", bookingDetail.data.customerSecondaryPhone) },
                  { key: "address", label: "Address", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "address", bookingDetail.data.serviceAddress) },
                  { key: "unit_number", label: "Unit number", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "unit_number") },
                  { key: "city", label: "City", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "city") },
                  { key: "state", label: "State", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "state") },
                  { key: "postal_code", label: "Postal code", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "postal_code") },
                  { key: "address_instructions", label: "Address instructions", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "address_instructions"), multiline: true },
                  { key: "entry_information", label: "Entry & access", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "entry_information") },
                  { key: "lockbox_code", label: "Lockbox combination", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "lockbox_code") },
                  { key: "lockbox_location", label: "Lockbox location", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "lockbox_location") },
                  { key: "gate_code", label: "Gate code", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "gate_code") },
                  { key: "building_code", label: "Building code", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "building_code") },
                  { key: "unit_code", label: "Unit door code", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "unit_code") },
                  { key: "alarm_code", label: "Alarm system code", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "alarm_code") },
                  { key: "entry_information_custom", label: "Custom entry notes", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "entry_information_custom"), multiline: true }
                ])}
              </Card>
            ) : null}

            <SectionJumpTarget id="booking-section-customer-property">
              <Card title="Customer & property" eyebrow="Profile">
              <div className="stack">
                {bookingDetail.data.zillowEnrichment ? (
                  <div className="zillow-booking-summary">
                    <div className="zillow-enrichment-panel__header">
                      <div>
                        <p className="eyebrow">Zillow enrichment</p>
                        <strong>Cached property snapshot</strong>
                        <p>{bookingDetail.data.zillowEnrichment.fetchedAt ? `Fetched ${formatDateTimeLabel(bookingDetail.data.zillowEnrichment.fetchedAt)}` : "Review-only Zillow data."}</p>
                      </div>
                      <Link className="button button--secondary" to={`/admin/customers/${bookingDetail.data.customerId}?tab=properties`}>
                        View property explorer
                      </Link>
                    </div>
                    <ZillowSummaryGrid enrichment={bookingDetail.data.zillowEnrichment} />
                  </div>
                ) : (
                  <div className="booking-meta-card">
                    <p className="eyebrow">Zillow enrichment</p>
                    <strong>No cached property snapshot</strong>
                    <p>Open the customer property tab to search Zillow and fetch a review-only snapshot.</p>
                    <Link className="button button--secondary" to={`/admin/customers/${bookingDetail.data.customerId}?tab=properties`}>
                      Open property tab
                    </Link>
                  </div>
                )}
                <DataTable
                  columns={["Field", "Value"]}
                  rows={bookingFieldRows([
                    ["Secondary phone", bookingDetailValue(bookingDetail.data.adminDetails, "secondary_phone", bookingDetail.data.customerSecondaryPhone)],
                    ["Address", bookingDetailValue(bookingDetail.data.adminDetails, "address")],
                    ["Unit number", bookingDetailValue(bookingDetail.data.adminDetails, "unit_number")],
                    ["City", bookingDetailValue(bookingDetail.data.adminDetails, "city")],
                    ["State", bookingDetailValue(bookingDetail.data.adminDetails, "state")],
                    ["Postal code", bookingDetailValue(bookingDetail.data.adminDetails, "postal_code")],
                    ["Address instructions", bookingDetailValue(bookingDetail.data.adminDetails, "address_instructions")],
                    ["Supplies provided by", bookingDetailValue(bookingDetail.data.adminDetails, "supplies_provided_by")],
                    ["Customer provides supplies/equipment", bookingDetailValue(bookingDetail.data.adminDetails, "customer_provides_supplies_equipment")],
                    ["Required supplies/equipment", bookingDetailValue(bookingDetail.data.adminDetails, "customer_supplies_equipment_items")],
                    ["Entry & access", bookingDetailValue(bookingDetail.data.adminDetails, "entry_information")],
                    ["Lockbox combination", bookingDetailValue(bookingDetail.data.adminDetails, "lockbox_code")],
                    ["Lockbox location", bookingDetailValue(bookingDetail.data.adminDetails, "lockbox_location")],
                    ["Gate code", bookingDetailValue(bookingDetail.data.adminDetails, "gate_code")],
                    ["Building code", bookingDetailValue(bookingDetail.data.adminDetails, "building_code")],
                    ["Unit door code", bookingDetailValue(bookingDetail.data.adminDetails, "unit_code")],
                    ["Alarm system code", bookingDetailValue(bookingDetail.data.adminDetails, "alarm_code")],
                    ["Custom entry notes", bookingDetailValue(bookingDetail.data.adminDetails, "entry_information_custom")]
                  ])}
                />
              </div>
              </Card>
            </SectionJumpTarget>

            <SectionJumpTarget id="booking-section-pricing-payment">
              <Card title="Pricing & payment" eyebrow="Finance">
              <div className="stack">
                <Card title="Invoice" eyebrow="Billing document">
                  {bookingInvoice.isLoading ? (
                    <p className="card-copy">Loading invoice status...</p>
                  ) : currentInvoice ? (
                    <div className="stack">
                      <div className="data-grid">
                        <div>
                          <p className="eyebrow">Invoice number</p>
                          <strong>{currentInvoice.invoiceNumber}</strong>
                          <p>{formatCurrency(Number(currentInvoice.amountDue ?? 0))} due</p>
                        </div>
                        <div>
                          <p className="eyebrow">Status</p>
                          <Badge tone={badgeToneForStatus(currentInvoice.status)}>{currentInvoice.status.replace(/_/g, " ")}</Badge>
                          <p>{currentInvoice.lastSentAt ? `Last sent ${formatDateTimeLabel(currentInvoice.lastSentAt)}` : "Not sent yet"}</p>
                        </div>
                        <div>
                          <p className="eyebrow">Revision</p>
                          <strong>v{currentInvoice.currentVersion}</strong>
                          <p>Due {formatDateLabel(currentInvoice.dueDate)}</p>
                        </div>
                      </div>
                      <div className="button-row">
                        <Button variant="secondary" onClick={() => setInvoiceEditorOpen((open) => !open)}>
                          {invoiceEditorOpen ? "Hide editor" : currentInvoice.status === "draft" ? "Edit draft" : "View invoice"}
                        </Button>
                        {invoiceCanEdit ? (
                          <>
                            <Button variant="secondary" onClick={() => saveInvoiceDraftMutation.mutate()} disabled={saveInvoiceDraftMutation.isPending || !invoiceDraft}>
                              {saveInvoiceDraftMutation.isPending ? "Saving..." : "Save draft"}
                            </Button>
                            <Button variant="secondary" onClick={() => previewInvoiceMutation.mutate()} disabled={previewInvoiceMutation.isPending || !invoiceDraft}>
                              {previewInvoiceMutation.isPending ? "Generating..." : "Preview PDF"}
                            </Button>
                            <Button onClick={() => sendInvoiceMutation.mutate()} disabled={sendInvoiceMutation.isPending || !invoiceRecipientEmail || !invoiceDraft}>
                              {sendInvoiceMutation.isPending ? "Sending..." : "Send invoice"}
                            </Button>
                          </>
                        ) : null}
                        {currentInvoice.pdfDownloadUrl ? (
                          <Button variant="secondary" onClick={() => window.open(currentInvoice.pdfDownloadUrl || `/api/invoices/${currentInvoice.id}/pdf`, "_blank", "noopener,noreferrer")}>
                            Download PDF
                          </Button>
                        ) : null}
                        {!invoiceCanEdit && currentInvoice.status !== "void" ? (
                          <Button variant="secondary" onClick={() => reviseInvoiceMutation.mutate()} disabled={reviseInvoiceMutation.isPending}>
                            {reviseInvoiceMutation.isPending ? "Creating..." : "Create revision"}
                          </Button>
                        ) : null}
                        {currentInvoice.status !== "void" ? (
                          <Button variant="secondary" onClick={() => voidInvoiceMutation.mutate()} disabled={voidInvoiceMutation.isPending}>
                            {voidInvoiceMutation.isPending ? "Voiding..." : "Void"}
                          </Button>
                        ) : null}
                      </div>
                      {invoiceFeedback ? <p className="card-copy">{invoiceFeedback}</p> : null}
                      {invoiceEditorOpen && invoiceDraft ? (
                        <div className="stack">
                          <Card title="Business & contact" eyebrow="Invoice sender">
                            <div className="grid two">
                              <Field label="Company name">
                                <input value={invoiceDraft.businessName} onChange={(event) => updateInvoiceDraftField("businessName", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Logo URL">
                                <input value={invoiceDraft.businessLogoUrl} onChange={(event) => updateInvoiceDraftField("businessLogoUrl", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Business address">
                                <textarea value={invoiceDraft.businessAddress} onChange={(event) => updateInvoiceDraftField("businessAddress", event.target.value)} disabled={!invoiceCanEdit} rows={2} />
                              </Field>
                              <Field label="Company website">
                                <input value={invoiceDraft.companyWebsite} onChange={(event) => updateInvoiceDraftField("companyWebsite", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Support phone">
                                <input value={invoiceDraft.supportPhone} onChange={(event) => updateInvoiceDraftField("supportPhone", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Support email">
                                <input value={invoiceDraft.supportEmail} onChange={(event) => updateInvoiceDraftField("supportEmail", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Terms & Conditions URL">
                                <input value={invoiceDraft.termsUrl} onChange={(event) => updateInvoiceDraftField("termsUrl", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                            </div>
                          </Card>
                          <Card title="Header" eyebrow="Invoice details">
                            <div className="grid two">
                              <Field label="Issue date">
                                <input type="date" value={invoiceIssueDate} onChange={(event) => setInvoiceIssueDate(event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Due date">
                                <input type="date" value={invoiceDueDate} onChange={(event) => setInvoiceDueDate(event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                            </div>
                          </Card>
                          <Card title="Customer & billing" eyebrow="Snapshot">
                            <div className="grid two">
                              <Field label="Customer name">
                                <input value={invoiceDraft.customerName} onChange={(event) => updateInvoiceDraftField("customerName", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Customer email">
                                <input value={invoiceDraft.customerEmail} onChange={(event) => updateInvoiceDraftField("customerEmail", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Customer phone">
                                <input value={invoiceDraft.customerPhone} onChange={(event) => updateInvoiceDraftField("customerPhone", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Billing address">
                                <textarea value={invoiceDraft.billingAddress} onChange={(event) => updateInvoiceDraftField("billingAddress", event.target.value)} disabled={!invoiceCanEdit} rows={3} />
                              </Field>
                              <Field label="Service address">
                                <textarea value={invoiceDraft.serviceAddress} onChange={(event) => updateInvoiceDraftField("serviceAddress", event.target.value)} disabled={!invoiceCanEdit} rows={3} />
                              </Field>
                            </div>
                          </Card>
                          <Card title="Appointment" eyebrow="Booking snapshot">
                            <div className="grid two">
                              <Field label="Booking reference">
                                <input value={invoiceDraft.bookingReference} onChange={(event) => updateInvoiceDraftField("bookingReference", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Service date">
                                <input value={invoiceDraft.serviceDateLabel} onChange={(event) => updateInvoiceDraftField("serviceDateLabel", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Appointment">
                                <input value={invoiceDraft.appointmentLabel} onChange={(event) => updateInvoiceDraftField("appointmentLabel", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Service">
                                <input value={invoiceDraft.serviceLabel} onChange={(event) => updateInvoiceDraftField("serviceLabel", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Frequency">
                                <input value={invoiceDraft.frequencyLabel} onChange={(event) => updateInvoiceDraftField("frequencyLabel", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Arrival window">
                                <input value={invoiceDraft.arrivalWindowLabel} onChange={(event) => updateInvoiceDraftField("arrivalWindowLabel", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Assigned maids">
                                <input value={invoiceDraft.maidNames} onChange={(event) => updateInvoiceDraftField("maidNames", event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                            </div>
                          </Card>
                          <Card title="Line items" eyebrow="Editable snapshot">
                            <div className="stack">
                              {(invoiceDraft.lineItems ?? []).map((item, index) => (
                                <div className="invoice-line-editor" key={item.id || index}>
                                  <Field label="Label">
                                    <input value={item.label} onChange={(event) => updateInvoiceLineItem(index, { label: event.target.value })} disabled={!invoiceCanEdit} />
                                  </Field>
                                  <Field label="Description">
                                    <input value={item.description} onChange={(event) => updateInvoiceLineItem(index, { description: event.target.value })} disabled={!invoiceCanEdit} />
                                  </Field>
                                  <Field label="Qty">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={item.quantity}
                                      onChange={(event) => {
                                        const quantity = Number(event.target.value || 0);
                                        updateInvoiceLineItem(index, {
                                          quantity,
                                          totalAmount: Math.round(quantity * Number(item.unitPriceAmount ?? 0))
                                        });
                                      }}
                                      disabled={!invoiceCanEdit}
                                    />
                                  </Field>
                                  <Field label="Unit price">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={centsToDollarInput(item.unitPriceAmount)}
                                      onChange={(event) => {
                                        const unitPriceAmount = dollarsToCents(event.target.value);
                                        updateInvoiceLineItem(index, {
                                          unitPriceAmount,
                                          totalAmount: Math.round(Number(item.quantity ?? 1) * unitPriceAmount)
                                        });
                                      }}
                                      disabled={!invoiceCanEdit}
                                    />
                                  </Field>
                                  <Field label="Line total">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={centsToDollarInput(item.totalAmount)}
                                      onChange={(event) => updateInvoiceLineItem(index, { totalAmount: dollarsToCents(event.target.value) })}
                                      disabled={!invoiceCanEdit}
                                    />
                                  </Field>
                                  {invoiceCanEdit ? (
                                    <Button variant="secondary" onClick={() => removeInvoiceLineItem(index)}>
                                      Remove
                                    </Button>
                                  ) : null}
                                </div>
                              ))}
                              {invoiceCanEdit ? (
                                <Button variant="secondary" onClick={addInvoiceLineItem}>
                                  Add line item
                                </Button>
                              ) : null}
                              <div className="grid two">
                                <Field label="Discount">
                                  <input type="number" step="0.01" value={centsToDollarInput(invoiceDraft.discountAmount)} onChange={(event) => updateInvoiceDraftField("discountAmount", dollarsToCents(event.target.value))} disabled={!invoiceCanEdit} />
                                </Field>
                                <Field label="Tax">
                                  <input type="number" step="0.01" value={centsToDollarInput(invoiceDraft.taxAmount)} onChange={(event) => updateInvoiceDraftField("taxAmount", dollarsToCents(event.target.value))} disabled={!invoiceCanEdit} />
                                </Field>
                                <Field label="Amount paid">
                                  <input type="number" step="0.01" value={centsToDollarInput(invoiceDraft.amountPaid)} onChange={(event) => updateInvoiceDraftField("amountPaid", dollarsToCents(event.target.value))} disabled={!invoiceCanEdit} />
                                </Field>
                                <div className="quote-callout">
                                  <p className="eyebrow">Invoice total</p>
                                  <h3>{formatCurrency(invoiceDraft.totalAmount)}</h3>
                                  <p>{formatCurrency(invoiceDraft.amountDue)} due after payments.</p>
                                </div>
                              </div>
                            </div>
                          </Card>
                          <Card title="Notes & terms" eyebrow="PDF copy">
                            <div className="grid two">
                              <Field label="Memo">
                                <textarea value={invoiceDraft.memo} onChange={(event) => updateInvoiceDraftField("memo", event.target.value)} disabled={!invoiceCanEdit} rows={4} />
                              </Field>
                              <Field label="Payment instructions">
                                <textarea value={invoiceDraft.paymentInstructions} onChange={(event) => updateInvoiceDraftField("paymentInstructions", event.target.value)} disabled={!invoiceCanEdit} rows={4} />
                              </Field>
                              <Field label="Footer">
                                <textarea value={invoiceDraft.footer} onChange={(event) => updateInvoiceDraftField("footer", event.target.value)} disabled={!invoiceCanEdit} rows={4} />
                              </Field>
                            </div>
                          </Card>
                          <Card title="Preview & send" eyebrow="Email">
                            <div className="grid two">
                              <Field label="Recipient email">
                                <input value={invoiceRecipientEmail} onChange={(event) => setInvoiceRecipientEmail(event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Subject">
                                <input value={invoiceEmailSubject} onChange={(event) => setInvoiceEmailSubject(event.target.value)} disabled={!invoiceCanEdit} />
                              </Field>
                              <Field label="Email body">
                                <textarea value={invoiceEmailBody} onChange={(event) => setInvoiceEmailBody(event.target.value)} disabled={!invoiceCanEdit} rows={8} />
                              </Field>
                            </div>
                          </Card>
                        </div>
                      ) : null}
                      {currentInvoice.revisions.length ? (
                        <DataTable
                          columns={["Version", "Status", "Sent", "Recipient", "Subject"]}
                          rows={currentInvoice.revisions.map((revision) => [
                            `v${revision.version}`,
                            revision.status,
                            revision.sentAt ? formatDateTimeLabel(revision.sentAt) : "Not sent",
                            revision.sentTo || "-",
                            revision.emailSubject || "-"
                          ])}
                        />
                      ) : null}
                    </div>
                  ) : (
                    <div className="stack">
                      <p className="card-copy">Create an editable invoice snapshot from this booking. Existing booking prices and payments will not be changed.</p>
                      <Button onClick={() => createInvoiceMutation.mutate()} disabled={createInvoiceMutation.isPending}>
                        {createInvoiceMutation.isPending ? "Creating..." : "Create invoice"}
                      </Button>
                      {invoiceFeedback ? <p className="error-text">{invoiceFeedback}</p> : null}
                    </div>
                  )}
                </Card>
                {isEditingBooking ? (
                  <Card title="Edit pricing & payment" eyebrow="Workbook fields">
                    {renderBookingAdminEditor([
                      { key: "service_total", label: "Service total", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "service_total", formatCurrency(Number(bookingDetail.data.totalAmount ?? 0))) },
                      { key: "extras_total", label: "Extras total", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "extras_total") },
                      { key: "final_amount", label: "Final amount", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "final_amount", formatCurrency(Number(bookingDetail.data.totalAmount ?? 0))) },
                      { key: "amount_paid_by_customer", label: "Amount paid", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "amount_paid_by_customer", formatCurrency(Number(bookingDetail.data.totalAmount ?? 0) - Number(bookingDetail.data.amountDue ?? 0))) },
                      { key: "amount_owed_by_customer", label: "Amount owed", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "amount_owed_by_customer", formatCurrency(Number(bookingDetail.data.amountDue ?? 0))) },
                      { key: "tip_type", label: "Tip type", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "tip_type") },
                      { key: "tip", label: "Tip", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "tip") },
                      { key: "payment_method", label: "Payment method", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "payment_method") },
                      { key: "frequency_discount_type", label: "Frequency discount type", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "frequency_discount_type") },
                      { key: "customer_supplies_equipment_discount", label: "Customer supplies discount", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "customer_supplies_equipment_discount") },
                      { key: "discount_code", label: "Discount code", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "discount_code") },
                      { key: "discount_from_code", label: "Discount from code", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "discount_from_code") },
                      { key: "gift_card_number", label: "Gift card number", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "gift_card_number") },
                      { key: "invoice_number", label: "Invoice number", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "invoice_number", bookingDetail.data.publicReference) },
                      { key: "quote_received", label: "Quote received", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "quote_received") },
                      { key: "quote_labor_hours", label: "Quote labor hours", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "quote_labor_hours") },
                      { key: "quote_amount", label: "Quote amount", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "quote_amount") },
                      { key: "payment_authorized", label: "Payment authorized", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "payment_authorized") }
                    ])}
                  </Card>
                ) : null}
                <DataTable
                  columns={["Field", "Value"]}
                  rows={bookingFieldRows([
                    ["Extras total", bookingDetailValue(bookingDetail.data.adminDetails, "extras_total")],
                    ["Tip type", bookingDetailValue(bookingDetail.data.adminDetails, "tip_type")],
                    ["Tip", bookingDetailValue(bookingDetail.data.adminDetails, "tip")],
                    ["Payment method", bookingDetailValue(bookingDetail.data.adminDetails, "payment_method")],
                    ["Frequency discount type", bookingDetailValue(bookingDetail.data.adminDetails, "frequency_discount_type")],
                    ["Customer supplies discount", bookingDetailValue(bookingDetail.data.adminDetails, "customer_supplies_equipment_discount")],
                    ["Discount code", bookingDetailValue(bookingDetail.data.adminDetails, "discount_code")],
                    ["Discount from code", bookingDetailValue(bookingDetail.data.adminDetails, "discount_from_code")],
                    ["Gift card number", bookingDetailValue(bookingDetail.data.adminDetails, "gift_card_number")],
                    ["Payment authorized", bookingDetailValue(bookingDetail.data.adminDetails, "payment_authorized")],
                    ["Quote received", bookingDetailValue(bookingDetail.data.adminDetails, "quote_received")],
                    ["Quote labor hours", bookingDetailValue(bookingDetail.data.adminDetails, "quote_labor_hours")],
                    ["Quote amount", bookingDetailValue(bookingDetail.data.adminDetails, "quote_amount")]
                  ])}
                />
                {bookingDetail.data.payments.length ? (
                  <DataTable
                    columns={["Date", "Type", "Amount", "Status", "Provider"]}
                    rows={bookingDetail.data.payments.map((payment) => [
                      formatDateTimeLabel(payment.createdAt),
                      payment.paymentType,
                      formatCurrency(Number(payment.amount ?? 0)),
                      payment.status,
                      payment.provider
                    ])}
                  />
                ) : (
                  <p className="card-copy">No payments recorded for this booking yet.</p>
                )}
                {bookingDetail.data.refunds.length ? (
                  <DataTable
                    columns={["Date", "Amount", "Reason", "Status"]}
                    rows={bookingDetail.data.refunds.map((refund) => [
                      formatDateTimeLabel(refund.createdAt),
                      formatCurrency(Number(refund.amount ?? 0)),
                      refund.reason,
                      refund.status
                    ])}
                  />
                ) : null}
              </div>
              </Card>
            </SectionJumpTarget>

            <SectionJumpTarget id="booking-section-service-notes">
              <Card title="Service notes" eyebrow="Operations">
              <div className="stack">
                <div className="grid two">
                  <Field label="Customer notes">
                    <textarea value={draftNotes} onChange={(event) => setDraftNotes(event.target.value)} rows={4} readOnly={!isEditingBooking} />
                  </Field>
                  <Field label="Internal notes">
                    <textarea value={draftInternalNotes} onChange={(event) => setDraftInternalNotes(event.target.value)} rows={4} readOnly={!isEditingBooking} />
                  </Field>
                </div>
                {isEditingBooking ? (
                  <Card title="Edit service notes" eyebrow="Workbook fields">
                    {renderBookingAdminEditor([
                      { key: "priority_areas", label: "Priority areas", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "priority_areas") },
                      { key: "skip_areas", label: "Skip areas", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "skip_areas") },
                      { key: "problem_areas", label: "Problem areas", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "problem_areas"), multiline: true },
                      { key: "special_incidents", label: "Special incidents", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "special_incidents"), multiline: true },
                      { key: "required_documents", label: "Required documents", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "required_documents") },
                      { key: "consent_received", label: "Consent received", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "consent_received") },
                      { key: "appointment_notifications", label: "Appointment notifications", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "appointment_notifications") },
                      { key: "arrival_flexibility", label: "Arrival flexibility", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "arrival_flexibility") }
                    ])}
                  </Card>
                ) : null}
                <DataTable
                  columns={["Field", "Value"]}
                  rows={bookingFieldRows([
                    ["Priority areas", bookingDetailValue(bookingDetail.data.adminDetails, "priority_areas")],
                    ["Skip areas", bookingDetailValue(bookingDetail.data.adminDetails, "skip_areas")],
                    ["Problem areas", bookingDetailValue(bookingDetail.data.adminDetails, "problem_areas")],
                    ["Special incidents", bookingDetailValue(bookingDetail.data.adminDetails, "special_incidents")],
                    ["Required documents", bookingDetailValue(bookingDetail.data.adminDetails, "required_documents")],
                    ["Consent received", bookingDetailValue(bookingDetail.data.adminDetails, "consent_received")],
                    ["Appointment notifications", bookingDetailValue(bookingDetail.data.adminDetails, "appointment_notifications")],
                    ["Arrival flexibility", bookingDetailValue(bookingDetail.data.adminDetails, "arrival_flexibility")]
                  ])}
                />
              </div>
              </Card>
            </SectionJumpTarget>

            <SectionJumpTarget id="booking-section-staff-preferences">
              <Card title="Staff preferences" eyebrow="Dispatch">
              <div className="stack">
                {isEditingBooking ? (
                  <Card title="Edit staff preferences" eyebrow="Workbook fields">
                    {renderBookingAdminEditor([
                      { key: "preferred_staff", label: "Preferred staff", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "preferred_staff"), multiline: true },
                      { key: "blocked_staff", label: "Blocked staff", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "blocked_staff"), multiline: true },
                      { key: "english_speaking_staff", label: "English speaking staff", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "english_speaking_staff") },
                      { key: "team_has_key", label: "Team has key", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "team_has_key") },
                      { key: "teams_assigned_without_ids", label: "Assigned team names", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "teams_assigned_without_ids"), multiline: true },
                      { key: "teams_assigned", label: "Assigned teams", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "teams_assigned"), multiline: true }
                    ])}
                  </Card>
                ) : null}
                <DataTable
                  columns={["Field", "Value"]}
                  rows={bookingFieldRows([
                    ["Preferred staff", bookingDetailValue(bookingDetail.data.adminDetails, "preferred_staff")],
                    ["Blocked staff", bookingDetailValue(bookingDetail.data.adminDetails, "blocked_staff")],
                    ["English speaking staff", bookingDetailValue(bookingDetail.data.adminDetails, "english_speaking_staff")],
                    ["Team has key", bookingDetailValue(bookingDetail.data.adminDetails, "team_has_key")],
                    ["Assigned team names", bookingDetailValue(bookingDetail.data.adminDetails, "teams_assigned_without_ids")],
                    ["Assigned teams", bookingDetailValue(bookingDetail.data.adminDetails, "teams_assigned")]
                  ])}
                />
                {bookingDetail.data.assignments.length ? (
                  <DataTable
                    columns={["Staff", "Role"]}
                    rows={bookingDetail.data.assignments.map((assignment) => [assignment.staffName, assignment.roleLabel || "Assigned"])}
                  />
                ) : (
                  <p className="card-copy">No staff has been assigned to this booking yet.</p>
                )}
              </div>
              </Card>
            </SectionJumpTarget>

            <SectionJumpTarget id="booking-section-home-profile">
              <Card title="Home / property profile" eyebrow="Property">
              {isEditingBooking ? (
                <Card title="Edit home / property profile" eyebrow="Workbook fields">
                  {renderBookingAdminEditor([
                    { key: "bedrooms", label: "Bedrooms", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "bedrooms") },
                    { key: "workstations", label: "Workstations", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "workstations") },
                    { key: "office_private_rooms", label: "Private rooms / offices / conference rooms", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "office_private_rooms") },
                    { key: "office_breakrooms", label: "Breakrooms / kitchenettes", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "office_breakrooms") },
                    { key: "bathrooms", label: "Bathrooms", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "bathrooms") },
                    { key: "levels", label: "Levels", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "levels") },
                    { key: "square_footage", label: "Square footage", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "square_footage") },
                    { key: "pets", label: "Pets", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "pets", bookingDetail.data.pets) },
                    { key: "sensitivities_allergies", label: "Sensitivities / allergies", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "sensitivities_allergies", bookingDetail.data.sensitivities), multiline: true },
                    { key: "green_supplies_only", label: "Green supplies only", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "green_supplies_only") },
                    { key: "supplies_provided_by", label: "Supplies provided by", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "supplies_provided_by") },
                    { key: "customer_provides_supplies_equipment", label: "Customer provides supplies/equipment", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "customer_provides_supplies_equipment") },
                    { key: "customer_supplies_equipment_items", label: "Required supplies/equipment", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "customer_supplies_equipment_items"), multiline: true },
                    { key: "property_pictures", label: "Property pictures", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "property_pictures"), multiline: true }
                  ])}
                </Card>
              ) : null}
              <DataTable
                columns={["Field", "Value"]}
                rows={bookingFieldRows([
                  ["Bedrooms", bookingDetailValue(bookingDetail.data.adminDetails, "bedrooms")],
                  ["Workstations", bookingDetailValue(bookingDetail.data.adminDetails, "workstations")],
                  ["Private rooms / offices / conference rooms", bookingDetailValue(bookingDetail.data.adminDetails, "office_private_rooms")],
                  ["Breakrooms / kitchenettes", bookingDetailValue(bookingDetail.data.adminDetails, "office_breakrooms")],
                  ["Bathrooms", bookingDetailValue(bookingDetail.data.adminDetails, "bathrooms")],
                  ["Levels", bookingDetailValue(bookingDetail.data.adminDetails, "levels")],
                  ["Square footage", bookingDetailValue(bookingDetail.data.adminDetails, "square_footage")],
                  ["Pets", bookingDetailValue(bookingDetail.data.adminDetails, "pets", bookingDetail.data.pets)],
                  ["Sensitivities / allergies", bookingDetailValue(bookingDetail.data.adminDetails, "sensitivities_allergies", bookingDetail.data.sensitivities)],
                  ["Green supplies only", bookingDetailValue(bookingDetail.data.adminDetails, "green_supplies_only")],
                  ["Supplies provided by", bookingDetailValue(bookingDetail.data.adminDetails, "supplies_provided_by")],
                  ["Customer provides supplies/equipment", bookingDetailValue(bookingDetail.data.adminDetails, "customer_provides_supplies_equipment")],
                  ["Required supplies/equipment", bookingDetailValue(bookingDetail.data.adminDetails, "customer_supplies_equipment_items")],
                  ["Property pictures", bookingDetailValue(bookingDetail.data.adminDetails, "property_pictures")]
                ])}
              />
              </Card>
            </SectionJumpTarget>

            <SectionJumpTarget id="booking-section-condition-quality">
              <Card title="Condition / quality" eyebrow="Quality">
              {isEditingBooking ? (
                <Card title="Edit condition / quality" eyebrow="Workbook fields">
                  {renderBookingAdminEditor([
                    { key: "overall_condition", label: "Overall condition", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "overall_condition") },
                    { key: "kitchen_condition", label: "Kitchen condition", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "kitchen_condition") },
                    { key: "bathrooms_condition", label: "Bathrooms condition", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "bathrooms_condition") },
                    { key: "bedrooms_condition", label: "Bedrooms condition", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "bedrooms_condition") },
                    { key: "living_areas_condition", label: "Living areas condition", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "living_areas_condition") },
                    { key: "rating_value", label: "Rating value", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "rating_value") },
                    { key: "rating_comment", label: "Rating comment", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "rating_comment"), multiline: true },
                    { key: "new_or_returning_customer", label: "New or returning customer", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "new_or_returning_customer") },
                    { key: "referral_source", label: "Referral source", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "referral_source") }
                  ])}
                </Card>
              ) : null}
              <DataTable
                columns={["Field", "Value"]}
                rows={bookingFieldRows([
                  ["Overall condition", bookingDetailValue(bookingDetail.data.adminDetails, "overall_condition")],
                  ["Kitchen condition", bookingDetailValue(bookingDetail.data.adminDetails, "kitchen_condition")],
                  ["Bathrooms condition", bookingDetailValue(bookingDetail.data.adminDetails, "bathrooms_condition")],
                  ["Bedrooms condition", bookingDetailValue(bookingDetail.data.adminDetails, "bedrooms_condition")],
                  ["Living areas condition", bookingDetailValue(bookingDetail.data.adminDetails, "living_areas_condition")],
                  ["Rating value", bookingDetailValue(bookingDetail.data.adminDetails, "rating_value")],
                  ["Rating comment", bookingDetailValue(bookingDetail.data.adminDetails, "rating_comment")],
                  ["New or returning customer", bookingDetailValue(bookingDetail.data.adminDetails, "new_or_returning_customer")],
                  ["Referral source", bookingDetailValue(bookingDetail.data.adminDetails, "referral_source")]
                ])}
              />
              </Card>
            </SectionJumpTarget>

            <SectionJumpTarget id="booking-section-customer-feedback">
              <Card title="Customer feedback" eyebrow="Post-visit">
              {bookingDetail.data.feedback ? (
                <div className="stack">
                  <div className="booking-meta-grid">
                    <div className="booking-meta-card">
                      <p className="eyebrow">Overall rating</p>
                      <strong>{bookingDetail.data.feedback.overallRating ?? bookingDetail.data.feedback.clickedRating ?? "Not rated"}</strong>
                      <p>{bookingDetail.data.feedback.clickedAt ? `Clicked ${formatDateTimeLabel(bookingDetail.data.feedback.clickedAt)}` : "No email star click logged."}</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">Submission</p>
                      <strong>{bookingDetail.data.feedback.submittedAt ? "Submitted" : "Draft only"}</strong>
                      <p>{bookingDetail.data.feedback.submittedAt ? formatDateTimeLabel(bookingDetail.data.feedback.submittedAt) : "Customer has not submitted detailed feedback yet."}</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">Office follow-up</p>
                      <strong>{bookingDetail.data.feedback.lowRatingFlaggedAt ? "Required" : "Not flagged"}</strong>
                      <p>{bookingDetail.data.feedback.lowRatingFlaggedAt ? formatDateTimeLabel(bookingDetail.data.feedback.lowRatingFlaggedAt) : "No low-rating alert on this booking."}</p>
                    </div>
                  </div>
                  <DataTable
                    columns={["Field", "Value"]}
                    rows={bookingFieldRows([
                      ["Clicked rating", bookingDetail.data.feedback.clickedRating == null ? "" : `${bookingDetail.data.feedback.clickedRating}/5`],
                      ["Overall rating", bookingDetail.data.feedback.overallRating == null ? "" : `${bookingDetail.data.feedback.overallRating}/5`],
                      ["Kitchen", bookingDetail.data.feedback.kitchenRating == null ? "" : `${bookingDetail.data.feedback.kitchenRating}/5`],
                      ["Bathrooms", bookingDetail.data.feedback.bathroomsRating == null ? "" : `${bookingDetail.data.feedback.bathroomsRating}/5`],
                      ["Bedrooms", bookingDetail.data.feedback.bedroomsRating == null ? "" : `${bookingDetail.data.feedback.bedroomsRating}/5`],
                      ["Living areas", bookingDetail.data.feedback.livingAreasRating == null ? "" : `${bookingDetail.data.feedback.livingAreasRating}/5`],
                      ["Comment", bookingDetail.data.feedback.comment || ""],
                      ["Request sent", bookingDetail.data.feedback.requestSentAt ? formatDateTimeLabel(bookingDetail.data.feedback.requestSentAt) : ""],
                      ["Viewed", bookingDetail.data.feedback.viewedAt ? formatDateTimeLabel(bookingDetail.data.feedback.viewedAt) : ""],
                      ["Submitted", bookingDetail.data.feedback.submittedAt ? formatDateTimeLabel(bookingDetail.data.feedback.submittedAt) : ""]
                    ])}
                  />
                  <Card title="Assigned staff receiving this feedback" eyebrow="Current visibility">
                    {bookingDetail.data.assignments.length ? (
                      <DataTable
                        columns={["Staff", "Role"]}
                        rows={bookingDetail.data.assignments.map((assignment) => [assignment.staffName, assignment.roleLabel || "Assigned"])}
                      />
                    ) : (
                      <p className="card-copy">Feedback exists for this booking, but no maids are assigned to it yet.</p>
                    )}
                  </Card>
                  {bookingDetail.data.feedback.photos?.length ? (
                    <div className="feedback-photo-grid">
                      {bookingDetail.data.feedback.photos.map((photo) => (
                        <a
                          key={photo.key}
                          href={`/api/bookings/${bookingDetail.data.id}/feedback/photos/download?key=${encodeURIComponent(photo.key)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="feedback-photo-card"
                        >
                          <img src={`/api/bookings/${bookingDetail.data.id}/feedback/photos/download?key=${encodeURIComponent(photo.key)}`} alt={photo.name} />
                          <span>{photo.name}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="card-copy">No feedback photos uploaded for this booking.</p>
                  )}
                </div>
              ) : (
                <p className="card-copy">No booking feedback has been recorded yet.</p>
              )}
              </Card>
            </SectionJumpTarget>

            <SectionJumpTarget id="booking-section-admin-history">
              <Card title="Admin / history" eyebrow="Back office">
              {isEditingBooking ? (
                <Card title="Edit admin / history" eyebrow="Workbook fields">
                  {renderBookingAdminEditor([
                    { key: "booking_id", label: "Booking ID", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "booking_id", bookingDetail.data.id), readOnly: true },
                    { key: "customer_id", label: "Customer ID", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "customer_id") },
                    { key: "date_created", label: "Date created", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "date_created", formatDateLabel(bookingDetail.data.startsAt)) },
                    { key: "created_by", label: "Created by", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "created_by") },
                    { key: "next_booking_date", label: "Next booking date", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "next_booking_date") },
                    { key: "date_cancelled", label: "Date cancelled", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "date_cancelled") },
                    { key: "cancelled_by", label: "Cancelled by", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "cancelled_by") },
                    { key: "cancellation_reason", label: "Cancellation reason", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "cancellation_reason"), multiline: true },
                    { key: "cancellation_fee", label: "Cancellation fee", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "cancellation_fee") },
                    { key: "price_adjustment", label: "Price adjustment", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "price_adjustment") },
                    { key: "is_price_adjustment_after_tax", label: "Adjustment after tax", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "is_price_adjustment_after_tax") },
                    { key: "price_adjustment_comment", label: "Adjustment comment", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "price_adjustment_comment"), multiline: true },
                    { key: "team_earnings_amount", label: "Team earnings amount", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "team_earnings_amount") },
                    { key: "team_earnings_summary", label: "Team earnings summary", fallback: bookingDetailValue(bookingDetail.data.adminDetails, "team_earnings_summary"), multiline: true }
                  ])}
                </Card>
              ) : null}
              <DataTable
                columns={["Field", "Value"]}
                rows={bookingFieldRows([
                  ["Customer ID", bookingDetailValue(bookingDetail.data.adminDetails, "customer_id")],
                  ["Created by", bookingDetailValue(bookingDetail.data.adminDetails, "created_by")],
                  ["Next booking date", bookingDetailValue(bookingDetail.data.adminDetails, "next_booking_date")],
                  ["Date cancelled", bookingDetailValue(bookingDetail.data.adminDetails, "date_cancelled")],
                  ["Cancelled by", bookingDetailValue(bookingDetail.data.adminDetails, "cancelled_by")],
                  ["Cancellation reason", bookingDetailValue(bookingDetail.data.adminDetails, "cancellation_reason")],
                  ["Cancellation fee", bookingDetailValue(bookingDetail.data.adminDetails, "cancellation_fee")],
                  ["Price adjustment", bookingDetailValue(bookingDetail.data.adminDetails, "price_adjustment")],
                  ["Adjustment after tax", bookingDetailValue(bookingDetail.data.adminDetails, "is_price_adjustment_after_tax")],
                  ["Adjustment comment", bookingDetailValue(bookingDetail.data.adminDetails, "price_adjustment_comment")],
                  ["Team earnings amount", bookingDetailValue(bookingDetail.data.adminDetails, "team_earnings_amount")],
                  ["Team earnings summary", bookingDetailValue(bookingDetail.data.adminDetails, "team_earnings_summary")]
                ])}
              />
              </Card>
            </SectionJumpTarget>

            <SectionJumpTarget id="booking-section-change-log">
              <Card title="Change log" eyebrow="History">
                {bookingChangeLog.isLoading ? (
                  <div className="page-loading">Loading change log...</div>
                ) : bookingChangeLog.error ? (
                  <p className="error-text">{(bookingChangeLog.error as Error).message}</p>
                ) : (bookingChangeLog.data?.items ?? []).length ? (
                  <div className="booking-change-log">
                    {(bookingChangeLog.data?.items ?? []).map((entry) => (
                      <article key={entry.id} className="booking-change-log__entry">
                        <div className="booking-change-log__summary">
                          <div className="booking-change-log__summary-copy">
                            <strong>{entry.summary}</strong>
                            <p className="booking-change-log__meta">
                              {formatDateTimeLabel(entry.createdAt)} | {entry.actorDisplay} | {formatBookingChangeSourceLabel(entry.source)}
                            </p>
                          </div>
                          <div className="booking-change-log__badges">
                            <Badge tone={bookingChangeSourceTone(entry.source)}>
                              {formatBookingChangeSourceLabel(entry.source)}
                            </Badge>
                          </div>
                        </div>

                        {entry.changes.length ? (
                          <div className="booking-change-log__changes">
                            {entry.changes.map((change, index) => (
                              <div key={`${entry.id}:${change.path}:${index}`} className="booking-change-log__change">
                                <div className="booking-change-log__change-head">
                                  <span className="booking-change-log__field">{change.fieldLabel}</span>
                                  <Badge tone={bookingChangeTypeTone(change.changeType)}>{change.changeType}</Badge>
                                </div>
                                <div className="booking-change-log__values">
                                  <span className="booking-change-log__value booking-change-log__value--before">
                                    <strong>Before:</strong> {formatBookingChangeValue(change.before)}
                                  </span>
                                  <span className="booking-change-log__value booking-change-log__value--after">
                                    <strong>After:</strong> {formatBookingChangeValue(change.after)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="card-copy">No field-level diffs were stored for this entry.</p>
                        )}
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="card-copy">No booking changes have been recorded yet.</p>
                )}
              </Card>
            </SectionJumpTarget>

            {updateCurrentBooking.error ? <p className="error-text">{(updateCurrentBooking.error as Error).message}</p> : null}
            {deleteCurrentBooking.error ? <p className="error-text">{(deleteCurrentBooking.error as Error).message}</p> : null}
          </div>
        ) : (
          <p className="card-copy">Booking details are unavailable for this record.</p>
        )}
        </Card>
      </SectionJumpTarget>
    </OpsPageShell>
  );
}

function PaymentsPage() {
  const queryClient = useQueryClient();
  const defaultDate = todayDateInputValue();
  const [startDate, setStartDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState(defaultDate);
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [locationId, setLocationId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedBookingIds, setSelectedBookingIds] = useState<string[]>([]);
  const [amountOverrides, setAmountOverrides] = useState<Record<string, string>>({});
  const [batchResult, setBatchResult] = useState<PaymentChargeBatch | null>(null);
  const [feedback, setFeedback] = useState("");
  const [selectedRefundBookingId, setSelectedRefundBookingId] = useState("");
  const [selectedRefundPaymentId, setSelectedRefundPaymentId] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("requested_by_customer");

  const locationsQuery = useQuery({
    queryKey: ["settings", "locations"],
    queryFn: () => apiFetch<{ items: Array<Record<string, unknown>> }>("/api/settings/locations")
  });
  const chargeQueue = useQuery({
    queryKey: ["payment-charge-queue", startDate, endDate, statusFilter, methodFilter, locationId, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        startDate,
        endDate,
        status: statusFilter,
        method: methodFilter,
        page: String(page),
        pageSize: String(pageSize)
      });
      if (locationId) {
        params.set("locationId", locationId);
      }
      return apiFetch<PaymentChargeQueueResponse>(`/api/payments/charge-queue?${params.toString()}`);
    }
  });

  const rows = chargeQueue.data?.items ?? [];
  const selectedRows = rows.filter((row) => selectedBookingIds.includes(row.id));
  const allVisibleSelected = rows.length > 0 && rows.every((row) => selectedBookingIds.includes(row.id));
  const selectedRefundRow = rows.find((row) => row.id === selectedRefundBookingId);
  const refundAmountCents = dollarsToCents(refundAmount);
  const batchAmountOverrides = Object.fromEntries(
    selectedBookingIds
      .map((id) => [id, amountOverrides[id] ? dollarsToCents(amountOverrides[id]) : 0] as const)
      .filter(([, amount]) => amount > 0)
  );

  useEffect(() => {
    setSelectedBookingIds((current) => current.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);

  const updateRowAmount = (bookingId: string, value: string) => {
    setAmountOverrides((current) => ({ ...current, [bookingId]: value }));
  };

  const toggleRow = (bookingId: string, checked: boolean) => {
    setSelectedBookingIds((current) =>
      checked ? [...new Set([...current, bookingId])] : current.filter((id) => id !== bookingId)
    );
  };

  const refundMutation = useMutation({
    mutationFn: async () =>
      apiFetch(`/api/payments/${selectedRefundPaymentId}/refund`, {
        method: "POST",
        body: JSON.stringify({
          amount: refundAmountCents,
          reason: refundReason
        })
      }),
    onSuccess: async () => {
      setFeedback("Refund issued.");
      setSelectedRefundBookingId("");
      setSelectedRefundPaymentId("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["payment-charge-queue"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] })
      ]);
    }
  });

  const batchMutation = useMutation({
    mutationFn: async (input: {
      actionMode: "charge_saved_card" | "mark_paid";
      bookingIds?: string[];
      overrides?: Record<string, number>;
    }) =>
      apiFetch<{ batch: PaymentChargeBatch }>("/api/payments/charge-queue/batches", {
        method: "POST",
        body: JSON.stringify({
          actionMode: input.actionMode,
          bookingIds: input.bookingIds ?? selectedBookingIds,
          amountOverrides: input.overrides ?? batchAmountOverrides
        })
      }),
    onSuccess: async (result) => {
      setBatchResult(result.batch);
      setFeedback("Payment batch processed.");
      setSelectedBookingIds([]);
      setAmountOverrides({});
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["payment-charge-queue"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] })
      ]);
    }
  });

  const holdMutation = useMutation({
    mutationFn: async ({ bookingId, hold }: { bookingId: string; hold: boolean }) =>
      hold
        ? apiFetch(`/api/payments/charge-queue/${bookingId}/hold`, {
            method: "POST",
            body: JSON.stringify({ reason: "Office payment review" })
          })
        : apiFetch(`/api/payments/charge-queue/${bookingId}/hold`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payment-charge-queue"] });
    }
  });

  const sendInvoiceMutation = useMutation({
    mutationFn: async (bookingId: string) =>
      apiFetch(`/api/payments/charge-queue/${bookingId}/send-invoice`, {
        method: "POST"
      }),
    onSuccess: async () => {
      setFeedback("Invoice emailed.");
      await queryClient.invalidateQueries({ queryKey: ["payment-charge-queue"] });
    }
  });

  const statusTone = (status: PaymentChargeQueueItem["paymentStatus"]) =>
    status === "paid" ? "success" : status === "refunded" || status === "on_hold" ? "warning" : status === "partially_paid" ? "info" : "neutral";

  const openRefund = (row: PaymentChargeQueueItem) => {
    setSelectedRefundBookingId(row.id);
    setSelectedRefundPaymentId(row.refundablePaymentId);
    setRefundAmount(centsToDollarInput(row.refundableAmount));
  };

  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow="Payment operations"
        title="Charge Customers"
        description="Select a date range, review bookings, collect balances, send invoices, and audit refunds."
      />
      <SectionJumpNav items={paymentSectionJumpItems} onSelect={(item) => scrollToSectionTarget(item.id)} />
      <div className="stack">
        <Card title="Filters" eyebrow="Date range">
          <div className="charge-queue-filters">
            <Field label="Start date">
              <input type="date" value={startDate} onChange={(event) => { setStartDate(event.target.value); setPage(1); }} />
            </Field>
            <Field label="End date">
              <input type="date" value={endDate} onChange={(event) => { setEndDate(event.target.value); setPage(1); }} />
            </Field>
            <Field label="Status">
              <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}>
                <option value="all">All statuses</option>
                <option value="unpaid">Unpaid</option>
                <option value="partially_paid">Partially paid</option>
                <option value="paid">Paid</option>
                <option value="on_hold">On hold</option>
                <option value="refunded">Refunded</option>
              </select>
            </Field>
            <Field label="Payment method">
              <select value={methodFilter} onChange={(event) => { setMethodFilter(event.target.value); setPage(1); }}>
                <option value="all">All methods</option>
                <option value="card">Card</option>
                <option value="manual">Manual</option>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="paypal">PayPal</option>
                <option value="no_card">Needs card</option>
              </select>
            </Field>
            <Field label="Location">
              <select value={locationId} onChange={(event) => { setLocationId(event.target.value); setPage(1); }}>
                <option value="">All locations</option>
                {(locationsQuery.data?.items ?? []).map((location) => (
                  <option key={String(location.id ?? "")} value={String(location.id ?? "")}>{String(location.name ?? "")}</option>
                ))}
              </select>
            </Field>
            <Field label="Page size">
              <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </Field>
          </div>
        </Card>

        <OpsMetricGrid>
          <OpsMetricCard
            label="Bookings"
            value={chargeQueue.data?.totalItems ?? 0}
            detail="In the selected payment window"
            icon={Receipt}
            tone="blue"
          />
          <OpsMetricCard
            label="Amount due"
            value={formatCurrency(chargeQueue.data?.summary.totalAmountDue ?? 0)}
            detail={`${chargeQueue.data?.summary.chargeableCount ?? 0} can be charged by card`}
            icon={CreditCard}
            tone="violet"
          />
          <OpsMetricCard
            label="Labor hours"
            value={(chargeQueue.data?.summary.totalLaborHours ?? 0).toFixed(1)}
            detail="Visible payroll workload"
            icon={Clock3}
            tone="amber"
          />
          <OpsMetricCard
            label="Est. maid pay"
            value={formatCurrency(chargeQueue.data?.summary.totalEstimatedMaidPay ?? 0)}
            detail="Estimated payout for selected range"
            icon={Banknote}
            tone="emerald"
          />
        </OpsMetricGrid>

        <SectionJumpTarget id="payments-section-ledger">
          <Card
            className="charge-queue-card"
            title={`Payment ledger${chargeQueue.data ? ` (${chargeQueue.data.totalItems})` : ""}`}
            eyebrow={`${formatDateLabel(`${startDate}T12:00:00`)} to ${formatDateLabel(`${endDate}T12:00:00`)}`}
            actions={
              <div className="table-action-row">
                <Button
                  variant="secondary"
                  onClick={() => batchMutation.mutate({ actionMode: "mark_paid" })}
                  disabled={!selectedBookingIds.length || batchMutation.isPending}
                >
                  Mark paid
                </Button>
                <Button
                  onClick={() => batchMutation.mutate({ actionMode: "charge_saved_card" })}
                  disabled={!selectedBookingIds.length || batchMutation.isPending}
                >
                  {batchMutation.isPending ? "Processing..." : "Charge cards"}
                </Button>
              </div>
            }
          >
            {chargeQueue.isLoading ? <div className="page-loading">Loading payments...</div> : null}
            {chargeQueue.error ? <p className="error-text">{(chargeQueue.error as Error).message}</p> : null}
            {feedback ? <p className="card-copy">{feedback}</p> : null}
            {batchMutation.error ? <p className="error-text">{(batchMutation.error as Error).message}</p> : null}
            {sendInvoiceMutation.error ? <p className="error-text">{(sendInvoiceMutation.error as Error).message}</p> : null}
            {holdMutation.error ? <p className="error-text">{(holdMutation.error as Error).message}</p> : null}

          <div className="charge-queue-selectbar">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={(event) => setSelectedBookingIds(event.target.checked ? rows.map((row) => row.id) : [])}
              />
              <span>Select visible bookings</span>
            </label>
            <span>{selectedBookingIds.length} selected</span>
            <span>Selected due: {formatCurrency(selectedRows.reduce((sum, row) => sum + dollarsToCents(amountOverrides[row.id] || centsToDollarInput(row.amountDue)), 0))}</span>
          </div>

          <div className="charge-queue-list">
            {rows.map((row) => {
              const amountValue = amountOverrides[row.id] ?? centsToDollarInput(row.amountDue);
              const cardLabel = row.savedCardLast4
                ? `${row.savedCardBrand || "Card"} - ${row.savedCardLast4}`
                : row.legacyPaymentMethod || "Needs card";
              return (
                <article className={`charge-queue-row charge-queue-row--${row.paymentStatus}`} key={row.id}>
                  <div className="charge-queue-row__select">
                    <input
                      type="checkbox"
                      checked={selectedBookingIds.includes(row.id)}
                      onChange={(event) => toggleRow(row.id, event.target.checked)}
                    />
                  </div>
                  <div className="charge-queue-row__customer">
                    <div className="booking-list-item__top">
                      <strong>{formatDateTimeLabel(row.startsAt)}</strong>
                      <Badge tone={statusTone(row.paymentStatus)}>{row.paymentStatus.replace(/_/g, " ")}</Badge>
                    </div>
                    <Link to={`/admin/bookings/${row.id}`}>{row.customerName || row.publicReference}</Link>
                    <p>{row.serviceAddress}</p>
                    <p>{row.customerPhone || row.customerEmail}</p>
                    {row.paymentHoldReason ? <p className="error-text">Hold: {row.paymentHoldReason}</p> : null}
                  </div>
                  <div className="charge-queue-row__service">
                    <strong>{row.serviceSummary || "Cleaning service"}</strong>
                    <p>Discount: {formatCurrency(row.discountAmount)}</p>
                    <p>Total: {formatCurrency(row.totalAmount)}</p>
                    <p>Paid: {formatCurrency(row.amountPaid)} - Due: {formatCurrency(row.amountDue)}</p>
                  </div>
                  <div className="charge-queue-row__labor">
                    <strong>
                      {row.assignedMaidCount
                        ? `${row.laborHours.toFixed(1)} labor hrs - ${row.assignedMaidCount} maids - ${row.perMaidHours.toFixed(1)} hrs each`
                        : "No maids assigned"}
                    </strong>
                    <p>{row.assignedMaids.map((maid) => maid.name).join(", ") || "Assign maids to calculate payroll."}</p>
                    <p>
                      Maid pay:{" "}
                      {row.estimatedMaidPayAmount == null
                        ? "Pay not configured"
                        : `${formatCurrency(row.estimatedMaidPayAmount)}${row.estimatedMaidPaySource === "imported_team_earnings" ? " imported" : ""}`}
                    </p>
                  </div>
                  <div className="charge-queue-row__payment">
                    <strong>{cardLabel}</strong>
                    {row.paymentStatus === "refunded" ? (
                      <p>Refunded {formatCurrency(row.refundedAmount || row.succeededPaymentAmount)}</p>
                    ) : row.paymentStatus === "paid" ? (
                      <p>Already charged {formatCurrency(row.succeededPaymentAmount)}</p>
                    ) : (
                      <div className="money-input-inline">
                        <span>$</span>
                        <input
                          type="number"
                          min="0.01"
                          max={centsToDollarInput(row.amountDue)}
                          step="0.01"
                          value={amountValue}
                          onChange={(event) => updateRowAmount(row.id, event.target.value)}
                        />
                      </div>
                    )}
                    {row.latestProviderPaymentId || row.latestProviderIntentId ? (
                      <p className="card-copy">{row.latestProviderPaymentId || row.latestProviderIntentId}</p>
                    ) : null}
                  </div>
                  <div className="charge-queue-row__actions">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        batchMutation.mutate({
                          actionMode: "charge_saved_card",
                          bookingIds: [row.id],
                          overrides: { [row.id]: dollarsToCents(amountValue) }
                        });
                      }}
                      disabled={!row.canChargeCard || batchMutation.isPending}
                    >
                      Charge
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        batchMutation.mutate({
                          actionMode: "mark_paid",
                          bookingIds: [row.id],
                          overrides: { [row.id]: dollarsToCents(amountValue) }
                        });
                      }}
                      disabled={!row.canMarkPaid || batchMutation.isPending}
                    >
                      Mark paid
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => holdMutation.mutate({ bookingId: row.id, hold: row.paymentStatus !== "on_hold" })}
                      disabled={holdMutation.isPending}
                    >
                      {row.paymentStatus === "on_hold" ? "Release hold" : "Put on hold"}
                    </Button>
                    <Button variant="ghost" onClick={() => sendInvoiceMutation.mutate(row.id)} disabled={!row.canSendInvoice || sendInvoiceMutation.isPending}>
                      Send invoice
                    </Button>
                    <Button variant="ghost" onClick={() => openRefund(row)} disabled={!row.canRefund || !row.refundablePaymentId}>
                      Refund
                    </Button>
                  </div>
                </article>
              );
            })}
            {!rows.length && !chargeQueue.isLoading ? <p className="card-copy">No bookings match this payment view.</p> : null}
          </div>

          <div className="pagination-row">
            <Button variant="secondary" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1}>Previous</Button>
            <span>Page {chargeQueue.data?.page ?? page} of {chargeQueue.data?.totalPages ?? 1}</span>
            <Button variant="secondary" onClick={() => setPage((current) => current + 1)} disabled={(chargeQueue.data?.page ?? page) >= (chargeQueue.data?.totalPages ?? 1)}>Next</Button>
          </div>
          </Card>
        </SectionJumpTarget>

        <SectionJumpTarget id="payments-section-refund">
          {selectedRefundRow ? (
          <Card
            title={`Refund ${selectedRefundRow.publicReference}`}
            eyebrow={selectedRefundRow.customerName}
            actions={
              <Button
                variant="secondary"
                onClick={() => refundMutation.mutate()}
                disabled={!selectedRefundPaymentId || refundMutation.isPending || refundAmountCents <= 0}
              >
                {refundMutation.isPending ? "Refunding..." : "Issue refund"}
              </Button>
            }
          >
            <div className="grid two">
              <Field label="Refund amount" hint={`Maximum ${formatCurrency(selectedRefundRow.refundableAmount)}.`}>
                <input
                  type="number"
                  min="0.01"
                  max={centsToDollarInput(selectedRefundRow.refundableAmount)}
                  step="0.01"
                  value={refundAmount}
                  onChange={(event) => setRefundAmount(event.target.value)}
                />
              </Field>
              <Field label="Reason">
                <select value={refundReason} onChange={(event) => setRefundReason(event.target.value)}>
                  <option value="requested_by_customer">Requested by customer</option>
                  <option value="duplicate">Duplicate charge</option>
                  <option value="fraudulent">Fraudulent</option>
                </select>
              </Field>
            </div>
            <p className="card-copy">This posts a Stripe refund against the latest payment and refreshes the booking and invoice balances.</p>
            {refundMutation.error ? <p className="error-text">{(refundMutation.error as Error).message}</p> : null}
          </Card>
          ) : (
            <Card title="Refund payment" eyebrow="Payment reversal">
              <p className="card-copy">Choose a refundable booking from the payment ledger to prepare a refund.</p>
            </Card>
          )}
        </SectionJumpTarget>

        {batchResult ? (
          <Card title="Last batch result" eyebrow={batchResult.status}>
            <DataTable
              columns={["Booking", "Customer", "Amount", "Status", "Message"]}
              rows={(batchResult.items ?? []).map((item) => [
                item.publicReference,
                item.customerName,
                formatCurrency(Number(item.amount ?? 0)),
                item.status,
                item.message
              ])}
            />
          </Card>
        ) : null}
      </div>
    </OpsPageShell>
  );
}

function ReportsPage() {
  const revenue = useQuery({ queryKey: ["report-revenue"], queryFn: () => apiFetch<{ items: any[] }>("/api/reports/revenue") });
  const bookings = useQuery({ queryKey: ["report-bookings"], queryFn: () => apiFetch<{ items: any[] }>("/api/reports/bookings") });
  const staff = useQuery({ queryKey: ["report-staff"], queryFn: () => apiFetch<{ items: any[] }>("/api/reports/staff") });
  const marketing = useQuery({ queryKey: ["report-marketing"], queryFn: () => apiFetch<{ items: any[] }>("/api/reports/marketing") });
  const notifications = useQuery({
    queryKey: ["report-notifications"],
    queryFn: () => apiFetch<{ items: any[] }>("/api/reports/notifications")
  });
  const revenueItems = revenue.data?.items ?? [];
  const totalReportedRevenue = revenueItems.reduce((sum, item) => sum + Number(item.total_revenue ?? 0), 0);
  const totalReportedBookings = revenueItems.reduce((sum, item) => sum + Number(item.booking_count ?? 0), 0);
  const bookingStatusTotal = (bookings.data?.items ?? []).reduce((sum, item) => sum + Number(item.total ?? 0), 0);
  const assignedStaffBookings = (staff.data?.items ?? []).reduce((sum, item) => sum + Number(item.assigned_bookings ?? 0), 0);
  const marketingCustomerCount = (marketing.data?.items ?? []).reduce((sum, item) => sum + Number(item.customers ?? 0), 0);
  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow="Business intelligence"
        title="Reports"
        description="Revenue, booking mix, staff productivity, and marketing performance."
      />
      <OpsMetricGrid>
        <OpsMetricCard
          label="Reported revenue"
          value={formatCurrency(totalReportedRevenue)}
          detail="Across loaded revenue buckets"
          icon={TrendingUp}
          tone="emerald"
        />
        <OpsMetricCard
          label="Revenue bookings"
          value={totalReportedBookings}
          detail="Bookings represented in revenue report"
          icon={CalendarDays}
          tone="blue"
        />
        <OpsMetricCard
          label="Booking status rows"
          value={bookingStatusTotal}
          detail="Total bookings in status mix"
          icon={BarChart3}
          tone="violet"
        />
        <OpsMetricCard
          label="Assigned staff bookings"
          value={assignedStaffBookings}
          detail="Workload represented in staff report"
          icon={Users}
          tone="amber"
        />
        <OpsMetricCard
          label="Marketing customers"
          value={marketingCustomerCount}
          detail="Customers grouped by source"
          icon={Megaphone}
          tone="blue"
        />
        <OpsMetricCard
          label="Notification records"
          value={notifications.data?.items?.length ?? 0}
          detail="Loaded delivery log rows"
          icon={BellRing}
          tone="rose"
        />
      </OpsMetricGrid>
      <div className="grid two">
        <Card title="Revenue">
          <DataTable
            columns={["Date", "Revenue", "Bookings"]}
            rows={(revenue.data?.items ?? []).map((item) => [
              item.bucket,
              formatCurrency(Number(item.total_revenue ?? 0)),
              item.booking_count
            ])}
          />
        </Card>
        <Card title="Booking status mix">
          <DataTable
            columns={["Status", "Total"]}
            rows={(bookings.data?.items ?? []).map((item) => [item.status, item.total])}
          />
        </Card>
        <Card title="Staff productivity">
          <DataTable
            columns={["Staff member", "Assigned bookings", "Average rating", "Ratings", "Low ratings"]}
            rows={(staff.data?.items ?? []).map((item) => [
              item.staff_name,
              item.assigned_bookings,
              item.average_rating == null ? "No ratings" : Number(item.average_rating).toFixed(2),
              item.rating_count ?? 0,
              item.low_rating_count ?? 0
            ])}
          />
        </Card>
        <Card title="Marketing sources">
          <DataTable
            columns={["Source", "Customers", "Lifetime value"]}
            rows={(marketing.data?.items ?? []).map((item) => [
              item.name,
              item.customers,
              formatCurrency(Number(item.total_value ?? 0))
            ])}
          />
        </Card>
        <Card title="Notification log">
          <DataTable
            columns={["When", "Event", "Channel", "Recipient", "Status"]}
            rows={(notifications.data?.items ?? []).map((item) => [
              item.bucket,
              String(item.event_key ?? "").replaceAll("_", " "),
              [item.channel, item.recipient_type].filter(Boolean).join(" / "),
              item.recipient,
              item.delivery_status ?? item.status
            ])}
          />
        </Card>
      </div>
    </OpsPageShell>
  );
}







